// playground.ts — the Playground tab: a canvas of nodes wired together, as Node
// Banana, Weavy and Flora are, run on FloorFlow's own engines and material.
// Classic script; it shares the global scope with the pipeline scripts and
// creator.html.
//
// Nodes give an image or a text, and take them in:
//   Image, Prompt, Generate, 4K, Output, Note                    the basics
//   Describe, Improve prompt                                      prompt helpers (Gemini, text)
//   Reference photo, Packshot, Neck label, Saved prompt           FloorFlow's own:
//     a brand's reference sets, the Packshot library, the brands' real neck labels
//     and the prompt manager's prompts.
// Generate runs on the same engines as Packshot and Studio (Gemini, GPT, KIE) with
// the server's keys, and keeps what it has made as versions; 4K is the server's
// upscaler; Output downloads with the AI metadata. A node runs again only when
// what goes into it has changed, so Run does not pay twice for the same picture,
// and the Run button says how many it will make. Workflows are kept in this
// browser (IndexedDB), the current one saved as it is changed, and can be
// exported as a file to give to a colleague.
//
// On the canvas: drag a wire into empty space (or double-click, or press /) to
// add a node; Shift-drag, or the right button held and dragged, to choose several; Ctrl+Z / Ctrl+Shift+Z, Ctrl+C / V / D,
// Delete; Ctrl+Enter runs. ? shows them all.
//
// Node Banana's workflow nodes, ported from its open source (Node Banana 2.0.0,
// MIT, © 2026 William Falloon, github.com/shrimbly/node-banana) on 2026-10-09 at
// Simon's request ("tag alt herfra som vi skal bruge inde i playground"):
//   LLM                                   text from a language model
//   Array, Prompt constructor             a text split into items; a template of @variables
//   Router, Switch, Condition             wires tidied, outputs turned on and off, text routed by rules
//   Gallery                               every picture that comes in
//   Resize, Split grid, Compare, Annotate the image tools, worked out in the browser
//   GIF                                   made in the browser, from pictures
// (Video was taken out on 2026-10-13: "det får vi ikke brugt", and sound on 2026-10-15:
// "fjern det her audio … helt"; workflows that had those nodes open without them.)
// A node can have several outputs, and a value can be a list: what an Array (its
// "Each item") feeds runs once for each item, and so does all that follows it, so a
// Gallery at the end shows them all. Node Banana runs only the first node per item;
// here the whole chain does, and a node already made from the same items is not
// made again.

type PgPort = 'image' | 'text' | 'any';
type PgType = 'image' | 'prompt' | 'savedPrompt' | 'reference' | 'packshot' | 'necktag' | 'generate' | 'upscale' | 'describe' | 'enhance' | 'output' | 'note'
    | 'llm' | 'array' | 'construct' | 'router' | 'switch' | 'condition' | 'gallery'
    | 'resize' | 'split' | 'compare' | 'annotate'
    | 'gif';

interface PgVersion { id: string; at: number; sig: string; meta?: Record<string, string>; thumb?: string; }
interface PgNode {
    id: string;
    type: PgType;
    x: number;
    y: number;
    data: Record<string, any>;
    result?: string | null;     // generate, 4K: the picture made (a data URL); describe, improve: the text written
    resultId?: string;          // generate: which of its versions it is
    versions?: PgVersion[];     // generate: what it has made, newest first; the pictures are in the 'results' store
    sig?: string;               // what the result was made from
    meta?: Record<string, string>;
    status?: 'idle' | 'waiting' | 'running' | 'done' | 'error' | 'skipped';
    message?: string;
    // made once for each item of a list that came in: the pictures (in the 'results'
    // store, by id) or the texts, in the items' order
    listIds?: string[];
    texts?: string[];
}
// out: which of the node's outputs the wire leaves from ('out' when it has one)
interface PgEdge { id: string; from: string; to: string; port: string; out?: string; }
// runCount: how many times Run runs it (Node Banana's Run N times), kept with it
interface PgWorkflow { id: string; name: string; created: number; updated: number; nodes: PgNode[]; edges: PgEdge[]; view: { x: number; y: number; k: number }; runCount?: number; groups?: PgGroup[]; chat?: PgAskMsg[]; }
interface PgInput { name: string; type: PgPort; label: string; multi?: boolean; }
interface PgOutput { name: string; type: PgPort; label?: string; }
// A value: one image or text, or a list of them (items). A list fans out: a node it
// goes into is made once for each item. bundle: a list that goes in as its items
// side by side (a Generate gets them as Image 1, Image 2 …), without fanning out.
interface PgValue { kind: 'image' | 'text'; value: string; key: string; label: string; items?: PgValue[]; bundle?: boolean; }
// runs: makes something with an engine (counted, kept, made again only when changed);
// flows: works out what it gives from what comes in, at once (Array, Router …);
// takesAll: a list that comes in goes in as its items side by side (GIF's pictures)
interface PgDef { title: string; desc: string; icon: string; ours?: boolean; out?: PgPort; outs?: (n: PgNode) => PgOutput[]; ins: PgInput[]; runs?: boolean; flows?: boolean; takesAll?: boolean; width: number; }

const PG_TYPES: Record<PgType, PgDef> = {
    image:       { title: 'Image', desc: 'A picture from your computer, dropped or pasted', icon: 'image', out: 'image', ins: [], width: 220 },
    prompt:      { title: 'Prompt', desc: 'Words for the picture, or one of our templates', icon: 'prompt', out: 'text', ins: [], width: 280 },
    generate:    { title: 'Generate', desc: 'Makes a picture from images and a prompt', icon: 'generate', out: 'image', runs: true, width: 280, ins: [
        { name: 'images', type: 'image', label: 'Images', multi: true },
        { name: 'prompt', type: 'text', label: 'Prompt', multi: true },
    ] },
    upscale:     { title: '4K', desc: 'Makes a picture four times as large', icon: 'upscale', out: 'image', runs: true, width: 220, ins: [{ name: 'image', type: 'image', label: 'Image' }] },
    describe:    { title: 'Describe', desc: 'Writes what is in a picture, as words for a prompt', icon: 'describe', out: 'text', runs: true, width: 280, ins: [{ name: 'image', type: 'image', label: 'Image' }] },
    enhance:     { title: 'Improve prompt', desc: 'Turns a short idea into a full prompt', icon: 'enhance', out: 'text', runs: true, width: 300, ins: [
        { name: 'idea', type: 'text', label: 'Idea', multi: true },
        { name: 'images', type: 'image', label: 'Images', multi: true },
    ] },
    output:      { title: 'Output', desc: 'Shows a picture or text, to look at and download', icon: 'output', ins: [{ name: 'image', type: 'any', label: 'In' }], width: 280 },
    note:        { title: 'Note', desc: 'A note on the canvas, for whoever opens it next', icon: 'note', ins: [], width: 240 },
    reference:   { title: 'Reference photo', desc: 'A photo from a brand\'s reference set', icon: 'reference', ours: true, out: 'image', ins: [], width: 260 },
    packshot:    { title: 'Packshot', desc: 'A picture from the Packshot library', icon: 'packshot', ours: true, out: 'image', ins: [], width: 240 },
    necktag:     { title: 'Neck label', desc: 'A brand\'s real neck label', icon: 'necktag', ours: true, out: 'image', ins: [], width: 220 },
    savedPrompt: { title: 'Saved prompt', desc: 'A prompt from the prompt manager, to change here', icon: 'savedPrompt', ours: true, out: 'text', ins: [], width: 280 },
    // from Node Banana
    llm:         { title: 'LLM', desc: 'Writes text with a language model, from a prompt and images', icon: 'llm', out: 'text', runs: true, width: 300, ins: [
        { name: 'prompt', type: 'text', label: 'Prompt', multi: true },
        { name: 'images', type: 'image', label: 'Images', multi: true },
    ] },
    array:       { title: 'Array', desc: 'Splits a text into items: what it feeds is made once for each', icon: 'array', flows: true, width: 280, ins: [{ name: 'text', type: 'text', label: 'Text' }],
        outs: (n) => [{ name: 'each', type: 'text', label: 'Each item' }, ...pgArrayRead(n).items.slice(0, PG_ARRAY_PORTS).map((it, i) => ({ name: 'i' + i, type: 'text' as PgPort, label: (i + 1) + '. ' + it }))] },
    construct:   { title: 'Prompt constructor', desc: 'A template whose @variables are filled from the texts wired in', icon: 'construct', out: 'text', flows: true, width: 300, ins: [{ name: 'vars', type: 'text', label: 'Variables', multi: true }] },
    router:      { title: 'Router', desc: 'Passes on what comes in, to tidy the wires', icon: 'router', flows: true, width: 180, ins: [{ name: 'in', type: 'any', label: 'In' }],
        outs: (n) => [{ name: 'out', type: n.data.kind || 'any' }] },
    switch:      { title: 'Switch', desc: 'Sends what comes in down the outputs that are on', icon: 'switch', flows: true, width: 240, ins: [{ name: 'in', type: 'any', label: 'In' }],
        outs: (n) => pgSwitches(n).map(s => ({ name: s.id, type: (n.data.kind || 'any') as PgPort, label: s.name })) },
    condition:   { title: 'Condition', desc: 'Sends a text on along the rules it matches, or Otherwise', icon: 'condition', flows: true, width: 300, ins: [{ name: 'text', type: 'text', label: 'Text' }],
        outs: (n) => [...pgRules(n).map(r => ({ name: r.id, type: 'text' as PgPort, label: r.label })), { name: 'else', type: 'text', label: 'Otherwise' }] },
    gallery:     { title: 'Gallery', desc: 'Shows every picture that comes in, to look at and download', icon: 'gallery', width: 300, ins: [{ name: 'images', type: 'image', label: 'Images', multi: true }] },
    resize:      { title: 'Resize', desc: 'Makes a picture a set size: width and height, its longest side, or a percentage', icon: 'resize', out: 'image', flows: true, width: 280, ins: [{ name: 'image', type: 'image', label: 'Image' }] },
    split:       { title: 'Split grid', desc: 'Cuts a picture into a grid, each cell a picture of its own', icon: 'split', flows: true, width: 280, ins: [{ name: 'image', type: 'image', label: 'Image' }],
        outs: (n) => { const [r, c] = pgSplitDims(n); return [{ name: 'each', type: 'image', label: 'Each cell' }, { name: 'all', type: 'image', label: 'All cells, as Image 1, 2 …' },
            ...(r * c <= 16 ? Array.from({ length: r * c }, (_, i) => ({ name: 'c' + i, type: 'image' as PgPort, label: 'Row ' + (Math.floor(i / c) + 1) + ' · column ' + (i % c + 1) })) : [])]; } },
    compare:     { title: 'Compare', desc: 'Two pictures on top of each other, with a slider between them', icon: 'compare', width: 300, ins: [{ name: 'a', type: 'image', label: 'A' }, { name: 'b', type: 'image', label: 'B' }] },
    gif:         { title: 'GIF', desc: 'Makes an animated GIF from pictures', icon: 'gif', out: 'image', runs: true, takesAll: true, width: 280, ins: [
        { name: 'frames', type: 'image', label: 'Pictures', multi: true },
    ] },
    annotate:    { title: 'Annotate', desc: 'Draws boxes, arrows, lines and text on a picture', icon: 'annotate', out: 'image', flows: true, width: 260, ins: [{ name: 'image', type: 'image', label: 'Image' }] },
};
// how the nodes are offered: in the bar, the search and the menus
const PG_GROUPS: { title: string; types: PgType[] }[] = [
    { title: 'Inputs', types: ['image', 'prompt'] },
    { title: 'Make', types: ['generate', 'upscale', 'llm'] },
    { title: 'Prompt tools', types: ['describe', 'enhance', 'construct', 'array'] },
    { title: 'Image tools', types: ['resize', 'split', 'annotate', 'compare', 'gif'] },
    { title: 'Flow', types: ['router', 'switch', 'condition'] },
    { title: 'FloorFlow', types: ['reference', 'packshot', 'necktag', 'savedPrompt'] },
    { title: 'Finish', types: ['output', 'gallery', 'note'] },
];
// Node Banana's text models (its lib/llm/catalog.ts), Gemini through our proxy
const PG_LLM_MODELS: [string, string][] = [
    ['gemini-3.8-flash', 'Gemini 3.8 Flash'], ['gemini-3.7-flash', 'Gemini 3.7 Flash'], ['gemini-3.6-flash', 'Gemini 3.6 Flash'],
    ['gemini-3.5-flash', 'Gemini 3.5 Flash'], ['gemini-3.5-flash-lite', 'Gemini 3.5 Flash Lite'], ['gemini-3.1-pro-preview', 'Gemini 3.1 Pro'],
];
// a workflow kept with a 2.x model (closed, or closing, to new users: 2026-10) uses its 3.x one
const PG_LLM_OLD: Record<string, string> = { 'gemini-2.5-pro': 'gemini-3.1-pro-preview', 'gemini-2.5-flash': 'gemini-3.8-flash', 'gemini-2.5-flash-lite': 'gemini-3.5-flash-lite', 'gemini-2.0-flash': 'gemini-3.8-flash' };
const pgLlmModel = (m: unknown) => { const s = String(m || '') || 'gemini-3.8-flash'; return PG_LLM_OLD[s] || s; };
const PG_LLM_TOKENS = ['512', '1024', '2048', '4096', '8192', '16384'];
// an Array shows an output for each of its first items; Each item gives them all
const PG_ARRAY_PORTS = 12;
const PG_ICONS: Record<string, string> = {
    image: '<rect x="2" y="3" width="12" height="10" rx="1.6"/><circle cx="6" cy="6.6" r="1.2"/><path d="m2.6 11.6 3.1-3.1 2.8 2.8 1.8-1.8 3.1 3.1"/>',
    prompt: '<path d="M3 4.5h10M3 8h10M3 11.5h6"/>',
    generate: '<path d="M8 2.2 9.3 6.7 13.8 8 9.3 9.3 8 13.8 6.7 9.3 2.2 8 6.7 6.7z"/>',
    upscale: '<path d="M9.5 2.5h4v4M13.5 2.5 9.2 6.8M6.5 13.5h-4v-4M2.5 13.5l4.3-4.3"/>',
    describe: '<path d="M1.8 8s2.3-4.4 6.2-4.4S14.2 8 14.2 8s-2.3 4.4-6.2 4.4S1.8 8 1.8 8z"/><circle cx="8" cy="8" r="1.9"/>',
    enhance: '<path d="m2.8 13.2 7.4-7.4M9 4.2l1-1M11.8 7l1-1M11.6 1.9v1.9M14.1 4.4h-1.9"/>',
    output: '<path d="M8 2.5v7.2M5 6.8l3 3 3-3M2.5 10.5v1.8a1.2 1.2 0 0 0 1.2 1.2h8.6a1.2 1.2 0 0 0 1.2-1.2v-1.8"/>',
    note: '<path d="M3.5 2.5h9v7l-4 4h-5z"/><path d="M8.5 13.5v-4h4"/>',
    reference: '<circle cx="8" cy="5.4" r="2.5"/><path d="M3 13.5c.8-2.6 2.7-4 5-4s4.2 1.4 5 4"/>',
    packshot: '<path d="M6 2.5 2.6 4.4l1.2 2.6 1.6-.7v7.2h5.2V6.3l1.6.7 1.2-2.6L10 2.5a2 2 0 0 1-4 0z"/>',
    necktag: '<path d="M2.6 8.3V3.4a.9.9 0 0 1 .9-.9h4.9l5.2 5.2-5.8 5.8z"/><circle cx="5.6" cy="5.6" r="1"/>',
    savedPrompt: '<path d="M4.2 2.5h7.6v11L8 11l-3.8 2.5z"/>',
    llm: '<path d="M2.5 4a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 13.5 4v5.5A1.5 1.5 0 0 1 12 11H7l-3 2.5V11a1.5 1.5 0 0 1-1.5-1.5z"/><path d="M5.5 6.8h5"/>',
    array: '<path d="M4.5 2.5h-2v11h2M11.5 2.5h2v11h-2"/><path d="M6 5.5h4M6 8h4M6 10.5h4"/>',
    construct: '<path d="M2.5 3.5h11M2.5 8h4M9.5 8h4M2.5 12.5h11"/><circle cx="8" cy="8" r="1.3"/>',
    router: '<circle cx="3.5" cy="8" r="1.5"/><circle cx="12.5" cy="8" r="1.5"/><path d="M5 8h6"/>',
    switch: '<rect x="2" y="5" width="12" height="6" rx="3"/><circle cx="11" cy="8" r="1.6"/>',
    condition: '<path d="M2.5 8h4l3-4h4M6.5 8l3 4h4"/>',
    gallery: '<rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1"/><rect x="9" y="2.5" width="4.5" height="4.5" rx="1"/><rect x="2.5" y="9" width="4.5" height="4.5" rx="1"/><rect x="9" y="9" width="4.5" height="4.5" rx="1"/>',
    plus: '<path d="M8 3v10M3 8h10"/>',
    resize: '<path d="M2.5 6V2.5H6M13.5 10v3.5H10"/><rect x="5" y="5" width="6" height="6" rx="1"/>',
    lock: '<rect x="3.5" y="7" width="9" height="6.5" rx="1.2"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/>',
    unlock: '<rect x="3.5" y="7" width="9" height="6.5" rx="1.2"/><path d="M5.5 7V5a2.5 2.5 0 0 1 4.9-.7"/>',
    trash: '<path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.6 4.5l.6 8.3a1 1 0 0 0 1 .9h3.6a1 1 0 0 0 1-.9l.6-8.3"/>',
    gif: '<rect x="1.8" y="3.5" width="12.4" height="9" rx="1.5"/><path d="M6.2 6.6H5a1.4 1.4 0 0 0 0 2.8h1.2V8M8.2 6.6v2.8M10.2 9.4V6.6h1.8M10.2 8h1.4"/>',
    split: '<rect x="2.5" y="2.5" width="11" height="11" rx="1.2"/><path d="M8 2.5v11M2.5 8h11"/>',
    annotate: '<path d="M10.5 2.8 13.2 5.5 6 12.7l-3.3.6.6-3.3z"/><path d="M9 4.3 11.7 7"/>',
    run: '<path d="M5 3.5v9l7.5-4.5z" fill="currentColor" stroke="none"/>',
    stop: '<rect x="4" y="4" width="8" height="8" rx="1.5" fill="currentColor" stroke="none"/>',
    send: '<path d="M2.5 8h10M8.5 4l4 4-4 4"/>',
    check: '<path d="m3.5 8.5 3 3 6-6.5"/>',
    close: '<path d="M4 4l8 8M12 4l-8 8"/>',
    copy: '<rect x="5.5" y="5.5" width="8" height="8" rx="1.5"/><path d="M10.5 5.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5"/>',
    expand: '<path d="M9.5 2.5h4v4M6.5 13.5h-4v-4M13.5 2.5 9.5 6.5M2.5 13.5l4-4"/>',
    download: '<path d="M8 2.5v8M4.8 7.6 8 10.8l3.2-3.2M3 13.5h10"/>',
    prev: '<path d="M10 3 5 8l5 5"/>',
    next: '<path d="m6 3 5 5-5 5"/>',
    compare: '<path d="M8 2v12M2.5 4.5h3M2.5 8h3M2.5 11.5h3"/><rect x="10" y="3.5" width="3.5" height="9" rx="1"/>',
    search: '<circle cx="7" cy="7" r="4.5"/><path d="m10.3 10.3 3.2 3.2"/>',
    keys: '<rect x="1.5" y="4" width="13" height="8" rx="1.5"/><path d="M4 6.5h.01M6.5 6.5h.01M9 6.5h.01M11.5 6.5h.01M5 9.5h6"/>',
    undo: '<path d="M5.5 3.5 2.5 6.5l3 3"/><path d="M2.8 6.5H10a3.5 3.5 0 0 1 0 7H7"/>',
    redo: '<path d="m10.5 3.5 3 3-3 3"/><path d="M13.2 6.5H6a3.5 3.5 0 0 0 0 7h3"/>',
};
const pgIcon = (name: string, cls = '') => '<svg class="pg-ico' + (cls ? ' ' + cls : '') + '" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (PG_ICONS[name] || '') + '</svg>';

const PG_MODELS = [{ id: 'gemini', label: 'Gemini 3 Pro' }, { id: 'openai', label: 'GPT Image' }, { id: 'kie', label: 'KIE (Gemini)' }];
const PG_FORMATS = ['1:1', '4:5', '3:4', '2:3', '9:16', '16:9'];
const PG_SIZES = ['1k', '2k', '4k'];
const PG_COUNTS = ['1', '2', '3', '4'];
// the most images each engine takes in one go
const PG_MAX_IMAGES: Record<string, number> = { gemini: 14, kie: 8, openai: 16 };
// what a picture costs, about, in USD (the engines' list prices): shown on Run
const PG_PRICE: Record<string, Record<string, number>> = {
    gemini: { '1k': 0.134, '2k': 0.134, '4k': 0.24 },
    kie: { '1k': 0.09, '2k': 0.09, '4k': 0.12 },
    openai: { '1k': 0.17, '2k': 0.17, '4k': 0.17 },
};
const PG_TEXT_MODELS = ['gemini-3.1-pro-preview', 'gemini-3.8-flash'];
const PG_SLOTS: Record<string, string> = { 'packshot-front': 'Packshot front', 'packshot-back': 'Packshot back', 'model-front': 'Model front', 'model-back': 'Model back' };
const PG_CURRENT_KEY = 'creator.playground.current';
const PG_KEEP_VERSIONS = 30;

const pg = {
    wf: null as PgWorkflow | null,
    el: {} as Record<string, HTMLElement>,
    sel: new Set<string>(),          // the chosen nodes
    edgeSel: '',                     // or a chosen wire
    pic: '',                         // or an Image whose picture is chosen: Del deletes the picture, not the node
    drag: null as null | Record<string, any>,
    noCtx: 0,             // when a right-button box ended: the menu that follows is not opened
    running: false,
    saveTimer: 0 as any,
    refs: null as null | any[],      // the reference sets, from /api/references
    packshots: null as null | any[], // the Packshot library, light
    tagUrls: {} as Record<string, string>,   // the neck labels, shown
    ready: false,
    needFit: false,                  // a fit asked for while the tab was hidden, done when it shows
    // motion: what was just made (its picture fades in), the wires a finished
    // node sends a pulse along, a node that just failed (it shakes), a new node
    fresh: new Set<string>(),
    pulse: new Map<string, number>(),
    shake: new Set<string>(),
    pop: new Set<string>(),
    made: new Set<string>(),         // the nodes made in this run
    since: new Map<string, number>(), // when each running node started
    queued: [] as (string[] | 'all')[], // runs asked for while one went on
    stopping: false,                 // Stop pressed: nothing new is started
    runOf: null as null | { i: number; times: number },   // which of several runs this is
    // changes, to undo and redo; what was copied; where the pointer is on the canvas
    undo: [] as { nodes: PgNode[]; edges: PgEdge[]; groups?: PgGroup[]; sel: string[] }[],
    redo: [] as { nodes: PgNode[]; edges: PgEdge[]; groups?: PgGroup[]; sel: string[] }[],
    typing: '',                      // the node whose text is being typed: one undo step for it
    clip: null as null | { nodes: PgNode[]; edges: PgEdge[] },
    pastes: 0,
    pointer: { x: 0, y: 0 },         // in the world's own pixels
    lb: null as null | { node: string; lib?: boolean; items: { id?: string; src?: string; thumb?: string; label: string }[]; index: number; compare: string; split: number },
};

const pgId = (p: string) => p + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const pgEsc = (s: any) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);
const pgNode = (id: string) => pg.wf!.nodes.find(n => n.id === id) || null;
const pgCard = (id: string) => pg.el.world.querySelector('.pg-node[data-id="' + id + '"]') as HTMLElement | null;
function pgHash(s: string): string {
    let h = 5381;
    for (let i = 0; i < s.length; i += Math.max(1, Math.floor(s.length / 4000))) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36) + ':' + s.length;
}
const pgBlobToDataUrl = (b: Blob) => new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = () => reject(r.error); r.readAsDataURL(b); });
async function pgFileOf(dataUrl: string, name: string): Promise<File> {
    const blob = await (await fetch(dataUrl)).blob();
    return new File([blob], name, { type: blob.type || 'image/png' });
}
// a small copy of a picture, for the versions and lists
async function pgThumb(src: string, size = 160): Promise<string> {
    try {
        const bmp = await createImageBitmap(await (await fetch(src)).blob());
        const k = size / Math.max(bmp.width, bmp.height);
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(bmp.width * k));
        c.height = Math.max(1, Math.round(bmp.height * k));
        c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
        return c.toDataURL('image/jpeg', 0.82);
    } catch { return ''; }
}
const pgEditable = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"]');

// ── Keeping workflows (IndexedDB) ──
// 'floorflow-playground' keeps the workflows; 'floorflow-playground-results' every
// picture a Generate has made, by id, so its versions can be gone back to without
// the workflow carrying them all. Each is opened at whatever version it has: asking
// for a newer one would wait for every other open tab of the app to let go of it.
const __pgDbs: Record<string, Promise<IDBDatabase> | null> = {};
function pgOpenDb(name: string, store: string, index?: string): Promise<IDBDatabase> {
    if (!__pgDbs[name]) __pgDbs[name] = new Promise((resolve, reject) => {
        const req = indexedDB.open(name);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(store)) { const s = db.createObjectStore(store, { keyPath: 'id' }); if (index) s.createIndex(index, index); }
        };
        req.onsuccess = () => {
            const db = req.result;
            // another tab with a newer app asks for it: let go, and open it again when next needed
            db.onversionchange = () => { db.close(); __pgDbs[name] = null; };
            if (!db.objectStoreNames.contains(store)) {
                // made before this store was: added in a version of its own
                const v = db.version + 1;
                db.close();
                const up = indexedDB.open(name, v);
                up.onupgradeneeded = () => { const s = up.result.createObjectStore(store, { keyPath: 'id' }); if (index) s.createIndex(index, index); };
                up.onsuccess = () => { up.result.onversionchange = () => { up.result.close(); __pgDbs[name] = null; }; resolve(up.result); };
                up.onerror = () => { __pgDbs[name] = null; reject(up.error); };
                up.onblocked = () => { __pgDbs[name] = null; reject(new Error('Another tab keeps the Playground store open')); };
                return;
            }
            resolve(db);
        };
        req.onerror = () => { __pgDbs[name] = null; reject(req.error); };
    });
    return __pgDbs[name]!;
}
// The workflows are kept in a store of their own since 2026-10-08. The first one,
// 'floorflow-playground', could be held up by an open tab of an older build (v2 asked
// it for a new version, and the browser lets nothing else in while that waits): the
// Playground no longer waits for it, and copies what is in it when it can be read.
const PG_DB = 'floorflow-playground-workflows';
const PG_OLD_DB = 'floorflow-playground';
const PG_MIGRATED_KEY = 'creator.playground.migrated';
const pgDb = () => pgOpenDb(PG_DB, 'workflows');
function pgReadOldWorkflows(): Promise<PgWorkflow[] | null> {
    return new Promise(resolve => {
        let req: IDBOpenDBRequest;
        try { req = indexedDB.open(PG_OLD_DB); } catch { resolve(null); return; }
        req.onsuccess = () => {
            const db = req.result;
            db.onversionchange = () => db.close();
            if (!db.objectStoreNames.contains('workflows')) { db.close(); resolve([]); return; }
            const r = db.transaction('workflows', 'readonly').objectStore('workflows').getAll();
            r.onsuccess = () => { db.close(); resolve(r.result as PgWorkflow[]); };
            r.onerror = () => { db.close(); resolve(null); };
        };
        req.onerror = () => resolve(null);
    });
}
// The old workflows into the new store, once; how many came over
async function pgMigrateOld(): Promise<number> {
    try { if (localStorage.getItem(PG_MIGRATED_KEY)) return 0; } catch { /* private */ }
    const done = () => { try { localStorage.setItem(PG_MIGRATED_KEY, '1'); } catch { /* private */ } };
    if (typeof indexedDB.databases === 'function') {
        try { if (!(await indexedDB.databases()).some(d => d.name === PG_OLD_DB)) { done(); return 0; } } catch { /* read it anyway */ }
    }
    const old = await pgReadOldWorkflows();
    if (!old) return 0;
    const have = new Set((await pgListWorkflows()).map(w => w.id));
    let n = 0;
    for (const w of old) if (w && w.id && !have.has(w.id)) { await pgStore('readwrite', st => st.put(w)); n++; }
    done();
    return n;
}
// A workflow not touched yet: the starter as it came
const pgPristine = (wf: PgWorkflow | null) => !!wf && !pg.undo.length && wf.nodes.every(n => !n.result && !n.data.src && !String(n.data.text || '').trim() && !n.data.entry && !n.data.set && !n.data.brand);
async function pgStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>, store = 'workflows'): Promise<T> {
    const db = store === 'results' ? await pgOpenDb('floorflow-playground-results', 'results', 'wf') : await pgDb();
    return new Promise((resolve, reject) => {
        const r = fn(db.transaction(store, mode).objectStore(store));
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
    });
}
const pgListWorkflows = async () => ((await pgStore('readonly', s => s.getAll())) as PgWorkflow[]).sort((a, b) => b.updated - a.updated);
const pgGetWorkflow = (id: string) => pgStore('readonly', s => s.get(id)) as Promise<PgWorkflow | undefined>;
async function pgDeleteWorkflow(id: string) {
    await pgStore('readwrite', s => s.delete(id));
    // and the pictures its Generates made
    try {
        const keys = await pgStore('readonly', s => s.index('wf').getAllKeys(id), 'results') as IDBValidKey[];
        for (const k of keys) await pgStore('readwrite', s => s.delete(k), 'results');
    } catch { /* kept */ }
}
const pgResultGet = (id: string) => pgStore('readonly', s => s.get(id), 'results').catch(() => undefined) as Promise<{ id: string; src: string } | undefined>;
const pgResultPut = (rec: { id: string; wf: string; node: string; src: string; at: number }) => pgStore('readwrite', s => s.put(rec), 'results');
const pgResultDelete = (id: string) => pgStore('readwrite', s => s.delete(id), 'results').catch(() => undefined);

function pgSaveSoon() {
    if (!pg.wf) return;
    pg.wf.updated = Date.now();
    clearTimeout(pg.saveTimer);
    pgSetSaved('Saving…');
    pg.saveTimer = setTimeout(pgSaveNow, 500);
    pgPaintPlan();
}
async function pgSaveNow() {
    if (!pg.wf) return;
    clearTimeout(pg.saveTimer);
    try {
        // the running state is not kept: a node running when the page goes is not running after it
        const copy = JSON.parse(JSON.stringify(pg.wf, (k, v) => k === 'result' && typeof v === 'string' && /^data:(video|audio)\//.test(v) ? undefined : v)) as PgWorkflow;
        copy.nodes.forEach(n => { if (n.status === 'running' || n.status === 'waiting') { n.status = 'idle'; n.message = ''; } });
        await pgStore('readwrite', s => s.put(copy));
        try { localStorage.setItem(PG_CURRENT_KEY, pg.wf.id); } catch { /* private */ }
        pgSetSaved('Saved ' + new Date(pg.wf.updated).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }));
    } catch (e) {
        pgSetSaved('Not saved');
        console.warn('playground: saving the workflow', e);
    }
}
function pgSetSaved(text: string) { const s = document.getElementById('pgSaved'); if (s) s.textContent = text; }

// ── Undo and redo ──
// A step is the nodes and wires as they were; the pictures in them are shared, not
// copied, so a step costs little.
function pgSnap() {
    return {
        nodes: pg.wf!.nodes.map(n => ({ ...n, data: { ...n.data, vars: n.data.vars ? { ...n.data.vars } : undefined }, meta: n.meta ? { ...n.meta } : n.meta, versions: n.versions ? [...n.versions] : n.versions, listIds: n.listIds ? [...n.listIds] : n.listIds, texts: n.texts ? [...n.texts] : n.texts })),
        edges: pg.wf!.edges.map(e => ({ ...e })),
        groups: pgGroups().map(g => ({ ...g, nodes: [...g.nodes] })),
        sel: [...pg.sel],     // what was chosen comes back with it
    };
}
function pgRecord() {
    if (!pg.wf) return;
    pg.undo.push(pgSnap());
    if (pg.undo.length > 80) pg.undo.shift();
    pg.redo = [];
    pgPaintUndo();
}
function pgUndoRedo(back: boolean) {
    if (!pg.wf) return;
    const from = back ? pg.undo : pg.redo, to = back ? pg.redo : pg.undo;
    if (!from.length) return;
    if (pg.running) { showToast('Wait for the run to finish, then undo.', { type: 'warn', title: 'Running' }); return; }
    to.push(pgSnap());
    const s = from.pop()!;
    pg.wf.nodes = s.nodes;
    pg.wf.edges = s.edges;
    pg.wf.groups = s.groups || [];
    pg.sel = new Set(s.sel.filter(id => pgNode(id)));
    if (pg.edgeSel && !pg.wf.edges.some(e => e.id === pg.edgeSel)) pg.edgeSel = '';
    pg.typing = '';
    pgRenderAll();
    pgSaveSoon();
    pgPaintUndo();
}
function pgPaintUndo() {
    const u = document.getElementById('pgUndo') as HTMLButtonElement | null, r = document.getElementById('pgRedo') as HTMLButtonElement | null;
    if (u) u.disabled = !pg.undo.length;
    if (r) r.disabled = !pg.redo.length;
}

// ── New workflows: the starter, and the templates ──
function pgMakeNode(type: PgType, x: number, y: number, data: Record<string, any> = {}): PgNode {
    const base: Record<string, any> = type === 'generate' ? { model: 'gemini', aspect: '4:5', size: '2k', count: '1' }
        : type === 'prompt' || type === 'savedPrompt' || type === 'note' ? { text: '' }
        : type === 'describe' ? { focus: 'garment' }
        : type === 'llm' ? { model: 'gemini-3.8-flash', temperature: '0.7', maxTokens: '8192' }
        : type === 'array' ? { text: '', mode: 'newline', delimiter: ',', regex: '', trim: true, dropEmpty: true }
        : type === 'construct' ? { template: '' }
        : type === 'switch' ? { switches: [{ id: pgId('s'), name: 'Output 1', on: true }] }
        : type === 'condition' ? { rules: [{ id: pgId('r'), label: 'Rule 1', mode: 'contains', value: '' }] }
        : type === 'resize' ? { mode: 'exact', width: '1024', height: '1024', fit: 'contain', maxEdge: '2048', scale: '50', format: 'keep', quality: '0.9' }
        : type === 'split' ? { rows: '2', cols: '3' }
        : type === 'annotate' ? { shapes: [] }
        : type === 'gif' ? { fps: '8', colours: '128', width: '512', maxKb: '' }
        : {};
    return { id: pgId('n'), type, x, y, data: { ...base, ...data } };
}
function pgWorkflowOf(name: string, nodes: PgNode[], wires: [PgNode, PgNode, string][]): PgWorkflow {
    const now = Date.now();
    return {
        id: pgId('wf'), name, created: now, updated: now, view: { x: 0, y: 0, k: 0 },   // k 0: fitted when first seen
        nodes, edges: wires.map(([a, b, port]) => ({ id: pgId('e'), from: a.id, to: b.id, port })),
    };
}
// A new workflow starts as the simplest one that makes a picture: an image and a
// prompt into Generate, and Generate into Output.
function pgNewWorkflow(name = 'Untitled workflow'): PgWorkflow {
    const img = pgMakeNode('image', 80, 60), prm = pgMakeNode('prompt', 80, 330);
    const gen = pgMakeNode('generate', 420, 140), out = pgMakeNode('output', 780, 140);
    return pgWorkflowOf(name, [img, prm, gen, out], [[img, gen, 'images'], [prm, gen, 'prompt'], [gen, out, 'image']]);
}

// ── What a node gives ──
// The images that go into a Generate come in the order their nodes stand, from the
// top: that order is Image 1, Image 2 … for the prompt, and the node lists it.
// Several texts into one input are put together in the same order.
function pgInEdges(nodeId: string, port: string): PgEdge[] {
    return pg.wf!.edges.filter(e => e.to === nodeId && e.port === port)
        .sort((a, b) => ((pgNode(a.from)?.y ?? 0) - (pgNode(b.from)?.y ?? 0)) || ((pgNode(a.from)?.x ?? 0) - (pgNode(b.from)?.x ?? 0)));
}

// ── Outputs, and what goes along a wire ──
// A node has one output ('out'), or several (an Array's items, a Switch's outputs, a
// Condition's rules). A Router or Switch takes the kind of what is wired to it.
const pgOuts = (n: PgNode): PgOutput[] => { const t = PG_TYPES[n.type]; return t.outs ? t.outs(n) : t.out ? [{ name: 'out', type: t.out }] : []; };
const pgOutDef = (n: PgNode, name = 'out') => pgOuts(n).find(o => o.name === name) || null;
const pgOutKind = (n: PgNode, name = 'out'): PgPort => pgOutDef(n, name)?.type || PG_TYPES[n.type].out || 'any';
const pgInKind = (n: PgNode, inp: PgInput): PgPort => inp.type === 'any' ? (n.data.kind || 'any') : inp.type;
const pgFits = (a: PgPort, b: PgPort) => a === b || a === 'any' || b === 'any';
const pgTakesKind = (n: PgNode) => n.type === 'router' || n.type === 'switch';

interface PgSwitch { id: string; name: string; on: boolean; }
interface PgRule { id: string; label: string; mode: string; value: string; }
const pgSwitches = (n: PgNode): PgSwitch[] => Array.isArray(n.data.switches) && n.data.switches.length ? n.data.switches : [{ id: 's1', name: 'Output 1', on: true }];
const pgRules = (n: PgNode): PgRule[] => Array.isArray(n.data.rules) ? n.data.rules : [];
// Node Banana's rules (utils/ruleEvaluation.ts): both sides lower-cased and trimmed,
// several values split by commas, any one of them matching; an empty text matches none
function pgRuleMatches(r: PgRule, text: string): boolean {
    const t = String(text || '').trim().toLowerCase();
    if (!t) return false;
    return String(r.value || '').split(',').map(v => v.trim().toLowerCase()).filter(Boolean)
        .some(v => r.mode === 'is' ? t === v : r.mode === 'starts' ? t.startsWith(v) : r.mode === 'ends' ? t.endsWith(v) : t.includes(v));
}
// the outputs of a Condition a text goes on along: the rules it matches, else Otherwise
function pgOpenRules(n: PgNode, text: string): Set<string> {
    const open = new Set(pgRules(n).filter(r => pgRuleMatches(r, text)).map(r => r.id));
    if (!open.size) open.add('else');
    return open;
}
// Node Banana's Array (utils/arrayParser.ts): by a delimiter, by line or by a pattern,
// each item trimmed and the empty ones dropped (both can be turned off)
function pgArrayItems(d: Record<string, any>, text: string | null): { items: string[]; error: string } {
    const src = String(text ?? d.text ?? '');
    if (src.length > 100000) return { items: [], error: 'The text is too long to split (over 100,000 characters)' };
    let parts: string[];
    const mode = d.mode || 'newline';
    if (mode === 'newline') parts = src.split(/\r?\n/);
    else if (mode === 'regex') {
        const raw = String(d.regex || '');
        if (!raw) parts = [src];
        else {
            if (raw.length > 100) return { items: [], error: 'The pattern is longer than 100 characters' };
            // a repeat of a repeat, as (a+)+, can take the page down on a long text
            if (/\((?:[^()\\]|\\.)*[+*}](?:[^()\\]|\\.)*\)\s*[+*{]/.test(raw)) return { items: [], error: 'The pattern repeats a repeat, which can hang the page' };
            const m = /^\/(.*)\/([a-z]*)$/i.exec(raw);
            try { parts = src.split(m ? new RegExp(m[1], m[2].replace(/g/g, '')) : new RegExp(raw)); }
            catch (e) { return { items: [], error: 'The pattern is not valid: ' + (e as Error).message }; }
        }
    } else {
        const del = String(d.delimiter ?? '*');
        parts = del ? src.split(del) : [src];
    }
    if (d.trim !== false) parts = parts.map(p => p.trim());
    if (d.dropEmpty !== false) parts = parts.filter(p => p.length > 0);
    return { items: parts, error: '' };
}
// A Prompt constructor's @variables: each text wired in has a name (kept on the node
// it comes from, as Node Banana's Prompt keeps its variable name)
const PG_VAR = /@(\w+)/g;
const pgVarSafe = (s: string) => String(s || '').replace(/[^A-Za-z0-9_]/g, '').slice(0, 30);
function pgVarName(src: PgNode, into: PgNode): string {
    if (src.data.varName) return src.data.varName;
    const i = pgInEdges(into.id, 'vars').findIndex(e => e.from === src.id);
    return 'text' + (i + 1);
}
// Node Banana fills them in with one pass (its node does; its runner's replaceAll
// let @color fill the start of @colorful): a name it has not got stays as written
function pgFillVars(template: string, vars: Map<string, string>): { text: string; missing: string[] } {
    const missing: string[] = [];
    const text = String(template || '').replace(PG_VAR, (m, name) => {
        if (vars.has(name)) return vars.get(name)!;
        if (!missing.includes(name)) missing.push(name);
        return m;
    });
    return { text, missing };
}

const pgTextValue = (text: string, label: string): PgValue => ({ kind: 'text', value: text, key: 'txt:' + pgHash(text), label });
const pgListKey = (keys: string[]) => 'list:' + pgHash(keys.join('|'));
// several values as one: a list (one item is just that item)
function pgList(items: PgValue[], label: string, key?: string): PgValue | null {
    if (!items.length) return null;
    if (items.length === 1 && !key) return items[0];
    return { kind: items[0].kind, value: items[0].value, key: key || pgListKey(items.map(i => i.key)), label, items };
}

// What comes along a wire, said at once without running anything: the key of what
// comes (as the run will have it), the texts when it is text, and how many items
// when it is a list. coming: a node before it will make it, so it is not known yet.
interface PgPeek { key: string | null; texts?: string[]; n?: number; coming?: boolean; }
const PG_NONE: PgPeek = { key: null };
const pgPeekEdge = (e: PgEdge, stale?: (id: string) => boolean): PgPeek => {
    const src = pgNode(e.from);
    return src ? pgPeekOut(src, e.out || 'out', stale) : PG_NONE;
};
function pgPeekTexts(texts: string[]): PgPeek {
    if (!texts.length) return PG_NONE;
    if (texts.length === 1) return { key: 'txt:' + pgHash(texts[0]), texts };
    return { key: pgListKey(texts.map(t => 'txt:' + pgHash(t))), texts, n: texts.length };
}
function pgPeekOut(n: PgNode, out: string, stale?: (id: string) => boolean): PgPeek {
    const t = PG_TYPES[n.type];
    if (t.runs) {
        if (stale && stale(n.id)) return { key: null, coming: true };
        const key = pgKeyOf(n);
        if (!key) return PG_NONE;
        if (t.out === 'text') { const texts = n.texts && n.texts.length > 1 ? n.texts : [n.result || '']; return { key, texts, n: texts.length > 1 ? texts.length : undefined }; }
        const many = (n.listIds || []).length;
        return { key, n: many > 1 ? many : undefined };
    }
    if (!t.flows) {
        const key = pgKeyOf(n);
        if (!key) return PG_NONE;
        return t.out === 'text' ? { key, texts: [pgTextOf(n)] } : { key };
    }
    const first = (port: string) => { const e = pgInEdges(n.id, port)[0]; return e ? pgPeekEdge(e, stale) : PG_NONE; };
    switch (n.type) {
        case 'router': return first('in');
        case 'switch': { const s = pgSwitches(n).find(x => x.id === out); return s && s.on ? first('in') : PG_NONE; }
        case 'condition': {
            const v = first('text');
            if (!v.key || v.coming) return v;
            const texts = v.texts || [];
            const kept = texts.filter(x => pgOpenRules(n, x).has(out));
            if (!kept.length) return PG_NONE;
            return v.n ? pgPeekTexts(kept) : v;   // one text goes on as it came
        }
        case 'array': {
            const r = pgArrayRead(n, stale);
            if (r.coming) return { key: null, coming: true };
            if (out === 'each') return pgPeekTexts(r.items);
            const it = r.items[Number(out.slice(1))];
            return it !== undefined ? pgPeekTexts([it]) : PG_NONE;
        }
        case 'construct': {
            const r = pgConstructRead(n, stale);
            if (r.coming) return { key: null, coming: true };
            return r.texts.some(x => x.trim()) ? pgPeekTexts(r.texts) : PG_NONE;
        }
        case 'resize':
        case 'annotate': {
            const own = n.type === 'annotate' && !pgInEdges(n.id, 'image').length;
            const v = own ? (pgAnnotateOwn(n) ? { key: pgAnnotateOwn(n)!.key } as PgPeek : PG_NONE) : first('image');
            if (!v.key || v.coming) return v;
            return { key: (n.type === 'resize' ? 'rs:' : 'an:') + pgHash(pgToolSettings(n) + '|' + v.key), n: v.n };
        }
        case 'split': {
            const v = first('image');
            if (!v.key || v.coming) return v;
            const [r, c] = pgSplitDims(n);
            const base = 'sg:' + pgHash(r + 'x' + c + '|' + v.key);
            if (out === 'each') { const total = r * c * (v.n || 1); return { key: base + ':each', n: total > 1 ? total : undefined }; }
            if (out === 'all') return { key: base + ':all' };
            return { key: base + ':' + out, n: v.n };
        }
    }
    return PG_NONE;
}
// what an Array splits: what is wired in (its texts as lines), else its own text
function pgArrayRead(n: PgNode, stale?: (id: string) => boolean): { items: string[]; error: string; coming: boolean; wired: boolean } {
    const e = pgInEdges(n.id, 'text')[0];
    if (!e) return { ...pgArrayItems(n.data, null), coming: false, wired: false };
    const v = pgPeekEdge(e, stale);
    if (v.coming) return { items: [], error: '', coming: true, wired: true };
    return { ...pgArrayItems(n.data, (v.texts || []).join('\n')), coming: false, wired: true };
}
// what a Prompt constructor writes: its template with the texts wired in; a list
// wired in makes a list, one text for each of its items
function pgConstructFill(n: PgNode, vars: Map<string, string[]>): { texts: string[]; missing: string[] } {
    const many = Math.max(1, ...[...vars.values()].map(v => v.length));
    const texts: string[] = [];
    let missing: string[] = [];
    for (let i = 0; i < many; i++) {
        const r = pgFillVars(n.data.template || '', new Map([...vars].filter(([, v]) => v.length).map(([k, v]) => [k, v[i % v.length]])));
        texts.push(r.text);
        missing = r.missing;
    }
    return { texts, missing };
}
function pgConstructRead(n: PgNode, stale?: (id: string) => boolean): { texts: string[]; missing: string[]; coming: boolean; names: string[] } {
    const vars = new Map<string, string[]>();
    let coming = false;
    for (const e of pgInEdges(n.id, 'vars')) {
        const src = pgNode(e.from);
        if (!src) continue;
        const v = pgPeekEdge(e, stale);
        if (v.coming) coming = true;
        vars.set(pgVarName(src, n), v.key && v.texts ? v.texts : []);
    }
    return { ...pgConstructFill(n, vars), coming, names: [...vars.keys()] };
}

// The value along a wire during a run (or to show it): the makers before it made
// first by make (a no-op when only showing what there is), what flows worked out.
type PgMakeFn = (id: string) => Promise<void>;
const PG_SHOW: PgMakeFn = async () => {};
async function pgValueAlong(e: PgEdge, make: PgMakeFn): Promise<PgValue | null> {
    const src = pgNode(e.from);
    return src ? pgValueOut(src, e.out || 'out', make) : null;
}
async function pgValueOut(n: PgNode, out: string, make: PgMakeFn): Promise<PgValue | null> {
    const t = PG_TYPES[n.type];
    if (t.runs) {
        await make(n.id);
        if (make !== PG_SHOW && n.status === 'error') throw new Error('“' + t.title + '” before it could not be made');
        // skipped (a switch before it is off): nothing goes on from it either
        if (make !== PG_SHOW && n.status === 'skipped') return null;
        return pgMadeValue(n);
    }
    if (!t.flows) return pgValueOf(n);
    const first = async (port: string) => { const e = pgInEdges(n.id, port)[0]; return e ? pgValueAlong(e, make) : null; };
    const texts = (v: PgValue | null) => v ? (v.items ? v.items.map(i => i.value) : [v.value]) : [];
    switch (n.type) {
        case 'router': return first('in');
        case 'switch': { const s = pgSwitches(n).find(x => x.id === out); return s && s.on ? first('in') : null; }
        case 'condition': {
            const v = await first('text');
            if (!v) return null;
            if (!v.items) return pgOpenRules(n, v.value).has(out) ? v : null;
            return pgList(v.items.filter(it => pgOpenRules(n, it.value).has(out)), t.title);
        }
        case 'array': {
            const e = pgInEdges(n.id, 'text')[0];
            const r = pgArrayItems(n.data, e ? texts(await pgValueAlong(e, make)).join('\n') : null);
            if (r.error) throw new Error(r.error);
            if (out === 'each') return pgList(r.items.map((x, i) => pgTextValue(x, 'Item ' + (i + 1))), t.title);
            const i = Number(out.slice(1));
            return r.items[i] !== undefined ? pgTextValue(r.items[i], 'Item ' + (i + 1)) : null;
        }
        case 'construct': {
            const vars = new Map<string, string[]>();
            for (const e of pgInEdges(n.id, 'vars')) {
                const src = pgNode(e.from);
                if (src) vars.set(pgVarName(src, n), texts(await pgValueAlong(e, make)));
            }
            const r = pgConstructFill(n, vars);
            if (!r.texts.some(x => x.trim())) return null;
            return pgList(r.texts.map(x => pgTextValue(x, t.title)), t.title);
        }
        case 'resize':
        case 'annotate': {
            const own = n.type === 'annotate' && !pgInEdges(n.id, 'image').length;
            const v = own ? pgAnnotateOwn(n) : await first('image');
            if (!v) return null;
            const pre = n.type === 'resize' ? 'rs:' : 'an:', set = pgToolSettings(n);
            const made: PgValue[] = [];
            for (const it of v.items || [v]) {
                const key = pre + pgHash(set + '|' + it.key);
                let src = __pgFlow.get(key) as string | undefined;
                if (!src) { src = n.type === 'resize' ? (await pgResize(it.value, n.data)).src : await pgAnnotateDraw(it.value, pgShapesOf(n)); pgFlowPut(key, src); }
                made.push({ kind: 'image', value: src, key, label: t.title });
            }
            const key = pre + pgHash(set + '|' + v.key);
            return v.items ? pgList(made, t.title, key) : { ...made[0], key };
        }
        case 'split': {
            const v = await first('image');
            if (!v) return null;
            const [r, c] = pgSplitDims(n);
            const base = 'sg:' + pgHash(r + 'x' + c + '|' + v.key);
            const per: PgValue[][] = [];
            for (const it of v.items || [v]) {
                const k = 'sg:' + pgHash(r + 'x' + c + '|' + it.key);
                let cells = __pgFlow.get(k) as string[] | undefined;
                if (!cells) { cells = await pgSplitImage(it.value, r, c); pgFlowPut(k, cells); }
                per.push(cells.map((src, i) => ({ kind: 'image' as const, value: src, key: k + ':c' + i, label: 'Row ' + (Math.floor(i / c) + 1) + ', column ' + (i % c + 1) })));
            }
            if (out === 'each' || out === 'all') {
                const all = pgList(per.flat(), t.title, base + ':' + out);
                return all && out === 'all' ? { ...all, items: all.items || [all], bundle: true } : all;
            }
            const picked = per.map(cells => cells[Number(out.slice(1))]).filter(Boolean);
            if (!picked.length) return null;
            return v.items ? pgList(picked, t.title, base + ':' + out) : { ...picked[0], key: base + ':' + out };
        }
    }
    return null;
}
// What a maker has made, as a value: one, or the list it made for a list
const __pgSrc = new Map<string, string>();
async function pgMadeValue(n: PgNode): Promise<PgValue | null> {
    const label = PG_TYPES[n.type].title;
    const key = pgKeyOf(n);
    // a GIF left in the store when the workflow was opened: fetched now
    if (key && !n.result && pgHeavy(n) && n.resultId) {
        n.result = await pgMediaSrc(n.resultId) || null;
        // its card, which said Opening, shows it now (whoever fetched it first)
        if (n.result) setTimeout(() => pgRenderNode(n.id), 0);
    }
    if (!key || !n.result) return null;
    if (pgHeavy(n)) return { kind: (PG_TYPES[n.type].out || 'image') as PgValue['kind'], value: n.result, key, label };
    if (PG_TYPES[n.type].out === 'text') {
        if (n.texts && n.texts.length > 1) return pgList(n.texts.map((x, i) => pgTextValue(x, label + ' ' + (i + 1))), label, key);
        return { kind: 'text', value: n.result, key, label };
    }
    if (n.listIds && n.listIds.length > 1) {
        const items: PgValue[] = [];
        for (const [i, id] of n.listIds.entries()) {
            let src = __pgSrc.get(id) || (id === n.resultId ? n.result : '');
            if (!src) { const rec = await pgResultGet(id); src = rec ? rec.src : ''; }
            if (!src) continue;
            __pgSrc.set(id, src);
            items.push({ kind: 'image', value: src, key: key + '#' + id, label: label + ' ' + (i + 1) });
        }
        return pgList(items, label, key);
    }
    return { kind: 'image', value: n.result, key, label };
}
// A Gallery keeps the pictures that came into it at each run, newest first, until
// cleared: their ids and small copies in its data, the pictures in the 'results' store. Its
// card and the viewer's strip show the small copies; a picture itself is read only when it is
// opened, downloaded or kept in the library (2026-10-26: a Gallery of 200 read them all, at full
// size, each time it was drawn).
const PG_GALLERY_KEEP = 200;
type PgKept = { id: string; key: string; thumb?: string };
async function pgKeepGalleries() {
    for (const n of pg.wf!.nodes.filter(x => x.type === 'gallery')) {
        const now = await pgImagesIn(n, 'images');
        const kept: PgKept[] = Array.isArray(n.data.kept) ? n.data.kept : [];
        const have = new Set(kept.map(k => k.key));
        const fresh: PgKept[] = [];
        for (const v of now) {
            if (have.has(v.key)) continue;
            have.add(v.key);
            const id = pgId('g');
            try { await pgResultPut({ id, wf: pg.wf!.id, node: n.id, src: v.value, at: Date.now() }); } catch { continue; }
            __pgSrc.set(id, v.value);
            fresh.push({ id, key: v.key, thumb: v.value.startsWith('data:video') ? '' : await pgThumb(v.value, 360) });
        }
        if (!fresh.length) continue;
        const all = [...fresh.reverse(), ...kept];
        all.slice(PG_GALLERY_KEEP).forEach(k => pgResultDelete(k.id));
        n.data.kept = all.slice(0, PG_GALLERY_KEEP);
    }
}
// what a Gallery shows on its card and in the viewer: what comes in now and is not kept yet,
// then what it has kept, as its small copy (made once for one kept before there were any)
type PgShown = { value?: string; id?: string; thumb?: string; key: string; label: string };
async function pgGalleryShown(n: PgNode): Promise<PgShown[]> {
    const kept: PgKept[] = Array.isArray(n.data.kept) ? n.data.kept : [];
    const keys = new Set(kept.map(k => k.key));
    const now = (await pgImagesIn(n, 'images')).filter(v => !keys.has(v.key));
    let made = false;
    for (const k of kept) {
        if (k.thumb !== undefined) continue;
        const src = __pgSrc.get(k.id) || (await pgResultGet(k.id))?.src || '';
        if (!src) continue;
        k.thumb = src.startsWith('data:video') ? '' : await pgThumb(src, 360);
        made = true;
    }
    if (made) pgSaveSoon();
    const old: PgShown[] = kept.filter(k => k.thumb).map(k => ({ id: k.id, thumb: k.thumb, key: k.key, label: 'Picture' }));
    return [...now.map(v => ({ value: v.value, key: v.key, label: v.label })), ...old].map((v, i) => ({ ...v, label: 'Picture ' + (i + 1) }));
}
// what a Gallery has, each picture itself: to download, or to keep in the library
async function pgGalleryItems(n: PgNode): Promise<PgValue[]> {
    const kept: { id: string; key: string }[] = Array.isArray(n.data.kept) ? n.data.kept : [];
    const keys = new Set(kept.map(k => k.key));
    const now = (await pgImagesIn(n, 'images')).filter(v => !keys.has(v.key));
    const old: PgValue[] = [];
    for (const k of kept) {
        let src = __pgSrc.get(k.id);
        if (!src) { const rec = await pgResultGet(k.id); src = rec ? rec.src : ''; if (src) __pgSrc.set(k.id, src); }
        if (src && !src.startsWith('data:video')) old.push({ kind: 'image', value: src, key: k.key, label: 'Picture' });
    }
    return [...now, ...old].map((v, i) => ({ ...v, label: 'Picture ' + (i + 1) }));
}

// everything a node's images input gets, each item of a list a picture of its own
async function pgImagesIn(n: PgNode, port: string, make: PgMakeFn = PG_SHOW): Promise<PgValue[]> {
    const all: PgValue[] = [];
    for (const e of pgInEdges(n.id, port)) {
        try {
            const v = await pgValueAlong(e, make);
            if (v) all.push(...(v.items || [v]));
        } catch { /* not there yet */ }
    }
    return all;
}

// ── Image tools (Node Banana's imageResize, splitGrid, imageCompare, annotation) ──
// Worked out in the browser, at once, from the picture that comes in: kept by key,
// so a picture is resized, split or drawn on once however often it is shown.
const __pgFlow = new Map<string, any>();
function pgFlowPut(key: string, v: any) {
    __pgFlow.set(key, v);
    if (__pgFlow.size > 80) __pgFlow.delete(__pgFlow.keys().next().value as string);
}
async function pgBitmap(src: string): Promise<ImageBitmap> {
    return createImageBitmap(await (await fetch(src)).blob());
}
const pgMimeOf = (src: string) => (/^data:(image\/[a-z+]+)/i.exec(src) || [])[1] || 'image/png';
// Resize (utils/imageResize.ts): exact size (contain, cover or stretch), the longest
// side (never larger) or a percentage; at most 8192 on the longest side
function pgResizeSettings(d: Record<string, any>) {
    const mode = d.mode || 'exact';
    return { mode, width: Math.max(1, Math.round(Number(d.width) || 1024)), height: Math.max(1, Math.round(Number(d.height) || 1024)), fit: d.fit || 'contain',
        maxEdge: Math.max(1, Math.round(Number(d.maxEdge) || 2048)), scale: Math.min(400, Math.max(1, Number(d.scale) || 50)), format: d.format || 'keep', quality: Math.min(1, Math.max(0.1, Number(d.quality) || 0.9)) };
}
async function pgResize(src: string, d: Record<string, any>): Promise<{ src: string; w: number; h: number }> {
    const s = pgResizeSettings(d);
    const bmp = await pgBitmap(src);
    const sw = bmp.width, sh = bmp.height;
    let W: number, H: number;
    if (s.mode === 'max') { const k = Math.min(1, s.maxEdge / Math.max(sw, sh)); W = Math.round(sw * k); H = Math.round(sh * k); }
    else if (s.mode === 'scale') { W = Math.round(sw * s.scale / 100); H = Math.round(sh * s.scale / 100); }
    else { W = s.width; H = s.height; }
    const cap = 8192 / Math.max(W, H);
    if (cap < 1) { W = Math.round(W * cap); H = Math.round(H * cap); }
    W = Math.max(1, W); H = Math.max(1, H);
    const mime = s.format === 'keep' ? (['image/png', 'image/jpeg', 'image/webp'].includes(pgMimeOf(src)) ? pgMimeOf(src) : 'image/png') : 'image/' + s.format;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d')!;
    if (mime === 'image/jpeg') { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H); }   // JPEG has no transparency
    ctx.imageSmoothingQuality = 'high';
    if (s.mode === 'exact' && s.fit !== 'stretch') {
        const k = s.fit === 'cover' ? Math.max(W / sw, H / sh) : Math.min(W / sw, H / sh);
        const dw = sw * k, dh = sh * k;
        ctx.drawImage(bmp, (W - dw) / 2, (H - dh) / 2, dw, dh);
    } else ctx.drawImage(bmp, 0, 0, W, H);
    return { src: c.toDataURL(mime, s.quality), w: W, h: H };
}
// Split grid (utils/gridSplitter.ts): cells row by row, meeting without a gap
const pgSplitDims = (n: PgNode): [number, number] => [Math.min(8, Math.max(1, Number(n.data.rows) || 2)), Math.min(8, Math.max(1, Number(n.data.cols) || 3))];
async function pgSplitImage(src: string, rows: number, cols: number): Promise<string[]> {
    const bmp = await pgBitmap(src);
    const xs = Array.from({ length: cols + 1 }, (_, i) => Math.round(i * bmp.width / cols));
    const ys = Array.from({ length: rows + 1 }, (_, i) => Math.round(i * bmp.height / rows));
    const cells: string[] = [];
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
        const w = xs[c + 1] - xs[c], h = ys[r + 1] - ys[r];
        const cv = document.createElement('canvas');
        cv.width = Math.max(1, w); cv.height = Math.max(1, h);
        cv.getContext('2d')!.drawImage(bmp, xs[c], ys[r], w, h, 0, 0, w, h);
        cells.push(cv.toDataURL('image/png'));
    }
    return cells;
}
// Annotate (AnnotationModal): boxes, ellipses, arrows, lines drawn by hand and text,
// in the picture's own pixels; the picture that comes in is drawn on again whenever
// it changes (Node Banana kept the old drawing)
interface PgShape { t: 'rect' | 'ellipse' | 'arrow' | 'draw' | 'text'; x: number; y: number; w?: number; h?: number; x2?: number; y2?: number; pts?: number[]; text?: string; size?: number; color: string; stroke: number; fill?: boolean; }
const PG_AN_COLOURS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#000000', '#ffffff'];
const pgShapesOf = (n: PgNode): PgShape[] => Array.isArray(n.data.shapes) ? n.data.shapes : [];
function pgDrawShape(ctx: CanvasRenderingContext2D, s: PgShape) {
    ctx.save();
    ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = s.stroke; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (s.t === 'rect') { if (s.fill) ctx.fillRect(s.x, s.y, s.w || 0, s.h || 0); else ctx.strokeRect(s.x, s.y, s.w || 0, s.h || 0); }
    else if (s.t === 'ellipse') {
        ctx.beginPath();
        ctx.ellipse(s.x + (s.w || 0) / 2, s.y + (s.h || 0) / 2, Math.abs((s.w || 0) / 2), Math.abs((s.h || 0) / 2), 0, 0, Math.PI * 2);
        if (s.fill) ctx.fill(); else ctx.stroke();
    } else if (s.t === 'arrow') {
        const x2 = s.x2 ?? s.x, y2 = s.y2 ?? s.y, a = Math.atan2(y2 - s.y, x2 - s.x), head = Math.max(12, s.stroke * 4);
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(x2, y2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(a - 0.45), y2 - head * Math.sin(a - 0.45));
        ctx.lineTo(x2 - head * Math.cos(a + 0.45), y2 - head * Math.sin(a + 0.45));
        ctx.closePath(); ctx.fill();
    } else if (s.t === 'draw' && s.pts && s.pts.length >= 4) {
        ctx.beginPath(); ctx.moveTo(s.pts[0], s.pts[1]);
        for (let i = 2; i < s.pts.length; i += 2) ctx.lineTo(s.pts[i], s.pts[i + 1]);
        ctx.stroke();
    } else if (s.t === 'text' && s.text) {
        ctx.font = '600 ' + (s.size || 24) + 'px system-ui, -apple-system, Segoe UI, sans-serif';
        ctx.textBaseline = 'top';
        String(s.text).split('\n').forEach((line, i) => ctx.fillText(line, s.x, s.y + i * (s.size || 24) * 1.2));
    }
    ctx.restore();
}
function pgShapeBox(s: PgShape): { x: number; y: number; w: number; h: number } {
    if (s.t === 'rect' || s.t === 'ellipse') return { x: Math.min(s.x, s.x + (s.w || 0)), y: Math.min(s.y, s.y + (s.h || 0)), w: Math.abs(s.w || 0), h: Math.abs(s.h || 0) };
    if (s.t === 'arrow') return { x: Math.min(s.x, s.x2 ?? s.x), y: Math.min(s.y, s.y2 ?? s.y), w: Math.abs((s.x2 ?? s.x) - s.x), h: Math.abs((s.y2 ?? s.y) - s.y) };
    if (s.t === 'draw' && s.pts) { const xs = s.pts.filter((_, i) => i % 2 === 0), ys = s.pts.filter((_, i) => i % 2 === 1); return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) }; }
    const lines = String(s.text || '').split('\n');
    return { x: s.x, y: s.y, w: Math.max(...lines.map(l => l.length)) * (s.size || 24) * 0.58, h: lines.length * (s.size || 24) * 1.2 };
}
async function pgAnnotateDraw(src: string, shapes: PgShape[]): Promise<string> {
    if (!shapes.length) return src;
    const bmp = await pgBitmap(src);
    const c = document.createElement('canvas');
    c.width = bmp.width; c.height = bmp.height;
    const ctx = c.getContext('2d')!;
    ctx.drawImage(bmp, 0, 0);
    shapes.forEach(s => pgDrawShape(ctx, s));
    return c.toDataURL('image/png');
}
// what a tool's result is made from, said as a key
function pgToolSettings(n: PgNode): string {
    if (n.type === 'resize') return JSON.stringify(pgResizeSettings(n.data));
    if (n.type === 'annotate') return JSON.stringify(pgShapesOf(n));
    return '';
}
const pgFrameSettings = (n: PgNode) => (n.data.at || 'first') + (n.data.at === 'time' ? ':' + (Number(n.data.time) || 0) : '');
// the picture an Annotate draws on: wired in, else its own
const pgAnnotateOwn = (n: PgNode): PgValue | null => n.data.src ? { kind: 'image', value: n.data.src, key: 'img:' + (n.data.key || pgHash(n.data.src)), label: n.data.name || 'Image' } : null;

// ── Heavy results ──
// A GIF is large: what it made is kept in the 'results' store by id, not in the
// workflow, and fetched when it is needed.
const pgHeavy = (n: PgNode) => n.type === 'gif';
// what a node holds in the store, by id: kept in memory once fetched
async function pgMediaSrc(id: string | undefined): Promise<string> {
    if (!id) return '';
    let src = __pgSrc.get(id);
    if (!src) { const rec = await pgResultGet(id); src = rec ? rec.src : ''; if (src) __pgSrc.set(id, src); }
    return src || '';
}

// ── The library (2026-10-16, Simon: "Vi skal have lavet et bibliotek herinde … lige nu bliver det ikke gemt") ──
// What the runs deliver is kept here, in this browser, until it is removed: each picture
// made that comes into an Output, a Gallery or a Compare, and each picture made that goes
// no further. A picture put in (an Image, a reference photo, a packshot, a neck label) is
// not. The list (a small picture and what made it) and the pictures are kept apart, so the
// list opens at once however much is kept. It opens in the assistant's place, and Download
// all saves what it shows as one ZIP, each file with its AI metadata.
interface PgLibItem { id: string; key: string; at: number; run: string; wf: string; wfName: string; node: string; title: string; thumb: string; w: number; h: number; meta?: Record<string, string>; gone?: boolean; }
const PG_LIB_DB = 'floorflow-playground-library', PG_LIB_PICS_DB = 'floorflow-playground-library-pictures', PG_LIB_FILTER_KEY = 'creator.playground.libFilter';
const pgLib = {
    run: '',                                   // the run going on: its pictures are shown together
    keys: null as Set<string> | null,          // what is kept (and what was removed), so nothing is kept twice
    filter: 'wf' as 'wf' | 'all',
    busy: '',                                  // what Download all is doing
    filled: new Set<string>(),                 // the workflows whose pictures from before have been looked for
    shown: [] as PgLibItem[],
};
async function pgLibReq<T>(store: 'items' | 'pictures', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = store === 'items' ? await pgOpenDb(PG_LIB_DB, 'items', 'wf') : await pgOpenDb(PG_LIB_PICS_DB, 'pictures');
    return new Promise((resolve, reject) => {
        const r = fn(db.transaction(store, mode).objectStore(store));
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
    });
}
const pgLibItems = async () => ((await pgLibReq('items', 'readonly', s => s.getAll())) as PgLibItem[]).sort((a, b) => b.at - a.at);
const pgLibPicture = async (id: string) => ((await pgLibReq('pictures', 'readonly', s => s.get(id)).catch(() => undefined)) as { src: string } | undefined)?.src || '';
// a picture made here, not one that was put in
const pgLibMade = (v: PgValue) => v.kind === 'image' && /^data:image\//.test(v.value) && !/^(img|ref|ps|tag):/.test(v.key);
async function pgLibSize(src: string): Promise<[number, number]> {
    try { const b = await createImageBitmap(await (await fetch(src)).blob()); const wh: [number, number] = [b.width, b.height]; b.close(); return wh; } catch { return [0, 0]; }
}
// Keep what the run delivered; all: everything the workflow shows now, made before too
async function pgLibKeep(all = false): Promise<number> {
    const wf = pg.wf;
    if (!wf) return 0;
    if (!pgLib.keys) pgLib.keys = new Set((await pgLibItems()).map(i => i.key));
    const keys = pgLib.keys;
    const fresh = (id: string) => all || pg.made.has(id) || pgUpstream(id).some(u => pg.made.has(u));
    const found: { v: PgValue; from: PgNode }[] = [];
    for (const n of wf.nodes) {
        const ports = n.type === 'output' ? ['image'] : n.type === 'gallery' ? ['images'] : n.type === 'compare' ? ['a', 'b'] : [];
        for (const port of ports) for (const e of pgInEdges(n.id, port)) {
            const from = pgNode(e.from);
            if (!from || !fresh(from.id)) continue;
            const v = await pgValueAlong(e, PG_SHOW).catch(() => null);
            for (const it of v ? (v.items || [v]) : []) found.push({ v: it, from });
        }
        // what a Gallery kept from the runs before
        if (all && n.type === 'gallery') for (const v of await pgGalleryItems(n).catch(() => [] as PgValue[])) found.push({ v, from: n });
        // a picture made that goes no further
        if (PG_TYPES[n.type].runs && PG_TYPES[n.type].out === 'image' && !wf.edges.some(e => e.from === n.id) && (all || pg.made.has(n.id))) {
            const v = await pgMadeValue(n).catch(() => null);
            for (const it of v ? (v.items || [v]) : []) found.push({ v: it, from: n });
        }
    }
    let kept = 0;
    for (const { v, from } of found) {
        if (!pgLibMade(v) || keys.has(v.key)) continue;
        keys.add(v.key);
        const origin = pgOrigin(from.id) || from;
        const [w, h] = await pgLibSize(v.value);
        const item: PgLibItem = {
            id: pgId('lib'), key: v.key, at: Date.now(), run: pgLib.run || 'before:' + wf.id, wf: wf.id, wfName: wf.name || 'Untitled workflow',
            node: origin.id, title: PG_TYPES[from.type].title, thumb: await pgThumb(v.value, 360), w, h, meta: origin.meta ? { ...origin.meta } : undefined,
        };
        try {
            await pgLibReq('pictures', 'readwrite', st => st.put({ id: item.id, src: v.value }));
            await pgLibReq('items', 'readwrite', st => st.put(item));
            kept++;
        } catch (err) { keys.delete(v.key); console.warn('playground: keeping a picture in the library', err); }
    }
    if (kept) pgLibRender();
    return kept;
}
const pgLibWhen = (at: number) => { const d = new Date(at); return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }); };
const pgLibHtml = () => '<aside class="pg-ask pg-lib" id="pgLib" aria-label="Library" hidden>'
    + '<div class="pg-ask-head"><span class="pg-ask-mark" aria-hidden="true">' + pgIcon('gallery') + '</span><div class="pg-ask-name"><b>Library</b><small>What the runs made, kept in this browser</small></div>'
    + '<button type="button" class="pg-icon" data-lib-act="close" aria-label="Close the library" title="Close (Esc)">' + pgIcon('close') + '</button></div>'
    + '<div class="pg-lib-bar"><div class="pg-lib-seg" role="group" aria-label="Which pictures">'
    + '<button type="button" data-lib-filter="wf" aria-pressed="true">This workflow</button><button type="button" data-lib-filter="all" aria-pressed="false">All</button></div>'
    + '<span class="pg-ask-gap"></span><button type="button" class="pg-chip is-primary is-small" data-lib-act="all" disabled>' + pgIcon('download') + 'Download all</button></div>'
    + '<div class="pg-lib-list" id="pgLibList"></div></aside>';
function pgLibOpen(open: boolean, focus = true) {
    const el = document.getElementById('pgLib'), btn = document.getElementById('pgLibBtn');
    if (!el || !btn) return;
    if (open) pgAskOpen(false, false);     // they take the same place
    el.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    if (open) { pgLibRender(); if (focus) (el.querySelector('[data-lib-act="close"]') as HTMLElement).focus(); }
}
function pgLibPaintBusy() {
    const btn = document.querySelector('#pgLib [data-lib-act="all"]') as HTMLButtonElement | null;
    if (!btn) return;
    btn.disabled = !!pgLib.busy || !pgLib.shown.length;
    btn.innerHTML = pgIcon('download') + pgEsc(pgLib.busy || (pgLib.shown.length > 1 ? 'Download all ' + pgLib.shown.length : 'Download all'));
}
async function pgLibRender() {
    const el = document.getElementById('pgLib'), list = document.getElementById('pgLibList');
    if (!el || !list || el.hidden) return;
    // what this workflow made before there was a library, or in another session: looked for once
    if (pg.wf && !pgLib.filled.has(pg.wf.id)) { pgLib.filled.add(pg.wf.id); pgLibKeep(true).catch(() => 0); }
    const all = (await pgLibItems().catch(() => [] as PgLibItem[])).filter(i => !i.gone);
    const items = pgLib.filter === 'wf' ? all.filter(i => i.wf === pg.wf?.id) : all;
    pgLib.shown = items;
    el.querySelectorAll<HTMLButtonElement>('[data-lib-filter]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.libFilter === pgLib.filter)));
    pgLibPaintBusy();
    if (!items.length) {
        list.innerHTML = '<p class="pg-lib-empty"><b>' + (all.length ? 'Nothing kept from this workflow yet.' : 'Nothing kept yet.') + '</b>'
            + 'Each picture a run makes that comes into an Output, a Gallery or a Compare, or that goes no further, is kept here, in this browser, until you remove it.</p>';
        return;
    }
    const runs = new Map<string, PgLibItem[]>();
    items.forEach(i => { if (!runs.has(i.run)) runs.set(i.run, []); runs.get(i.run)!.push(i); });
    list.innerHTML = [...runs.entries()].map(([run, its]) => {
        // what was there before the library (or before this session) is not known to the minute
        const when = run.startsWith('before:') ? 'Made earlier' : pgLibWhen(its[its.length - 1].at), many = its.length + (its.length === 1 ? ' picture' : ' pictures');
        return '<section class="pg-lib-run">'
            + '<div class="pg-lib-run-head"><b>' + pgEsc(pgLib.filter === 'all' ? its[0].wfName : when) + '</b><span>' + pgEsc(pgLib.filter === 'all' ? when + ' · ' + many : many) + '</span>'
            + '<button type="button" class="pg-icon" data-lib-act="run" data-run="' + pgEsc(run) + '" aria-label="Download these ' + many + '" title="Download these">' + pgIcon('download') + '</button></div>'
            + '<div class="pg-lib-grid">' + its.map(i => '<figure class="pg-lib-pic">'
                + '<button type="button" class="pg-lib-open" data-lib-act="view" data-id="' + i.id + '" aria-label="' + pgEsc(i.title + ', ' + pgLibWhen(i.at) + ': view it large') + '"><img src="' + pgEsc(i.thumb) + '" alt="" draggable="false"></button>'
                + '<figcaption>' + pgEsc(i.title) + (i.w ? '<span> · ' + i.w + ' × ' + i.h + '</span>' : '') + '</figcaption>'
                + '<span class="pg-lib-tools">'
                + '<button type="button" class="pg-pic-btn" data-lib-act="download" data-id="' + i.id + '" aria-label="Download it" title="Download">' + pgIcon('download') + '</button>'
                + '<button type="button" class="pg-pic-btn" data-lib-act="canvas" data-id="' + i.id + '" aria-label="Put it on the canvas, as an Image" title="On the canvas, as an Image">' + pgIcon('plus') + '</button>'
                + '<button type="button" class="pg-pic-btn" data-lib-act="remove" data-id="' + i.id + '" aria-label="Remove it from the library" title="Remove">' + pgIcon('trash') + '</button>'
                + '</span></figure>').join('') + '</div></section>';
    }).join('');
}
// the files' names: the workflow, the day and time it was made, and its number
const pgLibExt = (src: string) => { const m = (/^data:image\/(\w+)/.exec(src) || [])[1] || 'png'; return m === 'jpeg' ? 'jpg' : m; };
function pgLibName(i: PgLibItem, n: number, ext = '') {
    const d = new Date(i.at), p = (x: number) => String(x).padStart(2, '0');
    return 'floorflow-playground-' + pgSlug(i.wfName) + '-' + d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + '-' + p(d.getHours()) + p(d.getMinutes()) + (n ? '-' + n : '') + (ext ? '.' + ext : '');
}
const pgLibZipName = (its: PgLibItem[] | null) => its ? pgLibName(its[its.length - 1], 0, 'zip')
    : 'floorflow-playground-' + (pgLib.filter === 'all' ? 'library' : pgSlug(pg.wf ? pg.wf.name : '')) + '-' + new Date().toISOString().slice(0, 10) + '.zip';
// One picture as its file, or several as one ZIP; each with its AI metadata (C2PA), written on the server
async function pgLibDownload(items: PgLibItem[], zipName = '') {
    if (!items.length || pgLib.busy) return;
    const files: { name: string; data: Uint8Array }[] = [];
    let left = 0;
    try {
        for (const [k, i] of items.entries()) {
            if (items.length > 1) { pgLib.busy = 'Getting ' + (k + 1) + ' of ' + items.length + ' ready…'; pgLibPaintBusy(); }
            const src = await pgLibPicture(i.id);
            if (!src) { left++; continue; }
            const name = pgLibName(i, items.length > 1 ? k + 1 : 0, pgLibExt(src));
            try {
                const url = await exportAssetViaServer(src, name, { generation_id: i.node, model_name: (i.meta && i.meta.model) || 'unknown', engine: (i.meta && i.meta.engine) || '' });
                if (items.length === 1) saveDataUrlAsFile(url, name);
                else files.push({ name, data: dataUrlBytes(url) });
            } catch { left++; }
        }
        if (files.length) saveBlobAsFile(makeZip(files), zipName || pgLibZipName(null));
    } finally {
        pgLib.busy = '';
        pgLibPaintBusy();
        if (left) showToast(left === items.length && left === 1 ? 'The picture was not downloaded: its AI metadata could not be written.'
            : left + (left === 1 ? ' picture was' : ' pictures were') + ' left out: their AI metadata could not be written.', { type: 'warn', title: 'Library' });
    }
}
async function pgLibClick(t: HTMLElement): Promise<boolean> {
    if (t.closest('#pgLibBtn')) { const el = document.getElementById('pgLib'); pgLibOpen(!!el && el.hidden === true); return true; }
    const f = t.closest('[data-lib-filter]') as HTMLElement | null;
    if (f) {
        pgLib.filter = f.dataset.libFilter === 'all' ? 'all' : 'wf';
        try { localStorage.setItem(PG_LIB_FILTER_KEY, pgLib.filter); } catch { /* private */ }
        pgLibRender();
        return true;
    }
    const a = t.closest('[data-lib-act]') as HTMLElement | null;
    if (!a) return true;
    const act = a.dataset.libAct, item = pgLib.shown.find(i => i.id === a.dataset.id);
    if (act === 'close') { pgLibOpen(false); (document.getElementById('pgLibBtn') as HTMLElement | null)?.focus(); }
    else if (act === 'all') await pgLibDownload(pgLib.shown, pgLibZipName(null));
    else if (act === 'run') { const its = pgLib.shown.filter(i => i.run === a.dataset.run); await pgLibDownload(its, pgLibZipName(its)); }
    else if (act === 'download' && item) await pgLibDownload([item]);
    else if (act === 'view' && item) {
        pg.lb = { node: '', lib: true, items: pgLib.shown.map(i => ({ id: i.id, thumb: i.thumb, label: i.title + ' · ' + pgLibWhen(i.at) })), index: pgLib.shown.indexOf(item), compare: '', split: 50 };
        pg.el.lb.hidden = false;
        pg.el.lb.classList.remove('is-comparing');
        await pgPaintViewer();
        (pg.el.lb.querySelector('[data-lb="close"]') as HTMLElement).focus();
    }
    else if (act === 'canvas' && item && pg.wf) {
        const src = await pgLibPicture(item.id);
        if (!src) return true;
        // in the middle of what can be seen of the canvas, beside the library
        const c = pg.el.canvas.getBoundingClientRect(), v = pg.wf.view, k = v.k || 1, side = (document.getElementById('pgLib')?.offsetWidth || 0) + 16;
        const n = pgAddNode('image', { x: Math.round(((c.width - side) / 2 - v.x) / k - PG_TYPES.image.width / 2), y: Math.round((c.height / 2 - v.y) / k - 120) },
            { src, name: item.title + ' · ' + pgLibWhen(item.at), key: pgId('img') });
        pgRenderDown(n.id);
        showToast('It is on the canvas, as an Image.', { type: 'success', title: 'Library' });
    }
    else if (act === 'remove' && item) await pgLibRemove(item);
    return true;
}
async function pgLibRemove(item: PgLibItem): Promise<boolean> {
    if (!confirm('Remove this picture from the library? It cannot be brought back.')) return false;
    await pgLibReq('pictures', 'readwrite', st => st.delete(item.id)).catch(() => undefined);
    // its record stays, without the picture, so it is not kept again from the workflow
    await pgLibReq('items', 'readwrite', st => st.put({ id: item.id, key: item.key, at: item.at, run: item.run, wf: item.wf, wfName: item.wfName, node: item.node, title: item.title, thumb: '', w: 0, h: 0, gone: true })).catch(() => undefined);
    await pgLibRender();
    return true;
}

// ── Groups (Node Banana's groups, with their lock) ──
// A frame round some nodes, named and coloured: moved by its head, run on its own,
// and locked, when its nodes are not to be made again (they keep what they have
// made, and what follows them gets that).
interface PgGroup { id: string; name: string; color: string; nodes: string[]; locked?: boolean; }
const PG_GROUP_COLOURS = ['blue', 'amber', 'green', 'pink', 'teal', 'violet'];
const pgGroups = (): PgGroup[] => (pg.wf && Array.isArray(pg.wf.groups) ? pg.wf.groups : []);
const pgGroupOf = (id: string) => pgGroups().find(g => g.nodes.includes(id)) || null;
const pgLocked = (n: PgNode) => !!pgGroupOf(n.id)?.locked;
function pgGroupSelected() {
    const ids = [...pg.sel].filter(id => pgNode(id));
    if (!ids.length || !pg.wf) return;
    pgRecord();
    const rest = pgGroups().map(g => ({ ...g, nodes: g.nodes.filter(id => !ids.includes(id)) })).filter(g => g.nodes.length);
    const used = new Set(rest.map(g => g.color));
    const color = PG_GROUP_COLOURS.find(c => !used.has(c)) || PG_GROUP_COLOURS[rest.length % PG_GROUP_COLOURS.length];
    pg.wf.groups = [...rest, { id: pgId('g'), name: 'Group ' + (rest.length + 1), color, nodes: ids }];
    pgRenderGroups();
    pgSaveSoon();
}
function pgUngroup(groupId?: string) {
    if (!pg.wf) return;
    const gone = groupId ? [groupId] : pgGroups().filter(g => g.nodes.some(id => pg.sel.has(id))).map(g => g.id);
    if (!gone.length) return;
    pgRecord();
    pg.wf.groups = pgGroups().filter(g => !gone.includes(g.id));
    pgRenderGroups();
    pgPaintPlan();
    pgSaveSoon();
}
// the frames, behind the wires and the nodes: round their nodes as they stand now
function pgRenderGroups() {
    const world = pg.el.world;
    if (!world || !pg.wf) return;
    let layer = world.querySelector('.pg-groups') as HTMLElement | null;
    if (!layer) { layer = document.createElement('div'); layer.className = 'pg-groups'; world.insertBefore(layer, world.firstChild); }
    const nodes = new Map(pg.wf.nodes.map(n => [n.id, n] as [string, PgNode]));
    const was = new Map<string, HTMLElement>();
    layer.querySelectorAll<HTMLElement>(':scope > .pg-group').forEach(el => was.set(el.dataset.group!, el));
    const keep = new Set<string>();
    let changed = false;
    for (const g of pgGroups()) {
        const cards = g.nodes.map(id => ({ n: nodes.get(id), c: pgCard(id) })).filter(x => x.n && x.c);
        if (!cards.length) continue;
        const x0 = Math.min(...cards.map(x => x.n!.x)) - 24, y0 = Math.min(...cards.map(x => x.n!.y)) - 56;
        const x1 = Math.max(...cards.map(x => x.n!.x + pgCardSize(x.c!).w)) + 24, y1 = Math.max(...cards.map(x => x.n!.y + pgCardSize(x.c!).h)) + 24;
        const makers = g.nodes.filter(id => { const n = nodes.get(id); return n && PG_TYPES[n.type].runs; }).length;
        const sig = g.color + '|' + !!g.locked + '|' + makers;
        let el = was.get(g.id);
        if (!el || el.dataset.sig !== sig) {
            const html = '<div class="pg-group is-' + pgEsc(g.color) + (g.locked ? ' is-locked' : '') + '" data-group="' + pgEsc(g.id) + '" data-sig="' + pgEsc(sig) + '">'
                + '<div class="pg-group-head" data-group-head="' + pgEsc(g.id) + '">'
                + '<input type="text" class="pg-group-name" data-group="' + pgEsc(g.id) + '" value="' + pgEsc(g.name) + '" aria-label="Name of the group" spellcheck="false" autocomplete="off">'
                + (g.locked ? '<span class="pg-group-badge">Locked: not made again</span>' : '')
                + '<button type="button" class="pg-icon is-small" data-group-act="colour" data-group="' + pgEsc(g.id) + '" aria-label="Another colour" title="Another colour"><span class="pg-group-dot"></span></button>'
                + '<button type="button" class="pg-icon is-small" data-group-act="lock" data-group="' + pgEsc(g.id) + '" aria-pressed="' + !!g.locked + '" aria-label="' + (g.locked ? 'Unlock' : 'Lock') + ' the group" title="' + (g.locked ? 'Unlock: Run makes its nodes again when they change' : 'Lock: Run leaves its nodes as they are') + '">' + pgIcon(g.locked ? 'lock' : 'unlock') + '</button>'
                + (makers ? '<button type="button" class="pg-icon is-small" data-group-act="run" data-group="' + pgEsc(g.id) + '" aria-label="Make the group\'s nodes again" title="Make its ' + makers + ' node' + (makers > 1 ? 's' : '') + ' again"' + (g.locked ? ' disabled' : '') + '>' + pgIcon('run') + '</button>' : '')
                + '<button type="button" class="pg-icon is-small" data-group-act="ungroup" data-group="' + pgEsc(g.id) + '" aria-label="Ungroup" title="Ungroup (the nodes stay)">' + pgIcon('close') + '</button>'
                + '</div></div>';
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const fresh = tmp.firstElementChild as HTMLElement;
            if (el) el.replaceWith(fresh); else layer.appendChild(fresh);
            el = fresh;
            changed = true;
        } else {
            // its name, unless it is being typed
            const box = el.querySelector('.pg-group-name') as HTMLInputElement | null;
            if (box && box !== document.activeElement && box.value !== g.name) box.value = g.name;
        }
        el.style.left = x0 + 'px'; el.style.top = y0 + 'px'; el.style.width = (x1 - x0) + 'px'; el.style.height = (y1 - y0) + 'px';
        keep.add(g.id);
    }
    for (const [id, el] of was) if (!keep.has(id)) { el.remove(); changed = true; }
    if (changed || layer.dataset.n !== String(pg.wf.nodes.length)) {
        layer.dataset.n = String(pg.wf.nodes.length);
        pg.wf.nodes.forEach(n => pgCard(n.id)?.classList.toggle('is-locked', pgLocked(n)));
    }
}
// GIF (Node Banana's gifEncoder, with gifenc): here an encoder of our own. Each frame
// gets its own palette, the colours used most (at 5 bits a channel); LZW as the GIF
// standard has it; it loops for ever.
function pgGifPalette(px: Uint8ClampedArray, colours: number): { pal: number[]; index: (i: number) => number } {
    const count = new Uint32Array(32768);
    for (let i = 0; i < px.length; i += 4) count[((px[i] >> 3) << 10) | ((px[i + 1] >> 3) << 5) | (px[i + 2] >> 3)]++;
    const used: number[] = [];
    for (let k = 0; k < 32768; k++) if (count[k]) used.push(k);
    used.sort((a, b) => count[b] - count[a]);
    const top = used.slice(0, colours);
    const pal = top.flatMap(k => [((k >> 10) & 31) * 8 + 4, ((k >> 5) & 31) * 8 + 4, (k & 31) * 8 + 4]);
    while (pal.length < 6) pal.push(0, 0, 0);
    const near = new Int16Array(32768).fill(-1);
    top.forEach((k, i) => { near[k] = i; });
    const index = (i: number) => {
        const k = ((px[i] >> 3) << 10) | ((px[i + 1] >> 3) << 5) | (px[i + 2] >> 3);
        if (near[k] >= 0) return near[k];
        let best = 0, bd = Infinity;
        const r = px[i], g = px[i + 1], b = px[i + 2];
        for (let j = 0; j < pal.length / 3; j++) {
            const d = (pal[j * 3] - r) ** 2 + (pal[j * 3 + 1] - g) ** 2 + (pal[j * 3 + 2] - b) ** 2;
            if (d < bd) { bd = d; best = j; }
        }
        near[k] = best;
        return best;
    };
    return { pal, index };
}
function pgGifLzw(indices: Uint8Array, minSize: number): number[] {
    const out: number[] = [];
    let cur = 0, bits = 0;
    const clear = 1 << minSize, eoi = clear + 1;
    let size = minSize + 1, next = eoi + 1;
    let dict = new Map<number, number>();
    const emit = (code: number) => { cur |= code << bits; bits += size; while (bits >= 8) { out.push(cur & 255); cur >>= 8; bits -= 8; } };
    emit(clear);
    let prefix = indices[0];
    for (let i = 1; i < indices.length; i++) {
        const k = indices[i], key = (prefix << 8) | k;
        const hit = dict.get(key);
        if (hit !== undefined) { prefix = hit; continue; }
        emit(prefix);
        if (next < 4096) { dict.set(key, next++); if (next > (1 << size) && size < 12) size++; }
        else { emit(clear); dict = new Map(); size = minSize + 1; next = eoi + 1; }
        prefix = k;
    }
    emit(prefix);
    emit(eoi);
    if (bits > 0) out.push(cur & 255);
    return out;
}
function pgGifEncode(frames: ImageData[], delayMs: number, colours: number): Uint8Array {
    const W = frames[0].width, H = frames[0].height;
    const b: number[] = [];
    const s = (t: string) => { for (const c of t) b.push(c.charCodeAt(0)); };
    const u16 = (n: number) => b.push(n & 255, (n >> 8) & 255);
    s('GIF89a'); u16(W); u16(H); b.push(0, 0, 0);                          // no global palette
    b.push(0x21, 0xff, 11); s('NETSCAPE2.0'); b.push(3, 1, 0, 0, 0);      // loop for ever
    const delay = Math.max(2, Math.round(delayMs / 10));
    for (const f of frames) {
        const { pal, index } = pgGifPalette(f.data, Math.max(2, Math.min(256, colours)));
        const bitsPer = Math.max(1, Math.ceil(Math.log2(pal.length / 3)));
        const size = 1 << bitsPer;
        b.push(0x21, 0xf9, 4, 0x04, delay & 255, (delay >> 8) & 255, 0, 0);    // keep the frame, the delay
        b.push(0x2c); u16(0); u16(0); u16(W); u16(H); b.push(0x80 | (bitsPer - 1));
        for (let i = 0; i < size; i++) b.push(pal[i * 3] || 0, pal[i * 3 + 1] || 0, pal[i * 3 + 2] || 0);
        const idx = new Uint8Array(W * H);
        for (let i = 0, p = 0; p < idx.length; i += 4, p++) idx[p] = index(i);
        const minSize = Math.max(2, bitsPer);
        b.push(minSize);
        const data = pgGifLzw(idx, minSize);
        for (let i = 0; i < data.length; i += 255) { const n = Math.min(255, data.length - i); b.push(n); for (let j = 0; j < n; j++) b.push(data[i + j]); }
        b.push(0);
    }
    b.push(0x3b);
    return new Uint8Array(b);
}
// the frames of a GIF: the pictures, drawn in the first one's shape, at most so wide, on white
async function pgGifFrames(pictures: string[], width: number): Promise<ImageData[]> {
    const draw = (c: HTMLCanvasElement, src: CanvasImageSource, sw: number, sh: number) => {
        const ctx = c.getContext('2d', { willReadFrequently: true })!;
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
        const k = Math.min(c.width / sw, c.height / sh);
        ctx.drawImage(src, (c.width - sw * k) / 2, (c.height - sh * k) / 2, sw * k, sh * k);
        return ctx.getImageData(0, 0, c.width, c.height);
    };
    const canvasOf = (sw: number, sh: number) => { const c = document.createElement('canvas'); const k = Math.min(1, width / sw); c.width = Math.max(1, Math.round(sw * k)); c.height = Math.max(1, Math.round(sh * k)); return c; };
    const bmps = await Promise.all(pictures.map(pgBitmap));
    const c = canvasOf(bmps[0].width, bmps[0].height);
    return bmps.map(bmp => draw(c, bmp, bmp.width, bmp.height));
}
async function pgMakeGif(pictures: string[], d: Record<string, any>, say: (m: string) => void): Promise<{ src: string; bytes: number; w: number; h: number }> {
    const fps = Math.min(30, Math.max(1, Number(d.fps) || 8));
    let colours = Math.min(256, Math.max(2, Number(d.colours) || 128));
    let width = Math.min(1024, Math.max(32, Number(d.width) || 512));
    const most = Number(d.maxKb) > 0 ? Number(d.maxKb) * 1024 : 0;
    let frames = await pgGifFrames(pictures, width);
    say('Encoding');
    let gif = pgGifEncode(frames, 1000 / fps, colours);
    // too large (Node Banana's target size): fewer colours, then smaller, then fewer frames
    for (let i = 0; most && gif.length > most && i < 12; i++) {
        if (colours > 32) colours = Math.max(32, colours >> 1);
        else if (width > 96) { width = Math.round(width * 0.85); frames = await pgGifFrames(pictures, width); }
        else if (frames.length > 2) frames = frames.filter((_, j) => j % 2 === 0);
        else break;
        gif = pgGifEncode(frames, 1000 / fps, colours);
    }
    return { src: await pgBlobToDataUrl(new Blob([gif as Uint8Array<ArrayBuffer>], { type: 'image/gif' })), bytes: gif.length, w: frames[0].width, h: frames[0].height };
}

async function pgReferenceSets(force = false): Promise<any[]> {
    if (!pg.refs || force) {
        try {
            const res = await authFetch('/api/references');
            const json = res.ok ? await res.json() : { sets: [] };
            pg.refs = Array.isArray(json.sets) ? json.sets : [];
        } catch { pg.refs = []; }
    }
    return pg.refs!;
}
async function pgPackshotEntries(force = false): Promise<any[]> {
    if (!pg.packshots || force) {
        try {
            const all = typeof getPackshotLibraryLight === 'function' ? await getPackshotLibraryLight() : [];
            pg.packshots = all.filter((e: any) => e && e.status !== 'running' && e.images && Object.keys(PG_SLOTS).some(s => e.images[s]))
                .sort((a: any, b: any) => (b.created || 0) - (a.created || 0));
        } catch { pg.packshots = []; }
    }
    return pg.packshots!;
}
const pgPackshotName = (e: any) => [e.styleNr, e.colorCode].filter(Boolean).join(' · ') || 'Packshot';
const pgRefName = (set: any) => (typeof stRefMeta === 'function' ? stRefMeta(set).name : '') || set.name || set.id;

// A prompt's {{fields}}, and its text with those filled in sent
const PG_FIELD = /\{\{\s*([^{}]+?)\s*\}\}/g;
const pgFieldsOf = (text: string) => [...new Set([...String(text || '').matchAll(PG_FIELD)].map(m => m[1]))];
function pgTextOf(n: PgNode): string {
    const vars = n.data.vars || {};
    return String(n.data.text || '').replace(PG_FIELD, (m, name) => (vars[name] && String(vars[name]).trim()) ? String(vars[name]).trim() : m);
}
const pgResultKey = (n: PgNode) => 'res:' + n.id + ':' + (n.sig || '') + (n.resultId ? ':' + n.resultId : '')
    + (n.listIds && n.listIds.length > 1 ? ':' + pgHash(n.listIds.join(',')) : '') + (n.texts && n.texts.length > 1 ? ':' + pgHash(n.texts.join('\u0000')) : '');

// What a node will give, said without fetching it: the same keys as pgValueOf,
// so the Run button can tell what has changed. Null when it gives nothing.
function pgKeyOf(n: PgNode): string | null {
    const d = n.data;
    switch (n.type) {
        case 'image': return d.src ? 'img:' + (d.key || pgHash(d.src)) : null;
        case 'prompt':
        case 'savedPrompt': { const t = pgTextOf(n); return t.trim() ? 'txt:' + pgHash(t) : null; }
        case 'reference': return d.set && d.file ? 'ref:' + d.set + '/' + d.file : null;
        case 'packshot': {
            if (!d.entry || !d.slot) return null;
            const e = (pg.packshots || []).find(x => x.id === d.entry);
            return 'ps:' + d.entry + '/' + d.slot + '/' + (e ? (e.updated || e.created || '') : '?');
        }
        case 'necktag': return d.brand ? 'tag:' + d.brand : null;
        case 'generate': case 'upscale': case 'describe': case 'enhance': case 'llm': return n.result ? pgResultKey(n) : null;
        // a GIF made is kept in the store: known by its id before it is fetched
        case 'gif': return n.result || n.resultId ? pgResultKey(n) : null;
        default: return null;
    }
}

// The value a node puts out: an image as a data URL, or a text; key says what it is,
// so a node made from it knows when it has changed. Null when there is nothing yet.
async function pgValueOf(n: PgNode): Promise<PgValue | null> {
    const d = n.data;
    const label = PG_TYPES[n.type].title;
    switch (n.type) {
        case 'image':
            return d.src ? { kind: 'image', value: d.src, key: 'img:' + (d.key || pgHash(d.src)), label: d.name || label } : null;
        case 'prompt':
        case 'savedPrompt': {
            const t = pgTextOf(n);
            return t.trim() ? { kind: 'text', value: t, key: 'txt:' + pgHash(t), label } : null;
        }
        case 'reference': {
            if (!d.set || !d.file) return null;
            // the addresses are signed for a few hours: a fresh one each time
            const set = (await pgReferenceSets(true)).find(s => s.id === d.set);
            const url = set && (set.images as string[]).find(u => u.split('?')[0].endsWith('/' + d.file));
            if (!url) throw new Error('The reference photo is not there any more');
            const blob = await (await authFetch(url)).blob();
            return { kind: 'image', value: await pgBlobToDataUrl(blob), key: 'ref:' + d.set + '/' + d.file, label: pgRefName(set) };
        }
        case 'packshot': {
            if (!d.entry || !d.slot) return null;
            const e = await getPackshotEntry(d.entry);
            const src = e && e.images && e.images[d.slot];
            if (!src) throw new Error('That packshot is not in the library any more');
            return { kind: 'image', value: src, key: 'ps:' + d.entry + '/' + d.slot + '/' + (e.updated || e.created || ''), label: pgPackshotName(e) + ' · ' + PG_SLOTS[d.slot] };
        }
        case 'necktag': {
            if (!d.brand) return null;
            const f = await brandNecktagFile(d.brand);
            if (!f) throw new Error('This brand has no neck label');
            const brand = STUDIO_BRANDS.find(b => b.id === d.brand);
            return { kind: 'image', value: await pgBlobToDataUrl(f), key: 'tag:' + d.brand, label: (brand ? brand.name + '\'s' : 'The') + ' neck label' };
        }
        case 'generate':
        case 'upscale':
        case 'describe':
        case 'enhance':
        case 'llm':
        case 'gif':
            return pgMadeValue(n);
        default:
            return null;
    }
}
// What a node is, in a few words, and a small picture of it when there is one at hand
function pgLabelOf(n: PgNode): string {
    const d = n.data;
    switch (n.type) {
        case 'image': return d.name || 'Image';
        case 'reference': { const set = (pg.refs || []).find(s => s.id === d.set); return set ? pgRefName(set) : 'Reference photo'; }
        case 'packshot': { const e = (pg.packshots || []).find(x => x.id === d.entry); return e ? pgPackshotName(e) + ' · ' + (PG_SLOTS[d.slot] || '') : 'Packshot'; }
        case 'necktag': { const b = STUDIO_BRANDS.find(x => x.id === d.brand); return b ? b.name + ' neck label' : 'Neck label'; }
        case 'prompt': case 'savedPrompt': { const t = pgTextOf(n).trim().replace(/\s+/g, ' '); return t ? (t.length > 46 ? t.slice(0, 44) + '…' : t) : PG_TYPES[n.type].title; }
        default: return PG_TYPES[n.type].title;
    }
}
function pgThumbOf(n: PgNode): string {
    const d = n.data;
    switch (n.type) {
        case 'image': return d.src || '';
        case 'reference': {
            const set = (pg.refs || []).find(s => s.id === d.set);
            if (!set || !d.file) return '';
            const i = (set.images as string[]).findIndex(u => u.split('?')[0].endsWith('/' + d.file));
            return i >= 0 ? ((set.thumbs || set.images)[i] || '') : '';
        }
        case 'packshot': {
            const e = (pg.packshots || []).find(x => x.id === d.entry);
            const src = e && d.slot && e.images[d.slot];
            return src && !String(src).startsWith('pending') ? src : '';
        }
        case 'necktag': return pg.tagUrls[d.brand] || '';
        case 'generate': return (n.versions || []).find(v => v.id === n.resultId)?.thumb || n.result || '';
        case 'upscale': return n.result || '';
        default: return '';
    }
}

// ── What Run will make ──
// A node is made again when it has nothing yet, when what goes into it or its
// settings are not what its result was made from, or when a node before it is
// made again. The Run button counts them, and they say so.
const pgSettingsOf = (n: PgNode): Record<string, any> => n.type === 'generate'
    ? { model: n.data.model, aspect: n.data.aspect, size: n.data.size, ...(Number(n.data.count) > 1 ? { count: Number(n.data.count) } : {}) }
    : n.type === 'describe' ? { focus: n.data.focus || 'garment' }
    : n.type === 'llm' ? { model: pgLlmModel(n.data.model), temperature: Number(n.data.temperature ?? 0.7), maxTokens: Number(n.data.maxTokens || 8192) }
    : n.type === 'gif' ? { fps: Number(n.data.fps) || 8, colours: Number(n.data.colours) || 128, width: Number(n.data.width) || 512, maxKb: Number(n.data.maxKb) || 0 } : {};
// what each maker must have to be made
const PG_MUST: Partial<Record<PgType, string>> = { generate: 'prompt', upscale: 'image', describe: 'image', enhance: 'idea', llm: 'prompt' };
const pgSigOf = (n: PgNode, keys: Record<string, string[]>) => JSON.stringify({ d: pgSettingsOf(n), i: keys });
function pgPlan(): Set<string> {
    const plan = new Set<string>();
    if (!pg.wf) return plan;
    const memo = new Map<string, boolean>();
    const visiting = new Set<string>();
    const stale = (id: string): boolean => {
        if (memo.has(id)) return memo.get(id)!;
        const n = pgNode(id);
        if (!n || !PG_TYPES[n.type].runs || visiting.has(id) || pgLocked(n)) return false;
        visiting.add(id);
        let s = false;
        const keys: Record<string, string[]> = {};
        const coming = new Set<string>();   // inputs a node before it will make
        for (const inp of PG_TYPES[n.type].ins) {
            keys[inp.name] = [];
            for (const e of pgInEdges(n.id, inp.name)) {
                const v = pgPeekEdge(e, stale);
                if (v.coming) { s = true; coming.add(inp.name); continue; }
                if (v.key) keys[inp.name].push(v.key);
            }
        }
        visiting.delete(id);
        // a node that lacks what it must have is not counted: Run would only say what is missing
        const must = PG_MUST[n.type];
        if (must && !keys[must].length && !coming.has(must)) { memo.set(id, false); return false; }
        // a GIF kept in the store counts as made, before it is fetched
        if (!s) s = !(n.result || (pgHeavy(n) && n.resultId)) || n.sig !== pgSigOf(n, keys);
        memo.set(id, s);
        if (s) plan.add(id);
        return s;
    };
    pg.wf.nodes.forEach(n => stale(n.id));
    return plan;
}
// how many times a node will be made: once, or once for each item of a list that goes in
function pgTimesOf(n: PgNode): number {
    if (PG_TYPES[n.type].takesAll) return 1;
    let times = 1;
    for (const inp of PG_TYPES[n.type].ins) for (const e of pgInEdges(n.id, inp.name)) times = Math.max(times, pgPeekEdge(e).n || 1);
    return times;
}
// what a run of these costs, about: the pictures made, at the engines' list prices
function pgCostOf(ids: Iterable<string>): { pictures: number; usd: number } {
    let pictures = 0, usd = 0;
    for (const id of ids) {
        const n = pgNode(id);
        if (!n || n.type !== 'generate') continue;
        const k = Math.max(1, Number(n.data.count) || 1) * pgTimesOf(n);
        pictures += k;
        usd += k * ((PG_PRICE[n.data.model || 'gemini'] || PG_PRICE.gemini)[n.data.size || '2k'] || 0.134);
    }
    return { pictures, usd };
}
function pgPaintPlan() {
    if (!pg.wf || !pg.el.world) return;
    const plan = pg.running ? new Set<string>() : pgPlan();
    pg.wf.nodes.forEach(n => {
        const card = pgCard(n.id);
        if (!card) return;
        const stale = plan.has(n.id) && !!n.result && n.status !== 'running' && n.status !== 'waiting';
        card.classList.toggle('is-stale', stale);
        const badge = card.querySelector('.pg-stale') as HTMLElement | null;
        if (badge) badge.hidden = !stale;
    });
    const btn = document.getElementById('pgRun');
    if (!btn) return;
    const count = btn.querySelector('.pg-run-count') as HTMLElement | null;
    const cost = pgCostOf(plan);
    if (count) { count.textContent = plan.size ? String(plan.size) : ''; count.hidden = !plan.size || pg.running; }
    btn.title = pg.running ? 'Running' : plan.size
        ? 'Makes ' + plan.size + ' node' + (plan.size > 1 ? 's' : '')
            + (cost.pictures ? ' · ' + cost.pictures + ' picture' + (cost.pictures > 1 ? 's' : '') + ', about ' + cost.usd.toFixed(2) + ' USD' : '') + ' (Ctrl+Enter)'
        : 'Run (Ctrl+Enter): nothing new to make';
}

// ── Running ──
// Each node that makes something runs once per Run, after the nodes it is made
// from; nodes that do not wait for each other run at the same time. A node whose
// inputs and settings are what its result was made from is not made again (force:
// its own ▶, which always makes it again). A list that goes in (an Array's Each
// item) makes it once for each item, and what it makes is a list in turn.
async function pgRun(only?: string | string[]) {
    const onlyIds = only ? ([] as string[]).concat(only).filter(id => { const n = pgNode(id); return n && PG_TYPES[n.type].runs; }) : null;
    if (onlyIds && !onlyIds.length) return;
    if (!pg.wf) return;
    // asked for while a run goes on: it runs when that one is done
    if (pg.running) {
        pg.queued.push(onlyIds || 'all');
        showToast(onlyIds ? 'It is made when this run is done.' : 'Run again when this run is done.', { title: 'Next' });
        return;
    }
    pg.running = true;
    pg.stopping = false;
    pg.made.clear();
    pgLib.run = pgId('run');
    pgPaintRun();
    // Run N times (Node Banana's runBatch): one run after another; after the first,
    // every node is made again, for new pictures; a failure or Stop ends it
    const times = onlyIds ? 1 : Math.max(1, Math.min(50, Number(pg.wf.runCount) || 1));
    let madeAny = false;
    try {
        if (typeof discoverModels === 'function') await discoverModels();
        for (let i = 1; i <= times; i++) {
            pg.runOf = times > 1 ? { i, times } : null;
            pg.made.clear();
            pgPaintRun();
            await pgRunPass(onlyIds, i > 1);
            await pgKeepGalleries();
            await pgLibKeep().catch(err => { console.warn('playground: the library', err); return 0; });
            if (pg.made.size) madeAny = true;
            if (pg.stopping || pg.wf.nodes.some(n => n.status === 'error')) break;
        }
    } finally {
        const stopped = pg.stopping;
        pg.running = false;
        pg.stopping = false;
        pg.runOf = null;
        pgPaintRun();
        if (stopped) showToast('Stopped: what was being made is kept; nothing else was started.', { title: 'Stopped' });
        // the flows show what came through them; an Output or Gallery whose picture
        // was just made shows it fading in
        pg.wf.nodes.filter(n => PG_TYPES[n.type].flows).forEach(n => pgRenderNode(n.id, true));
        pg.wf.nodes.filter(n => n.type === 'output' || n.type === 'gallery').forEach(n => {
            if (pgUpstream(n.id).some(id => pg.made.has(id))) pg.fresh.add(n.id);
            pgRenderNode(n.id);
        });
        const failed = pg.wf.nodes.some(n => n.status === 'error');
        if (!onlyIds && !madeAny && !stopped && !failed && pg.wf.nodes.some(n => PG_TYPES[n.type].runs && n.status !== 'skipped')) {
            showToast('Nothing has changed since the last run, so nothing was made again. ▶ on a node makes it again.', { title: 'Up to date' });
        }
        pgSaveSoon();
        const next = pg.queued.shift();
        if (next) setTimeout(() => pgRun(next === 'all' ? undefined : next), 0);
    }
}
// One pass of a run: each maker made once, after what it is made from (force: made
// again even if nothing has changed: its own ▶, or a run after the first)
async function pgRunPass(onlyIds: string[] | null, forceAll: boolean) {
    const memo = new Map<string, Promise<void>>();
    const make = (id: string, force = forceAll): Promise<void> => {
        if (!memo.has(id)) memo.set(id, pgMake(id, force, (up) => make(up)).catch(() => undefined));
        return memo.get(id)!;
    };
    const targets = onlyIds || pg.wf!.nodes.filter(n => PG_TYPES[n.type].runs).map(n => n.id);
    await Promise.all(targets.map(id => make(id, forceAll || !!onlyIds)));
}
// At most this many engine calls at the same time (Node Banana: 3, 1 to 10), kept in this browser
const PG_LIMIT_KEY = 'creator.playground.atOnce';
function pgLimit(): number { try { return Math.min(10, Math.max(1, Number(localStorage.getItem(PG_LIMIT_KEY)) || 3)); } catch { return 3; } }
const __pgSlots = { busy: 0, queue: [] as (() => void)[] };
async function pgSlot(): Promise<() => void> {
    if (__pgSlots.busy >= pgLimit()) await new Promise<void>(r => __pgSlots.queue.push(r));
    __pgSlots.busy++;
    let freed = false;
    return () => {
        if (freed) return;
        freed = true;
        __pgSlots.busy--;
        const next = __pgSlots.queue.shift();
        if (next) next();
    };
}
// Stop: what is being made is finished, nothing new is started
function pgStop() {
    if (!pg.running || pg.stopping) return;
    pg.stopping = true;
    pgPaintRun();
}

// nothing comes along this wire because a Switch before it is off, or a Condition's
// rule is not matched (else: it is simply empty, and the node says what is missing)
function pgWireBlocked(e: PgEdge, seen = new Set<string>()): boolean {
    const src = pgNode(e.from);
    if (!src || seen.has(src.id)) return false;
    seen.add(src.id);
    if (pgWireClosed(e)) return true;
    if (!PG_TYPES[src.type].flows) return false;
    return pg.wf!.edges.filter(x => x.to === src.id).some(x => pgWireBlocked(x, seen));
}
// the nodes a node is made from, all the way back
function pgUpstream(id: string, seen = new Set<string>()): string[] {
    for (const e of pg.wf!.edges) if (e.to === id && !seen.has(e.from)) { seen.add(e.from); pgUpstream(e.from, seen); }
    return [...seen];
}

// One item of a node's work: what goes in, one value per input port (a list's item)
type PgIns = Record<string, PgValue[]>;
interface PgMade { src?: string; text?: string; meta: Record<string, string>; extra?: { src: string; meta: Record<string, string> }[]; prompt?: string; ref?: string; }

async function pgMake(id: string, force: boolean, make: PgMakeFn): Promise<void> {
    const n = pgNode(id);
    if (!n || !PG_TYPES[n.type].runs) return;
    // in a locked group: left as it is, what it has made goes on (as Node Banana's lock)
    if (pgLocked(n)) return;
    // what goes in, the makers among them made first: meanwhile it waits for them
    const inputs: PgIns = {};
    const keys: Record<string, string[]> = {};
    const makers = [...new Set(PG_TYPES[n.type].ins.flatMap(inp => pgInEdges(n.id, inp.name)).flatMap(e => [e.from, ...pgUpstream(e.from)]))]
        .map(x => pgNode(x)).filter((m): m is PgNode => !!m && !!PG_TYPES[m.type].runs);
    const direct = PG_TYPES[n.type].ins.flatMap(inp => pgInEdges(n.id, inp.name)).map(e => pgNode(e.from)).filter((m): m is PgNode => !!m);
    const before = n.status;
    if (makers.length) pgSetStatus(n, 'waiting', 'Waiting for ' + [...new Set((direct.some(d => PG_TYPES[d.type].runs) ? direct.filter(d => PG_TYPES[d.type].runs) : makers).map(m => PG_TYPES[m.type].title))].join(' and '));
    let blocked = false;
    try {
        for (const inp of PG_TYPES[n.type].ins) {
            inputs[inp.name] = [];
            keys[inp.name] = [];
            for (const e of pgInEdges(n.id, inp.name)) {
                const v = await pgValueAlong(e, make);
                if (!v) { if (inp.name === PG_MUST[n.type] && pgWireBlocked(e)) blocked = true; continue; }
                keys[inp.name].push(v.key);
                // a bundle goes in as its items, side by side (and any list, into GIF)
                if (v.items && (v.bundle || PG_TYPES[n.type].takesAll)) inputs[inp.name].push(...v.items); else inputs[inp.name].push(v);
            }
        }
    } catch (e) {
        pgSetStatus(n, 'error', (e as Error).message || String(e));
        return;
    }
    // wired, but nothing came: a switch or rule before it is closed. Skipped, as Node Banana does
    const must = PG_MUST[n.type];
    if (must && blocked && !inputs[must].length) { pgSetStatus(n, 'skipped', 'Skipped: a switch or rule before it is closed'); return; }
    const sig = pgSigOf(n, keys);
    if (!force && (n.result || (pgHeavy(n) && n.resultId)) && n.sig === sig) {
        if (n.status === 'waiting' || n.status === 'skipped') pgSetStatus(n, before === 'error' || before === 'skipped' ? 'done' : (before || 'done'), '');
        return;
    }
    // Stop pressed while it waited: it is not made
    if (pg.stopping) { pgSetStatus(n, before === 'waiting' ? 'idle' : (before || 'idle'), ''); return; }
    pg.made.add(n.id);
    // a list that goes in: made once for each item, the shorter lists starting over,
    // as many at a time as the run allows
    const lists = Object.values(inputs).flat().filter(v => v.items && v.items.length > 1);
    const times = lists.length ? Math.max(...lists.map(v => v.items!.length)) : 1;
    const pick = (i: number): PgIns => Object.fromEntries(Object.entries(inputs).map(([k, vs]) => [k, vs.map(v => v.items && v.items.length > 1 ? v.items[i % v.items.length] : v)]));
    try {
        const slots: (PgMade | null)[] = new Array(times).fill(null);
        let failed = 0, done = 0, firstError: unknown = null;
        if (__pgSlots.busy >= pgLimit()) pgSetStatus(n, 'waiting', 'Waiting its turn: ' + pgLimit() + ' at a time');
        await Promise.all(Array.from({ length: times }, async (_, i) => {
            const free = await pgSlot();
            try {
                if (pg.stopping) return;
                const say = (msg: string) => pgSetStatus(n, 'running', (times > 1 ? (done + 1) + ' of ' + times + ' · ' : '') + msg);
                say(times > 1 ? 'Making' : 'Starting');
                slots[i] = await pgMakeOne(n, pick(i), say);
                done++;
            } catch (e) { failed++; if (!firstError) firstError = e; }
            finally { free(); }
        }));
        const made = slots.filter((m): m is PgMade => !!m);
        if (!made.length) { if (pg.stopping) { pgSetStatus(n, 'idle', ''); pg.made.delete(n.id); return; } throw firstError || new Error('None of the ' + times + ' could be made'); }
        if (failed && times > 1) showToast((times - failed) + ' of ' + times + ' were made; the others failed.', { type: 'warn', title: PG_TYPES[n.type].title });
        else if (made.length < times) showToast(made.length + ' of ' + times + ' were made before Stop.', { title: PG_TYPES[n.type].title });
        await pgKeepMade(n, made, sig, times > 1);
        n.sig = sig;
        pgSetStatus(n, 'done', '');
        pgReportMade(n, made);
    } catch (e) {
        pgSetStatus(n, 'error', pgErrorText(e));
        pgReportError(n, e);
    }
    // what it feeds shows what it gets now (an Array its items, a constructor its text)
    pgRenderDown(n.id);
}

// What the Playground makes and what fails in it, for the admin page (2026-10-20, src/admin.ts)
function pgReportMade(n: PgNode, made: PgMade[]) {
    const act = (window as any).ffActivity;
    if (!act || !pg.wf) return;
    for (const m of made) for (const x of [m, ...(m.extra || [])]) {
        if (!x.src || !/^data:image\//.test(x.src)) continue;
        // what it was asked, and the first picture that went in, for the close look on the admin page
        act.image({ tool: 'playground', src: x.src, slot: PG_TYPES[n.type].title, product: pg.wf.name, model: x.meta?.model, engine: x.meta?.engine, settings: pgSettingsOf(n), ref: pg.wf.id + '/' + n.id,
            prompt: m.prompt ? m.prompt.slice(0, 4000) : undefined, refSrc: m.ref && /^data:image\//.test(m.ref) ? m.ref : undefined });
    }
}
function pgReportError(n: PgNode, e: unknown) {
    const msg = String((e as Error)?.message || e || '');
    // a node after one that failed says so; the one that failed is the one to see
    if (!msg || /before it could not be made|^Stopped/.test(msg)) return;
    (window as any).ffActivity?.error({ tool: 'playground', message: msg, slot: PG_TYPES[n.type].title, product: pg.wf?.name, model: n.data?.model, ref: pg.wf ? pg.wf.id + '/' + n.id : undefined });
}

// One go of a maker, on one value per input
async function pgMakeOne(n: PgNode, inputs: PgIns, say: (msg: string) => void): Promise<PgMade> {
    const joined = (vs: PgValue[]) => vs.map(v => v.value.trim()).filter(Boolean).join('\n\n');
    if (n.type === 'generate') {
        const prompt = joined(inputs.prompt);
        if (!prompt) throw new Error('Connect a prompt');
        const model = n.data.model || 'gemini';
        const max = PG_MAX_IMAGES[model] || 14;
        if (inputs.images.length > max) throw new Error((PG_MODELS.find(m => m.id === model)?.label || model) + ' takes at most ' + max + ' images; ' + inputs.images.length + ' go in');
        const count = Math.max(1, Math.min(4, Number(n.data.count) || 1));
        say(count > 1 ? 'Making ' + count : 'Starting');
        const files = await Promise.all(inputs.images.map((v, i) => pgFileOf(v.value, 'image-' + (i + 1))));
        let done = 0;
        const one = (i: number) => geminiImage(prompt, files, {
            aspect: n.data.aspect || '1:1', resolution: n.data.size || '2k', promptAsIs: true, fit: false,
            preferOpenAi: model === 'openai', preferKie: model === 'kie',
            onLog: i === 0 && count === 1 ? (msg: string) => say(String(msg).trim().slice(0, 90)) : () => {},
        }).then(r => { done++; if (count > 1) say('Making ' + count + ' · ' + done + ' done'); return r; });
        const settled = await Promise.allSettled(Array.from({ length: count }, (_, i) => one(i)));
        const ok = settled.filter((s): s is PromiseFulfilledResult<any> => s.status === 'fulfilled').map(s => s.value);
        if (!ok.length) throw (settled[0] as PromiseRejectedResult).reason;
        if (ok.length < count) showToast(ok.length + ' of ' + count + ' pictures were made; the others failed.', { type: 'warn', title: 'Generate' });
        const pics = ok.map(r => ({ src: 'data:' + (r.mime || 'image/png') + ';base64,' + r.data, meta: { engine: r.engine || model, model: r.model || '' } }));
        return { src: pics[0].src, meta: pics[0].meta, extra: pics.slice(1), prompt, ref: inputs.images[0]?.value };
    }
    if (n.type === 'upscale') {
        const src = inputs.image[0];
        if (!src) throw new Error('Connect an image');
        say('Waiting its turn');
        const up = await upscaleImageTo4K(src.value, (done, total, position) => {
            say(total ? '4K · ' + Math.round(100 * done / total) + '%' : position ? '4K · waiting (' + position + ')' : '4K · waiting');
        });
        return { src: await pgBlobToDataUrl(up.blob), meta: { engine: '4k', model: 'FloorFlow 4K', size: up.width + '×' + up.height } };
    }
    if (n.type === 'describe') {
        const src = inputs.image[0];
        if (!src) throw new Error('Connect an image');
        say('Looking at the picture');
        const text = await geminiText(PG_DESCRIBE[n.data.focus as string] || PG_DESCRIBE.garment, [await pgFileOf(src.value, 'image-1')], { models: PG_TEXT_MODELS, temperature: 1 });
        if (!text.trim()) throw new Error('No words came back');
        return { text: pgCleanText(text), meta: { engine: 'gemini', model: 'Gemini (text)' } };
    }
    if (n.type === 'enhance') {
        const idea = joined(inputs.idea);
        if (!idea) throw new Error('Connect an idea: a Prompt with a few words');
        say('Writing the prompt');
        const files = await Promise.all(inputs.images.map((v, i) => pgFileOf(v.value, 'image-' + (i + 1))));
        const text = await geminiText(pgEnhanceInstruction(idea, files.length), files, { models: PG_TEXT_MODELS, temperature: 1 });
        if (!text.trim()) throw new Error('No prompt came back');
        return { text: pgCleanText(text), meta: { engine: 'gemini', model: 'Gemini (text)' } };
    }
    if (n.type === 'llm') {
        const prompt = joined(inputs.prompt);
        if (!prompt) throw new Error('Connect a prompt');
        const s = pgSettingsOf(n);
        say('Writing');
        const text = await pgLlm(s.model, prompt, inputs.images.map(v => v.value), s.temperature, s.maxTokens);
        if (!text.trim()) throw new Error('No text came back');
        return { text: text.trim(), meta: { engine: 'gemini', model: (PG_LLM_MODELS.find(m => m[0] === s.model) || [s.model, s.model])[1] } };
    }
    if (n.type === 'gif') {
        const pictures = inputs.frames.map(v => v.value);
        if (pictures.length < 2) throw new Error('Connect two pictures or more');
        const r = await pgMakeGif(pictures, pgSettingsOf(n), say);
        const kb = Math.round(r.bytes / 1024);
        return { src: r.src, meta: { engine: 'browser', model: 'GIF', size: r.w + ' × ' + r.h + ' · ' + (kb > 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB') } };
    }
    throw new Error('This node makes nothing');
}

// What a maker made, kept: a picture as a version (a list's pictures each one, the
// list kept by their ids), a text as its result (a list's texts side by side).
async function pgKeepMade(n: PgNode, made: PgMade[], sig: string, list: boolean) {
    if (PG_TYPES[n.type].out === 'text') {
        n.texts = list ? made.map(m => m.text || '') : undefined;
        n.result = made[0].text || '';
        n.meta = made[0].meta;
        return;
    }
    if (n.type === 'generate' || pgHeavy(n)) {
        const ids = await pgAddVersions(n, made.flatMap(m => [{ src: m.src!, meta: m.meta }, ...(m.extra || [])]), sig, made.map(m => 1 + (m.extra || []).length));
        n.listIds = list ? ids : undefined;
        return;
    }
    // 4K: the picture itself, or a list of them in the results store
    if (list) {
        const ids: string[] = [];
        for (const m of made) {
            const rid = pgId('r');
            try { await pgResultPut({ id: rid, wf: pg.wf!.id, node: n.id, src: m.src!, at: Date.now() }); } catch { /* shown, not kept */ }
            __pgSrc.set(rid, m.src!);
            ids.push(rid);
        }
        (n.listIds || []).forEach(old => { if (!ids.includes(old)) pgResultDelete(old); });
        n.listIds = ids;
        n.resultId = ids[0];
    } else {
        (n.listIds || []).forEach(old => pgResultDelete(old));
        n.listIds = undefined;
        n.resultId = undefined;
    }
    n.result = made[0].src!;
    n.meta = made[0].meta;
}

// An LLM node's request: Node Banana's /api/llm sends the images, then the prompt,
// with the temperature and the most tokens to write; here through our Gemini proxy
async function pgLlm(model: string, prompt: string, images: string[], temperature: number, maxTokens: number, json = false): Promise<string> {
    const parts: any[] = [];
    for (const src of images) {
        const m = /^data:([^;,]+);base64,(.*)$/.exec(src);
        if (m) parts.push({ inlineData: { mimeType: m[1], data: m[2] } });
    }
    parts.push({ text: prompt });
    return withRetry<string>(async () => {
        const r = await apiFetch(GEMINI_BASE + '/' + model + ':generateContent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contents: [{ role: 'user', parts }], generationConfig: { temperature, maxOutputTokens: maxTokens, ...(json ? { responseMimeType: 'application/json' } : {}) } }),
        });
        if (!r.ok) throw new Error('LLM ' + r.status + ': ' + (await r.text()).slice(0, 200));
        const j = await r.json();
        return ((j.candidates?.[0]?.content?.parts || []) as { text?: string; thought?: boolean }[]).filter(p => p.text && !p.thought).map(p => p.text).join('');
    }, 'LLM (' + model + ')', 2);
}
// Gemini, streamed (as its own clients' sendMessageStream does, through our proxy): the
// text so far each time more comes, and the short summaries of its thinking; the
// whole text at the end. Stopped by the signal.
async function pgLlmStream(model: string, prompt: string, images: string[], temperature: number, maxTokens: number,
    on: { text?: (all: string) => void; thought?: (t: string) => void; signal?: AbortSignal }): Promise<string> {
    const parts: any[] = [];
    for (const src of images) {
        const m = /^data:([^;,]+);base64,(.*)$/.exec(src);
        if (m) parts.push({ inlineData: { mimeType: m[1], data: m[2] } });
    }
    parts.push({ text: prompt });
    const ask = (thoughts: boolean) => apiFetch(GEMINI_BASE + '/' + model + ':streamGenerateContent?alt=sse', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: on.signal,
        body: JSON.stringify({ contents: [{ role: 'user', parts }], generationConfig: { temperature, maxOutputTokens: maxTokens, responseMimeType: 'application/json', ...(thoughts ? { thinkingConfig: { includeThoughts: true } } : {}) } }),
    });
    let r = await ask(true);
    // a model that does not say what it is thinking is asked without
    if (r.status === 400) r = await ask(false);
    if (!r.ok || !r.body) throw new Error('LLM ' + r.status + ': ' + (await r.text()).slice(0, 200));
    if (!/event-stream/i.test(r.headers.get('content-type') || '')) {
        // not a stream after all: the chunks (or the one answer) read whole
        const j = await r.json().catch(() => null);
        const text = ([] as any[]).concat(j || []).flatMap(c => (c?.candidates?.[0]?.content?.parts || []) as { text?: string; thought?: boolean }[]).filter(x => x.text && !x.thought).map(x => x.text).join('');
        if (text) on.text?.(text);
        return text;
    }
    const reader = r.body.getReader();
    const dec = new TextDecoder();
    let buf = '', text = '';
    const line = (l: string) => {
        if (!l.startsWith('data:')) return;
        let j: any;
        try { j = JSON.parse(l.slice(5).trim()); } catch { return; }
        if (j.error) throw new Error('LLM: ' + String(j.error.message || '').slice(0, 200));
        for (const p of (j.candidates?.[0]?.content?.parts || []) as { text?: string; thought?: boolean }[]) {
            if (!p.text) continue;
            if (p.thought) on.thought?.(p.text);
            else { text += p.text; on.text?.(text); }
        }
    };
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf('\n')) >= 0) { line(buf.slice(0, i).replace(/\r$/, '')); buf = buf.slice(i + 1); }
    }
    if (buf.trim()) line(buf.trim());
    return text;
}
// The reply and the steps of an answer as it is written: the reply so far, and each
// step of "ops" once it is whole
function pgAskPartial(text: string): { reply: string; ops: any[]; plan: string[] } {
    let reply = '';
    // the plan's stages, each once it is whole
    const plan: string[] = [];
    const pm = /"plan"\s*:\s*\[/.exec(text);
    if (pm) {
        let k = pm.index + pm[0].length;
        for (;;) {
            while (k < text.length && /[\s,]/.test(text[k])) k++;
            if (k >= text.length || text[k] !== '"') break;
            let out = '', esc = false, closed = false;
            for (k++; k < text.length; k++) {
                const ch = text[k];
                if (esc) { out += '\\' + ch; esc = false; continue; }
                if (ch === '\\') { esc = true; continue; }
                if (ch === '"') { closed = true; k++; break; }
                out += ch;
            }
            if (!closed) break;
            try { plan.push(JSON.parse('"' + out + '"')); } catch { plan.push(out); }
        }
    }
    const m = /"reply"\s*:\s*"/.exec(text);
    if (m) {
        let out = '', esc = false;
        for (let k = m.index + m[0].length; k < text.length; k++) {
            const ch = text[k];
            if (esc) { out += '\\' + ch; esc = false; continue; }
            if (ch === '\\') { esc = true; continue; }
            if (ch === '"') break;
            out += ch;
        }
        try { reply = JSON.parse('"' + out.replace(/\\u[0-9a-fA-F]{0,3}$/, '') + '"'); } catch { reply = out; }
    }
    const ops: any[] = [];
    const o = /"ops"\s*:\s*\[/.exec(text);
    if (o) {
        let depth = 0, start = -1, inStr = false, esc = false;
        for (let k = o.index + o[0].length; k < text.length; k++) {
            const ch = text[k];
            if (inStr) { if (esc) esc = false; else if (ch === '\\') esc = true; else if (ch === '"') inStr = false; continue; }
            if (ch === '"') inStr = true;
            else if (ch === '{') { if (depth === 0) start = k; depth++; }
            else if (ch === '}') { depth--; if (depth === 0 && start >= 0) { try { ops.push(JSON.parse(text.slice(start, k + 1))); } catch { /* not a step */ } start = -1; } }
            else if (ch === ']' && depth === 0) break;
        }
    }
    return { reply, ops, plan };
}
// what a text model wrote, without the fences and headings it may put round it
const pgCleanText = (t: string) => t.trim().replace(/^```[a-z]*\s*/i, '').replace(/\s*```$/, '').replace(/^(?:\*\*)?prompt:?(?:\*\*)?\s*/i, '').trim();

// A Generate's pictures: each kept as a version, the newest shown and sent on. For a
// list, per: how many pictures each item made; the first of each is the list's.
async function pgAddVersions(n: PgNode, made: { src: string; meta: Record<string, string> }[], sig: string, per?: number[]): Promise<string[]> {
    const wf = pg.wf!.id;
    const versions = n.versions ? [...n.versions] : [];
    // a picture made before versions were kept becomes the first of them
    if (!versions.length && n.result) {
        const id = pgId('r');
        try { await pgResultPut({ id, wf, node: n.id, src: n.result, at: Date.now() - 1 }); versions.push({ id, at: Date.now() - 1, sig: n.sig || '', meta: n.meta, thumb: await pgThumb(n.result) }); } catch { /* not kept */ }
    }
    const fresh: PgVersion[] = [];
    for (const m of made) {
        const id = pgId('r');
        try { await pgResultPut({ id, wf, node: n.id, src: m.src, at: Date.now() }); } catch { /* shown, not kept */ }
        __pgSrc.set(id, m.src);
        fresh.push({ id, at: Date.now(), sig, meta: m.meta, thumb: await pgThumb(m.src) });
    }
    // the first picture of each item, in the items' order
    const firsts: string[] = [];
    if (per) { let at = 0; per.forEach(k => { firsts.push(fresh[at].id); at += k; }); }
    const all = [...fresh, ...versions];
    // a list's pictures are kept as long as the list is
    const keep = Math.max(PG_KEEP_VERSIONS, firsts.length + 10);
    all.slice(keep).forEach(v => { if (!firsts.includes(v.id)) pgResultDelete(v.id); });
    n.versions = all.slice(0, keep);
    n.result = made[0].src;
    n.resultId = fresh[0].id;
    n.meta = made[0].meta;
    return firsts;
}
// Going back to a version: it is shown and sent on, as made from what it was made from
async function pgUseVersion(n: PgNode, vid: string) {
    const v = (n.versions || []).find(x => x.id === vid);
    if (!v || n.resultId === vid) return;
    const rec = vid === n.resultId ? { src: n.result! } : await pgResultGet(vid);
    if (!rec) { showToast('That version is not kept in this browser any more.', { type: 'warn', title: 'Version' }); return; }
    pgRecord();
    n.result = rec.src;
    n.resultId = vid;
    n.listIds = undefined;   // the one chosen goes on, in place of a list
    n.sig = v.sig;
    n.meta = v.meta;
    n.status = 'done';
    n.message = '';
    pg.fresh.add(n.id);
    pgRenderNode(n.id);
    pgRenderDown(n.id);
    pgSaveSoon();
}

function pgErrorText(e: unknown): string {
    const msg = String((e as Error)?.message || e || 'Something went wrong');
    // the engines answer with a chain, a line per engine; the first line says it
    return msg.split('\n')[0].replace(/^Generation failed:\s*/i, '').slice(0, 160) || 'Something went wrong';
}
function pgSetStatus(n: PgNode, status: PgNode['status'], message: string) {
    const prev = n.status;
    n.status = status;
    n.message = message;
    const card = pgCard(n.id);
    // a new line while it runs: only the words change, the motion goes on
    if (status === prev && card && card.querySelector('.pg-status')) {
        card.querySelector('.pg-status')!.textContent = message;
        return;
    }
    if (status === 'done' && prev === 'running') {
        // its result fades in, and a pulse runs along the wires it feeds
        pg.fresh.add(n.id);
        const until = Date.now() + 1100;
        pg.wf!.edges.filter(e => e.from === n.id).forEach(e => pg.pulse.set(e.id, until));
        setTimeout(pgRenderEdges, 1150);
    }
    if (status === 'error' && prev !== 'error') pg.shake.add(n.id);
    if (status === 'running' && prev !== 'running') pg.since.set(n.id, Date.now());
    if (status !== 'running') pg.since.delete(n.id);
    pgRenderNode(n.id);
}

// ── Drawing ──
function pgRenderAll() {
    const world = pg.el.world;
    world.querySelectorAll('.pg-node').forEach(el => el.remove());
    pg.wf!.nodes.forEach(n => pgRenderNode(n.id, true));
    pgRenderEdges();
    pgApplyView();
    (document.getElementById('pgName') as HTMLInputElement).value = pg.wf!.name;
    pgPaintEmpty();
    pgPaintPlan();
}

function pgPaintEmpty() {
    const empty = document.getElementById('pgEmpty');
    if (empty) empty.hidden = pg.wf!.nodes.length > 0;
}

function pgRenderNode(id: string, quiet = false) {
    const n = pgNode(id);
    const world = pg.el.world;
    const old = pgCard(id);
    if (!n) { old?.remove(); if (!quiet) pgRenderEdges(); return; }
    const t = PG_TYPES[n.type];
    const el = document.createElement('div');
    el.className = 'pg-node is-' + n.type + (t.ours ? ' is-ours' : '') + (t.runs ? ' is-maker' : '') + (n.status === 'running' ? ' is-running' : '') + (n.status === 'waiting' ? ' is-waiting' : '') + (n.status === 'error' ? ' is-error' : '') + (pg.sel.has(n.id) ? ' is-selected' : '') + (pg.pic === n.id ? ' is-pic-chosen' : '');
    for (const [set, cls] of [[pg.fresh, 'is-fresh'], [pg.shake, 'is-shake'], [pg.pop, 'is-pop']] as [Set<string>, string][]) {
        if (set.has(n.id)) { el.classList.add(cls); set.delete(n.id); }
    }
    el.dataset.id = n.id;
    el.style.left = n.x + 'px';
    el.style.top = n.y + 'px';
    el.style.width = t.width + 'px';
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', t.title);
    const ins = t.ins.map(inp => { const kind = pgInKind(n, inp); return '<div class="pg-in" data-port="' + inp.name + '" data-type="' + kind + '"><span class="pg-port is-' + kind + '" data-port-in="' + inp.name + '" title="Drag from here to connect" aria-hidden="true"></span><span class="pg-in-label">' + pgEsc(inp.label) + (inp.multi && inp.type === 'text' && n.type !== 'construct' ? '<small>several are joined</small>' : '') + '</span></div>'; }).join('');
    // one output: a port on the header; several: a row each, under the body
    const outs = pgOuts(n);
    const single = outs.length === 1 && outs[0].name === 'out';
    el.innerHTML = '<div class="pg-head"><span class="pg-kind is-' + (outs[0]?.type || 'none') + '">' + pgIcon(t.icon) + '</span><span class="pg-title">' + pgEsc(t.title) + '</span>'
        + (t.ours ? '<span class="pg-ours" title="FloorFlow\'s own">FloorFlow</span>' : '')
        + (t.runs ? '<span class="pg-stale" hidden title="What goes in has changed since this was made: Run makes it again">Changed</span>' : '')
        + (t.runs ? '<button type="button" class="pg-icon" data-pg-act="run" aria-label="Run ' + pgEsc(t.title) + '" title="Make this node again">' + pgIcon('run') + '</button>' : '')
        + '<button type="button" class="pg-icon" data-pg-act="delete" aria-label="Delete ' + pgEsc(t.title) + '" title="Delete">' + pgIcon('close') + '</button></div>'
        + (ins ? '<div class="pg-ins">' + ins + '</div>' : '')
        + '<div class="pg-body">' + pgBodyHtml(n) + '</div>'
        + (!single && (outs.length || n.type === 'switch' || n.type === 'condition') ? '<div class="pg-outs">' + outs.map(o => pgOutRowHtml(n, o)).join('') + pgOutsFootHtml(n) + '</div>' : '')
        + (single ? '<span class="pg-port pg-out is-' + outs[0].type + '" data-port-out="out" title="Drag to connect, or into empty space to add a node" aria-hidden="true"></span>' : '');
    el.classList.toggle('is-locked', pgLocked(n));
    if (old) old.replaceWith(el); else world.appendChild(el);
    pgWatchCard(el, old);
    pgAfterRender(n, el);
    if (!quiet) { pgRenderEdges(); pgPaintPlan(); }
}
// An output's row: its name and port, and on a Switch or Condition what opens it
function pgOutRowHtml(n: PgNode, o: PgOutput): string {
    const port = '<span class="pg-port pg-out-port is-' + o.type + '" data-port-out="' + pgEsc(o.name) + '" title="Drag to connect, or into empty space to add a node" aria-hidden="true"></span>';
    if (n.type === 'switch') {
        const s = pgSwitches(n).find(x => x.id === o.name)!;
        return '<div class="pg-outrow is-switch' + (s.on ? '' : ' is-off') + '" data-out="' + pgEsc(o.name) + '">'
            + '<label class="pg-toggle" title="' + (s.on ? 'On: what comes in goes on here' : 'Off: what it feeds is skipped') + '"><input type="checkbox" data-pg-sw-on="' + pgEsc(s.id) + '"' + (s.on ? ' checked' : '') + ' aria-label="' + pgEsc(s.name) + ' on"><span aria-hidden="true"></span></label>'
            + '<input type="text" class="pg-mini" data-pg-sw-name="' + pgEsc(s.id) + '" value="' + pgEsc(s.name) + '" aria-label="Name of the output" spellcheck="false" autocomplete="off">'
            + (pgSwitches(n).length > 1 ? '<button type="button" class="pg-icon is-small" data-pg-sw-del="' + pgEsc(s.id) + '" aria-label="Remove ' + pgEsc(s.name) + '" title="Remove">' + pgIcon('close') + '</button>' : '')
            + port + '</div>';
    }
    if (n.type === 'condition' && o.name !== 'else') {
        const r = pgRules(n).find(x => x.id === o.name)!;
        const open = pgConditionOpen(n);
        return '<div class="pg-outrow is-rule' + (open && open.has(r.id) ? ' is-match' : '') + '" data-out="' + pgEsc(o.name) + '">'
            + '<div class="pg-rule-top"><span class="pg-match" title="' + (open && open.has(r.id) ? 'The text matches this rule' : 'The text does not match this rule') + '" aria-hidden="true"></span>'
            + '<input type="text" class="pg-mini is-label" data-pg-rule-label="' + pgEsc(r.id) + '" value="' + pgEsc(r.label) + '" aria-label="Name of the rule" spellcheck="false" autocomplete="off">'
            + '<button type="button" class="pg-icon is-small" data-pg-rule-del="' + pgEsc(r.id) + '" aria-label="Remove ' + pgEsc(r.label) + '" title="Remove">' + pgIcon('close') + '</button></div>'
            + '<div class="pg-rule-row"><select class="pg-mini" data-pg-rule-mode="' + pgEsc(r.id) + '" aria-label="How it matches">'
            + [['contains', 'contains'], ['is', 'is exactly'], ['starts', 'starts with'], ['ends', 'ends with']].map(([v, l]) => '<option value="' + v + '"' + (r.mode === v ? ' selected' : '') + '>' + l + '</option>').join('') + '</select>'
            + '<input type="text" class="pg-mini" data-pg-rule-value="' + pgEsc(r.id) + '" value="' + pgEsc(r.value) + '" placeholder="red, blue" aria-label="Words to match, split by commas" spellcheck="false" autocomplete="off"></div>'
            + port + '</div>';
    }
    if (n.type === 'condition') {
        const open = pgConditionOpen(n);
        return '<div class="pg-outrow is-rule is-else' + (open && open.has('else') ? ' is-match' : '') + '" data-out="else"><div class="pg-rule-top"><span class="pg-match" aria-hidden="true"></span><span class="pg-out-label">Otherwise</span></div>' + port + '</div>';
    }
    return '<div class="pg-outrow" data-out="' + pgEsc(o.name) + '"><span class="pg-out-label' + (o.name === 'each' ? ' is-strong' : '') + '" title="' + pgEsc(o.label || '') + '">' + pgEsc(o.label || '') + '</span>' + port + '</div>';
}
function pgOutsFootHtml(n: PgNode): string {
    if (n.type === 'switch') return '<button type="button" class="pg-add-row" data-pg-act="add-switch">' + pgIcon('plus') + 'Add output</button>';
    if (n.type === 'condition') return '<button type="button" class="pg-add-row" data-pg-act="add-rule">' + pgIcon('plus') + 'Add rule</button>';
    if (n.type === 'array') {
        const r = pgArrayRead(n);
        return r.items.length > PG_ARRAY_PORTS ? '<p class="pg-note is-outs">' + (r.items.length - PG_ARRAY_PORTS) + ' more in Each item</p>' : '';
    }
    return '';
}
// the rules a Condition's text opens, when the text is known now
function pgConditionOpen(n: PgNode): Set<string> | null {
    const e = pgInEdges(n.id, 'text')[0];
    const v = e ? pgPeekEdge(e) : PG_NONE;
    if (!v.key || !v.texts) return null;
    const open = new Set<string>();
    v.texts.forEach(x => pgOpenRules(n, x).forEach(id => open.add(id)));
    return open;
}
// The nodes a node feeds show what they get from it (the Output its picture, a
// Generate its list, a Prompt constructor its text): drawn again when it changes,
// through the Routers, Switches and Arrays between them.
const PG_SHOWS: PgType[] = ['output', 'gallery', 'generate', 'enhance', 'llm', 'construct', 'array', 'condition', 'switch', 'router', 'resize', 'split', 'annotate', 'compare'];
function pgRenderDown(id: string, seen = new Set<string>()) {
    for (const e of pg.wf!.edges) {
        if (e.from !== id || seen.has(e.to)) continue;
        seen.add(e.to);
        const n = pgNode(e.to);
        if (!n) continue;
        if (PG_SHOWS.includes(n.type)) pgRenderKeep(n.id);
        if (PG_TYPES[n.type].flows) pgRenderDown(n.id, seen);
    }
}
// drawn again without losing the field being typed in, or where in it
function pgRenderKeep(id: string) {
    const card = pgCard(id);
    const a = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
    if (!card || !a || !card.contains(a)) { pgRenderNode(id); return; }
    const attr = [...a.attributes].find(x => x.name.startsWith('data-pg-'));
    const sel = attr ? '[' + attr.name + '="' + CSS.escape(attr.value) + '"]' : '';
    const start = 'selectionStart' in a ? a.selectionStart : null, end = 'selectionEnd' in a ? a.selectionEnd : null;
    pgRenderNode(id);
    const again = sel ? pgCard(id)?.querySelector(sel) as HTMLInputElement | null : null;
    if (!again) return;
    again.focus();
    try { if (start !== null && end !== null) again.setSelectionRange(start, end); } catch { /* a select */ }
}

function pgSelect(options: [string, string][], value: string, act: string, label: string): string {
    return '<label class="pg-field"><span>' + pgEsc(label) + '</span><select data-pg-set="' + act + '">'
        + options.map(([v, l]) => '<option value="' + pgEsc(v) + '"' + (v === value ? ' selected' : '') + '>' + pgEsc(l) + '</option>').join('')
        + '</select></label>';
}
// A big picture is shown on a card as a smaller copy (720 px on its long side): the
// card shows it small, and the page paints it again whenever the view moves or zooms,
// which a 2K picture made slow. The picture itself (for Download, View, Compare in the
// viewer, and what goes on) stays as it is.
const __pgSmall = new Map<string, { id: string; url: string; src: string }>();
let __pgSmallN = 0;
async function pgMakeSmall(src: string): Promise<string> {
    try {
        const bmp = await createImageBitmap(await (await fetch(src)).blob());
        const k = 720 / Math.max(bmp.width, bmp.height);
        if (k >= 1) { bmp.close(); return src; }
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(bmp.width * k));
        c.height = Math.max(1, Math.round(bmp.height * k));
        const ctx = c.getContext('2d')!;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bmp, 0, 0, c.width, c.height);
        bmp.close();
        const blob = await new Promise<Blob | null>(r => c.toBlob(r, 'image/webp', 0.9));
        return blob ? URL.createObjectURL(blob) : src;
    } catch { return src; }
}
// the attributes of an <img> on a card: the small copy when there is one (until it is
// made, the picture itself, then swapped). A big picture is never written into the HTML
// (2026-10-23): a Gallery of many 2K pictures, each drawn as its data URL, made one string
// of more than the 512 MB a browser allows ("Invalid string length", ajj's run of eight);
// until its small copy is made, the picture is set on the element once it is drawn.
const __pgBig = new Map<string, { id: string; url: string; src: string }>();
function pgFillBig() {
    document.querySelectorAll<HTMLImageElement>('#playground img[data-pg-big]:not([src])').forEach(img => {
        const e = __pgBig.get(img.dataset.pgBig || '');
        if (e) img.src = e.url || e.src;
    });
}
function pgImgSrc(src: string): string {
    if (!src || src.length < 400_000 || !/^data:/i.test(src)) return 'src="' + pgEsc(src) + '" decoding="async"';
    let e = __pgSmall.get(src);
    if (!e) {
        const entry = { id: 'b' + (++__pgSmallN), url: '', src };
        e = entry;
        __pgSmall.set(src, entry);
        __pgBig.set(entry.id, entry);
        // as many as a Gallery keeps, and the cards around it
        if (__pgSmall.size > PG_GALLERY_KEEP + 100) {
            const [k, old] = __pgSmall.entries().next().value as [string, { id: string; url: string; src: string }];
            if (old.url.startsWith('blob:')) URL.revokeObjectURL(old.url);
            __pgSmall.delete(k);
            __pgBig.delete(old.id);
        }
        // a GIF keeps its motion: it has no small copy
        if (/^data:image\/(png|jpe?g|webp)/i.test(src)) pgMakeSmall(src).then(url => {
            entry.url = url;
            document.querySelectorAll<HTMLImageElement>('#playground img[data-pg-big="' + entry.id + '"]').forEach(img => { img.src = url; });
        });
    }
    if (e.url) return 'src="' + pgEsc(e.url) + '" data-pg-big="' + e.id + '" decoding="async"';
    // drawn as soon as the HTML it is in is on the page
    queueMicrotask(pgFillBig);
    requestAnimationFrame(pgFillBig);
    return 'data-pg-big="' + e.id + '" decoding="async"';
}
const pgPicture = (src: string, alt: string, tools = true, del = false) => '<div class="pg-pic">' + '<img ' + pgImgSrc(src) + ' alt="' + pgEsc(alt) + '" data-pg-view draggable="false">'
    + (tools ? '<span class="pg-pic-tools">' + (del ? '<button type="button" class="pg-pic-btn" data-pg-act="clear" aria-label="Delete the picture" title="Delete the picture">' + pgIcon('trash') + '</button>' : '')
        + '<button type="button" class="pg-pic-btn" data-pg-act="view" aria-label="View large" title="View large">' + pgIcon('expand') + '</button></span>' : '') + '</div>';
// the fields of a prompt, to fill in
function pgVarsHtml(n: PgNode): string {
    const names = pgFieldsOf(n.data.text);
    if (!names.length) return '';
    const vars = n.data.vars || {};
    return '<div class="pg-vars" data-pg-vars="' + pgEsc(names.join('|')) + '"><p class="pg-note">Fill in its fields. A field left empty is sent as written.</p>'
        + names.map(name => '<label class="pg-var"><span>' + pgEsc(name) + '</span><input type="text" data-pg-var="' + pgEsc(name) + '" value="' + pgEsc(vars[name] || '') + '" spellcheck="false" autocomplete="off"></label>').join('') + '</div>';
}
// what goes into a node, in order, with a small picture each
function pgOrderHtml(n: PgNode, port: string, word: string): string {
    const edges = pgInEdges(n.id, port);
    if (!edges.length || (word === 'Text' && edges.length < 2)) return '';
    return '<ol class="pg-order" aria-label="What goes in, in order">' + edges.map((e, i) => {
        const s = pgNode(e.from)!;
        const th = pgThumbOf(s);
        return '<li>' + (word === 'Image' ? (th ? '<img ' + pgImgSrc(th) + ' alt="">' : '<span class="pg-order-ph">' + pgIcon(PG_TYPES[s.type].icon) + '</span>') : '')
            + '<b>' + word + ' ' + (i + 1) + '</b> <span>' + pgEsc(pgLabelOf(s)) + '</span></li>';
    }).join('') + '</ol>';
}

function pgBodyHtml(n: PgNode): string {
    const d = n.data;
    switch (n.type) {
        case 'image':
            return d.src
                ? pgPicture(d.src, d.name || 'Image', true, true) + '<button type="button" class="pg-link" data-pg-act="upload">Replace</button><input type="file" accept="image/*" hidden data-pg-file>'
                : '<button type="button" class="pg-drop" data-pg-act="upload">' + pgIcon('image') + 'Drop or paste an image, or click to choose</button><input type="file" accept="image/*" hidden data-pg-file>';
        case 'prompt':
            return pgSelect([['', 'Start from a template…'], ...PG_PROMPT_TEMPLATES.map(p => [p.id, p.name] as [string, string])], '', 'template', 'Templates')
                + '<textarea class="pg-text" data-pg-text rows="7" placeholder="Describe the picture to make. Say Image 1, Image 2 … for the images that go in.">' + pgEsc(d.text || '') + '</textarea>'
                + pgVarsHtml(n);
        case 'note':
            return '<textarea class="pg-text is-note" data-pg-text rows="5" placeholder="A note: what this workflow is for, what to change…">' + pgEsc(d.text || '') + '</textarea>';
        case 'savedPrompt': {
            const keys = Object.keys(PROMPTS).filter(k => typeof (PROMPTS as any)[k] === 'string' && String((PROMPTS as any)[k]).trim());
            const pretty = (k: string) => k.toLowerCase().split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
            return pgSelect([['', 'Choose a prompt…'], ...keys.map(k => [k, pretty(k)] as [string, string])], d.key || '', 'key', 'From the prompt manager')
                + '<textarea class="pg-text" data-pg-text rows="7" placeholder="The prompt shows here, and can be changed">' + pgEsc(d.text || '') + '</textarea>'
                + pgVarsHtml(n);
        }
        case 'reference': {
            const sets = pg.refs || [];
            const set = sets.find(s => s.id === d.set);
            const thumbs = set ? (set.thumbs || set.images || []) as string[] : [];
            return pgSelect([['', sets.length ? 'Choose a reference set…' : 'Loading the sets…'], ...sets.map(s => [s.id, pgRefName(s)] as [string, string])], d.set || '', 'set', 'Set')
                + (set ? '<div class="pg-grid" role="listbox" aria-label="Photos">' + thumbs.map((src, i) => {
                    const file = String(set.images[i] || '').split('?')[0].split('/').pop();
                    return '<button type="button" class="pg-thumb" role="option" aria-selected="' + (file === d.file) + '" data-pg-file-pick="' + pgEsc(file) + '"><img src="' + pgEsc(src) + '" alt="Photo ' + (i + 1) + '"></button>';
                }).join('') + '</div>' : '');
        }
        case 'packshot': {
            const all = pg.packshots || [];
            const e = all.find(x => x.id === d.entry);
            const slots = e ? Object.keys(PG_SLOTS).filter(s => e.images[s]) : [];
            const thumb = e && d.slot && e.images[d.slot] && !String(e.images[d.slot]).startsWith('pending') ? e.images[d.slot] : '';
            return pgSelect([['', all.length ? 'Choose a packshot…' : (pg.packshots ? 'The Packshot library is empty' : 'Loading the library…')], ...all.map(x => [x.id, pgPackshotName(x)] as [string, string])], d.entry || '', 'entry', 'From the library')
                + (e ? pgSelect(slots.map(s => [s, PG_SLOTS[s]] as [string, string]), d.slot || '', 'slot', 'Picture') : '')
                + (thumb ? pgPicture(thumb, pgPackshotName(e), false) : '');
        }
        case 'necktag':
            return pgSelect([['', 'Choose a brand…'], ...STUDIO_BRANDS.map(b => [b.id, b.name] as [string, string])], d.brand || '', 'brand', 'Brand')
                + (d.brand ? '<div class="pg-pic" data-pg-tag></div>' : '');
        case 'generate': {
            const model = d.model || 'gemini';
            const max = PG_MAX_IMAGES[model] || 14;
            const many = pgInEdges(n.id, 'images').length;
            return pgOrderHtml(n, 'images', 'Image') + pgOrderHtml(n, 'prompt', 'Text')
                + (many > max ? '<p class="pg-note is-warn" role="alert">' + pgEsc((PG_MODELS.find(m => m.id === model)?.label || model) + ' takes at most ' + max + ' images; ' + many + ' go in.') + '</p>' : '')
                + pgResultHtml(n) + pgVersionsHtml(n)
                + (model === 'openai' && !['1:1', '2:3'].includes(d.aspect || '1:1') ? '<p class="pg-note">GPT Image makes upright pictures as 2:3 and wide ones as 3:2.</p>' : '')
                + '<div class="pg-settings">'
                + '<div class="pg-row is-two">' + pgSelect(PG_MODELS.map(m => [m.id, m.label] as [string, string]), model, 'model', 'Model')
                + pgSelect(PG_COUNTS.map(c => [c, c === '1' ? '1 picture' : c + ' pictures'] as [string, string]), String(d.count || '1'), 'count', 'At once') + '</div>'
                + '<div class="pg-row is-two">' + pgSelect(PG_FORMATS.map(f => [f, f] as [string, string]), d.aspect || '1:1', 'aspect', 'Format')
                + pgSelect(PG_SIZES.map(s => [s, s.toUpperCase()] as [string, string]), d.size || '2k', 'size', 'Size') + '</div></div>';
        }
        case 'upscale':
            return '<p class="pg-note">Makes the picture four times as large, on our own server.</p>' + pgResultHtml(n);
        case 'describe':
            return pgSelect([['garment', 'The garment: fabric, colour, fit, details'], ['photo', 'The whole photo: setting, light, camera'], ['style', 'The style and mood, for a new scene']], d.focus || 'garment', 'focus', 'Describe') + pgResultHtml(n);
        case 'enhance':
            return pgOrderHtml(n, 'images', 'Image') + '<p class="pg-note">Writes a full prompt from a few words, for Generate. Connect the same images as Generate gets.</p>' + pgResultHtml(n);
        case 'output':
            return '<div class="pg-out-pic" data-pg-out><span class="pg-placeholder">Connect a picture, and Run</span></div>'
                + '<div class="pg-actions"><button type="button" class="pg-btn" data-pg-act="download" disabled>' + pgIcon('download') + 'Download</button><button type="button" class="pg-btn" data-pg-act="view" disabled>' + pgIcon('expand') + 'View</button></div>';
        case 'llm': {
            const s = pgSettingsOf(n);
            return pgOrderHtml(n, 'images', 'Image') + pgOrderHtml(n, 'prompt', 'Text') + pgResultHtml(n)
                + '<div class="pg-settings">'
                + '<div class="pg-row is-two">' + pgSelect(PG_LLM_MODELS, s.model, 'model', 'Model')
                + pgSelect(PG_LLM_TOKENS.map(t => [t, Number(t).toLocaleString('da-DK') + ' tokens'] as [string, string]), String(s.maxTokens), 'maxTokens', 'At most') + '</div>'
                + '<label class="pg-field"><span>Temperature <b data-pg-temp>' + s.temperature.toFixed(2) + '</b><small>low keeps to the prompt, high is freer</small></span>'
                + '<input type="range" class="pg-range" min="0" max="2" step="0.05" value="' + s.temperature + '" data-pg-set="temperature" aria-label="Temperature"></label></div>';
        }
        case 'array': {
            const r = pgArrayRead(n);
            const mode = d.mode || 'newline';
            return (r.wired ? '<p class="pg-note">Splits the text wired in.</p>'
                : '<textarea class="pg-text is-short" data-pg-text rows="4" placeholder="The items, one per line (or split as set below)">' + pgEsc(d.text || '') + '</textarea>')
                + '<div class="pg-row is-two">' + pgSelect([['newline', 'By line'], ['delimiter', 'By a character'], ['regex', 'By a pattern']], mode, 'mode', 'Split')
                + (mode === 'newline' ? '<span></span>' : '<label class="pg-field"><span>' + (mode === 'regex' ? 'Pattern' : 'Character') + '</span><input type="text" class="pg-input" data-pg-set-text="' + (mode === 'regex' ? 'regex' : 'delimiter') + '" value="' + pgEsc(mode === 'regex' ? d.regex || '' : d.delimiter ?? ',') + '" placeholder="' + (mode === 'regex' ? '/\\s*;\\s*/' : ',') + '" spellcheck="false" autocomplete="off"></label>') + '</div>'
                + '<div class="pg-checks"><label class="pg-check"><input type="checkbox" data-pg-check="trim"' + (d.trim !== false ? ' checked' : '') + '><span>Trim spaces</span></label>'
                + '<label class="pg-check"><input type="checkbox" data-pg-check="dropEmpty"' + (d.dropEmpty !== false ? ' checked' : '') + '><span>Drop empty</span></label></div>'
                + (r.error ? '<p class="pg-note is-warn" role="alert">' + pgEsc(r.error) + '</p>'
                    : r.coming ? '<p class="pg-note">The items are known when what is wired in has been made.</p>'
                    : '<p class="pg-note"><b class="pg-count">' + r.items.length + ' item' + (r.items.length === 1 ? '' : 's') + '</b> · Each item makes what it feeds once for each</p>');
        }
        case 'construct': {
            const r = pgConstructRead(n);
            const edges = pgInEdges(n.id, 'vars');
            return (edges.length ? '<div class="pg-vars-list" aria-label="Variables">' + edges.map(e => {
                    const src = pgNode(e.from)!;
                    const name = pgVarName(src, n);
                    return '<div class="pg-varrow"><span class="pg-at">@</span><input type="text" class="pg-mini" data-pg-varname="' + pgEsc(src.id) + '" value="' + pgEsc(name) + '" aria-label="Variable name" spellcheck="false" autocomplete="off">'
                        + '<span class="pg-var-from" title="' + pgEsc(pgLabelOf(src)) + '">' + pgEsc(pgLabelOf(src)) + '</span>'
                        + '<button type="button" class="pg-chip is-small" data-pg-insert="' + pgEsc(name) + '" title="Put @' + pgEsc(name) + ' where the cursor is">Insert</button></div>';
                }).join('') + '</div>'
                : '<p class="pg-note">Wire texts into Variables, and write @name where each goes.</p>')
                + '<textarea class="pg-text" data-pg-text rows="6" placeholder="A template: The @garment on a model in @place …">' + pgEsc(d.template || '') + '</textarea>'
                + (r.missing.length ? '<p class="pg-note is-warn">Not filled in: ' + r.missing.map(m => '@' + pgEsc(m)).join(', ') + '</p>' : '')
                + (r.coming ? '<p class="pg-note">Filled in when what is wired in has been made.</p>'
                    : r.texts.some(x => x.trim()) ? '<div class="pg-textout is-preview" tabindex="0" aria-label="What it gives">' + pgEsc(r.texts[0]) + '</div>' + (r.texts.length > 1 ? '<p class="pg-note">' + r.texts.length + ' prompts: one for each item of the list wired in.</p>' : '') : '');
        }
        case 'router': {
            const kind = d.kind as PgPort | undefined;
            return '<p class="pg-note">' + (kind === 'image' ? 'Passes a picture on.' : kind === 'text' ? 'Passes a text on.' : 'Wire anything in: it goes on as it came.') + '</p>';
        }
        case 'switch':
            return '<p class="pg-note">What comes in goes out along each output that is on. What an output that is off feeds is skipped.</p>';
        case 'condition': {
            const e = pgInEdges(n.id, 'text')[0];
            const v = e ? pgPeekEdge(e) : PG_NONE;
            const shown = v.texts && v.texts.length ? v.texts[0] : '';
            return '<p class="pg-note">' + (!e ? 'Wire a text in. It goes on along every rule it matches; if none, along Otherwise.'
                : v.coming ? 'The text is known when it has been made.'
                : !v.key ? (pgWireBlocked(e) ? 'Nothing comes in: a switch or rule before it is closed.' : 'Nothing comes in yet.')
                : 'In: “' + pgEsc(shown.length > 60 ? shown.slice(0, 58) + '…' : shown) + '”' + (v.n ? ' and ' + (v.n - 1) + ' more' : '')) + '</p>';
        }
        case 'resize': {
            const r = pgResizeSettings(d);
            const num = (key: string, label: string, value: number, unit: string) => '<label class="pg-field"><span>' + label + '</span><span class="pg-unit"><input type="number" class="pg-input" min="1" data-pg-set-text="' + key + '" value="' + value + '" inputmode="numeric"><small>' + unit + '</small></span></label>';
            return '<div class="pg-pic is-preview" data-pg-preview><span class="pg-placeholder">Connect a picture</span></div><p class="pg-meta" data-pg-size></p>'
                + pgSelect([['exact', 'Exact size'], ['max', 'Longest side'], ['scale', 'Percentage']], r.mode, 'mode', 'Resize to')
                + (r.mode === 'exact' ? '<div class="pg-row is-two">' + num('width', 'Width', r.width, 'px') + num('height', 'Height', r.height, 'px') + '</div>'
                    + pgSelect([['contain', 'Contain: all of it, with edges'], ['cover', 'Cover: filled, the edges cut'], ['stretch', 'Stretch: to fit exactly']], r.fit, 'fit', 'Fit')
                    : r.mode === 'max' ? num('maxEdge', 'Longest side, at most', r.maxEdge, 'px') : num('scale', 'Size', r.scale, '%'))
                + '<div class="pg-row is-two">' + pgSelect([['keep', 'As it came'], ['png', 'PNG'], ['jpeg', 'JPEG'], ['webp', 'WEBP']], r.format, 'format', 'File type')
                + (r.format === 'jpeg' || r.format === 'webp' ? pgSelect(['0.6', '0.7', '0.8', '0.9', '1'].map(q => [q, Math.round(Number(q) * 100) + '%'] as [string, string]), String(r.quality), 'quality', 'Quality') : '<span></span>') + '</div>';
        }
        case 'split': {
            const [r, c] = pgSplitDims(n);
            const opts = (k: string) => Array.from({ length: 8 }, (_, i) => [String(i + 1), String(i + 1)] as [string, string]);
            return '<div class="pg-pic is-preview is-grid" data-pg-preview style="--rows:' + r + ';--cols:' + c + '"><span class="pg-placeholder">Connect a picture</span></div>'
                + '<div class="pg-row is-two">' + pgSelect(opts('rows'), String(r), 'rows', 'Rows') + pgSelect(opts('cols'), String(c), 'cols', 'Columns') + '</div>'
                + '<p class="pg-note"><b>' + (r * c) + ' cells</b> · Each cell makes what it feeds once for each; All cells gives them as Image 1, 2 …' + (r * c > 16 ? ' Over 16 cells: no output of their own.' : '') + '</p>';
        }
        case 'gif': {
            const s = pgSettingsOf(n);
            return pgResultHtml(n) + pgVersionsHtml(n)
                + '<div class="pg-row is-two">' + pgSelect(['4', '6', '8', '10', '12', '15', '20', '24', '30'].map(x => [x, x + ' a second'] as [string, string]), String(s.fps), 'fps', 'Frames')
                + pgSelect(['32', '64', '128', '256'].map(x => [x, x + ' colours'] as [string, string]), String(s.colours), 'colours', 'Colours') + '</div>'
                + '<div class="pg-row is-two">' + pgSelect(['256', '384', '512', '768', '1024'].map(x => [x, x + ' px wide'] as [string, string]), String(s.width), 'width', 'At most')
                + pgSelect([['', 'Any size'], ['256', '256 KB'], ['512', '512 KB'], ['1024', '1 MB'], ['2048', '2 MB']], String(d.maxKb || ''), 'maxKb', 'At most') + '</div>'
                + '<p class="pg-note">From the pictures, in the order they stand. It loops for ever.</p>';
        }
        case 'compare':
            return '<div class="pg-cmp" data-pg-cmp style="--pg-split:50%"><span class="pg-placeholder">Connect two pictures, A and B</span></div>'
                + '<div class="pg-actions is-one"><button type="button" class="pg-btn" data-pg-act="view" disabled>' + pgIcon('expand') + 'View large</button></div>';
        case 'annotate': {
            const own = !pgInEdges(n.id, 'image').length;
            const marks = pgShapesOf(n).length;
            return '<div class="pg-pic is-preview" data-pg-preview>' + (own && !d.src ? '<button type="button" class="pg-drop is-flat" data-pg-act="upload">' + pgIcon('image') + 'Drop a picture, or wire one in</button>' : '<span class="pg-placeholder">Connect a picture</span>') + '</div>'
                + (own ? '<input type="file" accept="image/*" hidden data-pg-file>' : '')
                + '<div class="pg-actions"><button type="button" class="pg-btn" data-pg-act="annotate">' + pgIcon('annotate') + (marks ? 'Edit' : 'Draw') + '</button>'
                + '<button type="button" class="pg-btn" data-pg-act="clear-marks"' + (marks ? '' : ' disabled') + '>Clear</button></div>'
                + '<p class="pg-note">' + (marks ? marks + ' mark' + (marks > 1 ? 's' : '') + ', drawn again on whatever picture comes in.' : 'Boxes, arrows, lines and text, to show an image model what to change.') + '</p>';
        }
        case 'gallery':
            return '<div class="pg-gallery" data-pg-gallery><span class="pg-placeholder">Connect pictures, and Run</span></div>'
                + '<div class="pg-actions is-three"><button type="button" class="pg-btn" data-pg-act="download-all" disabled>' + pgIcon('download') + 'Download all</button><button type="button" class="pg-btn" data-pg-act="view" disabled>' + pgIcon('expand') + 'View</button><button type="button" class="pg-btn" data-pg-act="clear-gallery" disabled title="Let go of the pictures kept from earlier runs">Clear</button></div>';
    }
    return '';
}
function pgResultHtml(n: PgNode): string {
    const text = PG_TYPES[n.type].out === 'text';
    // the size the picture will have: its format, at most as high as a shown picture (220px)
    const [w, h] = String(n.data.aspect || '1:1').split(':').map(Number);
    const shape = n.type === 'generate' && w && h ? ' style="aspect-ratio:' + w + ' / ' + h + ';width:min(100%, ' + Math.round(220 * w / h) + 'px);margin:0 auto"' : '';
    if (n.status === 'running') return '<div class="pg-result is-running' + (text ? ' is-text' : '') + '"' + shape + ' role="status"><span class="pg-spin" aria-hidden="true"></span><span class="pg-status">' + pgEsc(n.message || 'Running') + '</span><span class="pg-time" data-pg-since="' + (pg.since.get(n.id) || Date.now()) + '" aria-hidden="true"></span></div>';
    if (n.status === 'waiting') return '<div class="pg-result is-waiting" role="status"><span class="pg-status">' + pgEsc(n.message || 'Waiting') + '</span></div>';
    if (n.status === 'error') return '<div class="pg-result is-error" role="alert"><span class="pg-status">' + pgEsc(n.message || 'Could not be made') + '</span></div>';
    const skipped = n.status === 'skipped' ? '<p class="pg-note is-skipped" role="status">' + pgEsc(n.message || 'Skipped') + '</p>' : '';
    if (n.result && text && n.texts && n.texts.length > 1) return skipped + '<ol class="pg-texts" tabindex="0" aria-label="What was written, one for each item">' + n.texts.map(x => '<li>' + pgEsc(x) + '</li>').join('') + '</ol>'
        + '<div class="pg-actions"><button type="button" class="pg-btn" data-pg-act="copy">' + pgIcon('copy') + 'Copy all</button><button type="button" class="pg-btn" data-pg-act="to-prompt">' + pgIcon('prompt') + 'To a Prompt</button></div>';
    if (n.result && text) return skipped + '<div class="pg-textout" tabindex="0" aria-label="What was written">' + pgEsc(n.result) + '</div>'
        + '<div class="pg-actions"><button type="button" class="pg-btn" data-pg-act="copy">' + pgIcon('copy') + 'Copy</button><button type="button" class="pg-btn" data-pg-act="to-prompt">' + pgIcon('prompt') + 'To a Prompt</button></div>';
    if (n.result) return skipped + pgPicture(n.result, PG_TYPES[n.type].title + ' result') + (n.meta && (n.meta.model || n.meta.size) ? '<p class="pg-meta">' + pgEsc([n.meta.model, n.meta.size].filter(Boolean).join(' · ')) + '</p>' : '')
        + (n.listIds && n.listIds.length > 1 ? '<p class="pg-note">' + n.listIds.length + ' pictures, one for each item. A Gallery shows them all.</p>' : '');
    if (skipped) return skipped;
    if (pgHeavy(n) && n.resultId) return '<div class="pg-result"><span class="pg-status">Opening…</span></div>';
    return '<div class="pg-result"><span class="pg-status">' + (text ? 'Not written yet' : 'Not run yet') + '</span></div>';
}
// the versions a Generate has made: the newest first, the one sent on marked
function pgVersionsHtml(n: PgNode): string {
    const vs = n.versions || [];
    if (vs.length < 2 || n.status === 'running') return '';
    const shown = vs.slice(0, 7);
    return '<div class="pg-versions" role="listbox" aria-label="Versions: the one chosen is sent on">'
        + shown.map((v, i) => '<button type="button" class="pg-version" role="option" aria-selected="' + (v.id === n.resultId) + '" data-pg-version="' + v.id + '" title="Version ' + (vs.length - i) + '">' + (v.thumb ? '<img src="' + pgEsc(v.thumb) + '" alt="Version ' + (vs.length - i) + '">' : '<span>' + (vs.length - i) + '</span>') + '</button>').join('')
        + (vs.length > shown.length ? '<button type="button" class="pg-version is-more" data-pg-act="view" title="All ' + vs.length + ' versions">+' + (vs.length - shown.length) + '</button>' : '')
        + '</div>';
}

// Filled in after the card is in the page: what has to be fetched.
async function pgAfterRender(n: PgNode, el: HTMLElement) {
    if (n.type === 'necktag' && n.data.brand) {
        const box = el.querySelector('[data-pg-tag]');
        try {
            const f = await brandNecktagFile(n.data.brand);
            if (f && !pg.tagUrls[n.data.brand]) pg.tagUrls[n.data.brand] = URL.createObjectURL(f);
            if (box) box.innerHTML = f ? '<img src="' + pg.tagUrls[n.data.brand] + '" alt="Neck label">' : '<span class="pg-placeholder">No neck label for this brand</span>';
        } catch { /* shown as empty */ }
    }
    if (pgHeavy(n) && n.resultId && !n.result && n.status !== 'running') {
        // a GIF made earlier, opened now
        const src = await pgMediaSrc(n.resultId);
        if (src && el.isConnected && !n.result) { n.result = src; pgRenderNode(n.id); }
    }
    if (n.type === 'output') {
        const box = el.querySelector('[data-pg-out]') as HTMLElement | null;
        const items = box ? await pgImagesIn(n, 'image') : [];
        if (!box || !items.length || !el.isConnected) return;
        const v = items[0];
        box.classList.toggle('is-text', v.kind === 'text');
        box.innerHTML = (v.kind === 'text' ? '<div class="pg-textout" tabindex="0">' + pgEsc(v.value) + '</div>'
            : '<img ' + pgImgSrc(v.value) + ' alt="Output" data-pg-view draggable="false">')
            + (items.length > 1 ? '<span class="pg-out-count">1 of ' + items.length + '</span>' : '');
        el.querySelectorAll<HTMLButtonElement>('[data-pg-act="download"], [data-pg-act="view"]').forEach(b => { b.disabled = b.dataset.pgAct === 'view' && v.kind !== 'image'; });
    }
    if (n.type === 'resize' || n.type === 'split' || n.type === 'annotate') {
        const box = el.querySelector('[data-pg-preview]') as HTMLElement | null;
        if (!box) return;
        try {
            // what the tool gives (for Split, the picture it cuts, with the grid on it)
            const v = n.type === 'split' ? (await pgImagesIn(n, 'image'))[0] : await pgValueOut(n, 'out', PG_SHOW);
            const first = v && (v.items ? v.items[0] : v);
            if (!first || !el.isConnected) return;
            const img = '<img src="' + pgEsc(first.value) + '" alt="' + pgEsc(PG_TYPES[n.type].title) + '" draggable="false">';
            box.innerHTML = n.type === 'split' ? '<span class="pg-grid-wrap">' + img + '<span class="pg-grid-lines" aria-hidden="true"></span></span>' : img;
            if (n.type === 'resize') {
                const img = box.querySelector('img') as HTMLImageElement;
                const size = el.querySelector('[data-pg-size]');
                const kb = Math.round(first.value.length * 0.75 / 1024);
                img.onload = () => { if (size) size.textContent = img.naturalWidth + ' × ' + img.naturalHeight + ' · ' + (kb > 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB') + (v!.items ? ' · ' + v!.items.length + ' pictures' : ''); };
            }
        } catch (e) { box.innerHTML = '<span class="pg-placeholder is-warn">' + pgEsc((e as Error).message || 'Could not be worked out') + '</span>'; }
    }
    if (n.type === 'compare') {
        const box = el.querySelector('[data-pg-cmp]') as HTMLElement | null;
        const [a, b] = box ? [(await pgImagesIn(n, 'a'))[0], (await pgImagesIn(n, 'b'))[0]] : [];
        if (!box || !el.isConnected || (!a && !b)) return;
        if (!a || !b) { box.innerHTML = '<img ' + pgImgSrc((a || b)!.value) + ' alt="" draggable="false"><span class="pg-cmp-note">Connect ' + (a ? 'B' : 'A') + ' to compare</span>'; return; }
        box.innerHTML = '<img class="pg-cmp-b" ' + pgImgSrc(b.value) + ' alt="B" draggable="false"><img class="pg-cmp-a" ' + pgImgSrc(a.value) + ' alt="A" draggable="false">'
            + '<span class="pg-cmp-line" aria-hidden="true"></span><span class="pg-cmp-tag is-a">A</span><span class="pg-cmp-tag is-b">B</span>'
            + '<input type="range" class="pg-cmp-range" min="0" max="100" value="50" aria-label="Compare: drag between A and B">';
        el.querySelector<HTMLButtonElement>('[data-pg-act="view"]')!.disabled = false;
    }
    if (n.type === 'gallery') {
        const box = el.querySelector('[data-pg-gallery]') as HTMLElement | null;
        const items = box ? await pgGalleryShown(n) : [];
        if (!box || !items.length || !el.isConnected) return;
        box.innerHTML = '<div class="pg-gal-grid">' + items.map((v, i) => '<button type="button" class="pg-gal-item" data-pg-gal="' + i + '" title="' + pgEsc(v.label) + '">'
            + '<img ' + (v.value ? pgImgSrc(v.value) : 'src="' + pgEsc(v.thumb) + '" decoding="async"') + ' alt="' + pgEsc(v.label) + '" draggable="false"></button>').join('') + '</div>'
            + '<p class="pg-note">' + items.length + ' picture' + (items.length === 1 ? '' : 's') + (Array.isArray(n.data.kept) && n.data.kept.length ? ' · kept from each run until cleared' : '') + '</p>';
        el.querySelectorAll<HTMLButtonElement>('[data-pg-act="download-all"], [data-pg-act="view"], [data-pg-act="clear-gallery"]').forEach(b => { b.disabled = false; });
    }
}

// ── Wires ──
// Where a card's ports stand within it, and its size: measured once, and again when the
// card is drawn again or changes size. The wires are drawn many times a second while
// a node is moved; measuring the page each time is what made it stutter.
const __pgPorts = new WeakMap<HTMLElement, Map<string, { x: number; y: number }>>();
const __pgSizes = new WeakMap<HTMLElement, { w: number; h: number }>();
let __pgCardWatch: ResizeObserver | null = null;
function pgWatchCard(card: HTMLElement, old?: HTMLElement | null) {
    if (!window.ResizeObserver) return;
    if (!__pgCardWatch) __pgCardWatch = new ResizeObserver(list => {
        let changed = false;
        for (const r of list) { const el = r.target as HTMLElement; if (__pgPorts.has(el) || __pgSizes.has(el)) changed = true; __pgPorts.delete(el); __pgSizes.delete(el); }
        if (changed) pgEdgesSoon();
    });
    if (old) __pgCardWatch.unobserve(old);
    __pgCardWatch.observe(card);
}
const pgCardSize = (card: HTMLElement) => {
    let size = __pgSizes.get(card);
    if (!size) { size = { w: card.offsetWidth, h: card.offsetHeight }; __pgSizes.set(card, size); }
    return size;
};
// the wires drawn again at the next frame, once however often it is asked
let __pgEdgesAt = 0;
function pgEdgesSoon() {
    if (__pgEdgesAt) return;
    __pgEdgesAt = requestAnimationFrame(() => { __pgEdgesAt = 0; pgRenderEdges(); });
}
function pgPortPoint(nodeId: string, which: string, isOut = false, n0?: PgNode | null, card0?: HTMLElement | null): { x: number; y: number } | null {
    const n = n0 || pgNode(nodeId);
    const card = card0 || pgCard(nodeId);
    if (!n || !card) return null;
    let ports = __pgPorts.get(card);
    if (!ports) { ports = new Map(); __pgPorts.set(card, ports); }
    const key = (isOut ? 'o:' : 'i:') + which;
    let at = ports.get(key);
    if (!at) {
        const port = (isOut ? card.querySelector('[data-port-out="' + CSS.escape(which) + '"]') : card.querySelector('[data-port-in="' + CSS.escape(which) + '"]')) as HTMLElement | null;
        if (!port) return null;
        // offsets are in the world's own (unscaled) pixels
        let x = port.offsetLeft + port.offsetWidth / 2, y = port.offsetTop + port.offsetHeight / 2;
        let q = port.offsetParent as HTMLElement | null;
        while (q && q !== card) { x += q.offsetLeft; y += q.offsetTop; q = q.offsetParent as HTMLElement | null; }
        at = { x, y };
        ports.set(key, at);
    }
    return { x: n.x + at.x, y: n.y + at.y };
}
const pgCurve = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = Math.max(40, Math.abs(b.x - a.x) / 2);
    return 'M' + a.x + ' ' + a.y + ' C' + (a.x + dx) + ' ' + a.y + ' ' + (b.x - dx) + ' ' + b.y + ' ' + b.x + ' ' + b.y;
};
// a wire out of a Switch's output that is off, or a Condition's rule the text does not match
function pgWireClosed(e: PgEdge): boolean {
    const src = pgNode(e.from);
    if (!src) return false;
    if (src.type === 'switch') { const s = pgSwitches(src).find(x => x.id === (e.out || 'out')); return !!s && !s.on; }
    if (src.type === 'condition') { const open = pgConditionOpen(src); return !!open && !open.has(e.out || 'out'); }
    return false;
}
let __pgWireSig = '';
let __pgWireEls: { hit: Element; wire: Element; n: Element | null; del: Element | null }[] = [];
function pgRenderEdges() {
    const svg = pg.el.wires;
    if (!svg || !pg.wf) return;
    const nodes = new Map(pg.wf.nodes.map(n => [n.id, n] as [string, PgNode]));
    const cards = new Map<string, HTMLElement>();
    pg.el.world.querySelectorAll<HTMLElement>('.pg-node').forEach(c => cards.set(c.dataset.id!, c));
    // the wires into each input that takes several, in the order they go in
    const into = new Map<string, PgEdge[]>();
    for (const e of pg.wf.edges) { const k = e.to + '\n' + e.port; if (!into.has(k)) into.set(k, []); into.get(k)!.push(e); }
    for (const list of into.values()) if (list.length > 1) list.sort((a, b) => ((nodes.get(a.from)?.y ?? 0) - (nodes.get(b.from)?.y ?? 0)) || ((nodes.get(a.from)?.x ?? 0) - (nodes.get(b.from)?.x ?? 0)));
    const items: { e: PgEdge; a: { x: number; y: number }; b: { x: number; y: number }; cls: string; type: string; num: number; sel: boolean }[] = [];
    for (const e of pg.wf.edges) {
        const from = nodes.get(e.from), to = nodes.get(e.to);
        if (!from || !to) continue;
        const a = pgPortPoint(e.from, e.out || 'out', true, from, cards.get(e.from)), b = pgPortPoint(e.to, e.port, false, to, cards.get(e.to));
        if (!a || !b) continue;
        const type = pgOutKind(from, e.out || 'out');
        const sel = pg.edgeSel === e.id;
        const flowing = to.status === 'running';
        const pulse = (pg.pulse.get(e.id) || 0) > Date.now();
        // into an input that takes several, the wire says which one it is
        const multi = to.type !== 'construct' && PG_TYPES[to.type].ins.find(i => i.name === e.port)?.multi;
        const many = multi ? into.get(e.to + '\n' + e.port) || [] : [];
        const related = pg.sel.has(e.from) || pg.sel.has(e.to);
        const closed = pgWireClosed(e);
        const cls = 'pg-wire is-' + type + (sel ? ' is-selected' : '') + (related ? ' is-related' : '') + (flowing ? ' is-flowing' : '') + (pulse ? ' is-pulse' : '') + (closed ? ' is-closed' : '');
        items.push({ e, a, b, cls, type, num: many.length > 1 ? many.findIndex(x => x.id === e.id) + 1 : 0, sel });
    }
    const live = svg.querySelector('.pg-wire.is-live');
    const sig = items.map(it => it.e.id + '|' + it.cls + '|' + it.num + '|' + (it.sel ? 1 : 0)).join(';') + (live ? ';live' : '');
    if (sig === __pgWireSig && __pgWireEls.length === items.length && __pgWireEls.every(x => x.hit.isConnected)) {
        // the same wires: only where they run
        items.forEach((it, k) => {
            const el = __pgWireEls[k], d = pgCurve(it.a, it.b);
            el.hit.setAttribute('d', d);
            el.wire.setAttribute('d', d);
            if (el.n) el.n.setAttribute('transform', 'translate(' + (it.b.x - 22) + ' ' + it.b.y + ')');
            if (el.del) el.del.setAttribute('transform', 'translate(' + (it.a.x + it.b.x) / 2 + ' ' + (it.a.y + it.b.y) / 2 + ')');
        });
    } else {
        const parts: string[] = [];
        for (const it of items) {
            const d = pgCurve(it.a, it.b);
            parts.push('<path class="pg-wire-hit" d="' + d + '" data-edge="' + it.e.id + '"/><path class="' + it.cls + '" d="' + d + '" data-wire="' + it.e.id + '"/>');
            if (it.num) parts.push('<g class="pg-wire-n is-' + it.type + '" data-wire-n="' + it.e.id + '" transform="translate(' + (it.b.x - 22) + ' ' + it.b.y + ')"><circle r="7.5"/><text dy="3.4">' + it.num + '</text></g>');
            if (it.sel) parts.push('<g class="pg-wire-del" data-edge-del="' + it.e.id + '" transform="translate(' + (it.a.x + it.b.x) / 2 + ' ' + (it.a.y + it.b.y) / 2 + ')"><circle r="9"/><path d="M-3.5 -3.5 L3.5 3.5 M3.5 -3.5 L-3.5 3.5"/></g>');
        }
        svg.innerHTML = parts.join('') + (live ? live.outerHTML : '');
        const byId = (attr: string) => { const m = new Map<string, Element>(); svg.querySelectorAll('[' + attr + ']').forEach(x => m.set(x.getAttribute(attr)!, x)); return m; };
        const hits = byId('data-edge'), wires = byId('data-wire'), nums = byId('data-wire-n'), dels = byId('data-edge-del');
        __pgWireEls = items.map(it => ({ hit: hits.get(it.e.id)!, wire: wires.get(it.e.id)!, n: nums.get(it.e.id) || null, del: dels.get(it.e.id) || null }));
        __pgWireSig = sig;
    }
    svg.classList.toggle('is-focus', pg.sel.size > 0);
    pgRenderGroups();
}

const pgKindWord = (k: PgPort) => k === 'image' ? 'An image' : k === 'text' ? 'A text' : 'This';
// A connection: from an output to an input of the same kind, without a loop. A
// Router or Switch takes the kind of the first thing wired to it; a text wired
// into a Prompt constructor gets a variable name, if it has none.
function pgConnect(from: string, to: string, port: string, record = true, out = 'out'): string | null {
    const a = pgNode(from), b = pgNode(to);
    if (!a || !b || from === to) return null;
    const od = pgOutDef(a, out);
    const inp = PG_TYPES[b.type].ins.find(i => i.name === port);
    if (!od) return PG_TYPES[a.type].title + ' has no such output';
    const ak = od.type, bk = inp ? pgInKind(b, inp) : 'any';
    if (!inp || !pgFits(ak, bk)) return pgKindWord(ak) + ' cannot go into ' + (inp ? inp.label.toLowerCase() : PG_TYPES[b.type].title);
    // no loops: b may not lead back to a
    const leadsTo = (x: string, target: string, seen = new Set<string>()): boolean => {
        if (x === target) return true;
        if (seen.has(x)) return false;
        seen.add(x);
        return pg.wf!.edges.filter(e => e.from === x).some(e => leadsTo(e.to, target, seen));
    };
    if (leadsTo(to, from)) return 'That would make a loop';
    if (pg.wf!.edges.some(e => e.from === from && e.to === to && e.port === port && (e.out || 'out') === out)) return null;
    if (record) pgRecord();
    if (!inp.multi) pg.wf!.edges = pg.wf!.edges.filter(e => !(e.to === to && e.port === port));
    if (pgTakesKind(b) && bk === 'any' && ak !== 'any') b.data.kind = ak;
    if (pgTakesKind(a) && ak === 'any' && bk !== 'any') a.data.kind = bk;
    if (b.type === 'construct' && !a.data.varName) {
        const taken = new Set(pgInEdges(b.id, 'vars').map(e => pgNode(e.from)).filter((s): s is PgNode => !!s).map(s => pgVarName(s, b)));
        const base = a.type === 'prompt' || a.type === 'savedPrompt' ? 'prompt' : a.type === 'array' ? 'item' : pgVarSafe(a.type);
        let name = base, i = 2;
        while (taken.has(name)) name = base + i++;
        a.data.varName = name;
    }
    pg.wf!.edges.push({ id: pgId('e'), from, to, port, ...(out !== 'out' ? { out } : {}) });
    pgRenderNode(to);
    if (pgTakesKind(a)) pgRenderNode(from);
    pgRenderDown(to);
    pgSaveSoon();
    return null;
}
// a Router or Switch with nothing wired to it takes any kind again
function pgForgetKinds() {
    pg.wf!.nodes.forEach(n => {
        if (!pgTakesKind(n) || !n.data.kind) return;
        if (!pg.wf!.edges.some(e => e.from === n.id || e.to === n.id)) { delete n.data.kind; pgRenderNode(n.id, true); }
    });
}
// the input of a node (or a kind of node) that takes this kind: the same kind first
function pgInputFor(t: PgType | PgNode, kind: PgPort): PgInput | null {
    const n = typeof t === 'string' ? null : t;
    const ins = PG_TYPES[n ? n.type : t as PgType].ins;
    const k = (i: PgInput) => n ? pgInKind(n, i) : i.type;
    return ins.find(i => k(i) === kind) || ins.find(i => pgFits(kind, k(i))) || null;
}
// the output of a node that gives this kind
function pgOutFor(n: PgNode, kind: PgPort): string | null {
    const outs = pgOuts(n);
    return (outs.find(o => o.type === kind) || outs.find(o => pgFits(o.type, kind)))?.name || null;
}

function pgDeleteNodes(ids: string[]) {
    ids = ids.filter(id => pgNode(id));
    if (!ids.length) return;
    pgRecord();
    const gone = new Set(ids);
    if (gone.has(pg.pic)) pg.pic = '';
    const touched = new Set(pg.wf!.edges.filter(e => gone.has(e.from) && !gone.has(e.to)).map(e => e.to));
    pg.wf!.nodes = pg.wf!.nodes.filter(n => !gone.has(n.id));
    pg.wf!.edges = pg.wf!.edges.filter(e => !gone.has(e.from) && !gone.has(e.to));
    if (pg.wf!.groups) pg.wf!.groups = pg.wf!.groups.map(g => ({ ...g, nodes: g.nodes.filter(id => !gone.has(id)) })).filter(g => g.nodes.length);
    ids.forEach(id => { pg.sel.delete(id); pgRenderNode(id, true); });
    touched.forEach(t => pgRenderNode(t, true));
    pgForgetKinds();
    touched.forEach(t => pgRenderDown(t));
    pgRenderEdges();
    pgPaintEmpty();
    pgSaveSoon();
}
const pgDeleteNode = (id: string) => pgDeleteNodes([id]);
function pgDeleteEdge(id: string, record = true) {
    const e = pg.wf!.edges.find(x => x.id === id);
    if (!e) return;
    if (record) pgRecord();
    pg.wf!.edges = pg.wf!.edges.filter(x => x.id !== id);
    if (pg.edgeSel === id) pg.edgeSel = '';
    pgForgetKinds();
    pgRenderNode(e.to);
    pgRenderDown(e.to);
    pgSaveSoon();
}

// A new node: where asked, or in the middle of what is on screen, a little lower each time.
let __pgAddCount = 0;
function pgAddNode(type: PgType, at?: { x: number; y: number }, data?: Record<string, any>): PgNode {
    let x: number, y: number;
    if (at) { x = at.x; y = at.y; }
    else {
        const c = pg.el.canvas.getBoundingClientRect();
        const v = pg.wf!.view;
        const k = v.k || 1;
        const off = (__pgAddCount++ % 6) * 24;
        x = (c.width / 2 - v.x) / k - PG_TYPES[type].width / 2 + off;
        y = (c.height / 2 - v.y) / k - 120 + off;
    }
    pgRecord();
    const n = pgMakeNode(type, Math.round(x), Math.round(y), data);
    pg.wf!.nodes.push(n);
    pg.sel = new Set([n.id]);
    pg.edgeSel = '';
    pg.pop.add(n.id);
    pgSelectPaint();
    pgRenderNode(n.id);
    pgPaintEmpty();
    pgSaveSoon();
    if (type === 'reference') pgReferenceSets().then(() => pgRenderNode(n.id));
    if (type === 'packshot') pgPackshotEntries(true).then(() => pgRenderNode(n.id));
    return n;
}

// ── Copy, paste, duplicate ──
function pgCopy(ids: string[]): { nodes: PgNode[]; edges: PgEdge[] } | null {
    const set = new Set(ids.filter(id => pgNode(id)));
    if (!set.size) return null;
    return {
        nodes: pg.wf!.nodes.filter(n => set.has(n.id)).map(n => JSON.parse(JSON.stringify(n))),
        edges: pg.wf!.edges.filter(e => set.has(e.from) && set.has(e.to)).map(e => ({ ...e })),
    };
}
// Pasted nodes are new ones: their own ids, the wires between them kept, the
// picture they show kept, its older versions left with the original.
function pgPaste(clip: { nodes: PgNode[]; edges: PgEdge[] }, at?: { x: number; y: number }) {
    if (!pg.wf || !clip.nodes.length) return;
    pgRecord();
    const x0 = Math.min(...clip.nodes.map(n => n.x)), y0 = Math.min(...clip.nodes.map(n => n.y));
    const off = at ? { x: at.x - x0, y: at.y - y0 } : { x: 40 * (1 + (pg.pastes % 5)), y: 40 * (1 + (pg.pastes % 5)) };
    pg.pastes++;
    const ids = new Map<string, string>();
    const made = clip.nodes.map(src => {
        const n: PgNode = JSON.parse(JSON.stringify(src));
        n.id = pgId('n');
        ids.set(src.id, n.id);
        n.x = Math.round(src.x + off.x);
        n.y = Math.round(src.y + off.y);
        if (n.status === 'running' || n.status === 'waiting') { n.status = 'idle'; n.message = ''; }
        if (n.versions) { n.versions = n.versions.filter(v => v.id === n.resultId); }
        if (n.listIds) { delete n.listIds; }   // a list's pictures stay with the original
        return n;
    });
    pg.wf.nodes.push(...made);
    clip.edges.forEach(e => pg.wf!.edges.push({ id: pgId('e'), from: ids.get(e.from)!, to: ids.get(e.to)!, port: e.port, ...(e.out ? { out: e.out } : {}) }));
    pg.sel = new Set(made.map(n => n.id));
    pg.edgeSel = '';
    made.forEach(n => { pg.pop.add(n.id); pgRenderNode(n.id, true); });
    pgSelectPaint();
    pgPaintEmpty();
    pgSaveSoon();
    if (made.some(n => n.type === 'reference')) pgReferenceSets().then(() => made.filter(n => n.type === 'reference').forEach(n => pgRenderNode(n.id)));
    if (made.some(n => n.type === 'packshot')) pgPackshotEntries().then(() => made.filter(n => n.type === 'packshot').forEach(n => pgRenderNode(n.id)));
}
function pgDuplicate() {
    const clip = pgCopy([...pg.sel]);
    if (clip) pgPaste(clip);
}
// Images pasted or dropped: a new Image node each, side by side
async function pgImagesToNodes(files: File[], at: { x: number; y: number }) {
    files = files.filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    pgRecord();
    const made: PgNode[] = [];
    files.forEach((f, i) => {
        const n = pgMakeNode('image', Math.round(at.x - 110 + i * 240), Math.round(at.y - 40));
        pg.wf!.nodes.push(n);
        made.push(n);
    });
    pg.sel = new Set(made.map(n => n.id));
    made.forEach(n => { pg.pop.add(n.id); pgRenderNode(n.id, true); });
    pgPaintEmpty();
    for (let i = 0; i < made.length; i++) await pgSetImage(made[i], files[i], false);
    pgSelectPaint();
}

// ── The view: pan and zoom ──
let __pgMoving = 0;
function pgApplyView() {
    const v = pg.wf!.view;
    if (!v.k) return;   // not fitted yet: pgFit makes the view
    const world = pg.el.world;
    world.style.transform = 'translate(' + v.x + 'px,' + v.y + 'px) scale(' + v.k + ')';
    world.classList.add('is-moving');
    clearTimeout(__pgMoving);
    __pgMoving = window.setTimeout(() => world.classList.remove('is-moving'), 200);
    const dots = pg.el.dots, step = 22 * v.k;
    if (dots) {
        if (dots.dataset.step !== String(step)) { dots.style.backgroundSize = step + 'px ' + step + 'px'; dots.dataset.step = String(step); }
        // the layer stands 100 px out on each side; it is moved by less than one step
        dots.style.transform = 'translate(' + (((v.x + 100) % step + step) % step) + 'px,' + (((v.y + 100) % step + step) % step) + 'px)';
    }
    const z = document.getElementById('pgZoom');
    if (z) z.textContent = Math.round(v.k * 100) + '%';
}
function pgZoomAt(k: number, cx: number, cy: number) {
    const v = pg.wf!.view;
    if (!v.k) v.k = 1;
    const nk = Math.min(2, Math.max(0.2, k));
    v.x = cx - (cx - v.x) * (nk / v.k);
    v.y = cy - (cy - v.y) * (nk / v.k);
    v.k = nk;
    pgApplyView();
    pgSaveSoon();
}
// outOnly: the view only draws back to take more in, never closer (while the assistant builds)
function pgFit(glide = false, only?: string[], outOnly = false) {
    if (glide) {
        pg.el.world.classList.add('is-gliding');
        pg.el.canvas.classList.add('is-gliding');
        setTimeout(() => { pg.el.world.classList.remove('is-gliding'); pg.el.canvas.classList.remove('is-gliding'); }, 420);
    }
    const nodes = only && only.length ? pg.wf!.nodes.filter(n => only.includes(n.id)) : pg.wf!.nodes;
    const c = pg.el.canvas.getBoundingClientRect();
    // hidden (another tab is open): fitted when it shows
    if (!c.width) { pg.needFit = true; return; }
    pg.needFit = false;
    if (!nodes.length) { pg.wf!.view = { x: 0, y: 0, k: 1 }; pgApplyView(); return; }
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    nodes.forEach(n => {
        const card = pgCard(n.id);
        x0 = Math.min(x0, n.x); y0 = Math.min(y0, n.y);
        x1 = Math.max(x1, n.x + PG_TYPES[n.type].width); y1 = Math.max(y1, n.y + (card ? card.offsetHeight : 200));
    });
    // clear of the workflow's name at the top and the bar at the foot, and of the assistant or the library when one is open
    const pad = 60, top = 72, bottom = 86;
    const ask = [document.getElementById('pgAsk'), document.getElementById('pgLib')].find(x => x && !x.hidden);
    const right = ask && !ask.hidden && c.width > 900 ? ask.offsetWidth + 16 : 0;
    let k = Math.min(1.2, Math.max(0.2, Math.min((c.width - right - 2 * pad) / (x1 - x0), (c.height - top - bottom) / (y1 - y0))));
    if (outOnly && pg.wf!.view.k) k = Math.min(k, pg.wf!.view.k);
    pg.wf!.view = { k, x: (c.width - right - (x1 - x0) * k) / 2 - x0 * k, y: top + (c.height - top - bottom - (y1 - y0) * k) / 2 - y0 * k };
    pgApplyView();
    pgSaveSoon();
}
// what on a card is used, not taken hold of to move it
const PG_NO_GRAB = 'input, textarea, select, button, a, label, audio, video, [contenteditable="true"], .pg-textout, .pg-texts';
// a point on the screen, in the world's own pixels
function pgToWorld(clientX: number, clientY: number) {
    const c = pg.el.canvas.getBoundingClientRect(), v = pg.wf!.view, k = v.k || 1;
    return { x: (clientX - c.left - v.x) / k, y: (clientY - c.top - v.y) / k };
}

function pgPaintRun() {
    const btn = document.getElementById('pgRun') as HTMLButtonElement | null;
    if (!btn) return;
    const times = Math.max(1, Number(pg.wf?.runCount) || 1);
    btn.classList.toggle('is-running', pg.running);
    btn.classList.toggle('is-stopping', pg.stopping);
    btn.setAttribute('aria-label', pg.running ? 'Stop' : 'Run');
    btn.querySelector('.pg-run-label')!.textContent = pg.stopping ? 'Stopping' : pg.running ? (pg.runOf ? 'Stop · ' + pg.runOf.i + ' of ' + pg.runOf.times : 'Stop') : times > 1 ? 'Run ×' + times : 'Run';
    const more = document.getElementById('pgRunMore') as HTMLButtonElement | null;
    if (more) more.disabled = pg.running;
    pgPaintPlan();
}
// The Run menu: how many times, and how many at a time
function pgPaintRunMenu() {
    const menu = document.getElementById('pgRunMenu');
    if (!menu || !pg.wf) return;
    const times = Math.max(1, Number(pg.wf.runCount) || 1);
    menu.innerHTML = '<div class="pg-run-row"><span><b>Run</b><small>after the first, every node is made again</small></span>'
        + '<span class="pg-step"><button type="button" class="pg-step-btn" data-pg-times="-1" aria-label="Fewer times"' + (times <= 1 ? ' disabled' : '') + '>−</button>'
        + '<output class="pg-step-val" aria-live="polite">' + times + '×</output>'
        + '<button type="button" class="pg-step-btn" data-pg-times="1" aria-label="More times"' + (times >= 50 ? ' disabled' : '') + '>+</button></span></div>'
        + '<label class="pg-run-row"><span><b>At the same time</b><small>engine calls at once, in this browser</small></span>'
        + '<select class="pg-mini" data-pg-limit aria-label="At the same time">' + Array.from({ length: 10 }, (_, i) => '<option value="' + (i + 1) + '"' + (i + 1 === pgLimit() ? ' selected' : '') + '>' + (i + 1) + '</option>').join('') + '</select></label>';
}

// ── Adding a node by search: double-click, /, or a wire let go of in empty space ──
interface PgPickFor { from?: string; out?: string; to?: string; port?: string; kind?: PgPort; }
function pgOpenPicker(clientX: number, clientY: number, forWire: PgPickFor = {}) {
    const picker = pg.el.picker;
    const shell = pg.el.root.querySelector('.pg-shell') as HTMLElement;
    const r = shell.getBoundingClientRect();
    const at = pgToWorld(clientX, clientY);
    pg.drag = null;
    picker.hidden = false;
    // kept inside the canvas
    const w = 300, h = 380;
    picker.style.left = Math.max(12, Math.min(r.width - w - 12, clientX - r.left)) + 'px';
    picker.style.top = Math.max(12, Math.min(r.height - h - 12, clientY - r.top)) + 'px';
    (picker as any).__for = { ...forWire, at };
    const q = picker.querySelector('input') as HTMLInputElement;
    q.value = '';
    pgPaintPicker();
    q.focus();
}
function pgPickerTypes(): PgType[] {
    const f = (pg.el.picker as any).__for as PgPickFor & { at: { x: number; y: number } };
    const q = (pg.el.picker.querySelector('input') as HTMLInputElement).value.trim().toLowerCase();
    // a new node of this kind, to see the outputs it would have
    const fits = (t: PgType) => f.from ? !!pgInputFor(t, f.kind!) : f.to ? !!pgOutFor(pgMakeNode(t, 0, 0), f.kind!) : true;
    return PG_GROUPS.flatMap(g => g.types).filter(t => fits(t) && (!q || (PG_TYPES[t].title + ' ' + PG_TYPES[t].desc).toLowerCase().includes(q)));
}
function pgPaintPicker() {
    const list = pg.el.picker.querySelector('.pg-picker-list') as HTMLElement;
    const types = pgPickerTypes();
    const f = (pg.el.picker as any).__for as PgPickFor;
    list.innerHTML = types.length ? PG_GROUPS.map(g => {
        const ts = g.types.filter(t => types.includes(t));
        return ts.length ? '<div class="pg-picker-group">' + pgEsc(g.title) + '</div>' + ts.map(t => '<button type="button" class="pg-picker-item" role="option" data-pg-pick="' + t + '" aria-selected="' + (t === types[0]) + '"><span class="pg-kind is-' + (pgOuts(pgMakeNode(t, 0, 0))[0]?.type || 'none') + '">' + pgIcon(PG_TYPES[t].icon) + '</span><span><b>' + pgEsc(PG_TYPES[t].title) + '</b><small>' + pgEsc(PG_TYPES[t].desc) + '</small></span></button>').join('') : '';
    }).join('') : '<p class="pg-picker-none">No node ' + (f.kind ? 'takes this' : 'is called that') + '</p>';
}
function pgClosePicker() { if (pg.el.picker && !pg.el.picker.hidden) pg.el.picker.hidden = true; }
function pgPick(type: PgType) {
    const f = (pg.el.picker as any).__for as PgPickFor & { at: { x: number; y: number } };
    pgClosePicker();
    // its input (or output) where the wire was let go of
    const inp = f.from ? pgInputFor(type, f.kind!) : null;
    const idx = inp ? PG_TYPES[type].ins.indexOf(inp) : 0;
    const x = f.to ? f.at.x - PG_TYPES[type].width : f.from ? f.at.x + 8 : f.at.x - PG_TYPES[type].width / 2;
    const y = f.from ? f.at.y - (36 + 6 + 24 * idx + 12) : f.to ? f.at.y - 18 : f.at.y - 20;
    const n = pgAddNode(type, { x, y });
    if (f.from && inp) { pgConnect(f.from, n.id, inp.name, false, f.out || 'out'); }
    else if (f.to && f.port) { const out = pgOutFor(n, f.kind!); if (out) pgConnect(n.id, f.to, f.port, false, out); }
}

// ── A node's menu (right-click) ──
function pgOpenNodeMenu(clientX: number, clientY: number, id: string) {
    const menu = pg.el.ctx;
    const shell = pg.el.root.querySelector('.pg-shell') as HTMLElement;
    const r = shell.getBoundingClientRect();
    if (!pg.sel.has(id)) { pg.sel = new Set([id]); pg.edgeSel = ''; pgSelectPaint(); }
    const n = pgNode(id)!;
    const many = pg.sel.size > 1;
    const item = (act: string, label: string, keys = '') => '<button type="button" class="pg-menu-item is-row" role="menuitem" data-pg-ctx="' + act + '"><span>' + pgEsc(label) + '</span>' + (keys ? '<kbd>' + keys + '</kbd>' : '') + '</button>';
    menu.innerHTML = (!many && PG_TYPES[n.type].runs ? item('run', 'Make again') : '')
        + (!many && n.type === 'generate' && n.result ? item('view', 'View large') : '')
        + item('duplicate', many ? 'Duplicate ' + pg.sel.size + ' nodes' : 'Duplicate', 'Ctrl D')
        + item('copy', 'Copy', 'Ctrl C')
        + (many ? item('fit', 'Show them all') : '')
        + item('group', many ? 'Group ' + pg.sel.size + ' nodes' : 'Group', 'Ctrl G')
        + ([...pg.sel].some(sid => pgGroupOf(sid)) ? item('ungroup', 'Ungroup', 'Ctrl Shift G') : '')
        + '<div class="pg-menu-sep"></div>'
        + item('delete', many ? 'Delete ' + pg.sel.size + ' nodes' : 'Delete', 'Del');
    menu.hidden = false;
    menu.style.left = Math.min(r.width - 230, clientX - r.left) + 'px';
    menu.style.top = Math.min(r.height - 290, clientY - r.top) + 'px';
    (menu.querySelector('button') as HTMLElement | null)?.focus();
}
const pgCloseCtx = () => { if (pg.el.ctx) pg.el.ctx.hidden = true; };

// ── Looking at a picture large: the versions, and side by side with what went in ──
async function pgOpenViewer(nodeId: string, start = 0) {
    const n = pgNode(nodeId);
    if (!n) return;
    let src = n;
    if (n.type === 'compare') {
        const a = (await pgImagesIn(n, 'a'))[0], b = (await pgImagesIn(n, 'b'))[0];
        if (!a || !b) return;
        pg.lb = { node: n.id, items: [{ src: b.value, label: 'B, with A to slide over it' }], index: 0, compare: a.value, split: 50 };
        pg.el.lb.hidden = false;
        pg.el.lb.classList.add('is-comparing');
        pg.el.lb.querySelector('[data-lb="compare"]')!.setAttribute('aria-pressed', 'true');
        await pgPaintViewer();
        (pg.el.lb.querySelector('[data-lb="close"]') as HTMLElement).focus();
        return;
    }
    if (n.type === 'output' || n.type === 'gallery') {
        const port = n.type === 'gallery' ? 'images' : 'image';
        const all: PgShown[] = n.type === 'gallery' ? await pgGalleryShown(n) : (await pgImagesIn(n, port)).map(v => ({ value: v.value, key: v.key, label: v.label }));
        const e = pgInEdges(n.id, port)[0];
        const direct = e && pgNode(e.from);
        // an Output of a Generate shows that Generate's versions
        if (n.type === 'output' && all.length === 1 && direct && direct.type === 'generate') src = direct;
        else {
            if (!all.length) return;
            pg.lb = { node: n.id, items: all.map(v => ({ src: v.value, id: v.id, thumb: v.thumb, label: v.label })), index: Math.max(0, Math.min(start, all.length - 1)), compare: '', split: 50 };
            pg.el.lb.hidden = false;
            pg.el.lb.classList.remove('is-comparing');
            await pgPaintViewer();
            (pg.el.lb.querySelector('[data-lb="close"]') as HTMLElement).focus();
            return;
        }
    }
    const items: { id?: string; src?: string; thumb?: string; label: string }[] = [];
    if (src.type === 'generate' && src.versions && src.versions.length) {
        src.versions.forEach((v, i) => items.push({ id: v.id, thumb: v.thumb, src: v.id === src.resultId ? src.result || undefined : undefined, label: 'Version ' + (src.versions!.length - i) }));
    } else {
        try { const v = await pgValueOf(src); if (v && v.kind === 'image') items.push({ src: v.value, label: v.label }); } catch { /* none */ }
    }
    if (!items.length) return;
    // what to put it next to: the first image that went in
    const first = PG_TYPES[src.type].ins.find(i => i.type === 'image');
    const e = first && pgInEdges(src.id, first.name)[0];
    let compare = '';
    if (e) { try { const v = await pgValueOf(pgNode(e.from)!); compare = v ? v.value : ''; } catch { /* none */ } }
    const index = Math.max(0, items.findIndex(it => it.id && it.id === src.resultId));
    pg.lb = { node: src.id, items, index, compare, split: 50 };
    pg.el.lb.hidden = false;
    pg.el.lb.classList.remove('is-comparing');
    pg.el.lb.querySelector('[data-lb="compare"]')!.setAttribute('aria-pressed', 'false');
    await pgPaintViewer();
    (pg.el.lb.querySelector('[data-lb="close"]') as HTMLElement).focus();
}
async function pgPaintViewer() {
    const lb = pg.lb, el = pg.el.lb;
    if (!lb) return;
    const it = lb.items[lb.index];
    if (!it.src && it.id && lb.lib) it.src = await pgLibPicture(it.id) || it.thumb;
    else if (!it.src && it.id) { const rec = await pgResultGet(it.id); it.src = rec ? rec.src : it.thumb; }
    const n = pgNode(lb.node);
    el.querySelector('.pg-lb-title')!.textContent = (n ? PG_TYPES[n.type].title : lb.lib ? 'Library' : '') + (it.label ? ' · ' + it.label : '');
    el.querySelector('.pg-lb-count')!.textContent = lb.items.length > 1 ? (lb.index + 1) + ' of ' + lb.items.length : '';
    (el.querySelector('.pg-lb-img') as HTMLImageElement).src = it.src || '';
    (el.querySelector('.pg-lb-before') as HTMLImageElement).src = lb.compare || '';
    (el.querySelector('[data-lb="compare"]') as HTMLButtonElement).hidden = !lb.compare;
    (el.querySelector('[data-lb="use"]') as HTMLButtonElement).hidden = !(n && n.type === 'generate' && it.id && it.id !== n.resultId);
    (el.querySelector('[data-lb="delete"]') as HTMLButtonElement).hidden = !pgViewerCanDelete();
    (el.querySelector('[data-lb="prev"]') as HTMLButtonElement).hidden = lb.items.length < 2;
    (el.querySelector('[data-lb="next"]') as HTMLButtonElement).hidden = lb.items.length < 2;
    (el.querySelector('.pg-lb-split') as HTMLInputElement).value = String(lb.split);
    el.style.setProperty('--pg-split', lb.split + '%');
    const strip = el.querySelector('.pg-lb-strip') as HTMLElement;
    strip.hidden = lb.items.length < 2;
    strip.innerHTML = lb.items.map((x, i) => '<button type="button" class="pg-version" aria-selected="' + (i === lb.index) + '" data-lb-go="' + i + '" title="' + pgEsc(x.label) + '">' + (x.thumb ? '<img src="' + pgEsc(x.thumb) + '" alt="' + pgEsc(x.label) + '">' : '<span>' + (i + 1) + '</span>') + '</button>').join('');
}
function pgCloseViewer() { if (pg.el.lb && !pg.el.lb.hidden) { pg.el.lb.hidden = true; pg.lb = null; } }
// Deleted from where it is looked at (2026-10-29, Simon: "så man kan slette sådan et billede her ved
// at trykke ind på det"): an Image's picture, the node staying empty where it is, wires and all
// (Ctrl Z brings it back); or a picture of the library, as its own Remove does
const pgViewerCanDelete = () => { const lb = pg.lb, n = lb && pgNode(lb.node); return !!lb && (lb.lib ? !!lb.items.length : !!n && n.type === 'image' && !!n.data.src); };
async function pgViewerDelete() {
    const lb = pg.lb;
    if (!lb || !pgViewerCanDelete()) return;
    if (lb.lib) {
        const item = pgLib.shown.find(i => i.id === lb.items[lb.index].id);
        if (!item || !(await pgLibRemove(item))) return;
        lb.items.splice(lb.index, 1);
        if (!lb.items.length) { pgCloseViewer(); return; }
        lb.index = Math.min(lb.index, lb.items.length - 1);
        await pgPaintViewer();
        return;
    }
    pgCloseViewer();
    pgClearImage(pgNode(lb.node)!);
}
// on the picture itself too, beside View large
function pgClearImage(n: PgNode) {
    if (n.type !== 'image' || !n.data.src) return;
    pgRecord();
    if (pg.pic === n.id) pg.pic = '';
    delete n.data.src;
    delete n.data.name;
    delete n.data.key;
    pgRenderNode(n.id);
    pgRenderDown(n.id);
    pgSaveSoon();
    (pg.el.world.querySelector('.pg-node[data-id="' + n.id + '"] [data-pg-act="upload"]') as HTMLElement | null)?.focus();
    showToast('The picture is deleted. Ctrl Z brings it back.', { type: 'success', title: 'Image' });
}

// ── Shortcuts ──
const PG_KEYS: [string, string][] = [
    ['Double-click, or /', 'Add a node by searching'],
    ['Drag a wire into empty space', 'Add a node that takes it'],
    ['Drag from an input', 'Take its wire off, or draw one back'],
    ['Shift + drag, or right-drag', 'Choose several nodes'],
    ['Ctrl + A', 'Choose them all'],
    ['Ctrl + C, Ctrl + V', 'Copy and paste nodes; paste an image as an Image node'],
    ['Ctrl + D', 'Duplicate'],
    ['Ctrl + G, Ctrl + Shift + G', 'Group what is chosen; ungroup it'],
    ['Delete', 'Delete what is chosen'],
    ['Ctrl + Z, Ctrl + Shift + Z', 'Undo, redo'],
    ['Arrow keys', 'Move what is chosen (with Shift, further)'],
    ['Ctrl + Enter', 'Run'],
    ['Alt + Enter', 'Make the chosen nodes again'],
    ['Shift + I, P, G, O, N', 'Add an Image, Prompt, Generate, Output or Note where the pointer is'],
    ['F', 'Fit everything on the screen (or what is chosen)'],
    ['Wheel', 'Zoom; drag the canvas to move it'],
    ['Esc', 'Close, or let go of what is chosen'],
];

// ── Workflows: the menu, export and import ──
async function pgRenderWorkflowMenu() {
    const menu = document.getElementById('pgWfMenu');
    if (!menu) return;
    const list = await pgListWorkflows();
    menu.innerHTML = (pgShares.list.length ? '<div class="pg-menu-label">Sent to you</div>' + pgShares.list.map(x => '<div class="pg-menu-row is-sent">'
            + '<button type="button" class="pg-menu-item" data-pg-wf="take" data-id="' + x.id + '"><span>' + pgEsc(x.name) + '</span><small>From ' + pgEsc(pgWho(x.from)) + ' · ' + pgAgo(x.at) + ' · ' + x.nodes + ' nodes' + (x.size >= 10 * 1048576 ? ' · ' + pgMB(x.size) + ' MB' : '') + (x.note ? ' · ' + pgEsc(x.note) : '') + '</small></button>'
            + '<button type="button" class="pg-icon" data-pg-wf="drop" data-id="' + x.id + '" aria-label="Take ' + pgEsc(x.name) + ' away, unopened" title="Take it away, unopened">' + pgIcon('close') + '</button></div>').join('') + '<div class="pg-menu-sep"></div>' : '')
        + '<button type="button" class="pg-menu-item" data-pg-wf="new">New workflow<small>Image and Prompt into Generate</small></button>'
        + '<button type="button" class="pg-menu-item" data-pg-wf="describe">Describe a workflow…<small>Say what it is to make, and Gemini builds it</small></button>'
        + '<div class="pg-menu-sep"></div>'
        + '<div class="pg-menu-label">In this browser</div>'
        + list.map(w => '<div class="pg-menu-row' + (pg.wf && w.id === pg.wf.id ? ' is-current' : '') + '">'
            + '<button type="button" class="pg-menu-item" data-pg-wf="open" data-id="' + w.id + '"><span>' + pgEsc(w.name) + '</span><small>' + new Date(w.updated).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) + ' · ' + w.nodes.length + ' nodes</small></button>'
            + (pg.wf && w.id === pg.wf.id ? '' : '<button type="button" class="pg-icon" data-pg-wf="delete" data-id="' + w.id + '" aria-label="Delete ' + pgEsc(w.name) + '" title="Delete">' + pgIcon('close') + '</button>')
            + '</div>').join('')
        + '<div class="pg-menu-sep"></div>'
        + '<button type="button" class="pg-menu-item" data-pg-wf="send">Send to a colleague…<small>Straight into their Playground, no file</small></button>'
        + '<button type="button" class="pg-menu-item" data-pg-wf="export">Export this workflow<small>As a file</small></button>'
        + '<button type="button" class="pg-menu-item" data-pg-wf="import">Import a workflow<small>From a file exported here</small></button>';
}
// What a workflow saved earlier has that is not here any more (the video nodes, taken
// out on 2026-10-13) is left out when it opens: those nodes, their wires, and wires
// into an input that has gone (a Gallery's Videos, a GIF's video).
function pgDropGone(wf: PgWorkflow) {
    const known = (t: string) => Object.prototype.hasOwnProperty.call(PG_TYPES, t);
    const gone = new Set(wf.nodes.filter(n => !known(n.type)).map(n => n.id));
    wf.nodes = wf.nodes.filter(n => !gone.has(n.id));
    wf.nodes.forEach(n => { if (n.data && (n.data.kind === 'video' || n.data.kind === 'audio')) delete n.data.kind; });
    wf.edges = wf.edges.filter(e => {
        if (gone.has(e.from) || gone.has(e.to)) return false;
        const b = wf.nodes.find(n => n.id === e.to);
        return !!b && PG_TYPES[b.type].ins.some(i => i.name === e.port);
    });
    if (wf.groups) wf.groups = wf.groups.map(g => ({ ...g, nodes: g.nodes.filter(id => !gone.has(id)) })).filter(g => g.nodes.length);
}
async function pgOpenWorkflow(wf: PgWorkflow) {
    if (pgAsk.busy) { showToast('The assistant is working on this workflow: stop it first.', { type: 'warn', title: 'Assistant' }); return; }
    pgDropGone(wf);
    pg.wf = wf;
    pg.sel = new Set();
    pg.edgeSel = '';
    pg.undo = [];
    pg.redo = [];
    pgPaintUndo();
    pgRenderAll();
    pgPaintRun();
    pgAskRender();
    pgLibRender();
    if (!wf.view.k) pgFit();
    if (wf.nodes.some(n => n.type === 'reference')) pgReferenceSets().then(() => wf.nodes.filter(n => n.type === 'reference').forEach(n => pgRenderNode(n.id)));
    if (wf.nodes.some(n => n.type === 'packshot')) pgPackshotEntries(true).then(() => wf.nodes.filter(n => n.type === 'packshot' || n.type === 'generate').forEach(n => pgRenderNode(n.id)));
    try { localStorage.setItem(PG_CURRENT_KEY, wf.id); } catch { /* private */ }
    pgSaveNow();
}
// the workflow as its file has it: the pictures shown go along; the versions behind them stay in this browser
function pgExportData(): { format: string; version: number; exported: string; workflow: PgWorkflow } | null {
    if (!pg.wf) return null;
    const wf = JSON.parse(JSON.stringify(pg.wf)) as PgWorkflow;
    delete wf.chat;
    wf.nodes.forEach(n => { delete n.versions; delete n.resultId; delete n.listIds; if (n.data.kept) delete n.data.kept; if (n.status === 'running' || n.status === 'waiting') { n.status = 'idle'; n.message = ''; } });
    return { format: 'floorflow-playground', version: 1, exported: new Date().toISOString(), workflow: wf };
}
function pgExportWorkflow() {
    const data = pgExportData();
    if (!data) return;
    const wf = data.workflow;
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'floorflow-workflow-' + (wf.name || 'workflow').toLowerCase().replace(/[^a-z0-9æøå]+/g, '-').replace(/^-|-$/g, '') + '.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
// ── Sent between people (2026-10-29, Simon: "så det i stedet gik direkte igennem, altså man kan
// sende det til hinanden på siden") ──
// A workflow goes straight to a colleague's Playground, with a note: no file to download and
// import. What was sent to me is under Workflows, "Sent to you", with its count on the button.
interface PgShare { id: string; at: number; from: string; name: string; nodes: number; note: string; size: number }
const PG_SEEN_SHARES = 'creator.playground.seenShares';
const pgShares = { list: [] as PgShare[], people: null as string[] | null, timer: 0 as any, to: '', shown: [] as string[], active: 0, sending: false, stop: false, opening: new Set<string>() };
// Any size (2026-10-29, Simon: "Hvorfor bliver større ikke sendt over også? det skal altså fixes"):
// a workflow goes in parts of 8 MB, one request each, and its pictures go as they are
const PG_SEND_PART = 8 * 1024 * 1024;
const pgMB = (n: number) => { const m = n / 1048576; return m < 10 ? m.toFixed(1) : String(Math.round(m)); };
// a call about one sent: tried again (twice, unless tries says less) when the line or the server fails on the way
async function pgShareCall(url: string, body?: any, tries = 3): Promise<any> {
    let last = '';
    for (let t = 0; t < tries; t++) {
        if (t) await new Promise(r => setTimeout(r, 1500 * t));
        let r: Response;
        try { r = await apiFetch(url, body === undefined ? undefined : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); }
        catch (e) { last = (e as Error).message; continue; }
        const j = await r.json().catch(() => ({}));
        if (r.ok) return j;
        last = j.error || 'The server answered ' + r.status;
        if (r.status < 500 && r.status !== 408 && r.status !== 429) break;
    }
    throw new Error(last || 'The server did not answer');
}
const pgWho = (email: string) => String(email || '').split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const pgAgo = (at: number) => { const m = Math.round((Date.now() - at) / 60000); return m < 1 ? 'just now' : m < 60 ? m + ' min ago' : m < 1440 ? Math.round(m / 60) + ' h ago' : new Date(at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }); };
function pgSharesPaint() {
    const btn = document.getElementById('pgWfBtn');
    if (!btn) return;
    let badge = btn.querySelector('.pg-wf-count') as HTMLElement | null;
    const n = pgShares.list.length;
    if (!n) { badge?.remove(); btn.removeAttribute('title'); return; }
    if (!badge) { badge = document.createElement('span'); badge.className = 'pg-wf-count'; btn.insertBefore(badge, btn.querySelector('svg')); }
    badge.textContent = String(n);
    btn.title = n === 1 ? 'A workflow was sent to you' : n + ' workflows were sent to you';
}
// wait: the server answers as soon as something new is sent to me (or after 25 seconds)
async function pgSharesCheck(wait = false): Promise<boolean> {
    if (typeof apiFetch !== 'function') return false;
    try {
        const r = await apiFetch('/api/playground/inbox' + (wait ? '?wait=25&known=' + pgShares.list.map(x => x.id).join(',') : ''));
        if (!r.ok) return false;
        pgShares.list = ((await r.json()).shares || []) as PgShare[];
    } catch { return false; }
    pgSharesPaint();
    // each new one said once, as a notice that stays until it is opened or put off
    let seen: string[] = [];
    try { seen = JSON.parse(localStorage.getItem(PG_SEEN_SHARES) || '[]'); } catch { /* none */ }
    const fresh = pgShares.list.filter(x => !seen.includes(x.id));
    fresh.slice(0, 3).reverse().forEach(pgNotice);
    if (fresh.length) { try { localStorage.setItem(PG_SEEN_SHARES, JSON.stringify([...seen, ...fresh.map(f => f.id)].slice(-200))); } catch { /* private */ } }
    // gone from the list (opened or taken away elsewhere): its notice goes too
    document.querySelectorAll<HTMLElement>('.pg-notice[data-share]').forEach(el => { if (!pgShares.list.some(x => x.id === el.dataset.share)) el.remove(); });
    return true;
}
// "You have received a workflow": wherever in Creator the person is
function pgNotice(x: PgShare) {
    const box = document.getElementById('toastContainer');
    if (!box || box.querySelector('.pg-notice[data-share="' + x.id + '"]')) return;
    const el = document.createElement('div');
    el.className = 'toast pg-notice';
    el.dataset.share = x.id;
    el.setAttribute('role', 'alert');
    el.innerHTML = '<span class="pg-notice-av" aria-hidden="true">' + pgEsc(pgInitials(x.from)) + '</span><div class="toast-body"><div class="toast-title">You have received a workflow from ' + pgEsc(pgWho(x.from)) + '</div>'
        + '<div class="toast-message">“' + pgEsc(x.name) + '”, ' + x.nodes + ' node' + (x.nodes === 1 ? '' : 's') + '. It waits for you in the Playground, under Workflows.</div>'
        + '<div class="pg-notice-actions"><button type="button" class="pg-notice-btn is-primary" data-pg-notice="open">Open it</button><button type="button" class="pg-notice-btn" data-pg-notice="later">Later</button></div></div>';
    box.appendChild(el);
    void el.offsetWidth;
    el.classList.add('show');
}
// one being opened: its notice shows how far it is
function pgNoticeBusy(x: PgShare) {
    pgNotice(x);
    const el = document.querySelector<HTMLElement>('.pg-notice[data-share="' + x.id + '"]');
    if (!el) return () => undefined;
    el.classList.add('is-busy');
    el.setAttribute('role', 'status');
    el.querySelector('.toast-title')!.textContent = 'Opening the workflow from ' + pgWho(x.from);
    el.querySelector('.toast-message')!.textContent = '“' + x.name + '”, ' + x.nodes + ' node' + (x.nodes === 1 ? '' : 's') + '.';
    el.querySelector('.pg-notice-actions')!.innerHTML = '<span class="pg-notice-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-label="Opened"><i></i></span><span class="pg-notice-busy"></span>';
    const bar = el.querySelector('.pg-notice-bar') as HTMLElement, fill = bar.firstElementChild as HTMLElement, text = el.querySelector('.pg-notice-busy') as HTMLElement;
    return (got: number) => {
        const of = Math.max(x.size, got, 1), pct = Math.round(got / of * 100);
        fill.style.width = pct + '%';
        bar.setAttribute('aria-valuenow', String(pct));
        text.textContent = pgMB(got) + ' of ' + pgMB(of) + ' MB';
    };
}
function pgNoticeGone(id: string) {
    const el = document.querySelector<HTMLElement>('.pg-notice[data-share="' + id + '"]');
    if (!el) return;
    el.classList.remove('show');
    setTimeout(() => el.remove(), 320);
}
// listened for all the time Creator is open, on any tab: a new one comes at once
function pgSharesStart() {
    if (pgShares.timer) return;
    pgShares.timer = 1;
    document.addEventListener('click', (ev) => {
        const b = (ev.target as HTMLElement).closest('[data-pg-notice]') as HTMLElement | null;
        const el = b && b.closest('.pg-notice') as HTMLElement | null;
        if (!b || !el) return;
        const id = el.dataset.share!;
        if (b.dataset.pgNotice === 'open') {
            (document.querySelector('.tab-btn[data-tab="playground"]') as HTMLElement | null)?.click();
            pgTakeShare(id);
        } else pgNoticeGone(id);
    });
    // the page closed while one is sent: it would not come
    window.addEventListener('beforeunload', (ev) => { if (pgShares.sending && !pgShares.stop) { ev.preventDefault(); ev.returnValue = ''; } });
    (async () => {
        await pgSharesCheck();
        for (;;) {
            const ok = await pgSharesCheck(true);
            // the server away, or not logged in: a little later
            if (!ok) await new Promise(r => setTimeout(r, 15_000));
        }
    })();
}
const pgInitials = (email: string) => { const parts = String(email || '').split('@')[0].split(/[._-]+/).filter(Boolean); return (parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] || '?').slice(0, 2)).toUpperCase(); };
const pgPersonHtml = (email: string) => '<span class="pg-send-av" aria-hidden="true">' + pgEsc(pgInitials(email)) + '</span><span class="pg-send-who"><b>' + pgEsc(pgWho(email)) + '</b><small>' + pgEsc(email) + '</small></span>';
// the list under To: everyone at first, then those whose name or address has what is typed
function pgSendList(open: boolean) {
    const el = document.getElementById('pgSend')!;
    const input = el.querySelector('[data-pg-send-to]') as HTMLInputElement, list = el.querySelector('#pgSendList') as HTMLElement;
    if (!open || !pgShares.people) { list.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); return; }
    const q = input.value.trim().toLowerCase();
    pgShares.shown = pgShares.people.filter(p => !q || p.includes(q) || pgWho(p).toLowerCase().includes(q));
    pgShares.active = Math.max(0, Math.min(pgShares.active, pgShares.shown.length - 1));
    list.innerHTML = pgShares.shown.length
        ? pgShares.shown.map((p, i) => '<li class="pg-send-opt' + (i === pgShares.active ? ' is-active' : '') + '" id="pgSendOpt' + i + '" role="option" aria-selected="' + (i === pgShares.active) + '" data-pg-send-pick="' + pgEsc(p) + '">' + pgPersonHtml(p) + '</li>').join('')
        : '<li class="pg-send-none">' + (pgShares.people.length ? 'No one by that name or address has used FloorFlow.' : 'No one else has used FloorFlow yet.') + '</li>';
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    if (pgShares.shown.length) input.setAttribute('aria-activedescendant', 'pgSendOpt' + pgShares.active); else input.removeAttribute('aria-activedescendant');
    (list.querySelector('.is-active') as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
}
// the one chosen, shown as a card; × chooses again
function pgSendChoose(email: string) {
    // being sent: to whom stays as it is
    if (pgShares.sending) return;
    const el = document.getElementById('pgSend')!;
    const input = el.querySelector('[data-pg-send-to]') as HTMLInputElement, card = el.querySelector('.pg-send-chosen') as HTMLElement;
    pgShares.to = email;
    pgSendList(false);
    if (email) {
        card.innerHTML = pgPersonHtml(email) + '<button type="button" class="pg-icon" data-pg-send-clear aria-label="Choose someone else" title="Choose someone else">' + pgIcon('close') + '</button>';
        card.hidden = false;
        input.hidden = true;
        (el.querySelector('.pg-describe-status') as HTMLElement).textContent = '';
        (el.querySelector('[data-pg-send="send"]') as HTMLElement).focus();
    } else {
        card.hidden = true;
        input.hidden = false;
        input.value = '';
        pgShares.active = 0;
        input.focus();
        pgSendList(true);
    }
}
function pgSendSetup(el: HTMLElement) {
    const input = el.querySelector('[data-pg-send-to]') as HTMLInputElement;
    input.addEventListener('focus', () => pgSendList(true));
    input.addEventListener('input', () => { pgShares.active = 0; pgSendList(true); });
    input.addEventListener('blur', () => setTimeout(() => { if (document.activeElement !== input) pgSendList(false); }, 120));
    input.addEventListener('keydown', (ev) => {
        const n = pgShares.shown.length, open = input.getAttribute('aria-expanded') === 'true';
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            ev.preventDefault(); ev.stopPropagation();
            if (!open) { pgSendList(true); return; }
            if (n) { pgShares.active = (pgShares.active + (ev.key === 'ArrowDown' ? 1 : n - 1)) % n; pgSendList(true); }
        } else if (ev.key === 'Enter') {
            ev.preventDefault(); ev.stopPropagation();
            const typed = input.value.trim().toLowerCase();
            if (open && n) pgSendChoose(pgShares.shown[pgShares.active]);
            else if (typed && (pgShares.people || []).includes(typed)) pgSendChoose(typed);
        } else if (ev.key === 'Escape' && open) { ev.preventDefault(); ev.stopPropagation(); pgSendList(false); }
    });
    // chosen with the mouse: the field keeps its focus until then
    el.querySelector('#pgSendList')!.addEventListener('mousedown', (ev) => {
        const opt = (ev.target as HTMLElement).closest('[data-pg-send-pick]') as HTMLElement | null;
        ev.preventDefault();
        if (opt) pgSendChoose(opt.dataset.pgSendPick!);
    });
}
async function pgOpenSend() {
    if (!pg.wf) return;
    const el = document.getElementById('pgSend')!;
    el.hidden = false;
    // still sending the one before: shown as it is
    if (pgShares.sending) return;
    el.querySelector('.pg-send-name')!.textContent = pg.wf.name || 'This workflow';
    (el.querySelector('.pg-describe-status') as HTMLElement).textContent = '';
    pgSendChoose('');
    // the people read again each time (someone new may have come); what is typed meanwhile stays, and is looked up when they are here
    try { const r = await apiFetch('/api/playground/people'); if (r.ok) pgShares.people = (await r.json()).people || []; } catch { /* the ones read before */ }
    if (!pgShares.people) pgShares.people = [];
    const input = el.querySelector('[data-pg-send-to]') as HTMLInputElement;
    if (!el.hidden && !pgShares.to && document.activeElement === input) pgSendList(true);
}
// closed while it is sent: it is not sent
function pgCloseSend() { const el = document.getElementById('pgSend'); if (el && !el.hidden && pgShares.sending) pgShares.stop = true; if (el) el.hidden = true; }
async function pgSend() {
    const el = document.getElementById('pgSend')!;
    const to = pgShares.to || (el.querySelector('[data-pg-send-to]') as HTMLInputElement).value.trim().toLowerCase();
    const status = el.querySelector('.pg-describe-status') as HTMLElement;
    const btn = el.querySelector('[data-pg-send="send"]') as HTMLButtonElement;
    if (!to) { status.textContent = 'Choose who it is for.'; pgSendChoose(''); return; }
    if (pgShares.sending) return;
    pgShares.sending = true;
    pgShares.stop = false;
    btn.disabled = true;
    el.classList.add('is-busy');
    status.textContent = 'Sending…';
    let id = '', name = '';
    try {
        await pgSaveNow();
        const data = pgExportData();
        if (!data) throw new Error('There is no workflow open');
        name = data.workflow.name;
        // in parts of 8 MB, each cut where a character ends
        const text = JSON.stringify(data), parts: string[] = [];
        for (let i = 0; i < text.length;) {
            let j = Math.min(text.length, i + PG_SEND_PART);
            const c = text.charCodeAt(j - 1);
            if (j < text.length && c >= 0xd800 && c < 0xdc00) j--;
            parts.push(text.slice(i, j));
            i = j;
        }
        id = (await pgShareCall('/api/playground/send', { to, name, nodes: data.workflow.nodes.length, size: text.length, parts: parts.length }, 1)).id;
        let sent = 0;
        for (let n = 0; n < parts.length; n++) {
            if (pgShares.stop) throw new Error('Not sent');
            if (parts.length > 1) status.textContent = 'Sending… ' + pgMB(sent) + ' of ' + pgMB(text.length) + ' MB';
            await pgShareCall('/api/playground/shares/' + id + '/parts/' + n, { data: parts[n] });
            sent += parts[n].length;
        }
        if (pgShares.stop) throw new Error('Not sent');
        // all of it there: now they are told
        await pgShareCall('/api/playground/shares/' + id + '/done', {});
        id = '';
        el.hidden = true;
        showToast('“' + pgEsc(name) + '” is sent to ' + pgEsc(pgWho(to)) + '. It is in their Playground, under Workflows.', { type: 'success', title: 'Sent' });
    } catch (e) {
        // what came of it is taken away
        if (id) apiFetch('/api/playground/shares/' + id, { method: 'DELETE' }).catch(() => undefined);
        const msg = (e as Error).message || 'It could not be sent';
        if (!pgShares.stop) {
            status.textContent = msg;
            if (el.hidden) showToast('“' + pgEsc(name || 'The workflow') + '” was not sent to ' + pgEsc(pgWho(to)) + ': ' + pgEsc(msg), { type: 'warn', title: 'Not sent' });
        }
    } finally {
        pgShares.sending = false;
        pgShares.stop = false;
        btn.disabled = false;
        el.classList.remove('is-busy');
    }
}
// one sent to me: opened as a workflow of my own, and it leaves "Sent to you". One sent in parts
// is read part by part, its notice showing how far it is
async function pgTakeShare(id: string) {
    if (pgShares.opening.has(id)) return;
    pgShares.opening.add(id);
    const base = '/api/playground/shares/' + encodeURIComponent(id);
    let share: any = null;
    try {
        const r = await apiFetch(base);
        if (!r.ok) { pgNoticeGone(id); showToast('It could not be opened: it was taken away.', { type: 'warn', title: 'Sent to you' }); pgSharesCheck(); return; }
        share = (await r.json()).share;
        let file = share.workflow;
        if (!file) {
            const busy = pgNoticeBusy(share), texts: string[] = [];
            let got = 0;
            busy(0);
            for (let n = 0; n < share.parts; n++) {
                const t = String((await pgShareCall(base + '/parts/' + n)).data);
                texts.push(t);
                got += t.length;
                busy(got);
            }
            file = JSON.parse(texts.join(''));
        }
        const wf = pgReadWorkflowFile(file);
        await pgSaveNow();
        await pgOpenWorkflow(wf);
        // not opened (the assistant is working): it stays under Workflows
        if (pg.wf !== wf) { pgNoticeGone(id); return; }
        await apiFetch(base + '/taken', { method: 'POST' }).catch(() => undefined);
        pgShares.list = pgShares.list.filter(x => x.id !== id);
        pgSharesPaint();
        pgNoticeGone(id);
        showToast('“' + pgEsc(wf.name) + '” from ' + pgEsc(pgWho(share.from)) + ' is open, with ' + wf.nodes.length + ' nodes. It is kept in this browser, under Workflows.', { type: 'success', title: 'Sent to you' });
    } catch (e) {
        pgNoticeGone(id);
        showToast('“' + pgEsc(share ? share.name : 'The workflow') + '” could not be opened: ' + pgEsc((e as Error).message) + '. It is still under Workflows: try again from there.', { type: 'warn', title: 'Sent to you' });
    } finally {
        pgShares.opening.delete(id);
    }
}
async function pgDropShare(id: string) {
    await apiFetch('/api/playground/shares/' + encodeURIComponent(id), { method: 'DELETE' }).catch(() => undefined);
    pgShares.list = pgShares.list.filter(x => x.id !== id);
    pgSharesPaint();
    pgNoticeGone(id);
}

// A workflow from a file: only what a workflow is made of is taken in.
function pgReadWorkflowFile(json: any): PgWorkflow {
    const w = json && json.format === 'floorflow-playground' ? json.workflow : null;
    if (!w || !Array.isArray(w.nodes) || !Array.isArray(w.edges)) throw new Error('This is not a Playground workflow');
    const str = (v: any, max = 200000) => typeof v === 'string' ? v.slice(0, max) : '';
    const pic = (v: any) => typeof v === 'string' && /^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(v) ? v : null;
    const ids = new Map<string, string>();
    const nodes: PgNode[] = w.nodes.filter((n: any) => n && PG_TYPES[n.type as PgType]).map((n: any) => {
        const id = pgId('n');
        ids.set(String(n.id), id);
        const data: Record<string, any> = {};
        for (const [k, v] of Object.entries(n.data || {})) {
            if (k === 'src') { const p = pic(v); if (p) data.src = p; }
            else if (k === 'vars' && v && typeof v === 'object') data.vars = Object.fromEntries(Object.entries(v as any).map(([a, b]) => [str(a, 80), str(b, 2000)]));
            else if (k === 'switches' && Array.isArray(v)) data.switches = v.slice(0, 20).map((x: any) => ({ id: str(x && x.id, 40) || pgId('s'), name: str(x && x.name, 40), on: !!(x && x.on) }));
            else if (k === 'rules' && Array.isArray(v)) data.rules = v.slice(0, 20).map((x: any) => ({ id: str(x && x.id, 40) || pgId('r'), label: str(x && x.label, 40), mode: ['contains', 'is', 'starts', 'ends'].includes(x && x.mode) ? x.mode : 'contains', value: str(x && x.value, 500) }));
            else if (k === 'varName') data.varName = pgVarSafe(String(v));
            else if (k === 'shapes' && Array.isArray(v)) data.shapes = v.slice(0, 500).filter((x: any) => x && ['rect', 'ellipse', 'arrow', 'draw', 'text'].includes(x.t)).map((x: any) => {
                const num = (y: any) => Number.isFinite(Number(y)) ? Number(y) : 0;
                const shape: PgShape = { t: x.t, x: num(x.x), y: num(x.y), color: /^#[0-9a-f]{3,8}$/i.test(String(x.color)) ? x.color : '#ef4444', stroke: Math.min(200, Math.max(0.5, num(x.stroke) || 4)) };
                if (x.w !== undefined) { shape.w = num(x.w); shape.h = num(x.h); }
                if (x.x2 !== undefined) { shape.x2 = num(x.x2); shape.y2 = num(x.y2); }
                if (Array.isArray(x.pts)) shape.pts = x.pts.slice(0, 20000).map(num);
                if (x.t === 'text') { shape.text = str(x.text, 2000); shape.size = Math.min(2000, Math.max(4, num(x.size) || 24)); }
                if (x.fill) shape.fill = true;
                return shape;
            });
            else if (k === 'kind') { if (v === 'image' || v === 'text') data.kind = v; }
            else if (typeof v === 'boolean') data[k] = v;
            else if (typeof v === 'string' || typeof v === 'number') data[k] = typeof v === 'string' ? str(v) : v;
        }
        const text = PG_TYPES[n.type as PgType].out === 'text';
        return {
            id, type: n.type, x: Number(n.x) || 0, y: Number(n.y) || 0, data,
            result: text ? (str(n.result) || null) : pic(n.result), sig: str(n.sig, 20000) || undefined,
            texts: text && Array.isArray(n.texts) ? n.texts.slice(0, 200).map((x: any) => str(x)) : undefined,
            meta: n.meta && typeof n.meta === 'object' ? Object.fromEntries(Object.entries(n.meta).map(([a, b]) => [str(a, 40), str(b, 200)])) : undefined,
            status: n.result ? 'done' : 'idle',
        } as PgNode;
    });
    const edges = w.edges.filter((e: any) => e && ids.has(String(e.from)) && ids.has(String(e.to))).map((e: any) => ({ id: pgId('e'), from: ids.get(String(e.from))!, to: ids.get(String(e.to))!, port: str(e.port, 40), ...(e.out && e.out !== 'out' ? { out: str(e.out, 40) } : {}) }))
        .filter((e: PgEdge) => { const to = nodes.find(n => n.id === e.to)!; return PG_TYPES[to.type].ins.some(i => i.name === e.port); });
    const now = Date.now();
    const runCount = Math.min(50, Math.max(1, Math.round(Number(w.runCount) || 1)));
    const groups: PgGroup[] = (Array.isArray(w.groups) ? w.groups : []).slice(0, 50).map((g: any) => ({
        id: pgId('g'), name: str(g && g.name, 60) || 'Group', color: PG_GROUP_COLOURS.includes(g && g.color) ? g.color : PG_GROUP_COLOURS[0],
        nodes: (Array.isArray(g && g.nodes) ? g.nodes : []).map((id: any) => ids.get(String(id))).filter(Boolean), locked: !!(g && g.locked),
    })).filter((g: PgGroup) => g.nodes.length);
    return { id: pgId('wf'), name: str(w.name, 80) || 'Imported workflow', created: now, updated: now, nodes, edges, view: { x: 0, y: 0, k: 0 }, ...(runCount > 1 ? { runCount } : {}), ...(groups.length ? { groups } : {}) };
}

// ── Setting it up ──
function setupPlayground() {
    const root = document.getElementById('playground');
    if (!root || pg.ready) return;
    pg.ready = true;
    const shell = root.querySelector('.pg-shell') as HTMLElement;
    // the search, a node's menu, the viewer and the shortcuts are made here
    shell.insertAdjacentHTML('beforeend',
        '<div class="pg-picker" id="pgPicker" role="dialog" aria-label="Add a node" hidden><label class="pg-picker-q">' + pgIcon('search') + '<input type="text" placeholder="Search for a node…" aria-label="Search for a node" spellcheck="false" autocomplete="off"></label><div class="pg-picker-list" role="listbox" aria-label="Nodes"></div></div>'
        + '<div class="pg-menu pg-ctx" id="pgCtx" role="menu" hidden></div>'
        + '<div class="pg-box" id="pgBox" hidden></div>'
        + '<div class="pg-keys" id="pgKeys" role="dialog" aria-label="Shortcuts" hidden><div class="pg-keys-head"><b>Shortcuts</b><button type="button" class="pg-icon" data-pg-keys-close aria-label="Close">' + pgIcon('close') + '</button></div><dl>'
        + PG_KEYS.map(([k, d]) => '<dt>' + pgEsc(k) + '</dt><dd>' + pgEsc(d) + '</dd>').join('') + '</dl></div>'
        + '<div class="pg-lb" id="pgLb" role="dialog" aria-modal="true" aria-label="The picture, large" hidden>'
        + '<div class="pg-lb-top"><span class="pg-lb-title"></span><span class="pg-lb-count"></span><span class="pg-lb-gap"></span>'
        + '<button type="button" class="pg-chip" data-lb="compare" title="Side by side with the first image that went in">' + pgIcon('compare') + 'Compare</button>'
        + '<button type="button" class="pg-chip" data-lb="use">Use this version</button>'
        + '<button type="button" class="pg-chip" data-lb="download">' + pgIcon('download') + 'Download</button>'
        + '<button type="button" class="pg-chip" data-lb="delete" title="Delete (Del)">' + pgIcon('trash') + 'Delete</button>'
        + '<button type="button" class="pg-icon is-lb" data-lb="close" aria-label="Close" title="Close (Esc)">' + pgIcon('close') + '</button></div>'
        + '<div class="pg-lb-stage"><div class="pg-lb-frame"><img class="pg-lb-before" alt="What went in"><img class="pg-lb-img" alt="The picture"><input type="range" class="pg-lb-split" min="0" max="100" value="50" aria-label="Compare: drag to see what went in"></div>'
        + '<button type="button" class="pg-lb-nav is-prev" data-lb="prev" aria-label="The version before">' + pgIcon('prev') + '</button><button type="button" class="pg-lb-nav is-next" data-lb="next" aria-label="The version after">' + pgIcon('next') + '</button></div>'
        + '<div class="pg-lb-strip"></div></div>'
        + '<input type="file" accept="application/json,.json" hidden id="pgImport">'
        + '<div class="pg-describe pg-send" id="pgSend" role="dialog" aria-label="Send to a colleague" hidden>'
        + '<div class="pg-describe-head"><b>Send “<span class="pg-send-name"></span>” to a colleague</b><button type="button" class="pg-icon" data-pg-send="close" aria-label="Close">' + pgIcon('close') + '</button></div>'
        + '<p class="pg-note">It goes straight into their Playground, with the pictures it shows; they find it under Workflows. Your own stays as it is.</p>'
        + '<div class="pg-send-field"><span id="pgSendToLabel">To</span><div class="pg-send-to">'
        + '<div class="pg-send-chosen" hidden></div>'
        + '<input class="pg-text" type="text" data-pg-send-to role="combobox" aria-expanded="false" aria-controls="pgSendList" aria-autocomplete="list" aria-labelledby="pgSendToLabel" placeholder="A name or an address" autocomplete="off" spellcheck="false">'
        + '<ul class="pg-send-list" id="pgSendList" role="listbox" aria-labelledby="pgSendToLabel" hidden></ul></div></div>'
        + '<div class="pg-describe-foot"><span class="pg-describe-status" role="status"></span><button type="button" class="pg-chip" data-pg-send="close">Cancel</button><button type="button" class="pg-chip is-primary" data-pg-send="send">Send</button></div></div>'
        + '<div class="pg-describe" id="pgDescribe" role="dialog" aria-label="Describe a workflow" hidden>'
        + '<div class="pg-describe-head"><b>Describe a workflow</b><button type="button" class="pg-icon" data-pg-describe="close" aria-label="Close">' + pgIcon('close') + '</button></div>'
        + '<p class="pg-note">Say what the workflow is to make. Gemini builds it from the nodes there are, and you look it over before you Run.</p>'
        + '<textarea class="pg-text" rows="4" placeholder="A packshot on white from a photo of a garment, then the same garment on a model, both made 4K" aria-label="The workflow to build"></textarea>'
        + '<div class="pg-describe-ex">' + PG_DESCRIBE_EXAMPLES.map((x, i) => '<button type="button" class="pg-chip is-small" data-pg-describe="ex" data-i="' + i + '">' + pgEsc(x) + '</button>').join('') + '</div>'
        + '<div class="pg-describe-foot"><span class="pg-describe-status" role="status"></span><button type="button" class="pg-chip" data-pg-describe="close">Cancel</button><button type="button" class="pg-chip is-primary" data-pg-describe="build">' + pgIcon('generate') + 'Build it</button></div></div>');
    shell.insertAdjacentHTML('beforeend', pgAskHtml() + pgLibHtml());
    pgAskSetupPics(document.getElementById('pgAsk')!);
    pgSendSetup(document.getElementById('pgSend')!);
    pgSetupAnnotate(shell);
    pg.el = {
        root,
        canvas: document.getElementById('pgCanvas')!,
        world: document.getElementById('pgWorld')!,
        wires: document.getElementById('pgWires') as unknown as HTMLElement,
        picker: document.getElementById('pgPicker')!,
        ctx: document.getElementById('pgCtx')!,
        box: document.getElementById('pgBox')!,
        keys: document.getElementById('pgKeys')!,
        lb: document.getElementById('pgLb')!,
    };
    // in a box of the canvas's own size, so the layer standing out does not let the canvas scroll further
    pg.el.canvas.insertAdjacentHTML('afterbegin', '<div class="pg-dots-box" aria-hidden="true"><div class="pg-dots"></div></div>');
    pg.el.dots = pg.el.canvas.querySelector('.pg-dots') as HTMLElement;
    const canvas = pg.el.canvas;
    try { if (localStorage.getItem(PG_ASK_OPEN_KEY) === '1') pgAskOpen(true, false); } catch { /* private */ }
    try { if (localStorage.getItem(PG_LIB_FILTER_KEY) === 'all') pgLib.filter = 'all'; } catch { /* private */ }

    // the workflow last open, or a new one. Another tab of the app can keep the
    // store waiting (an older one, while a newer one changes the store): it says so.
    (async () => {
        let wf: PgWorkflow | undefined;
        const waiting = setTimeout(() => {
            const empty = document.getElementById('pgEmpty');
            if (!empty || pg.wf) return;
            empty.textContent = 'The Playground is waiting for another FloorFlow tab. Reload or close your other FloorFlow tabs, and it opens.';
            empty.classList.add('is-waiting');
            empty.hidden = false;
        }, 4000);
        // the workflows of the first store come over; usually at once, else when they can be read
        let lastOpen = '';
        try { lastOpen = localStorage.getItem(PG_CURRENT_KEY) || ''; } catch { /* private */ }
        const migrating = pgMigrateOld().catch(() => 0);
        const early = await Promise.race([migrating, new Promise<number>(r => setTimeout(() => r(-1), 1500))]);
        const latest = async () => {
            let found: PgWorkflow | undefined;
            try {
                const id = localStorage.getItem(PG_CURRENT_KEY);
                if (id) found = await pgGetWorkflow(id);
                if (!found) found = (await pgListWorkflows())[0];
            } catch { /* a new one */ }
            return found;
        };
        wf = await latest();
        if (early === -1) migrating.then(async n => {
            if (!n) return;
            showToast('Your earlier workflows are back, under Workflows.', { type: 'success', title: 'Workflows' });
            // the one open last comes back in place of the starter, if that is still untouched
            if (!pgPristine(pg.wf)) return;
            const starter = pg.wf!.id;
            const back = (lastOpen && await pgGetWorkflow(lastOpen)) || (await pgListWorkflows()).find(w => w.id !== starter);
            if (!back || back.id === starter) return;
            await pgOpenWorkflow(back);
            await pgDeleteWorkflow(starter);
        });
        clearTimeout(waiting);
        const empty = document.getElementById('pgEmpty');
        if (empty && empty.classList.contains('is-waiting')) { empty.classList.remove('is-waiting'); empty.textContent = 'Add a node from the bar below, or double-click here to search'; }
        await pgOpenWorkflow(wf || pgNewWorkflow());
        pgSharesStart();
    })();

    // the bar: add a node, or run
    root.addEventListener('dragstart', (ev) => { if (!pgEditable(ev.target)) ev.preventDefault(); });
    root.addEventListener('click', async (ev) => {
        const t = ev.target as HTMLElement;
        if (t.closest('#pgLibBtn, #pgLib')) { await pgLibClick(t); return; }
        if (t.closest('#pgAskBtn, #pgAsk')) { await pgAskClick(t); return; }
        const add = t.closest('[data-pg-add]') as HTMLElement | null;
        if (add) { pgAddNode(add.dataset.pgAdd as PgType); return; }
        if (t.closest('#pgRun')) { if (pg.running) pgStop(); else pgRun(); return; }
        const gAct = t.closest('[data-group-act]') as HTMLElement | null;
        if (gAct && pg.wf) {
            const g = pgGroups().find(x => x.id === gAct.dataset.group);
            if (!g) return;
            const act2 = gAct.dataset.groupAct;
            if (act2 === 'ungroup') { pgUngroup(g.id); return; }
            if (act2 === 'run') { pgRun(g.nodes.filter(id => { const m = pgNode(id); return m && PG_TYPES[m.type].runs; })); return; }
            pgRecord();
            pg.wf.groups = pgGroups().map(x => x.id !== g.id ? x : act2 === 'lock' ? { ...x, locked: !x.locked } : { ...x, color: PG_GROUP_COLOURS[(PG_GROUP_COLOURS.indexOf(x.color) + 1) % PG_GROUP_COLOURS.length] });
            pgRenderGroups();
            pgPaintPlan();
            pgSaveSoon();
            (pg.el.world.querySelector('[data-group-act="' + act2 + '"][data-group="' + CSS.escape(g.id) + '"]') as HTMLElement | null)?.focus();
            return;
        }
        if (t.closest('[data-pg-send-clear]')) { pgSendChoose(''); return; }
        const snd = t.closest('[data-pg-send]') as HTMLElement | null;
        if (snd) { if (snd.dataset.pgSend === 'close') pgCloseSend(); else if (snd.dataset.pgSend === 'send') pgSend(); return; }
        const dsc = t.closest('[data-pg-describe]') as HTMLElement | null;
        if (dsc) {
            const a = dsc.dataset.pgDescribe;
            if (a === 'close') pgCloseDescribe();
            else if (a === 'build') pgDescribe();
            else if (a === 'ex') { const box = document.querySelector('#pgDescribe textarea') as HTMLTextAreaElement; box.value = PG_DESCRIBE_EXAMPLES[Number(dsc.dataset.i)]; box.focus(); }
            return;
        }
        const runMore = t.closest('#pgRunMore');
        const runMenu = document.getElementById('pgRunMenu')!;
        if (runMore) { const open = runMenu.hidden; if (open) pgPaintRunMenu(); runMenu.hidden = !open; runMore.setAttribute('aria-expanded', String(open)); return; }
        const step = t.closest('[data-pg-times]') as HTMLElement | null;
        if (step && pg.wf) {
            pg.wf.runCount = Math.max(1, Math.min(50, (Number(pg.wf.runCount) || 1) + Number(step.dataset.pgTimes)));
            if (pg.wf.runCount === 1) delete pg.wf.runCount;
            pgPaintRunMenu();
            pgPaintRun();
            pgSaveSoon();
            return;
        }
        if (!t.closest('.pg-runbox')) { runMenu.hidden = true; document.getElementById('pgRunMore')?.setAttribute('aria-expanded', 'false'); }
        if (t.closest('#pgFit')) { pgFit(true); return; }
        if (t.closest('#pgUndo')) { pgUndoRedo(true); return; }
        if (t.closest('#pgRedo')) { pgUndoRedo(false); return; }
        if (t.closest('#pgKeysBtn')) { pg.el.keys.hidden = !pg.el.keys.hidden; return; }
        const more = t.closest('#pgMore') as HTMLElement | null;
        if (more) {
            if (!pg.el.picker.hidden) { pgClosePicker(); return; }
            const b = more.getBoundingClientRect();
            pgOpenPicker(b.left + b.width / 2 - 150, b.top - 392);
            return;
        }
        if (t.closest('[data-pg-keys-close]')) { pg.el.keys.hidden = true; return; }
        if (t.closest('#pgZoomIn') || t.closest('#pgZoomOut')) {
            const c = canvas.getBoundingClientRect();
            pgZoomAt(pg.wf!.view.k * (t.closest('#pgZoomIn') ? 1.2 : 1 / 1.2), c.width / 2, c.height / 2);
            return;
        }
        const pick = t.closest('[data-pg-pick]') as HTMLElement | null;
        if (pick) { pgPick(pick.dataset.pgPick as PgType); return; }
        const ctx = t.closest('[data-pg-ctx]') as HTMLElement | null;
        if (ctx) {
            const ids = [...pg.sel];
            pgCloseCtx();
            const act = ctx.dataset.pgCtx;
            if (act === 'run' && ids[0]) pgRun(ids[0]);
            else if (act === 'view' && ids[0]) pgOpenViewer(ids[0]);
            else if (act === 'duplicate') pgDuplicate();
            else if (act === 'group') pgGroupSelected();
            else if (act === 'ungroup') pgUngroup();
            else if (act === 'copy') pg.clip = pgCopy(ids);
            else if (act === 'fit') pgFit(true, ids);
            else if (act === 'delete') pgDeleteNodes(ids);
            return;
        }
        // the viewer
        const lbAct = (t.closest('[data-lb]') as HTMLElement | null)?.dataset.lb;
        const lbGo = t.closest('[data-lb-go]') as HTMLElement | null;
        if (lbAct || lbGo) {
            const lb = pg.lb!;
            if (!lb) return;
            if (lbGo) { lb.index = Number(lbGo.dataset.lbGo); pgPaintViewer(); }
            else if (lbAct === 'close') pgCloseViewer();
            else if (lbAct === 'prev' || lbAct === 'next') { lb.index = (lb.index + (lbAct === 'next' ? 1 : -1) + lb.items.length) % lb.items.length; pgPaintViewer(); }
            else if (lbAct === 'compare') { const on = pg.el.lb.classList.toggle('is-comparing'); pg.el.lb.querySelector('[data-lb="compare"]')!.setAttribute('aria-pressed', String(on)); }
            else if (lbAct === 'use') { const n = pgNode(lb.node); const it = lb.items[lb.index]; if (n && it.id) { await pgUseVersion(n, it.id); pgPaintViewer(); } }
            else if (lbAct === 'delete') await pgViewerDelete();
            else if (lbAct === 'download' && lb.lib) { const item = pgLib.shown.find(i => i.id === lb.items[lb.index].id); if (item) pgLibDownload([item]); }
            else if (lbAct === 'download') { const n = pgNode(lb.node); const it = lb.items[lb.index]; if (n && it.src) { pgDownload(n, it.src, lb.items[lb.index].id ? (n.versions || []).find(v => v.id === it.id)?.meta : n.meta); } }
            return;
        }
        if (t === pg.el.lb) { pgCloseViewer(); return; }

        const wfBtn = t.closest('#pgWfBtn');
        const menu = document.getElementById('pgWfMenu')!;
        if (wfBtn) {
            const open = menu.hidden;
            if (open) { await pgSharesCheck(); await pgRenderWorkflowMenu(); }
            menu.hidden = !open;
            wfBtn.setAttribute('aria-expanded', String(open));
            return;
        }
        const wfItem = t.closest('[data-pg-wf]') as HTMLElement | null;
        if (wfItem) {
            const act = wfItem.dataset.pgWf;
            if (act === 'new') { await pgSaveNow(); await pgOpenWorkflow(pgNewWorkflow()); menu.hidden = true; }
            else if (act === 'open') { await pgSaveNow(); const wf = await pgGetWorkflow(wfItem.dataset.id!); if (wf) { await pgOpenWorkflow(wf); } menu.hidden = true; }
            else if (act === 'delete') {
                if (wfItem.classList.contains('is-armed')) { await pgDeleteWorkflow(wfItem.dataset.id!); await pgRenderWorkflowMenu(); }
                else { wfItem.classList.add('is-armed'); wfItem.setAttribute('title', 'Click again to delete'); }
            }
            else if (act === 'export') { pgExportWorkflow(); menu.hidden = true; }
            else if (act === 'send') { menu.hidden = true; pgOpenSend(); }
            else if (act === 'take') { menu.hidden = true; await pgTakeShare(wfItem.dataset.id!); }
            else if (act === 'drop') {
                if (wfItem.classList.contains('is-armed')) { await pgDropShare(wfItem.dataset.id!); await pgRenderWorkflowMenu(); }
                else { wfItem.classList.add('is-armed'); wfItem.setAttribute('title', 'Click again to take it away'); }
            }
            else if (act === 'import') { (document.getElementById('pgImport') as HTMLInputElement).click(); menu.hidden = true; }
            else if (act === 'describe') { menu.hidden = true; pgOpenDescribe(); }
            document.getElementById('pgWfBtn')!.setAttribute('aria-expanded', String(!menu.hidden));
            return;
        }
        if (!t.closest('.pg-wf')) { menu.hidden = true; document.getElementById('pgWfBtn')?.setAttribute('aria-expanded', 'false'); }

        // inside a node
        const card = t.closest('.pg-node') as HTMLElement | null;
        if (!card) return;
        const n = pgNode(card.dataset.id!);
        if (!n) return;
        const act = (t.closest('[data-pg-act]') as HTMLElement | null)?.dataset.pgAct;
        if (act === 'delete') { pgDeleteNode(n.id); return; }
        if (act === 'run') { pgRun(n.id); return; }
        if (act === 'upload') { (card.querySelector('[data-pg-file]') as HTMLInputElement)?.click(); return; }
        if (act === 'view') { pgOpenViewer(n.id); return; }
        if (act === 'clear') { pgClearImage(n); return; }
        if (act === 'copy' && n.result) {
            try { await navigator.clipboard.writeText(n.texts && n.texts.length > 1 ? n.texts.join('\n\n') : n.result); showToast('The text is copied.', { type: 'success', title: 'Copied' }); }
            catch { showToast('The text could not be copied here: select it and copy it.', { type: 'warn', title: 'Not copied' }); }
            return;
        }
        if (act === 'to-prompt' && n.result) {
            const p = pgAddNode('prompt', { x: n.x, y: n.y + card.offsetHeight + 30 }, { text: n.texts && n.texts.length > 1 ? n.texts.join('\n') : n.result });
            pgRenderNode(p.id);
            return;
        }
        if (act === 'download') {
            const items = n.type === 'output' ? await pgImagesIn(n, 'image') : [];
            const e = pgInEdges(n.id, 'image')[0];
            const origin = e ? pgOrigin(e.from) : n;
            const v = items[0];
            if (!v) return;
            if (v.kind === 'image') pgDownload(origin || n, v.value, origin?.meta);
            else pgSaveAs('data:text/plain;charset=utf-8,' + encodeURIComponent(v.value), 'txt');
            return;
        }
        if (act === 'clear-gallery') {
            const kept: { id: string }[] = Array.isArray(n.data.kept) ? n.data.kept : [];
            pgRecord();
            n.data.kept = [];
            kept.forEach(k => pgResultDelete(k.id));
            pgRenderNode(n.id);
            pgSaveSoon();
            return;
        }
        if (act === 'download-all') {
            const items = await pgGalleryItems(n);
            // Safari takes only some of many files at once: there they come as one ZIP (SAVES_AS_ZIP)
            if (SAVES_AS_ZIP && items.length > 1) {
                const files: { name: string; data: Uint8Array }[] = [];
                for (const [i, v] of items.entries()) { const r = await pgPrepareDownload(n, v.value, undefined, String(i + 1)); if (r) files.push({ name: r.name, data: dataUrlBytes(r.url) }); }
                if (files.length) saveBlobAsFile(makeZip(files), 'floorflow-playground-' + pgSlug(pg.wf!.name) + '-' + new Date().toISOString().slice(0, 10) + '.zip');
                return;
            }
            for (const [i, v] of items.entries()) await pgDownload(n, v.value, undefined, items.length > 1 ? String(i + 1) : '');
            return;
        }
        const gal = t.closest('[data-pg-gal]') as HTMLElement | null;
        if (gal) { pgOpenViewer(n.id, Number(gal.dataset.pgGal)); return; }
        if (act === 'annotate') { pgOpenAnnotate(n); return; }
        if (act === 'clear-marks' && pgShapesOf(n).length) { pgRecord(); n.data.shapes = []; pgRenderNode(n.id); pgRenderDown(n.id); pgSaveSoon(); return; }
        // a Switch's outputs and a Condition's rules: added and removed
        if (act === 'add-switch' || act === 'add-rule') {
            pgRecord();
            if (act === 'add-switch') { const sw = pgSwitches(n); n.data.switches = [...sw, { id: pgId('s'), name: 'Output ' + (sw.length + 1), on: true }]; }
            else { const rs = pgRules(n); n.data.rules = [...rs, { id: pgId('r'), label: 'Rule ' + (rs.length + 1), mode: 'contains', value: '' }]; }
            pgRenderNode(n.id);
            pgSaveSoon();
            return;
        }
        const swDel = (t.closest('[data-pg-sw-del]') as HTMLElement | null)?.dataset.pgSwDel;
        const ruleDel = (t.closest('[data-pg-rule-del]') as HTMLElement | null)?.dataset.pgRuleDel;
        if (swDel || ruleDel) {
            pgRecord();
            if (swDel) n.data.switches = pgSwitches(n).filter(x => x.id !== swDel);
            else n.data.rules = pgRules(n).filter(x => x.id !== ruleDel);
            const gone = pg.wf!.edges.filter(e => e.from === n.id && e.out === (swDel || ruleDel));
            pg.wf!.edges = pg.wf!.edges.filter(e => !gone.includes(e));
            pgRenderNode(n.id);
            gone.forEach(e => { pgRenderNode(e.to); pgRenderDown(e.to); });
            pgSaveSoon();
            return;
        }
        const ins = (t.closest('[data-pg-insert]') as HTMLElement | null)?.dataset.pgInsert;
        if (ins && n.type === 'construct') {
            const box = card.querySelector('[data-pg-text]') as HTMLTextAreaElement;
            const at = box.selectionStart ?? box.value.length, to = box.selectionEnd ?? at;
            const word = '@' + ins + ' ';
            pgRecord();
            n.data.template = box.value.slice(0, at) + word + box.value.slice(to);
            box.value = n.data.template;
            box.focus();
            box.setSelectionRange(at + word.length, at + word.length);
            pgRenderKeep(n.id);
            pgRenderDown(n.id);
            pgSaveSoon();
            return;
        }
        const version = t.closest('[data-pg-version]') as HTMLElement | null;
        if (version) { pgUseVersion(n, version.dataset.pgVersion!); return; }
        const filePick = t.closest('[data-pg-file-pick]') as HTMLElement | null;
        if (filePick && n.type === 'reference') { pgRecord(); n.data.file = filePick.dataset.pgFilePick; pgRenderNode(n.id); pgRenderDown(n.id); pgSaveSoon(); return; }
    });
    root.addEventListener('dblclick', (ev) => {
        const t = ev.target as HTMLElement;
        if (t.closest('[data-pg-view]')) { const card = t.closest('.pg-node') as HTMLElement; if (card) pgOpenViewer(card.dataset.id!); return; }
        if (t.closest('.pg-node, .pg-bar, .pg-wf, .pg-zoom, .pg-picker, .pg-menu, .pg-lb, .pg-keys') || !t.closest('#pgCanvas')) return;
        pgOpenPicker(ev.clientX, ev.clientY);
    });

    // settings in a node
    root.addEventListener('change', async (ev) => {
        const t = ev.target as HTMLElement;
        if (t.matches('[data-ask-budget]')) {
            try { localStorage.setItem(PG_ASK_BUDGET_KEY, (t as HTMLSelectElement).value); } catch { /* private */ }
            return;
        }
        if (t.matches('[data-pg-limit]')) {
            try { localStorage.setItem(PG_LIMIT_KEY, (t as HTMLSelectElement).value); } catch { /* private */ }
            return;
        }
        if (t.id === 'pgImport') {
            const f = (t as HTMLInputElement).files?.[0];
            (t as HTMLInputElement).value = '';
            if (!f) return;
            try {
                const wf = pgReadWorkflowFile(JSON.parse(await f.text()));
                await pgSaveNow();
                await pgOpenWorkflow(wf);
                showToast('“' + wf.name + '” is in, with ' + wf.nodes.length + ' nodes.', { type: 'success', title: 'Imported' });
            } catch (e) { showToast((e as Error).message || 'The file could not be read', { type: 'warn', title: 'Not imported' }); }
            return;
        }
        const card = t.closest('.pg-node') as HTMLElement | null;
        const n = card && pgNode(card.dataset.id!);
        if (!n) return;
        if (t.matches('[data-pg-file]')) {
            const f = (t as HTMLInputElement).files?.[0];
            (t as HTMLInputElement).value = '';
            if (f) await pgSetImage(n, f);
            return;
        }
        if (t.matches('[data-pg-var]')) return;   // typed: kept by input
        const swOn = (t as HTMLElement).dataset.pgSwOn;
        const ruleMode = (t as HTMLElement).dataset.pgRuleMode;
        const check = (t as HTMLElement).dataset.pgCheck;
        if (swOn || ruleMode || check) {
            pgRecord();
            if (swOn) n.data.switches = pgSwitches(n).map(x => x.id === swOn ? { ...x, on: (t as HTMLInputElement).checked } : x);
            else if (ruleMode) n.data.rules = pgRules(n).map(x => x.id === ruleMode ? { ...x, mode: (t as HTMLSelectElement).value } : x);
            else n.data[check!] = (t as HTMLInputElement).checked;
            pgRenderKeep(n.id);
            pgRenderDown(n.id);
            pgSaveSoon();
            return;
        }
        if (t.matches('[data-pg-sw-name], [data-pg-rule-label], [data-pg-rule-value], [data-pg-varname], [data-pg-set-text]')) return;   // typed: kept by input
        const key = (t as HTMLElement).dataset.pgSet;
        if (!key) return;
        const value = (t as HTMLSelectElement).value;
        if (key === 'template') {
            const tpl = PG_PROMPT_TEMPLATES.find(p => p.id === value);
            if (tpl) {
                pgRecord();
                n.data.text = tpl.text;
                n.data.vars = {};
                pgRenderNode(n.id);
                pgRenderDown(n.id);
                pgSaveSoon();
                if (tpl.aspect) {
                    // the format the template is meant for goes to the Generate it feeds, if that is still as it came
                    pg.wf!.edges.filter(e => e.from === n.id).map(e => pgNode(e.to)).forEach(g => { if (g && g.type === 'generate' && !g.result) { g.data.aspect = tpl.aspect; pgRenderNode(g.id); } });
                }
            }
            return;
        }
        pgRecord();
        n.data[key] = value;
        if (n.type === 'reference' && key === 'set') n.data.file = '';
        if (n.type === 'packshot' && key === 'entry') {
            const e = (pg.packshots || []).find(x => x.id === n.data.entry);
            n.data.slot = e ? (Object.keys(PG_SLOTS).find(s => e.images[s]) || '') : '';
        }
        if (n.type === 'savedPrompt' && key === 'key') { n.data.text = n.data.key ? String((PROMPTS as any)[n.data.key] || '') : ''; n.data.vars = {}; }
        pgRenderNode(n.id);
        pgRenderDown(n.id);
        pgSaveSoon();
    });
    root.addEventListener('input', (ev) => {
        const t = ev.target as HTMLElement;
        if (t.matches('.pg-group-name') && pg.wf) {
            const g = pgGroups().find(x => x.id === (t as HTMLElement).dataset.group);
            if (g) { g.name = (t as HTMLInputElement).value.slice(0, 60); pgSaveSoon(); }
            return;
        }
        // a Compare's slider: only the view moves
        if (t.matches('.pg-cmp-range')) { (t.closest('.pg-cmp') as HTMLElement).style.setProperty('--pg-split', (t as HTMLInputElement).value + '%'); return; }
        if (t.id === 'pgName') { pg.wf!.name = (t as HTMLInputElement).value.trim() || 'Untitled workflow'; pgSaveSoon(); return; }
        if (t.closest('.pg-picker')) { pgPaintPicker(); return; }
        if (t.matches('.pg-lb-split')) { pg.lb && (pg.lb.split = Number((t as HTMLInputElement).value)); pg.el.lb.style.setProperty('--pg-split', (t as HTMLInputElement).value + '%'); return; }
        const card = t.closest('.pg-node') as HTMLElement | null;
        const n = card && pgNode(card.dataset.id!);
        if (!n) return;
        // one undo step for each stretch of typing in a node
        if (pg.typing !== n.id) { pgRecord(); pg.typing = n.id; }
        if (t.matches('[data-pg-var]')) {
            n.data.vars = { ...(n.data.vars || {}), [(t as HTMLInputElement).dataset.pgVar!]: (t as HTMLInputElement).value };
            pgSaveSoon();
            pgLater('down:' + n.id, () => pgRenderDown(n.id));
            return;
        }
        const value = (t as HTMLInputElement).value;
        if (t.matches('[data-pg-set="temperature"]')) {
            const b = card!.querySelector('[data-pg-temp]');
            if (b) b.textContent = Number(value).toFixed(2);
            return;
        }
        const swName = (t as HTMLElement).dataset.pgSwName, ruleLabel = (t as HTMLElement).dataset.pgRuleLabel, ruleValue = (t as HTMLElement).dataset.pgRuleValue;
        if (swName) { n.data.switches = pgSwitches(n).map(x => x.id === swName ? { ...x, name: value.slice(0, 40) } : x); pgSaveSoon(); return; }
        if (ruleLabel) { n.data.rules = pgRules(n).map(x => x.id === ruleLabel ? { ...x, label: value.slice(0, 40) } : x); pgSaveSoon(); return; }
        if (ruleValue) {
            n.data.rules = pgRules(n).map(x => x.id === ruleValue ? { ...x, value: value.slice(0, 500) } : x);
            pgSaveSoon();
            pgLater('keep:' + n.id, () => { pgRenderKeep(n.id); pgRenderDown(n.id); pgRenderEdges(); });
            return;
        }
        const varOf = (t as HTMLElement).dataset.pgVarname;
        if (varOf) {
            // the variable's new name, and the template that used the old one with it
            const src = pgNode(varOf);
            if (!src) return;
            const old = pgVarName(src, n), name = pgVarSafe(value);
            if (!name || name === old) return;
            src.data.varName = name;
            n.data.template = String(n.data.template || '').replace(new RegExp('@' + old + '(?!\\w)', 'g'), '@' + name);
            const box = card!.querySelector('[data-pg-text]') as HTMLTextAreaElement | null;
            if (box) box.value = n.data.template;
            pgSaveSoon();
            pgLater('keep:' + n.id, () => { pgRenderKeep(n.id); pgRenderDown(n.id); });
            return;
        }
        const setText = (t as HTMLElement).dataset.pgSetText;
        if (setText) {
            n.data[setText] = value.slice(0, 200);
            pgSaveSoon();
            pgLater('keep:' + n.id, () => { pgRenderKeep(n.id); pgRenderDown(n.id); pgRenderEdges(); });
            return;
        }
        if (!t.matches('[data-pg-text]')) return;
        if (n.type === 'construct') {
            n.data.template = (t as HTMLTextAreaElement).value;
            pgSaveSoon();
            pgLater('keep:' + n.id, () => { pgRenderKeep(n.id); pgRenderDown(n.id); });
            return;
        }
        n.data.text = (t as HTMLTextAreaElement).value;
        // what it feeds shows what it gets (an Array its items, a constructor its text)
        pgLater('down:' + n.id, () => { if (n.type === 'array') { pgRenderKeep(n.id); pgRenderEdges(); } pgRenderDown(n.id); });
        // its fields follow the text, without the text box losing its place
        if (n.type === 'prompt' || n.type === 'savedPrompt') {
            const names = pgFieldsOf(n.data.text).join('|');
            const box = card!.querySelector('.pg-vars') as HTMLElement | null;
            if ((box ? box.dataset.pgVars : '') !== names) {
                const html = pgVarsHtml(n);
                if (box) { if (html) box.outerHTML = html; else box.remove(); }
                else if (html) card!.querySelector('.pg-body')!.insertAdjacentHTML('beforeend', html);
                pgRenderEdges();
            }
        }
        pgSaveSoon();
    });
    root.addEventListener('focusout', (ev) => { if ((ev.target as HTMLElement).closest?.('.pg-node')) pg.typing = ''; });

    // dropping images on an Image node, or on the canvas (a new Image node each)
    canvas.addEventListener('dragover', (ev) => { if (ev.dataTransfer && [...ev.dataTransfer.types].includes('Files')) ev.preventDefault(); });
    canvas.addEventListener('drop', async (ev) => {
        const all = [...(ev.dataTransfer?.files || [])];
        const files = all.filter(f => f.type.startsWith('image/'));
        const card = (ev.target as HTMLElement).closest('.pg-node') as HTMLElement | null;
        const n = card && pgNode(card.dataset.id!);
        if (!files.length) return;
        ev.preventDefault();
        if (n && (n.type === 'image' || (n.type === 'annotate' && !pgInEdges(n.id, 'image').length)) && files.length === 1) { await pgSetImage(n, files[0]); return; }
        await pgImagesToNodes(files, pgToWorld(ev.clientX, ev.clientY));
    });

    // pointer: pan, choose, move nodes, draw a wire
    canvas.addEventListener('pointerdown', (ev) => {
        // the right button held and dragged: a box chooses what it takes in (with Shift or
        // Ctrl, added to what is chosen); a right click without a drag opens the menu as before
        if (ev.button === 2 && pg.wf) {
            const t = ev.target as HTMLElement;
            if (pgEditable(t) || t.closest('.pg-picker, .pg-menu, .pg-lb, .pg-keys')) return;
            pg.drag = { kind: 'box', right: true, live: false, startX: ev.clientX, startY: ev.clientY, base: ev.shiftKey || ev.ctrlKey || ev.metaKey ? new Set(pg.sel) : new Set<string>() };
            canvas.setPointerCapture(ev.pointerId);
            return;
        }
        if (ev.button !== 0 && ev.button !== 1) return;
        const t = ev.target as HTMLElement;
        if (!t.closest('.pg-picker')) pgClosePicker();
        if (!t.closest('.pg-ctx')) pgCloseCtx();
        const v = pg.wf!.view;
        const outPort = t.closest('[data-port-out]');
        if (outPort) {
            const card = t.closest('.pg-node') as HTMLElement;
            const outName = (outPort as HTMLElement).dataset.portOut || 'out';
            pg.drag = { kind: 'wire', from: card.dataset.id, fromOut: outName, kind2: pgOutKind(pgNode(card.dataset.id!)!, outName) };
            canvas.setPointerCapture(ev.pointerId);
            ev.preventDefault();
            return;
        }
        const inPort = t.closest('[data-port-in]') as HTMLElement | null;
        if (inPort) {
            // from an input: its wire comes off, to be put elsewhere; or, with none, a wire drawn back from it
            const card = t.closest('.pg-node') as HTMLElement;
            const port = inPort.dataset.portIn!;
            const into = pg.wf!.edges.filter(e => e.to === card.dataset.id && e.port === port);
            const kind = (inPort.closest('.pg-in') as HTMLElement).dataset.type as PgPort;
            if (into.length) {
                const e = into[into.length - 1];
                pgRecord();
                pg.wf!.edges = pg.wf!.edges.filter(x => x.id !== e.id);
                pgRenderNode(e.to);
                pgRenderDown(e.to);
                pg.drag = { kind: 'wire', from: e.from, fromOut: e.out || 'out', kind2: pgOutKind(pgNode(e.from)!, e.out || 'out'), detached: true };
            } else {
                pg.drag = { kind: 'wire', to: card.dataset.id, port, kind2: kind };
            }
            canvas.setPointerCapture(ev.pointerId);
            ev.preventDefault();
            return;
        }
        const wire = t.closest('[data-edge]') as HTMLElement | null;
        const wireDel = t.closest('[data-edge-del]') as HTMLElement | null;
        if (wireDel) { pgDeleteEdge(wireDel.getAttribute('data-edge-del')!); ev.preventDefault(); return; }
        if (wire) { pg.edgeSel = wire.getAttribute('data-edge')!; pg.sel.clear(); pgSelectPaint(); ev.preventDefault(); return; }
        // a group's head: its nodes move with it
        const gHead = t.closest('[data-group-head]') as HTMLElement | null;
        if (gHead && !t.closest('button, input')) {
            const g = pgGroups().find(x => x.id === gHead.dataset.groupHead);
            if (g) {
                pg.sel = new Set(g.nodes.filter(id => pgNode(id)));
                pg.edgeSel = '';
                pgSelectPaint();
                const origin = new Map([...pg.sel].map(sid => { const m = pgNode(sid)!; return [sid, { x: m.x, y: m.y }] as [string, { x: number; y: number }]; }));
                pg.drag = { kind: 'node', startX: ev.clientX, startY: ev.clientY, origin, moved: false };
                ev.preventDefault();
                return;
            }
        }
        if (t.closest('.pg-group-head')) return;
        const head = t.closest('.pg-head');
        const card = t.closest('.pg-node') as HTMLElement | null;
        if (card) {
            const id = card.dataset.id!;
            const add = ev.shiftKey || ev.ctrlKey || ev.metaKey;
            // a node is moved by any part of it that is not a field, a button, a text to
            // select or a scroll bar (by its head as before)
            const grab = head ? !t.closest('button') : !t.closest(PG_NO_GRAB) && !t.matches('.pg-order, .pg-grid, .pg-gal-grid');
            // an Image's picture is chosen by a click on it, not only its node (2026-10-29, Simon:
            // "Den vælger kun KASSEN"): Del then deletes the picture; the node is still moved by it
            const m = pgNode(id), onPic = !add && !!m && m.type === 'image' && !!m.data.src && !!t.closest('.pg-pic') && !t.closest('button') && !(pg.sel.has(id) && pg.sel.size > 1);
            if (onPic) { pg.sel = new Set([id]); pg.edgeSel = ''; pg.pic = id; pgSelectPaint(); }
            else if (add && grab) { if (pg.sel.has(id)) pg.sel.delete(id); else pg.sel.add(id); pg.edgeSel = ''; pgSelectPaint(); }
            else if (!pg.sel.has(id)) { pg.sel = new Set([id]); pg.edgeSel = ''; pgSelectPaint(); }
            else if (pg.pic && !t.closest('.pg-pic')) { pg.pic = ''; pgSelectPaint(false); }
            if (grab && pg.sel.has(id)) {
                const origin = new Map([...pg.sel].map(s => { const m = pgNode(s)!; return [s, { x: m.x, y: m.y }] as [string, { x: number; y: number }]; }));
                pg.drag = { kind: 'node', startX: ev.clientX, startY: ev.clientY, origin, moved: false };
                ev.preventDefault();
            }
            return;
        }
        if (t.closest('.pg-picker, .pg-menu, .pg-lb, .pg-keys')) return;
        // the empty part of a group's frame moves the group (with Shift or Ctrl: a box, as anywhere);
        // its nodes are chosen once it moves, and a click without a move chooses nothing
        const frame = t.closest('.pg-group') as HTMLElement | null;
        if (frame && !(ev.shiftKey || ev.ctrlKey || ev.metaKey)) {
            const g = pgGroups().find(x => x.id === frame.dataset.group);
            if (g) {
                const ids = g.nodes.filter(sid => pgNode(sid));
                const origin = new Map(ids.map(sid => { const m = pgNode(sid)!; return [sid, { x: m.x, y: m.y }] as [string, { x: number; y: number }]; }));
                pg.drag = { kind: 'node', startX: ev.clientX, startY: ev.clientY, origin, moved: false, frame: ids };
                ev.preventDefault();
                return;
            }
        }
        // the empty canvas: with Shift (or Ctrl), choose what a box takes in; else move the canvas
        if (ev.shiftKey || ev.ctrlKey || ev.metaKey) {
            pg.drag = { kind: 'box', startX: ev.clientX, startY: ev.clientY, base: new Set(pg.sel) };
            canvas.setPointerCapture(ev.pointerId);
            ev.preventDefault();
            return;
        }
        if (pg.sel.size || pg.edgeSel) { pg.sel.clear(); pg.edgeSel = ''; pgSelectPaint(); }
        pg.drag = { kind: 'pan', startX: ev.clientX, startY: ev.clientY, ox: v.x, oy: v.y };
        canvas.classList.add('is-panning');
        canvas.setPointerCapture(ev.pointerId);
    });
    const liveTarget = (ev: PointerEvent, d: Record<string, any>) => {
        // what the wire would go into: an input of the right kind, or a node that takes it
        const under = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
        const card = under?.closest('.pg-node') as HTMLElement | null;
        if (!card) return null;
        const n = pgNode(card.dataset.id!);
        if (!n) return null;
        if (d.to) {
            if (n.id === d.to) return null;
            // the output under the pointer, else the first that gives this kind
            const at = (under!.closest('[data-port-out], .pg-outrow') as HTMLElement | null);
            const name = at ? (at.dataset.portOut || at.dataset.out || '') : '';
            const out = name && pgOutDef(n, name) && pgFits(pgOutKind(n, name), d.kind2) ? name : pgOutFor(n, d.kind2);
            return out ? { node: n.id, port: '', out } : null;
        }
        if (n.id === d.from) return null;
        const row = under!.closest('.pg-in') as HTMLElement | null;
        if (row && pgFits(d.kind2, row.dataset.type as PgPort)) return { node: n.id, port: row.dataset.port! };
        const inp = pgInputFor(n, d.kind2);
        return inp ? { node: n.id, port: inp.name } : null;
    };
    canvas.addEventListener('pointermove', (ev) => {
        if (pg.wf) pg.pointer = pgToWorld(ev.clientX, ev.clientY);
        const d = pg.drag;
        if (!d) return;
        const v = pg.wf!.view;
        if (d.kind === 'pan') { v.x = d.ox + ev.clientX - d.startX; v.y = d.oy + ev.clientY - d.startY; pgApplyView(); return; }
        if (d.kind === 'node') {
            if (!d.moved) {
                if (Math.abs(ev.clientX - d.startX) + Math.abs(ev.clientY - d.startY) < 3) return;
                d.moved = true;
                // held from here on; not on the press, so a click or a double-click on a picture is still one
                try { canvas.setPointerCapture(ev.pointerId); } catch { /* the pointer is gone */ }
                pgRecord();
                if (d.frame) { pg.sel = new Set(d.frame); pg.edgeSel = ''; pgSelectPaint(); }
            }
            for (const [id, o] of d.origin as Map<string, { x: number; y: number }>) {
                const n = pgNode(id);
                const card = pgCard(id);
                if (!n || !card) continue;
                n.x = Math.round(o.x + (ev.clientX - d.startX) / v.k);
                n.y = Math.round(o.y + (ev.clientY - d.startY) / v.k);
                card.style.left = n.x + 'px';
                card.style.top = n.y + 'px';
            }
            pgEdgesSoon();
            return;
        }
        if (d.kind === 'box') {
            if (d.right && !d.live) {
                if (Math.abs(ev.clientX - d.startX) + Math.abs(ev.clientY - d.startY) < 5) return;
                // a box after all: a menu opened on the press (as on a Mac) closes
                d.live = true;
                pgCloseCtx();
                pgClosePicker();
                pg.edgeSel = '';
            }
            const r = (pg.el.root.querySelector('.pg-shell') as HTMLElement).getBoundingClientRect();
            const x0 = Math.min(d.startX, ev.clientX), x1 = Math.max(d.startX, ev.clientX), y0 = Math.min(d.startY, ev.clientY), y1 = Math.max(d.startY, ev.clientY);
            const box = pg.el.box;
            box.hidden = false;
            box.style.left = (x0 - r.left) + 'px'; box.style.top = (y0 - r.top) + 'px';
            box.style.width = (x1 - x0) + 'px'; box.style.height = (y1 - y0) + 'px';
            const sel = new Set<string>(d.base);
            pg.wf!.nodes.forEach(n => {
                const b = pgCard(n.id)?.getBoundingClientRect();
                if (b && b.right > x0 && b.left < x1 && b.bottom > y0 && b.top < y1) sel.add(n.id);
            });
            pg.sel = sel;
            pgSelectPaint(false);
            return;
        }
        if (d.kind === 'wire') {
            const p = pgToWorld(ev.clientX, ev.clientY);
            const a = d.from ? pgPortPoint(d.from, d.fromOut || 'out', true) : p;
            const b = d.to ? pgPortPoint(d.to, d.port) : p;
            if (!a || !b) return;
            pg.el.wires.querySelector('.pg-wire.is-live')?.remove();
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('class', 'pg-wire is-live is-' + d.kind2);
            path.setAttribute('d', pgCurve(a, b));
            pg.el.wires.appendChild(path);
            // what it would go into lights up
            document.querySelectorAll('#pgWorld .is-target').forEach(x => x.classList.remove('is-target'));
            const target = liveTarget(ev, d);
            if (target) {
                const card = pgCard(target.node);
                if (target.port) card?.querySelector('.pg-in[data-port="' + target.port + '"]')?.classList.add('is-target');
                else card?.querySelector('[data-port-out="' + CSS.escape((target as any).out || 'out') + '"]')?.classList.add('is-target');
                card?.classList.add('is-target');
            }
        }
    });
    const end = (ev: PointerEvent) => {
        const d = pg.drag;
        pg.drag = null;
        canvas.classList.remove('is-panning');
        if (!d) return;
        if (d.kind === 'wire') {
            pg.el.wires.querySelector('.pg-wire.is-live')?.remove();
            document.querySelectorAll('#pgWorld .is-target').forEach(x => x.classList.remove('is-target'));
            const target = liveTarget(ev, d);
            if (target) {
                const problem = d.to ? pgConnect(target.node, d.to, d.port, true, (target as any).out || 'out') : pgConnect(d.from, target.node, target.port, !d.detached, d.fromOut || 'out');
                if (problem) showToast(problem, { type: 'warn', title: 'Not connected' });
                return;
            }
            const under = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
            // let go of on a node that does not take it: it says why
            const onCard = under?.closest('.pg-node') as HTMLElement | null;
            const other = onCard && pgNode(onCard.dataset.id!);
            if (other && other.id !== d.from && other.id !== d.to) {
                const row = under!.closest('.pg-in') as HTMLElement | null;
                const word = pgKindWord(d.kind2);
                showToast(d.to ? PG_TYPES[other.type].title + ' gives no ' + d.kind2
                    : row ? word + ' cannot go into ' + (row.querySelector('.pg-in-label')?.firstChild?.textContent || '').toLowerCase()
                    : PG_TYPES[other.type].title + ' takes no ' + d.kind2, { type: 'warn', title: 'Not connected' });
                if (d.detached) pgSaveSoon();
                return;
            }
            const onCanvas = under && under.closest('#pgCanvas') && !under.closest('.pg-node, .pg-bar, .pg-zoom, .pg-wf');
            // let go of in empty space: a node to take it (a wire taken off is simply gone)
            if (onCanvas && !d.detached) pgOpenPicker(ev.clientX, ev.clientY, d.from ? { from: d.from, out: d.fromOut || 'out', kind: d.kind2 } : { to: d.to, port: d.port, kind: d.kind2 });
            else if (d.detached) { pgForgetKinds(); pgRenderDown(d.from); pgSaveSoon(); }
            return;
        }
        if (d.kind === 'box') {
            if (d.right && !d.live) return;
            if (d.right) pg.noCtx = Date.now();
            pg.el.box.hidden = true;
            pgSelectPaint();
            return;
        }
        if (d.kind === 'node' && !d.moved && d.frame) { if (pg.sel.size || pg.edgeSel) { pg.sel.clear(); pg.edgeSel = ''; pgSelectPaint(); } return; }
        if (d.kind === 'node' && d.moved) {
            // the order of what goes into a Generate follows where the nodes stand
            const fed = new Set<string>();
            for (const id of (d.origin as Map<string, any>).keys()) pg.wf!.edges.filter(e => e.from === id).forEach(e => fed.add(e.to));
            fed.forEach(id => { const n = pgNode(id); if (n && (n.type === 'generate' || n.type === 'enhance' || n.type === 'llm' || n.type === 'construct')) pgRenderNode(id); });
            pgRenderEdges();
            pgSaveSoon();
            return;
        }
        if (d.kind === 'pan') pgSaveSoon();
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('contextmenu', (ev) => {
        const t = ev.target as HTMLElement;
        if (pgEditable(t) || t.closest('.pg-picker, .pg-menu, .pg-lb, .pg-keys')) return;
        ev.preventDefault();
        // the right button made a box: its release is not a click for the menu
        if (pg.noCtx && Date.now() - pg.noCtx < 800) { pg.noCtx = 0; return; }
        if (pg.drag && pg.drag.right && pg.drag.live) return;
        const card = t.closest('.pg-node') as HTMLElement | null;
        if (card) pgOpenNodeMenu(ev.clientX, ev.clientY, card.dataset.id!);
        else pgOpenPicker(ev.clientX, ev.clientY);
    });

    // a field gone into below the edge makes the browser scroll the canvas: that is
    // made a move of the view instead, so the cards, wires and dots stay together
    canvas.addEventListener('scroll', () => {
        if (!pg.wf || (!canvas.scrollTop && !canvas.scrollLeft)) return;
        const v = pg.wf.view;
        v.x -= canvas.scrollLeft;
        v.y -= canvas.scrollTop;
        canvas.scrollTop = 0;
        canvas.scrollLeft = 0;
        pgApplyView();
        pgSaveSoon();
    });

    // the wheel zooms, about the pointer; a scrolling box keeps its wheel
    canvas.addEventListener('wheel', (ev) => {
        const t = ev.target as HTMLElement;
        if (t.closest('.pg-picker, .pg-menu, .pg-lb, .pg-keys')) return;
        const box = t.closest('textarea, .pg-grid, select, .pg-textout, .pg-order, .pg-gal-grid, .pg-texts') as HTMLElement | null;
        if (box && box.scrollHeight > box.clientHeight) return;
        ev.preventDefault();
        const c = canvas.getBoundingClientRect();
        pgZoomAt(pg.wf!.view.k * Math.exp(-ev.deltaY * (ev.ctrlKey ? 0.01 : 0.0015)), ev.clientX - c.left, ev.clientY - c.top);
    }, { passive: false });

    // the keyboard
    document.addEventListener('keydown', (ev) => {
        if (!root.classList.contains('active') || !pg.wf || pgAn.open) return;
        // the Send dialog keeps its keys: Esc closes it, Enter in To (or Ctrl+Enter) sends
        const sendEl = document.getElementById('pgSend');
        if (sendEl && !sendEl.hidden) {
            if (ev.key === 'Escape') { ev.preventDefault(); pgCloseSend(); }
            else if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); pgSend(); }
            return;
        }
        // the Describe dialog keeps its keys: Esc closes it, Ctrl+Enter builds
        const dsc = document.getElementById('pgDescribe');
        if (dsc && !dsc.hidden) {
            if (ev.key === 'Escape') { ev.preventDefault(); pgCloseDescribe(); }
            else if ((ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') { ev.preventDefault(); pgDescribe(); }
            return;
        }
        // the assistant keeps its keys: Enter sends (Shift+Enter: a new line), Esc closes it
        const ask = document.getElementById('pgAsk');
        if (ask && !ask.hidden && ask.contains(ev.target as Node)) {
            if (ev.key === 'Escape') { ev.preventDefault(); pgAskOpen(false); (document.getElementById('pgAskBtn') as HTMLElement | null)?.focus(); }
            else if (ev.key === 'Enter' && !ev.shiftKey && (ev.target as HTMLElement).matches('textarea')) { ev.preventDefault(); pgAskSend(); }
            return;
        }
        // the library: Esc closes it
        const lib = document.getElementById('pgLib');
        if (lib && !lib.hidden && lib.contains(ev.target as Node)) {
            if (ev.key === 'Escape') { ev.preventDefault(); pgLibOpen(false); (document.getElementById('pgLibBtn') as HTMLElement | null)?.focus(); }
            return;
        }
        const mod = ev.ctrlKey || ev.metaKey;
        const key = ev.key.toLowerCase();
        // the search and the menus have their own keys
        if (!pg.el.picker.hidden) {
            const items = [...pg.el.picker.querySelectorAll<HTMLElement>('[data-pg-pick]')];
            const at = items.findIndex(x => x.getAttribute('aria-selected') === 'true');
            if (ev.key === 'Escape') { pgClosePicker(); ev.preventDefault(); return; }
            if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
                ev.preventDefault();
                const next = items[(at + (ev.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
                items.forEach(x => x.setAttribute('aria-selected', String(x === next)));
                next?.scrollIntoView({ block: 'nearest' });
                return;
            }
            if (ev.key === 'Enter') { ev.preventDefault(); const it = items[Math.max(0, at)]; if (it) pgPick(it.dataset.pgPick as PgType); return; }
            return;
        }
        if (!pg.el.lb.hidden) {
            if (ev.key === 'Escape') { pgCloseViewer(); ev.preventDefault(); }
            else if ((ev.key === 'Delete' || ev.key === 'Backspace') && !pgEditable(ev.target) && pgViewerCanDelete()) { ev.preventDefault(); pgViewerDelete(); }
            else if ((ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') && pg.lb && pg.lb.items.length > 1 && !pgEditable(ev.target)) {
                pg.lb.index = (pg.lb.index + (ev.key === 'ArrowRight' ? 1 : -1) + pg.lb.items.length) % pg.lb.items.length;
                pgPaintViewer();
                ev.preventDefault();
            }
            return;
        }
        if (ev.key === 'Escape') {
            const runMenu = document.getElementById('pgRunMenu');
            if (runMenu && !runMenu.hidden) { runMenu.hidden = true; document.getElementById('pgRunMore')?.setAttribute('aria-expanded', 'false'); (document.getElementById('pgRunMore') as HTMLElement)?.focus(); return; }
            if (!pg.el.ctx.hidden) { pgCloseCtx(); return; }
            if (!pg.el.keys.hidden) { pg.el.keys.hidden = true; return; }
            if (pgEditable(ev.target)) { (ev.target as HTMLElement).blur(); return; }
            if (pg.sel.size || pg.edgeSel) { pg.sel.clear(); pg.edgeSel = ''; pgSelectPaint(); }
            return;
        }
        if (mod && ev.key === 'Enter') { ev.preventDefault(); pgRun(); return; }
        if (ev.altKey && ev.key === 'Enter') { ev.preventDefault(); pgRun([...pg.sel]); return; }
        if (pgEditable(ev.target)) return;
        if (mod && key === 'z') { ev.preventDefault(); pgUndoRedo(!ev.shiftKey); return; }
        if (mod && key === 'y') { ev.preventDefault(); pgUndoRedo(false); return; }
        if (mod && key === 'a') { ev.preventDefault(); pg.sel = new Set(pg.wf.nodes.map(n => n.id)); pg.edgeSel = ''; pgSelectPaint(); return; }
        if (mod && (key === 'c' || key === 'x')) {
            const clip = pgCopy([...pg.sel]);
            if (!clip) return;
            ev.preventDefault();
            pg.clip = clip;
            pg.pastes = 0;
            if (key === 'x') pgDeleteNodes([...pg.sel]);
            return;
        }
        if (mod && key === 'd') { ev.preventDefault(); pgDuplicate(); return; }
        if (mod && key === 'g') { ev.preventDefault(); if (ev.shiftKey) pgUngroup(); else pgGroupSelected(); return; }
        if (mod) return;
        if (ev.key === 'Delete' || ev.key === 'Backspace') {
            if (!pg.sel.size && !pg.edgeSel) return;
            ev.preventDefault();
            if (pg.pic && pg.sel.size === 1 && pg.sel.has(pg.pic)) { pgClearImage(pgNode(pg.pic)!); return; }
            if (pg.edgeSel) pgDeleteEdge(pg.edgeSel); else pgDeleteNodes([...pg.sel]);
            return;
        }
        if (ev.key === '/' ) { ev.preventDefault(); const c = canvas.getBoundingClientRect(); pgOpenPicker(c.left + c.width / 2 - 150, c.top + c.height / 2 - 160); return; }
        if (ev.key === '?') { pg.el.keys.hidden = !pg.el.keys.hidden; return; }
        const quick: Record<string, PgType> = { I: 'image', P: 'prompt', G: 'generate', O: 'output', N: 'note' };
        if (ev.shiftKey && !ev.altKey && quick[ev.key]) {
            ev.preventDefault();
            const n = pgAddNode(quick[ev.key], { x: pg.pointer.x - PG_TYPES[quick[ev.key]].width / 2, y: pg.pointer.y - 20 });
            if (pg.sel.size === 1) pgRenderNode(n.id);
            return;
        }
        if (key === 'f') { ev.preventDefault(); pgFit(true, [...pg.sel]); return; }
        if (ev.key.startsWith('Arrow') && pg.sel.size) {
            ev.preventDefault();
            const step = ev.shiftKey ? 22 : 2;
            const dx = ev.key === 'ArrowLeft' ? -step : ev.key === 'ArrowRight' ? step : 0, dy = ev.key === 'ArrowUp' ? -step : ev.key === 'ArrowDown' ? step : 0;
            if (pg.typing !== 'nudge') { pgRecord(); pg.typing = 'nudge'; }
            pg.sel.forEach(id => { const n = pgNode(id); const c = pgCard(id); if (n && c) { n.x += dx; n.y += dy; c.style.left = n.x + 'px'; c.style.top = n.y + 'px'; } });
            pgRenderEdges();
            pgSaveSoon();
        }
    });
    document.addEventListener('keyup', (ev) => { if (ev.key.startsWith('Arrow') && pg.typing === 'nudge') pg.typing = ''; });
    // pasting: an image from anywhere becomes an Image node; else the nodes copied
    document.addEventListener('paste', (ev) => {
        if (!root.classList.contains('active') || !pg.wf || pgEditable(ev.target)) return;
        const files = [...(ev.clipboardData?.files || [])].filter(f => f.type.startsWith('image/'));
        if (files.length) { ev.preventDefault(); pgImagesToNodes(files, pg.pointer); return; }
        if (pg.clip) { ev.preventDefault(); pgPaste(pg.clip, pg.pastes ? undefined : pg.pointer); }
    });
    // a click outside the menus closes them
    document.addEventListener('pointerdown', (ev) => {
        const t = ev.target as HTMLElement;
        if (!t.closest('.pg-ctx')) pgCloseCtx();
        if (!t.closest('.pg-picker') && !t.closest('#pgCanvas') && !t.closest('#pgMore')) pgClosePicker();
        if (!t.closest('.pg-keys') && !t.closest('#pgKeysBtn')) { if (pg.el.keys) pg.el.keys.hidden = true; }
    }, true);

    // The wires are measured from the cards, which have no size while another tab is
    // open; when the canvas shows (or changes size) they are drawn again, and a fit
    // asked for meanwhile is made.
    setInterval(() => {
        if (!pg.running) return;
        pg.el.world.querySelectorAll<HTMLElement>('.pg-time[data-pg-since]').forEach(t => {
            const s = Math.round((Date.now() - Number(t.dataset.pgSince)) / 1000);
            t.textContent = s >= 2 ? s + ' s' : '';
        });
    }, 1000);
    if (window.ResizeObserver) new ResizeObserver(() => {
        if (!pg.wf || !canvas.clientWidth) return;
        if (pg.needFit) pgFit();
        pgRenderEdges();
    }).observe(canvas);
    // a picture that opens makes its card taller: the wires under it and the
    // group's frame follow
    let grown = 0;
    const regrow = (ev: Event) => {
        if (!(ev.target as HTMLElement).closest?.('.pg-node') || grown) return;
        grown = requestAnimationFrame(() => { grown = 0; if (pg.wf && canvas.clientWidth) pgRenderEdges(); });
    };
    pg.el.world.addEventListener('load', regrow, true);
    pg.el.world.addEventListener('loadedmetadata', regrow, true);
}
(window as any).setupPlayground = setupPlayground;

// the node a picture really comes from, back through Routers, Switches and Conditions
function pgOrigin(id: string): PgNode | null {
    let n = pgNode(id);
    const seen = new Set<string>();
    while (n && PG_TYPES[n.type].flows && !seen.has(n.id)) {
        seen.add(n.id);
        const e = pg.wf!.edges.find(x => x.to === n!.id);
        n = e ? pgNode(e.from) : null;
    }
    return n;
}
// a call made once things have stopped changing for a moment (typing)
const __pgLater = new Map<string, any>();
function pgLater(key: string, fn: () => void, ms = 250) {
    clearTimeout(__pgLater.get(key));
    __pgLater.set(key, setTimeout(() => { __pgLater.delete(key); fn(); }, ms));
}

function pgSelectPaint(edges = true) {
    // a chosen picture stays chosen only while its node is all that is chosen
    if (pg.pic && !(pg.sel.size === 1 && pg.sel.has(pg.pic) && pgNode(pg.pic)?.data.src)) pg.pic = '';
    pg.el.world.querySelectorAll('.pg-node').forEach(el => {
        el.classList.toggle('is-selected', pg.sel.has((el as HTMLElement).dataset.id!));
        el.classList.toggle('is-pic-chosen', (el as HTMLElement).dataset.id === pg.pic);
    });
    if (edges) pgRenderEdges();
}
async function pgSetImage(n: PgNode, f: File, record = true) {
    if (typeof (window as any).ensureNotHeic === 'function') f = await (window as any).ensureNotHeic(f);
    if (record) pgRecord();
    n.data.src = await pgBlobToDataUrl(f);
    n.data.name = f.name;
    n.data.key = pgId('img');
    pgRenderNode(n.id);
    pgRenderDown(n.id);
    pgSaveSoon();
}
// a text saved as it is (C2PA is for pictures)
function pgSaveAs(src: string, ext: string, suffix = '') {
    const name = 'floorflow-playground-' + (pg.wf!.name || 'workflow').toLowerCase().replace(/[^a-z0-9æøå]+/g, '-').replace(/^-|-$/g, '') + '-' + new Date().toISOString().slice(0, 10) + (suffix ? '-' + suffix : '') + '.' + ext;
    saveDataUrlAsFile(src, name);
}
// A download carries what made it (C2PA), written on the server
async function pgDownload(src: PgNode, value: string, meta?: Record<string, string>, suffix = '') {
    const r = await pgPrepareDownload(src, value, meta, suffix);
    if (r) saveDataUrlAsFile(r.url, r.name);
}
// the file as it is downloaded, its AI metadata written; null (and said) when it could not be
async function pgPrepareDownload(src: PgNode, value: string, meta?: Record<string, string>, suffix = ''): Promise<{ url: string; name: string } | null> {
    const ext = (/^data:image\/(\w+)/.exec(value) || [])[1] === 'jpeg' ? 'jpg' : ((/^data:image\/(\w+)/.exec(value) || [])[1] || 'png');
    const name = 'floorflow-playground-' + pgSlug(pg.wf!.name) + '-' + new Date().toISOString().slice(0, 10) + (suffix ? '-' + suffix : '') + '.' + ext;
    try {
        return { url: await exportAssetViaServer(value, name, { generation_id: src.id, model_name: (meta && meta.model) || 'unknown', engine: (meta && meta.engine) || '' }), name };
    } catch (err) {
        showToast('The file was not downloaded: ' + ((err as Error).message || err), { type: 'warn', title: 'AI metadata could not be written' });
        return null;
    }
}
const pgSlug = (s: string) => (s || 'workflow').toLowerCase().replace(/[^a-z0-9æøå]+/g, '-').replace(/^-|-$/g, '') || 'workflow';
// ── The Annotate editor (Node Banana's AnnotationModal, in FloorFlow's look) ──
// Select, Box, Ellipse, Arrow, Draw and Text; eight colours, three line widths and
// fill; undo, redo and clear; zoom. A shape smaller than 5 px (an arrow shorter than
// 10) is let go. Done keeps the shapes on the node; Esc or Cancel leaves them as they were.
const PG_AN_TOOLS: [string, string, string][] = [
    ['select', 'Select and move', '<path d="M3.5 2.5 12 8l-4 1 2.5 4.5-1.6.9L6.4 9.9 3.5 12.5z"/>'],
    ['rect', 'Box', '<rect x="2.5" y="3.5" width="11" height="9" rx="1"/>'],
    ['ellipse', 'Ellipse', '<ellipse cx="8" cy="8" rx="5.6" ry="4.4"/>'],
    ['arrow', 'Arrow', '<path d="M3 13 13 3M7.5 3H13v5.5"/>'],
    ['draw', 'Draw', '<path d="M2.5 12.5c2-3 3.4-.2 5-2.4S9.6 4 13.5 3.5"/>'],
    ['text', 'Text', '<path d="M3.5 4V3h9v1M8 3v10M6 13h4"/>'],
];
const pgAn = {
    open: false, node: '', bmp: null as ImageBitmap | null, shapes: [] as PgShape[], undo: [] as PgShape[][], redo: [] as PgShape[][],
    tool: 'rect', colour: PG_AN_COLOURS[0], width: 1 as 0 | 1 | 2, fill: false, zoom: 1, fitK: 1, sel: -1, drag: null as null | Record<string, any>,
};
// line widths and text size follow the picture's size, so they read the same on a 1K and a 4K picture
const pgAnUnit = () => pgAn.bmp ? Math.max(1, Math.max(pgAn.bmp.width, pgAn.bmp.height) / 1000) : 1;
const pgAnStroke = () => Math.round([2, 4, 8][pgAn.width] * pgAnUnit() * 10) / 10;
async function pgOpenAnnotate(n: PgNode) {
    const own = !pgInEdges(n.id, 'image').length;
    let src: PgValue | null = null;
    try { src = own ? pgAnnotateOwn(n) : (await pgImagesIn(n, 'image'))[0] || null; } catch { /* none */ }
    if (!src) { showToast(own ? 'Add a picture first: drop one on the node or click it.' : 'The picture wired in is not made yet: Run, then annotate.', { title: 'Annotate' }); return; }
    pgAn.bmp = await pgBitmap(src.value);
    pgAn.node = n.id;
    pgAn.shapes = pgShapesOf(n).map(s => ({ ...s, pts: s.pts ? [...s.pts] : undefined }));
    pgAn.undo = []; pgAn.redo = []; pgAn.sel = -1; pgAn.zoom = 1;
    pgAn.open = true;
    const el = document.getElementById('pgAn')!;
    el.hidden = false;
    pgAnPaintBar();
    requestAnimationFrame(() => { pgAnFit(); pgAnDraw(); (el.querySelector('[data-an-tool="' + pgAn.tool + '"]') as HTMLElement)?.focus(); });
}
function pgCloseAnnotate(keep: boolean) {
    if (!pgAn.open) return;
    pgAnTextDone(true);
    const n = pgNode(pgAn.node);
    if (keep && n) {
        pgRecord();
        n.data.shapes = pgAn.shapes.map(s => ({ ...s }));
        pgRenderNode(n.id);
        pgRenderDown(n.id);
        pgSaveSoon();
    }
    pgAn.open = false;
    pgAn.bmp = null;
    document.getElementById('pgAn')!.hidden = true;
    pgCard(pgAn.node)?.querySelector<HTMLElement>('[data-pg-act="annotate"]')?.focus();
}
function pgAnPaintBar() {
    const el = document.getElementById('pgAn')!;
    el.querySelectorAll<HTMLElement>('[data-an-tool]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.anTool === pgAn.tool)));
    el.querySelectorAll<HTMLElement>('[data-an-colour]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.anColour === pgAn.colour)));
    el.querySelectorAll<HTMLElement>('[data-an-width]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.anWidth) === pgAn.width)));
    el.querySelector('[data-an="fill"]')!.setAttribute('aria-pressed', String(pgAn.fill));
    (el.querySelector('[data-an="undo"]') as HTMLButtonElement).disabled = !pgAn.undo.length;
    (el.querySelector('[data-an="redo"]') as HTMLButtonElement).disabled = !pgAn.redo.length;
    (el.querySelector('[data-an="clear"]') as HTMLButtonElement).disabled = !pgAn.shapes.length;
    (el.querySelector('[data-an="delete"]') as HTMLButtonElement).hidden = pgAn.sel < 0;
    el.querySelector('.pg-an-zoom')!.textContent = Math.round(pgAn.zoom * 100) + '%';
    el.querySelector('.pg-an-count')!.textContent = pgAn.shapes.length ? pgAn.shapes.length + ' mark' + (pgAn.shapes.length > 1 ? 's' : '') : '';
}
function pgAnFit() {
    const stage = document.querySelector('#pgAn .pg-an-stage') as HTMLElement;
    if (!pgAn.bmp || !stage) return;
    pgAn.fitK = Math.min((stage.clientWidth - 48) / pgAn.bmp.width, (stage.clientHeight - 48) / pgAn.bmp.height, 1);
}
function pgAnDraw() {
    const c = document.querySelector('#pgAn canvas') as HTMLCanvasElement | null;
    if (!c || !pgAn.bmp) return;
    const k = pgAn.fitK * pgAn.zoom, dpr = window.devicePixelRatio || 1;
    c.style.width = Math.round(pgAn.bmp.width * k) + 'px';
    c.style.height = Math.round(pgAn.bmp.height * k) + 'px';
    c.width = Math.round(pgAn.bmp.width * k * dpr);
    c.height = Math.round(pgAn.bmp.height * k * dpr);
    const ctx = c.getContext('2d')!;
    ctx.setTransform(k * dpr, 0, 0, k * dpr, 0, 0);
    ctx.drawImage(pgAn.bmp, 0, 0);
    pgAn.shapes.forEach(s => pgDrawShape(ctx, s));
    if (pgAn.sel >= 0 && pgAn.shapes[pgAn.sel]) {
        const b = pgShapeBox(pgAn.shapes[pgAn.sel]), pad = 6 / k;
        ctx.save(); ctx.setLineDash([6 / k, 4 / k]); ctx.lineWidth = 1.5 / k; ctx.strokeStyle = '#2f6fe4';
        ctx.strokeRect(b.x - pad, b.y - pad, b.w + 2 * pad, b.h + 2 * pad); ctx.restore();
    }
}
function pgAnPoint(ev: PointerEvent): { x: number; y: number } {
    const c = document.querySelector('#pgAn canvas') as HTMLCanvasElement;
    const r = c.getBoundingClientRect(), k = pgAn.fitK * pgAn.zoom;
    return { x: (ev.clientX - r.left) / k, y: (ev.clientY - r.top) / k };
}
function pgAnRecord() { pgAn.undo.push(pgAn.shapes.map(s => ({ ...s, pts: s.pts ? [...s.pts] : undefined }))); if (pgAn.undo.length > 60) pgAn.undo.shift(); pgAn.redo = []; }
function pgAnHit(p: { x: number; y: number }): number {
    const slack = 8 / (pgAn.fitK * pgAn.zoom);
    for (let i = pgAn.shapes.length - 1; i >= 0; i--) {
        const b = pgShapeBox(pgAn.shapes[i]);
        if (p.x >= b.x - slack && p.x <= b.x + b.w + slack && p.y >= b.y - slack && p.y <= b.y + b.h + slack) return i;
    }
    return -1;
}
// a text being written: a field over the picture, put on it with Enter
function pgAnTextAt(p: { x: number; y: number }, edit = -1) {
    pgAnTextDone(true);
    const stage = document.querySelector('#pgAn .pg-an-stage') as HTMLElement;
    const c = document.querySelector('#pgAn canvas') as HTMLCanvasElement;
    const k = pgAn.fitK * pgAn.zoom, cr = c.getBoundingClientRect(), sr = stage.getBoundingClientRect();
    const s = edit >= 0 ? pgAn.shapes[edit] : null;
    const size = s ? s.size || 24 : Math.round(24 * pgAnUnit());
    const box = document.createElement('textarea');
    box.className = 'pg-an-text';
    box.rows = 1;
    box.value = s ? s.text || '' : '';
    box.setAttribute('aria-label', 'Text to put on the picture');
    box.style.left = (cr.left - sr.left + (s ? s.x : p.x) * k) + 'px';
    box.style.top = (cr.top - sr.top + (s ? s.y : p.y) * k) + 'px';
    box.style.fontSize = Math.max(11, size * k) + 'px';
    box.style.color = s ? s.color : pgAn.colour;
    (box as any).__at = { x: s ? s.x : p.x, y: s ? s.y : p.y, size, edit };
    stage.appendChild(box);
    // at once, so no key typed straight after the click is lost
    box.focus();
}
function pgAnTextDone(keep: boolean) {
    const box = document.querySelector('#pgAn .pg-an-text') as HTMLTextAreaElement | null;
    if (!box) return;
    const at = (box as any).__at;
    box.remove();
    const text = box.value.trim();
    (document.querySelector('#pgAn canvas') as HTMLElement | null)?.focus();
    if (!keep) return;
    if (at.edit >= 0) {
        pgAnRecord();
        if (text) pgAn.shapes[at.edit] = { ...pgAn.shapes[at.edit], text };
        else { pgAn.shapes.splice(at.edit, 1); pgAn.sel = -1; }
    } else if (text) {
        pgAnRecord();
        pgAn.shapes.push({ t: 'text', x: at.x, y: at.y, text, size: at.size, color: pgAn.colour, stroke: 1 });
    }
    pgAnDraw();
    pgAnPaintBar();
}
function pgSetupAnnotate(shell: HTMLElement) {
    shell.insertAdjacentHTML('beforeend', '<div class="pg-an" id="pgAn" role="dialog" aria-modal="true" aria-label="Annotate the picture" hidden>'
        + '<div class="pg-an-bar"><div class="pg-an-group" role="group" aria-label="Tool">'
        + PG_AN_TOOLS.map(([id, label, path]) => '<button type="button" class="pg-an-btn" data-an-tool="' + id + '" aria-label="' + label + '" title="' + label + '"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg></button>').join('')
        + '</div><span class="pg-an-sep"></span><div class="pg-an-group" role="group" aria-label="Colour">'
        + PG_AN_COLOURS.map(c => '<button type="button" class="pg-an-swatch" data-an-colour="' + c + '" style="--sw:' + c + '" aria-label="Colour ' + c + '" title="' + c + '"></button>').join('')
        + '</div><span class="pg-an-sep"></span><div class="pg-an-group" role="group" aria-label="Line">'
        + [0, 1, 2].map(i => '<button type="button" class="pg-an-btn" data-an-width="' + i + '" aria-label="' + ['Thin', 'Medium', 'Thick'][i] + ' line" title="' + ['Thin', 'Medium', 'Thick'][i] + ' line"><span class="pg-an-line" style="height:' + [2, 3.5, 6][i] + 'px"></span></button>').join('')
        + '<button type="button" class="pg-an-btn" data-an="fill" aria-label="Fill shapes" title="Fill boxes and ellipses"><svg viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="3" width="10" height="10" rx="1.5" fill="currentColor"/></svg></button>'
        + '</div><span class="pg-an-sep"></span><div class="pg-an-group">'
        + '<button type="button" class="pg-an-btn" data-an="undo" aria-label="Undo" title="Undo (Ctrl+Z)">' + pgIcon('undo') + '</button>'
        + '<button type="button" class="pg-an-btn" data-an="redo" aria-label="Redo" title="Redo (Ctrl+Shift+Z)">' + pgIcon('redo') + '</button>'
        + '<button type="button" class="pg-an-btn is-wide" data-an="delete" title="Delete the chosen mark (Delete)" hidden>Delete</button>'
        + '<button type="button" class="pg-an-btn is-wide" data-an="clear" title="Remove every mark">Clear</button>'
        + '</div><span class="pg-an-gap"></span><span class="pg-an-count"></span>'
        + '<div class="pg-an-group"><button type="button" class="pg-an-btn" data-an="zoom-out" aria-label="Zoom out">−</button><span class="pg-an-zoom">100%</span><button type="button" class="pg-an-btn" data-an="zoom-in" aria-label="Zoom in">+</button></div>'
        + '<button type="button" class="pg-chip" data-an="cancel">Cancel</button><button type="button" class="pg-chip is-primary" data-an="done">Done</button></div>'
        + '<div class="pg-an-stage"><canvas tabindex="0" aria-label="The picture: draw on it"></canvas></div></div>');
    const el = document.getElementById('pgAn')!;
    const canvas = el.querySelector('canvas') as HTMLCanvasElement;
    el.addEventListener('click', (ev) => {
        const t = ev.target as HTMLElement;
        const tool = (t.closest('[data-an-tool]') as HTMLElement | null)?.dataset.anTool;
        const colour = (t.closest('[data-an-colour]') as HTMLElement | null)?.dataset.anColour;
        const width = (t.closest('[data-an-width]') as HTMLElement | null)?.dataset.anWidth;
        const act = (t.closest('[data-an]') as HTMLElement | null)?.dataset.an;
        if (tool) { pgAnTextDone(true); pgAn.tool = tool; if (tool !== 'select') pgAn.sel = -1; }
        else if (colour || width) {
            if (colour) pgAn.colour = colour; else pgAn.width = Number(width) as 0 | 1 | 2;
            // the chosen mark takes it too
            const s = pgAn.sel >= 0 ? pgAn.shapes[pgAn.sel] : null;
            if (s) { pgAnRecord(); if (colour) s.color = colour; else if (s.t !== 'text') s.stroke = pgAnStroke(); }
        }
        else if (act === 'fill') { pgAn.fill = !pgAn.fill; const s = pgAn.sel >= 0 ? pgAn.shapes[pgAn.sel] : null; if (s && (s.t === 'rect' || s.t === 'ellipse')) { pgAnRecord(); s.fill = pgAn.fill; } }
        else if (act === 'undo' || act === 'redo') {
            const from = act === 'undo' ? pgAn.undo : pgAn.redo, to = act === 'undo' ? pgAn.redo : pgAn.undo;
            if (from.length) { to.push(pgAn.shapes); pgAn.shapes = from.pop()!; pgAn.sel = -1; }
        }
        else if (act === 'delete' && pgAn.sel >= 0) { pgAnRecord(); pgAn.shapes.splice(pgAn.sel, 1); pgAn.sel = -1; }
        else if (act === 'clear' && pgAn.shapes.length) { pgAnRecord(); pgAn.shapes = []; pgAn.sel = -1; }
        else if (act === 'zoom-in' || act === 'zoom-out') pgAn.zoom = Math.min(5, Math.max(0.1, Math.round((pgAn.zoom + (act === 'zoom-in' ? 0.1 : -0.1)) * 10) / 10));
        else if (act === 'cancel') { pgCloseAnnotate(false); return; }
        else if (act === 'done') { pgCloseAnnotate(true); return; }
        else return;
        pgAnDraw();
        pgAnPaintBar();
    });
    el.querySelector('.pg-an-stage')!.addEventListener('wheel', (ev) => {
        const e = ev as WheelEvent;
        e.preventDefault();
        pgAn.zoom = Math.min(5, Math.max(0.1, pgAn.zoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
        pgAnDraw();
        pgAnPaintBar();
    }, { passive: false });
    canvas.addEventListener('pointerdown', (ev) => {
        if (ev.button !== 0) return;
        if (document.querySelector('#pgAn .pg-an-text')) { pgAnTextDone(true); return; }
        const p = pgAnPoint(ev);
        const style = { color: pgAn.colour, stroke: pgAnStroke() };
        if (pgAn.tool === 'select') {
            pgAn.sel = pgAnHit(p);
            if (pgAn.sel >= 0) pgAn.drag = { kind: 'move', from: p, recorded: false };
        } else if (pgAn.tool === 'text') {
            ev.preventDefault();
            pgAnTextAt(p);
            return;
        } else {
            const s: PgShape = pgAn.tool === 'arrow' ? { t: 'arrow', x: p.x, y: p.y, x2: p.x, y2: p.y, ...style }
                : pgAn.tool === 'draw' ? { t: 'draw', x: p.x, y: p.y, pts: [p.x, p.y], ...style }
                : { t: pgAn.tool as 'rect' | 'ellipse', x: p.x, y: p.y, w: 0, h: 0, fill: pgAn.fill, ...style };
            pgAnRecord();
            pgAn.shapes.push(s);
            pgAn.drag = { kind: 'make', index: pgAn.shapes.length - 1 };
        }
        canvas.setPointerCapture(ev.pointerId);
        pgAnDraw();
        pgAnPaintBar();
    });
    canvas.addEventListener('pointermove', (ev) => {
        const d = pgAn.drag;
        if (!d) return;
        const p = pgAnPoint(ev);
        if (d.kind === 'move') {
            const s = pgAn.shapes[pgAn.sel];
            if (!s) return;
            if (!d.recorded) { pgAnRecord(); d.recorded = true; }
            const dx = p.x - d.from.x, dy = p.y - d.from.y;
            s.x += dx; s.y += dy;
            if (s.x2 !== undefined) { s.x2 += dx; s.y2 = (s.y2 || 0) + dy; }
            if (s.pts) s.pts = s.pts.map((v, i) => v + (i % 2 ? dy : dx));
            d.from = p;
        } else {
            const s = pgAn.shapes[d.index];
            if (s.t === 'arrow') { s.x2 = p.x; s.y2 = p.y; }
            else if (s.t === 'draw') s.pts!.push(p.x, p.y);
            else { s.w = p.x - s.x; s.h = p.y - s.y; }
        }
        pgAnDraw();
    });
    const end = () => {
        const d = pgAn.drag;
        pgAn.drag = null;
        if (!d || d.kind !== 'make') return;
        const s = pgAn.shapes[d.index];
        // too small to be meant: let go (Node Banana's thresholds)
        const small = s.t === 'arrow' ? Math.hypot((s.x2 || 0) - s.x, (s.y2 || 0) - s.y) <= 10
            : s.t === 'draw' ? (s.pts || []).length < 4
            : Math.abs(s.w || 0) <= 5 || Math.abs(s.h || 0) <= 5;
        if (small) { pgAn.shapes.splice(d.index, 1); pgAn.undo.pop(); }
        else if (s.t === 'rect' || s.t === 'ellipse') { const b = pgShapeBox(s); Object.assign(s, b); }
        pgAnDraw();
        pgAnPaintBar();
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('dblclick', (ev) => {
        const i = pgAnHit(pgAnPoint(ev as PointerEvent));
        if (i >= 0 && pgAn.shapes[i].t === 'text') { pgAn.sel = i; pgAnTextAt({ x: 0, y: 0 }, i); }
    });
    // the editor's keys, wherever the focus is while it is open
    document.addEventListener('keydown', (ev) => {
        if (!pgAn.open) return;
        const t = ev.target as HTMLElement;
        if (t.classList.contains('pg-an-text')) {
            if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); pgAnTextDone(true); }
            else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); pgAnTextDone(false); }
            return;
        }
        const mod = ev.ctrlKey || ev.metaKey;
        if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); pgCloseAnnotate(false); }
        else if ((ev.key === 'Delete' || ev.key === 'Backspace') && pgAn.sel >= 0) { ev.preventDefault(); (el.querySelector('[data-an="delete"]') as HTMLElement).click(); }
        else if (mod && ev.key.toLowerCase() === 'z') { ev.preventDefault(); (el.querySelector('[data-an="' + (ev.shiftKey ? 'redo' : 'undo') + '"]') as HTMLElement).click(); }
        else if (ev.key === 'Tab') {
            // the keys stay in the editor
            const items = [...el.querySelectorAll<HTMLElement>('button:not([hidden]):not(:disabled)')];
            const i = items.indexOf(document.activeElement as HTMLElement);
            if (ev.shiftKey && i <= 0) { ev.preventDefault(); items[items.length - 1].focus(); }
            else if (!ev.shiftKey && i === items.length - 1) { ev.preventDefault(); items[0].focus(); }
        }
    });
    if (window.ResizeObserver) new ResizeObserver(() => { if (pgAn.open) { pgAnFit(); pgAnDraw(); } }).observe(el);
}

// ── Describe a workflow (Node Banana's prompt-to-workflow) ──
// Gemini is given every node, what goes in and out of it and its settings, and the
// words; it answers with the workflow as JSON. Each node and wire is checked as the
// canvas checks them (a wire that does not fit is left out, and it says so), and the
// nodes are set out in columns, each after what it is made from.
const PG_SETTINGS_HELP: Partial<Record<PgType, string>> = {
    image: 'none (the person drops a picture on it afterwards)',
    prompt: 'text: the prompt, written in full, in English (say "Image 1", "Image 2" … for the images that go into the same Generate, in the order their nodes stand from the top); varName: its @name when it feeds a Prompt constructor',
    generate: 'aspect: 1:1 | 4:5 | 3:4 | 2:3 | 9:16 | 16:9; size: 1k | 2k | 4k; model: gemini | openai | kie; count: 1-4 (pictures at once)',
    upscale: 'none',
    describe: 'focus: garment | photo | style',
    enhance: 'none (it writes a full prompt from a few words in its idea input)',
    llm: 'model: ' + PG_LLM_MODELS.map(m => m[0]).join(' | ') + '; temperature: 0-2; maxTokens: 512-16384',
    array: 'mode: newline | delimiter | regex; delimiter: a character; text: the items, when nothing is wired into it',
    construct: 'template: a text with @names; each text wired into its "vars" input is named by the varName in that node\'s data (set it on the Prompt, Describe, LLM, Array or Condition that is wired in, e.g. "garment", "concept", "pose")',
    router: 'none',
    switch: 'switches: [{ "name": "…", "on": true }] (its outputs are "1", "2" … in this order)',
    condition: 'rules: [{ "label": "…", "mode": "contains | is | starts | ends", "value": "word, other word" }] (its outputs are "1", "2" … in this order, and "else")',
    gallery: 'none',
    output: 'none',
    note: 'text: a note for the person who opens the workflow (what to drop where, what to change)',
    reference: 'none (the person chooses a brand\'s reference photo)',
    packshot: 'none (the person chooses a packshot from the library)',
    necktag: 'brand: a brand id (listed below)',
    savedPrompt: 'none',
    resize: 'mode: exact | max | scale; width, height: px; fit: contain | cover | stretch; maxEdge: px; scale: 1-400; format: keep | png | jpeg | webp',
    split: 'rows: 1-8; cols: 1-8',
    compare: 'none',
    annotate: 'none (the person draws the marks)',
    gif: 'fps: 4-30; colours: 32-256; width: 256-1024',
};
function pgCatalog(): string {
    return (Object.keys(PG_TYPES) as PgType[]).map(t => {
        const d = PG_TYPES[t];
        const n = pgMakeNode(t, 0, 0);
        const outs = t === 'array' ? 'each (text, a list: what it feeds is made once for each item), i0, i1 … (text, one item)'
            : t === 'split' ? 'each (image, a list: one for each cell), all (image, all cells side by side), c0, c1 … (image, one cell)'
            : t === 'switch' ? '"1", "2" … (the kind wired in)' : t === 'condition' ? '"1", "2" … and "else" (text)'
            : pgOuts(n).map(o => o.name + ' (' + o.type + ')').join(', ') || 'none';
        return '- ' + t + ' — ' + d.title + ': ' + d.desc + '. In: ' + (d.ins.map(i => i.name + ' (' + i.type + (i.multi ? ', several' : '') + ')').join(', ') || 'none')
            + '. Out: ' + outs + '. Settings in data: ' + (t === 'necktag' ? 'brand: ' + STUDIO_BRANDS.map(b => b.id).join(' | ') : PG_SETTINGS_HELP[t] || 'none') + '.';
    }).join('\n');
}
function pgDescribeInstruction(words: string): string {
    return 'You build workflows for the Playground of FloorFlow, a tool for the photo team of a Scandinavian fashion web shop: nodes on a canvas, wired together, that make product pictures and texts with Google\'s models.\n\n'
        + 'The nodes (type — title: what it does. In: its inputs. Out: its outputs. Settings):\n' + pgCatalog() + '\n\n'
        + 'A wire goes from an output of one node to an input of another of the same kind (image or text; "any" takes either). An input that does not say "several" takes one wire. No loops.\n'
        + 'Answer with JSON only, in this shape:\n'
        + '{"name": "a short name", "nodes": [{"id": "n1", "type": "prompt", "data": {"text": "…"}}], "edges": [{"from": "n1", "out": "out", "to": "n2", "port": "prompt"}]}\n'
        + '"out" is the output it leaves from ("out" when the node has one). Use only the types and the ports listed. Start with a Note that says what to drop or choose where. Write prompts in full, as an experienced fashion photographer would brief an image model: what to do, what each image is, the picture, the light and the camera, and a last line "Preserve exactly: …". End with an Output or a Gallery.\n\n'
        + 'The workflow to build:\n' + words;
}
// The answer made a workflow: nodes made (with their defaults), wires checked, set out
// A node's settings as Gemini gave them: only what settings can be, never a picture
function pgDataFrom(raw: any): Record<string, any> {
    const str = (v: any, max = 20000) => typeof v === 'string' ? v.slice(0, max) : '';
    const data: Record<string, any> = {};
    for (const [k, v] of Object.entries(raw && typeof raw === 'object' ? raw : {})) {
        if (k === 'switches' && Array.isArray(v)) data.switches = v.slice(0, 10).map((x: any, i: number) => ({ id: pgId('s'), name: str(x && x.name, 40) || 'Output ' + (i + 1), on: x && x.on !== false }));
        else if (k === 'rules' && Array.isArray(v)) data.rules = v.slice(0, 10).map((x: any, i: number) => ({ id: pgId('r'), label: str(x && x.label, 40) || 'Rule ' + (i + 1), mode: ['contains', 'is', 'starts', 'ends'].includes(x && x.mode) ? x.mode : 'contains', value: str(x && x.value, 500) }));
        else if (k === 'varName') data.varName = pgVarSafe(String(v));
        else if (['src', 'mediaId', 'key', 'shapes', 'kept', 'kind'].includes(k)) continue;
        else if (typeof v === 'boolean') data[k] = v;
        else if (typeof v === 'string' || typeof v === 'number') data[k] = typeof v === 'string' ? str(v) : String(v);
    }
    return data;
}
// the outputs by place: a Switch's "1", "2" …, a Condition's rules
function pgOutByPlace(n: PgNode, out: any): string {
    const k = String(out || 'out');
    if (n.type === 'switch' && /^\d+$/.test(k)) return pgSwitches(n)[Number(k) - 1]?.id || k;
    if (n.type === 'condition' && /^\d+$/.test(k)) return pgRules(n)[Number(k) - 1]?.id || k;
    return k;
}
async function pgBuildDescribed(json: any): Promise<{ wf: PgWorkflow; dropped: string[] }> {
    if (!json || !Array.isArray(json.nodes)) throw new Error('No workflow came back');
    const dropped: string[] = [];
    const ids = new Map<string, PgNode>();
    const str = (v: any, max = 20000) => typeof v === 'string' ? v.slice(0, max) : '';
    for (const raw of json.nodes.slice(0, 60)) {
        const type = (raw && raw.type) as PgType;
        if (!type || !Object.prototype.hasOwnProperty.call(PG_TYPES, type)) { dropped.push('a node of type "' + str(raw && raw.type, 40) + '"'); continue; }
        ids.set(String(raw.id), pgMakeNode(type, 0, 0, pgDataFrom(raw.data)));
    }
    const outOf = pgOutByPlace;
    type PgWire = { a: PgNode; b: PgNode; port: string; out: string };
    const edges: PgWire[] = (Array.isArray(json.edges) ? json.edges : []).slice(0, 200)
        .map((e: any) => ({ a: ids.get(String(e && e.from)), b: ids.get(String(e && e.to)), port: str(e && e.port, 40), out: str(e && e.out, 40) || 'out' }))
        .filter((e: any): e is PgWire => { if (!e.a || !e.b) dropped.push('a wire to or from a node that is not there'); return !!(e.a && e.b); });
    // columns: each node after the longest way to it
    const nodes = [...ids.values()];
    const level = new Map<string, number>();
    const depth = (n: PgNode, seen = new Set<string>()): number => {
        if (level.has(n.id)) return level.get(n.id)!;
        if (seen.has(n.id)) return 0;
        seen.add(n.id);
        const from = edges.filter(e => e.b === n).map(e => depth(e.a, seen) + 1);
        const d = from.length ? Math.max(...from) : 0;
        level.set(n.id, d);
        return d;
    };
    nodes.forEach(n => depth(n));
    const rows = new Map<number, number>();
    nodes.forEach(n => {
        const d = n.type === 'note' ? 0 : level.get(n.id) || 0;
        const y = rows.get(d) || (n.type === 'note' ? -200 : 40);
        n.x = 80 + d * 400;
        n.y = n.type === 'note' ? -220 : y;
        if (n.type !== 'note') rows.set(d, y + (PG_TYPES[n.type].runs || n.type === 'prompt' || n.type === 'construct' ? 460 : 340));
    });
    const wf = pgWorkflowOf(str(json.name, 80) || 'Described workflow', nodes, []);
    await pgOpenWorkflow(wf);
    for (const e of edges) {
        const problem = pgConnect(e.a.id, e.b.id, e.port, false, outOf(e.a, e.out));
        if (problem) dropped.push('a wire from ' + PG_TYPES[e.a.type].title + ' to ' + PG_TYPES[e.b.type].title + ' (' + problem.toLowerCase() + ')');
    }
    pgRenderAll();
    pgFit(true);
    pgSaveSoon();
    return { wf, dropped };
}
const PG_DESCRIBE_EXAMPLES = [
    'A packshot on white from a photo of a garment, made 4K',
    'The garment in four colours from a list, each on a model, in a gallery',
    'A model picture of the garment, checked by a language model and made again where it fails',
];
function pgOpenDescribe() {
    const el = document.getElementById('pgDescribe')!;
    el.hidden = false;
    (el.querySelector('textarea') as HTMLTextAreaElement).focus();
    el.querySelector('.pg-describe-status')!.textContent = '';
}
function pgCloseDescribe() { const el = document.getElementById('pgDescribe'); if (el) el.hidden = true; }
async function pgDescribe() {
    const el = document.getElementById('pgDescribe')!;
    const box = el.querySelector('textarea') as HTMLTextAreaElement;
    const words = box.value.trim();
    const status = el.querySelector('.pg-describe-status') as HTMLElement;
    const btn = el.querySelector('[data-pg-describe="build"]') as HTMLButtonElement;
    if (!words) { status.textContent = 'Say what the workflow is to make.'; box.focus(); return; }
    btn.disabled = true;
    el.classList.add('is-busy');
    status.textContent = 'Building the workflow…';
    try {
        await pgSaveNow();
        const text = await pgLlm('gemini-3.1-pro-preview', pgDescribeInstruction(words), [], 0.4, 16384, true);
        const json = JSON.parse(text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, ''));
        const { wf, dropped } = await pgBuildDescribed(json);
        pgCloseDescribe();
        box.value = '';
        showToast('“' + wf.name + '” is built, with ' + wf.nodes.length + ' nodes. Look it over, drop in the pictures, and Run.' + (dropped.length ? ' Left out: ' + dropped.slice(0, 3).join('; ') + (dropped.length > 3 ? ' and ' + (dropped.length - 3) + ' more' : '') + '.' : ''), { type: dropped.length ? 'warn' : 'success', title: 'Built' });
    } catch (e) {
        status.textContent = 'It could not be built: ' + pgErrorText(e);
    } finally {
        btn.disabled = false;
        el.classList.remove('is-busy');
    }
}

// ── The assistant (2026-10-13) ──
// A chat with Gemini that builds the workflow on the canvas and changes it as it is
// asked (nodes, wires, prompts, settings, groups), each answer one step to undo.
// Asked to, it tests what it built: the pictures made at 1K, looked at against what
// a good picture is here, the flow changed and made again, until they are good or
// the money set aside is spent. The round that did best is kept, its Generates at
// their own size again, so Run (or Make at full size) makes the final pictures.
interface PgAskShot { node: string; title: string; thumb: string; score?: number; good?: string; problems?: string }
interface PgAskMsg {
    id: string; role: 'user' | 'assistant' | 'test'; text: string; at: number;
    changes?: string; dropped?: string; shots?: PgAskShot[]; spent?: number; budget?: number;
    test?: { goal: string; nodes: string[] }; final?: string[]; words?: string; note?: string; best?: boolean; plan?: string[];
    pics?: string[];   // the person's: small copies of the pictures that went with the message
}
// a picture put in a message, before it is sent
interface PgAskPic { id: string; src: string; thumb: string; name: string; }
const PG_ASK_PICS = 4;          // pictures in one message, at most
// A big flow is built in parts (2026-10-19, Simon: "Det går altså ikke at siden går i stykker
// når den skal lave så store prompts"): at most this many steps in one answer, and this many
// answers; all of it one step to undo
const PG_ASK_PART_OPS = 80, PG_ASK_PARTS = 12, PG_ASK_STEPS = 1200;
// a part in which Gemini says nothing (no thought, no text) for this long is stopped
// (2026-10-19, Simon: "Sidder den nu fast med at bygge videre af sig selv?")
const pgAskQuietMs = () => Number((window as any).__pgAskQuietMs) || 150_000;
const PG_ASK_MODEL = 'gemini-3.1-pro-preview';
const PG_ASK_TEXT_USD = 0.01;   // about what one of its calls costs (the text, and the pictures it looks at)
const PG_ASK_GOOD = 8;          // out of 10: good enough to use
const PG_ASK_ROUNDS = 5;        // tests at most
const PG_ASK_BUDGETS = [0.5, 1, 2, 5];
const PG_ASK_TEST_KEY = 'creator.playground.askTest', PG_ASK_BUDGET_KEY = 'creator.playground.askBudget', PG_ASK_OPEN_KEY = 'creator.playground.askOpen';
const PG_ASK_EXAMPLES = [
    'Three creative directions for this garment on a model, and test them',
    'A clean packshot on white from my photo, then made 4K',
    'Make the light warmer and the background calmer',
];
const pgAsk = { busy: false, stopping: false, abort: null as AbortController | null, pics: [] as PgAskPic[] };
const pgAskTesting = () => { try { return localStorage.getItem(PG_ASK_TEST_KEY) !== '0'; } catch { return true; } };
const pgAskBudget = () => { try { const v = Number(localStorage.getItem(PG_ASK_BUDGET_KEY)); return PG_ASK_BUDGETS.includes(v) ? v : 1; } catch { return 1; } };
const pgAskMsgs = (): PgAskMsg[] => (pg.wf ? (pg.wf.chat ||= []) : []);
const pgUsd = (v: number) => v.toFixed(2) + ' USD';
const pgSleep = (ms: number) => new Promise(r => setTimeout(r, ms));

// The workflow as the assistant is shown it: no pictures, only what they are
function pgAskView(): string {
    const wf = pg.wf!;
    const held = (n: PgNode) => n.type === 'image' ? (n.data.src ? 'a picture' : 'no picture yet: the person drops one')
        : n.type === 'reference' || n.type === 'packshot' || n.type === 'necktag' ? (pgKeyOf(n) ? 'chosen' : 'nothing chosen yet') : '';
    const nodes = wf.nodes.map(n => {
        const data: Record<string, any> = {};
        for (const [k, v] of Object.entries(n.data)) {
            if (['src', 'mediaId', 'key', 'kept', 'shapes', 'name'].includes(k) || v === undefined) continue;
            data[k] = typeof v === 'string' ? v.slice(0, 2000) : v;
        }
        const g = pgGroupOf(n.id);
        // the images it takes, as Image 1, Image 2 …
        const order = PG_TYPES[n.type].ins.some(i => i.name === 'images') ? pgInEdges(n.id, 'images').map(e => e.from) : [];
        return { id: n.id, type: n.type, data, ...(held(n) ? { holds: held(n) } : {}), ...(PG_TYPES[n.type].runs ? { made: n.result ? 'yes' : 'no' } : {}), ...(g ? { group: g.name } : {}), ...(order.length ? { images: order } : {}) };
    });
    const edges = wf.edges.map(e => ({ from: e.from, out: e.out || 'out', to: e.to, port: e.port }));
    return JSON.stringify({ name: wf.name, nodes, edges });
}
const PG_ASK_OPS = '{"op": "add", "ref": "new1", "type": "generate", "data": {…}}: a new node; later ops name it by its ref\n'
    + '{"op": "set", "id": "…", "data": {…}}: new settings for a node (only the keys given change; a prompt\'s "text" is written in full)\n'
    + '{"op": "connect", "from": "…", "out": "out", "to": "…", "port": "…"}\n'
    + '{"op": "disconnect", "from": "…", "to": "…", "port": "…"}\n'
    + '{"op": "delete", "id": "…"}\n'
    + '{"op": "group", "name": "…", "ids": ["…"]}: a named frame round nodes\n'
    + 'A node on the canvas is named by its id, a new one by its ref. "out" is the output a wire leaves from ("out" when the node has one; a Switch\'s or Condition\'s by number, "1", "2" …).\n';
function pgAskInstruction(words: string, pictures: string[], go?: { part: number; left: string; broke: boolean }): string {
    const talk = pgAskMsgs().filter(m => m.role !== 'test').slice(-13, -1).map(m => (m.role === 'user' ? 'Person: ' : 'You: ') + m.text.slice(0, 1500)).join('\n');
    return 'You are the creative assistant and workflow engineer in the Playground of FloorFlow, a tool for the photo team of a Scandinavian fashion web shop: nodes on a canvas, wired together, that make product and model pictures and texts with Google\'s models. You build the workflow on the canvas yourself, and change it as the person asks. Think like a creative director who is also a pipeline engineer: bold about how the pictures look (setting, light, mood, styling, camera, composition), and exact about the garment, which always stays as it is.\n\n'
        + 'The nodes (type — title: what it does. In: its inputs. Out: its outputs. Settings in data):\n' + pgCatalog() + '\n\n'
        + 'How the nodes work together:\n'
        + '- A wire goes from an output of one node to an input of the same kind (image or text; "any" takes either). An input that does not say "several" takes one wire; a new wire into it takes the place of the old. No loops.\n'
        + '- The images that go into a node are its Image 1, Image 2 … in the order you wire them: the first "connect" into its "images" is Image 1. Write prompts by those numbers, and also say what each image shows ("Image 1, the photo of the garment").\n'
        + '- Texts wired into one input are joined. A Prompt constructor fills its @names from the texts wired into "vars", each named by the varName in its data.\n'
        + '- A list fans out: an Array\'s "each" output (or a Split grid\'s "each") makes every node after it run once for each item, all the way down, and a Gallery at the end shows them all. So one chain makes many pictures.\n'
        + '- A Condition sends its text on along the outputs whose rules match; a Generate (or anything) on an output that does not match is skipped. That is how a flow decides.\n'
        + '- A picture a Generate makes can go into another Generate, an LLM, Describe, 4K, Resize, Compare, Gallery or Output.\n\n'
        + 'What an advanced ("crazy", "best") flow is here: one or two pictures in, ONE picture out, and many steps between, each making that same picture better at one thing, until it looks unbelievably real: a photograph no one would doubt. Depth, not breadth: never several pictures that do not belong together, and never copies of one chain with different prompts.\n\n'
        + 'The chain for an advanced flow (adapt it to what is asked; 10 to 16 steps is normal):\n'
        + '1. Understand: Describe (focus "garment") on the garment photo, varName "garment". An LLM (temperature 0.9) looks at the garment photo and writes ONE shot brief as a full image prompt: the model, hair and make-up, styling, the location, the light, the camera and lens, the mood (its prompt says to write only the prompt). A Prompt constructor joins @brief and @garment.\n'
        + '2. Base: a Generate makes the first picture from it: Image 1 the garment photo (and Image 2 a second picture, when one is given: a model, a face, a location or a brand reference).\n'
        + '3. Garment pass: a Generate takes the picture so far as Image 1 and the garment photo as Image 2: "Correct the garment in Image 1 so it matches Image 2 exactly (colour, fabric, cut, length, seams, and every logo, label and print letter for letter). Change nothing else in Image 1."\n'
        + '4. Critique passes, two or three in a row: an LLM (temperature 0.3) gets the picture so far and the garment photo, and writes the ONE edit that would now make the picture most real, as a full edit prompt for an image model that ends with what to keep; a Generate makes that edit on the picture so far. These steps answer what each picture needs.\n'
        + '5. Specialist passes, one job each, each on the picture so far, each prompt ending with "Change nothing else": face and skin (real skin texture and pores, natural asymmetry, no smoothing); hands and pose; fabric (weave, weight, drape, real creases where the body bends); light (one light direction, true shadows, a contact shadow); lens and camera (a real lens\'s depth of field and falloff, a little grain, no AI sheen); colour (a restrained editorial grade).\n'
        + '6. Final check: an LLM (temperature 0.2) compares the picture with the garment photo and answers PASS, or FAIL: what differs; a Condition (contains "FAIL") sends a FAIL through a Prompt constructor (@check) into one more garment pass.\n'
        + '7. Finish: 4K on the final picture, into an Output; a Compare with the garment photo as A and the final picture as B.\n'
        + 'Each pass takes the picture of the pass before as Image 1, so the picture builds up step by step; the garment photo goes in as Image 2 wherever the garment matters. Group the chain in stages, each a group named after what it does (Understand, Base, Garment, Critique, Refine, Check, Finish), in order. Make several pictures only when the person asks for variations or a set; then they share one location plate and one hero picture, so they belong together.\n\n'
        + 'A simple ask gets a simple flow. Keep what is on the canvas unless the person asks otherwise, and build onto it. The garment picture comes from an Image node (the person drops it), or a Packshot or Reference photo they choose; add an Image when there is none. Every prompt and template is written in full, in English, as an experienced fashion photographer and retoucher briefs an image model: what to do, what each image is, and what to keep. An LLM\'s prompt says exactly what to write and in what form. Give Generate the size the final picture is to have ("2k" unless asked); the tests are made at 1K by themselves.\n\n'
        + 'The workflow on the canvas now (JSON; "images" lists what goes into a node as Image 1, Image 2 …):\n' + pgAskView() + '\n\n'
        + (pictures.length ? 'The pictures that go with this message, in order: ' + pictures.join('; ') + '.\n\n' : '')
        + (talk ? 'The conversation so far:\n' + talk + '\n\n' : '')
        + 'Answer with JSON only, in this order and shape:\n{"plan": ["…"], "reply": "…", "ops": [ … ], "more": false, "next": "", "test": {"goal": "…", "nodes": ["…"]}}\n'
        + 'plan: the steps of the flow, one short line each, in order, in the language the person writes in (empty when nothing is built).\n'
        + 'reply: what you did, or your answer, in one to three short sentences, in the language the person writes in, without markdown.\n'
        + 'ops: the changes, done in order (an empty list when nothing is to change):\n' + PG_ASK_OPS
        + 'more and next: a big flow is built in parts. Put at most ' + PG_ASK_PART_OPS + ' ops in one answer. When more is to be built after them, set "more" to true and say in "next" what is left, in a few short lines, with the ids or refs it builds on; you are then asked to go on, with the canvas as it is by then, as many times as it takes (a flow of 250 nodes is fine). Otherwise "more" is false and "next" is empty. '
        + 'When "more" is true you go on at once, by yourself: never ask the person whether to go on or wait for a yes; "reply" says what is being built.\n'
        + 'test: give it in the answer that ends the build ("more" false): what a good picture is here, said as things to look for (the garment kept exactly, and what the person asked for), and the ids or refs of the last Generate nodes, whose pictures show it; null when nothing is to be made, or the person only asks a question.\n\n'
        + (go ? 'You are going on with the flow you started building for what the person says below: this is part ' + go.part + '. What you have built so far is on the canvas above'
            + (go.broke ? '; your last answer broke off before it ended, so the step it was writing may be missing' : '') + '. What is left: ' + (go.left || 'the rest of what you planned') + '\n'
            + 'Add only what is left, wired to what is there (the ids on the canvas; the refs of the parts before still work). Add nothing again that is there, and change or delete nothing that is not part of what is left. Keep "plan" and "reply" short in this part.\n\n' : '')
        + 'The person says:\n' + words;
}
function pgAskJudgeInstruction(goal: string, words: string, lines: string[]): string {
    return 'You test the pictures a workflow in the Playground of FloorFlow (a tool for the photo team of a Scandinavian fashion web shop) has made, as a demanding creative director and retoucher, and change the workflow so the next round is better.\n\n'
        + 'What a good picture is here: ' + goal + '\nThe person asked: ' + words + '\n\n'
        + 'The pictures that go with this message:\n' + lines.join('\n\n') + '\n\n'
        + 'Score each picture made from 0 to 10. What counts most: the garment exactly as in the picture that went in (colour, fabric, cut, and every logo, label and print, letter for letter). Then how well it does what was asked. Then the photograph itself: light, composition, believable skin, hands and fabric, and no faults an image model makes (warped seams, extra fingers, stray text). 8 or more: good enough to use.\n\n'
        + 'Answer with JSON only:\n{"results": [{"id": "the node\'s id", "score": 7, "good": "what works, in a few words", "problems": "what is wrong, in a few words"}], "ops": [ … ], "note": "…"}\n'
        + 'ops: changes that make the pictures under 8 better in the next round: most often a prompt or template written again in full that fixes what is wrong, or a step the chain lacks (a garment pass against its photo, a pass for skin, hands, fabric, light or lens). Change only what leads to the pictures under 8, and not the sizes. The images going into a node are its Image 1, Image 2 … in the order the workflow below lists them. The forms:\n' + PG_ASK_OPS
        + 'note: one sentence, in the language the person writes in, on what you changed and why.\n\n'
        + 'The workflow (JSON):\n' + pgAskView();
}
// a text value of an answer, as far as it has come
function pgAskStr(text: string, key: string): string {
    const m = new RegExp('"' + key + '"\\s*:\\s*"').exec(text);
    if (!m) return '';
    let out = '', esc = false;
    for (let k = m.index + m[0].length; k < text.length; k++) {
        const ch = text[k];
        if (esc) { out += '\\' + ch; esc = false; continue; }
        if (ch === '\\') { esc = true; continue; }
        if (ch === '"') break;
        out += ch;
    }
    try { return JSON.parse('"' + out.replace(/\\u[0-9a-fA-F]{0,3}$/, '') + '"'); } catch { return out; }
}
// an object value of an answer, when it is whole
function pgAskObj(text: string, key: string): any {
    const m = new RegExp('"' + key + '"\\s*:\\s*\\{').exec(text);
    if (!m) return null;
    let depth = 0, inStr = false, esc = false;
    const start = m.index + m[0].length - 1;
    for (let k = start; k < text.length; k++) {
        const ch = text[k];
        if (inStr) { if (esc) esc = false; else if (ch === '\\') esc = true; else if (ch === '"') inStr = false; continue; }
        if (ch === '"') inStr = true;
        else if (ch === '{') depth++;
        else if (ch === '}' && --depth === 0) { try { return JSON.parse(text.slice(start, k + 1)); } catch { return null; } }
    }
    return null;
}
// An answer read whole when it is valid JSON; else as it came, as it was built (the plan, the
// reply, each whole step, more, next and the test), so a long answer with a comma missing, or
// one that broke off, is not lost. cut: it broke off before it ended.
function pgAskRead(full: string): { j: any; whole: boolean; cut: boolean } {
    try { return { j: pgAskJson(full), whole: true, cut: false }; } catch { /* read as it came */ }
    const p = pgAskPartial(full);
    return {
        j: { plan: p.plan, reply: p.reply, ops: p.ops, more: /"more"\s*:\s*true/.test(full), next: pgAskStr(full, 'next'), test: pgAskObj(full, 'test') },
        whole: false,
        cut: !/\}\s*(?:```)?\s*$/.test(full.trim()),
    };
}
const pgAskJson = (raw: string): any => {
    const t = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
    try { return JSON.parse(t); } catch { /* the object inside it */ }
    const a = t.indexOf('{'), b = t.lastIndexOf('}');
    if (a >= 0 && b > a) return JSON.parse(t.slice(a, b + 1));
    throw new Error('Gemini did not answer as asked');
};

// The changes, done as one step (Ctrl+Z undoes them all), one at a time: as Gemini
// writes them, each is shown on the canvas as it is done. What does not fit is left
// out and said.
interface PgAskBuild {
    refs: Map<string, string>; added: PgNode[]; dropped: string[]; steps: number; last: string;
    wired: Map<string, string[]>;   // a new node's images, in the order Gemini wired them (its Image 1, 2 …)
    said: { added: string[]; changed: string[]; wires: number; unwired: number; deleted: string[]; grouped: string[] };
}
function pgAskBegin(): PgAskBuild {
    pgRecord();
    return { refs: new Map(), added: [], dropped: [], steps: 0, last: '', wired: new Map(), said: { added: [], changed: [], wires: 0, unwired: 0, deleted: [], grouped: [] } };
}
// one step; the nodes it changed
function pgAskStep(b: PgAskBuild, o: any): string[] {
    if (!pg.wf || b.steps >= PG_ASK_STEPS) return [];
    b.steps++;
    const idOf = (x: any) => { const k = String(x ?? ''); return b.refs.get(k) || (pgNode(k) ? k : ''); };
    const op = o && o.op;
    if (op === 'add') {
        const type = o.type as PgType;
        if (!type || !Object.prototype.hasOwnProperty.call(PG_TYPES, type)) { b.dropped.push('a node of type "' + String(o.type).slice(0, 30) + '"'); return []; }
        const n = pgMakeNode(type, 0, 0, pgDataFrom(o.data));
        pg.wf.nodes.push(n);
        b.added.push(n);
        if (o.ref !== undefined) b.refs.set(String(o.ref), n.id);
        b.said.added.push(PG_TYPES[type].title);
        b.last = 'Added ' + PG_TYPES[type].title;
        return [n.id];
    }
    if (op === 'set') {
        const n = pgNode(idOf(o.id));
        if (!n) { b.dropped.push('a change to a node that is not there'); return []; }
        Object.assign(n.data, pgDataFrom(o.data));
        if (!b.said.changed.includes(PG_TYPES[n.type].title)) b.said.changed.push(PG_TYPES[n.type].title);
        b.last = 'Changed ' + PG_TYPES[n.type].title;
        return [n.id];
    }
    if (op === 'connect') {
        const a = pgNode(idOf(o.from)), c = pgNode(idOf(o.to));
        if (!a || !c) { b.dropped.push('a wire to or from a node that is not there'); return []; }
        const problem = pgConnect(a.id, c.id, String(o.port || ''), false, pgOutByPlace(a, o.out));
        if (problem) { b.dropped.push('a wire from ' + PG_TYPES[a.type].title + ' to ' + PG_TYPES[c.type].title + ' (' + problem.toLowerCase() + ')'); return []; }
        b.said.wires++;
        if (String(o.port) === 'images' && b.added.some(n => n.id === c.id)) b.wired.set(c.id, [...(b.wired.get(c.id) || []).filter(x => x !== a.id), a.id]);
        b.last = 'Wired ' + PG_TYPES[a.type].title + ' to ' + PG_TYPES[c.type].title;
        return [c.id];
    }
    if (op === 'disconnect') {
        const from = idOf(o.from), to = idOf(o.to), before = pg.wf.edges.length;
        pg.wf.edges = pg.wf.edges.filter(e => !(e.from === from && e.to === to && (!o.port || e.port === o.port)));
        b.said.unwired += before - pg.wf.edges.length;
        b.last = 'Took off a wire';
        return pgNode(to) ? [to] : [];
    }
    if (op === 'delete') {
        const n = pgNode(idOf(o.id));
        if (!n) return [];
        pg.wf.nodes = pg.wf.nodes.filter(x => x.id !== n.id);
        pg.wf.edges = pg.wf.edges.filter(e => e.from !== n.id && e.to !== n.id);
        if (pg.wf.groups) pg.wf.groups = pg.wf.groups.map(g => ({ ...g, nodes: g.nodes.filter(x => x !== n.id) })).filter(g => g.nodes.length);
        b.added = b.added.filter(x => x.id !== n.id);
        pgCard(n.id)?.remove();
        b.said.deleted.push(PG_TYPES[n.type].title);
        b.last = 'Deleted ' + PG_TYPES[n.type].title;
        return [];
    }
    if (op === 'group') {
        const ids = [...new Set((Array.isArray(o.ids) ? o.ids : []).map(idOf).filter(Boolean))] as string[];
        if (!ids.length) return [];
        const rest = pgGroups().map(g => ({ ...g, nodes: g.nodes.filter(x => !ids.includes(x)) })).filter(g => g.nodes.length);
        const used = new Set(rest.map(g => g.color));
        const name = String(o.name || 'Group').slice(0, 60);
        pg.wf.groups = [...rest, { id: pgId('g'), name, color: PG_GROUP_COLOURS.find(c => !used.has(c)) || PG_GROUP_COLOURS[rest.length % PG_GROUP_COLOURS.length], nodes: ids }];
        b.said.grouped.push('“' + name + '”');
        b.last = 'Grouped “' + name + '”';
        return [];
    }
    if (op) b.dropped.push('a step "' + String(op).slice(0, 20) + '"');
    return [];
}
// What has been done so far, on the canvas: a new card pops in, the new nodes are set
// out again and glide to their places, the wires and frames follow them, a node that
// changed lights up, and the view keeps them in sight
let __pgAskFitAt = 0;
function pgAskShow(b: PgAskBuild, touched: string[]) {
    if (!pg.wf) return;
    const world = pg.el.world;
    world.classList.add('is-building');
    pgAskPlace(b.added);
    const fresh = new Set<string>();
    for (const n of b.added) {
        const card = pgCard(n.id);
        if (!card) { pg.pop.add(n.id); pgRenderNode(n.id, true); fresh.add(n.id); }
        else { card.style.left = n.x + 'px'; card.style.top = n.y + 'px'; }
    }
    for (const id of touched) {
        if (!fresh.has(id)) pgRenderNode(id, true);
        const card = pgCard(id);
        if (card) { card.classList.add('is-touched'); setTimeout(() => card.classList.remove('is-touched'), 1100); }
    }
    pgPaintEmpty();
    pgAskFollow();
    if (b.added.length && performance.now() - __pgAskFitAt > 450) { __pgAskFitAt = performance.now(); pgFit(true, undefined, true); }
}
// the wires follow the cards into place for a moment after each step: one loop, however
// many steps come (a loop each made a big build crawl)
let __pgAskFollow = 0;
function pgAskFollow() {
    const going = __pgAskFollow > performance.now();
    __pgAskFollow = performance.now() + 380;
    if (going) return;
    const follow = () => { pgRenderEdges(); if (performance.now() < __pgAskFollow) requestAnimationFrame(follow); };
    requestAnimationFrame(follow);
}
function pgAskSummary(b: PgAskBuild): string {
    const count = (words: string[]) => { const m = new Map<string, number>(); words.forEach(w => m.set(w, (m.get(w) || 0) + 1)); return [...m].map(([w, k]) => (k > 1 ? k + ' × ' : '') + w).join(', '); };
    const said = b.said;
    return [
        said.added.length ? 'Added ' + count(said.added) : '',
        said.wires ? said.wires + ' wire' + (said.wires > 1 ? 's' : '') : '',
        said.changed.length ? 'Changed ' + said.changed.join(', ') : '',
        said.unwired ? said.unwired + ' wire' + (said.unwired > 1 ? 's' : '') + ' taken off' : '',
        said.deleted.length ? 'Deleted ' + count(said.deleted) : '',
        said.grouped.length ? 'Grouped ' + said.grouped.join(', ') : '',
    ].filter(Boolean).join(' · ');
}
// A node takes its images as Image 1, 2 … in the order their nodes stand from the top,
// and the canvas has set them out: where that is not the order Gemini wired them in,
// the prompts that lead only to that node are numbered again to match
function pgAskRenumber(b: PgAskBuild) {
    for (const [id, meant] of b.wired) {
        const n = pgNode(id);
        if (!n) continue;
        const now = pgInEdges(id, 'images').map(e => e.from);
        const map = new Map<number, number>();
        meant.forEach((src, i) => { const at = now.indexOf(src); if (at >= 0 && at !== i) map.set(i + 1, at + 1); });
        if (!map.size) continue;
        // the texts that lead to it: Prompts and constructors, back through the flows
        const port = PG_TYPES[n.type].ins.some(i => i.name === 'prompt') ? 'prompt' : 'idea';
        const texts = new Set<string>();
        const walk = (to: string, ports: string[] | null, seen = new Set<string>()) => {
            for (const e of pg.wf!.edges.filter(x => x.to === to && (!ports || ports.includes(x.port)))) {
                const src = pgNode(e.from);
                if (!src || seen.has(src.id)) continue;
                seen.add(src.id);
                if (src.type === 'prompt' || src.type === 'construct') texts.add(src.id);
                if (PG_TYPES[src.type].flows) walk(src.id, null, seen);
            }
        };
        walk(id, [port]);
        for (const tid of texts) {
            // only a text that leads to no other node that takes images
            const t = pgNode(tid)!;
            const takers = new Set<string>();
            const down = (from: string, seen = new Set<string>()) => {
                for (const e of pg.wf!.edges.filter(x => x.from === from)) {
                    const d = pgNode(e.to);
                    if (!d || seen.has(d.id)) continue;
                    seen.add(d.id);
                    if (PG_TYPES[d.type].ins.some(i => i.name === 'images')) takers.add(d.id);
                    else if (PG_TYPES[d.type].flows) down(d.id, seen);
                }
            };
            down(tid);
            if (takers.size !== 1 || !takers.has(id)) continue;
            const key = t.type === 'construct' ? 'template' : 'text';
            if (typeof t.data[key] === 'string') t.data[key] = t.data[key].replace(/\bImage (\d+)\b/g, (m: string, k: string) => map.has(Number(k)) ? 'Image ' + map.get(Number(k)) : m);
        }
    }
}
function pgAskEnd(b: PgAskBuild | null): { summary: string; dropped: string[]; refs: Map<string, string>; added: string[] } {
    if (!b || !pg.wf) return { summary: '', dropped: [], refs: new Map(), added: [] };
    pgForgetKinds();
    pgAskPlace(b.added);
    pgAskRenumber(b);
    pg.el.world.classList.remove('is-building');
    pgRenderAll();
    if (b.added.length) pgFit(true);
    pgSaveSoon();
    return { summary: pgAskSummary(b), dropped: b.dropped, refs: b.refs, added: b.added.map(n => n.id) };
}
// Steps as they come: each done and shown, a moment apart, so they can be followed
// (faster when many are waiting); done when no more will come
function pgAskQueue(say: (b: PgAskBuild) => void) {
    const q = { items: [] as any[], closed: false, b: null as PgAskBuild | null, stop: false, done: Promise.resolve() };
    q.done = (async () => {
        for (;;) {
            if (q.stop || pgAsk.stopping) return;
            if (q.items.length) {
                if (!q.b) q.b = pgAskBegin();
                // many waiting (a big flow): several at a time, shown together
                const many = q.items.length > 200 ? 16 : q.items.length > 40 ? 6 : 1;
                const touched: string[] = [];
                for (let k = 0; k < many && q.items.length; k++) touched.push(...pgAskStep(q.b, q.items.shift()));
                pgAskShow(q.b, touched);
                say(q.b);
                await pgSleep(q.items.length > 40 ? 8 : q.items.length > 10 || q.b.steps > 150 ? 40 : 160);
            } else if (q.closed) return;
            else await pgSleep(50);
        }
    })();
    return q;
}
// the changes a test asks for, shown as they are done
async function pgAskApplyLive(ops: any, status: string) {
    const q = pgAskQueue(b => pgAskSay(status + ' · ' + b.last));
    q.items.push(...(Array.isArray(ops) ? ops.slice(0, 150) : []));
    q.closed = true;
    await q.done;
    return pgAskEnd(q.b);
}
// New nodes set out in columns, each after what it is made from, and in rows. Stages
// (groups that follow one another) go on in the same row, each in columns of its own,
// and a row that grows too wide goes on in the next, from the left again; directions
// (groups side by side in the same columns) each get a row. The rows start after what
// on the canvas the new nodes are made from (else before what they feed, else under it
// all), and move down until they are clear of what is there.
function pgAskPlace(added: PgNode[]) {
    if (!added.length || !pg.wf) return;
    const wf = pg.wf;
    const fresh = new Set(added.map(n => n.id));
    const old = wf.nodes.filter(n => !fresh.has(n.id));
    const w = (n: PgNode) => PG_TYPES[n.type].width;
    const h = (n: PgNode) => pgCard(n.id)?.offsetHeight || (PG_TYPES[n.type].runs || n.type === 'prompt' ? 440 : 300);
    const gid = (n: PgNode) => pgGroupOf(n.id)?.id || '';
    // columns: the longest way to a node through the new ones
    const col = new Map<string, number>();
    const colOf = (n: PgNode, seen = new Set<string>()): number => {
        if (col.has(n.id)) return col.get(n.id)!;
        if (seen.has(n.id)) return 0;
        seen.add(n.id);
        const from = wf.edges.filter(e => e.to === n.id && fresh.has(e.from)).map(e => colOf(pgNode(e.from)!, seen) + 1);
        const c = from.length ? Math.max(...from) : 0;
        col.set(n.id, c);
        return c;
    };
    added.forEach(n => colOf(n));
    // a node nothing goes into (a Prompt) stands just before what it feeds; in the same
    // column as it, when the column before belongs to a stage that comes earlier
    const earlier = (g: string) => new Set(wf.edges.filter(e => fresh.has(e.from) && fresh.has(e.to) && gid(pgNode(e.to)!) === g && gid(pgNode(e.from)!) !== g).map(e => gid(pgNode(e.from)!)));
    for (const n of added) {
        if (wf.edges.some(e => e.to === n.id)) continue;
        const into = wf.edges.filter(e => e.from === n.id && fresh.has(e.to)).map(e => col.get(e.to)!);
        if (!into.length) continue;
        const k = Math.min(...into), before = earlier(gid(n));
        const clash = added.some(o => o !== n && col.get(o.id) === k - 1 && gid(o) !== gid(n) && before.has(gid(o)));
        col.set(n.id, clash ? k : Math.max(0, k - 1));
    }
    const cols = Math.max(...col.values()) + 1;
    const colW = Array.from({ length: cols }, (_, c) => Math.max(0, ...added.filter(n => col.get(n.id) === c).map(w)));
    const total = colW.reduce((sum, x) => sum + x, 0) + 120 * (cols - 1);
    const preds = old.filter(o => wf.edges.some(e => e.from === o.id && fresh.has(e.to)));
    const succs = old.filter(o => wf.edges.some(e => e.to === o.id && fresh.has(e.from)));
    let x0 = 80, y0 = 80;
    if (preds.length) { x0 = Math.max(...preds.map(o => o.x + w(o))) + 120; y0 = Math.min(...preds.map(o => o.y)); }
    else if (succs.length) { x0 = Math.min(...succs.map(o => o.x)) - 120 - Math.min(total, 3000); y0 = Math.min(...succs.map(o => o.y)); }
    else if (old.length) { x0 = Math.min(...old.map(o => o.x)); y0 = Math.max(...old.map(o => o.y + h(o))) + 120; }
    const colX = colW.map((_, c) => x0 + colW.slice(0, c).reduce((sum, x) => sum + x + 120, 0));
    // the rows
    const ROW = 3000;
    const wide = (spans: [number, number][]) => { const a = Math.min(...spans.map(x => x[0])), z = Math.max(...spans.map(x => x[1])); return colX[z] + colW[z] - colX[a]; };
    const byGroup = new Map<string, PgNode[]>();
    for (const n of added) { const k = gid(n); if (!byGroup.has(k)) byGroup.set(k, []); byGroup.get(k)!.push(n); }
    const rows: { keys: string[]; spans: [number, number][]; nodes: PgNode[] }[] = [];
    for (const [key, nodes] of byGroup) {
        const cs = nodes.map(n => col.get(n.id)!), span: [number, number] = [Math.min(...cs), Math.max(...cs)];
        const last = rows[rows.length - 1];
        const goesOn = !!key && !!last && !last.keys.includes('') && last.spans.every(([a, z]) => span[0] > z || span[1] < a) && span[0] > Math.max(...last.spans.map(x => x[1])) && wide([...last.spans, span]) <= ROW;
        if (goesOn) { last.keys.push(key); last.spans.push(span); last.nodes.push(...nodes); }
        else rows.push({ keys: [key], spans: [span], nodes: [...nodes] });
    }
    type Box = { n?: PgNode; x: number; y: number; w: number; h: number };
    const taken: Box[] = old.map(o => ({ x: o.x, y: o.y, w: w(o), h: h(o) }));
    const clear = (boxes: Box[]) => boxes.every(b => !taken.some(t => b.x < t.x + t.w + 40 && b.x + b.w + 40 > t.x && b.y < t.y + t.h + 40 && b.y + b.h + 40 > t.y));
    let y = y0;
    for (const r of rows) {
        const grouped = r.keys.some(k => k);
        // each row from the left
        const shift = colX[Math.min(...r.spans.map(x => x[0]))] - x0;
        // in each column, the row's nodes one under the other (a group keeps room for its head)
        const layout = (top: number): Box[] => { const at = new Map<number, number>(); return r.nodes.map(n => { const c = col.get(n.id)!; const yy = at.get(c) ?? top; at.set(c, yy + h(n) + 40); return { n, x: colX[c] - shift, y: yy, w: w(n), h: h(n) }; }); };
        let top = y + (grouped ? 56 : 0), boxes = layout(top);
        for (let k = 0; k < 120 && !clear(boxes); k++) { top += 40; boxes = layout(top); }
        boxes.forEach(b => { b.n!.x = Math.round(b.x); b.n!.y = Math.round(b.y); taken.push(b); });
        // a maker not yet run grows by its picture when it runs: room for that, so the rows stay apart
        const grows = r.nodes.some(n => PG_TYPES[n.type].runs && !n.result) ? 180 : 0;
        y = Math.max(...boxes.map(b => b.y + b.h)) + (grouped ? 24 : 0) + 80 + grows;
    }
}

// What the person must drop or choose before the pictures can be made
function pgAskMissing(targets: string[]): { nodes: string[]; say: string[] } {
    const up = [...new Set(targets.flatMap(id => pgUpstream(id)))].map(pgNode).filter((n): n is PgNode => !!n);
    const nodes = up.filter(n => ['image', 'reference', 'packshot', 'necktag'].includes(n.type) && !pgKeyOf(n));
    const say = [...new Set(nodes.map(n => n.type === 'image' ? 'drop the garment’s picture on Image' : n.type === 'reference' ? 'choose a reference photo'
        : n.type === 'packshot' ? 'choose a packshot' : 'choose a brand on Neck label'))];
    return { nodes: nodes.map(n => n.id), say };
}
// the Generates to test: those named, else each that no other Generate follows
function pgAskTargets(named: string[]): string[] {
    const gens = pg.wf!.nodes.filter(n => n.type === 'generate');
    const ids = named.filter(id => pgNode(id)?.type === 'generate');
    return ids.length ? [...new Set(ids)] : gens.filter(g => !gens.some(o => o !== g && pgUpstream(o.id).includes(g.id))).map(g => g.id);
}
const pgAskTitle = (id: string) => { const g = pgGroupOf(id); const n = pgNode(id); return g ? g.name : n ? PG_TYPES[n.type].title : '?'; };

// One look at the pictures: each with what went into it and its prompt
async function pgAskJudge(goal: string, words: string, ids: string[], todo: string[]): Promise<{ results: { id: string; score: number; good: string; problems: string }[]; ops: any[]; note: string }> {
    const images: string[] = [], lines: string[] = [];
    for (const id of ids) {
        const n = pgNode(id);
        if (!n || !n.result) continue;
        const ins = (await pgImagesIn(n, 'images')).slice(0, 3);
        const first = images.length + 1;
        for (const v of ins) images.push(await pgThumb(v.value, 1024) || v.value);
        // what it made: its picture, or the first four of a list (one for each item)
        const made = await pgMadeValue(n);
        const pics = made ? (made.items || [made]).slice(0, 4).map(v => v.value) : [n.result];
        for (const src of pics) images.push(await pgThumb(src, 1024) || src);
        const prompt = (await pgImagesIn(n, 'prompt')).map(v => v.value).join('\n\n');
        const g = pgGroupOf(id);
        lines.push('Node ' + id + (g ? ' (the group “' + g.name + '”)' : '') + ': '
            + (ins.length ? 'picture' + (ins.length > 1 ? 's ' + first + '–' + (first + ins.length - 1) : ' ' + first) + ' went into it (its Image 1' + (ins.length > 1 ? ' …' : '') + '); ' : '')
            + (pics.length > 1 ? 'pictures ' + (images.length - pics.length + 1) + '–' + images.length + ' are what it made, one for each item of a list (score them together)' : 'picture ' + images.length + ' is what it made') + (todo.includes(id) ? '' : ' (good already: score it, change nothing that leads to it)') + '. Its prompt:\n' + prompt.slice(0, 4000));
    }
    const j = pgAskJson(await pgLlm(PG_ASK_MODEL, pgAskJudgeInstruction(goal, words, lines), images, 0.4, 16384, true));
    const results = (Array.isArray(j.results) ? j.results : []).map((r: any) => ({
        id: String(r && r.id || ''), score: Math.max(0, Math.min(10, Math.round(Number(r && r.score) || 0))),
        good: String(r && r.good || '').slice(0, 300), problems: String(r && r.problems || '').slice(0, 300),
    }));
    return { results, ops: Array.isArray(j.ops) ? j.ops : [], note: typeof j.note === 'string' ? j.note.slice(0, 400) : '' };
}

// A message: kept with the workflow, shown at once
function pgAskPost(m: Omit<PgAskMsg, 'id' | 'at'>): PgAskMsg {
    const msg: PgAskMsg = { id: pgId('m'), at: Date.now(), ...m };
    const all = pgAskMsgs();
    all.push(msg);
    if (all.length > 80) all.splice(0, all.length - 80);
    pgAskRender();
    pgSaveSoon();
    return msg;
}
function pgAskSay(text: string) {
    const el = document.getElementById('pgAskStatus');
    if (!el) return;
    el.hidden = !text;
    (el.querySelector('.pg-ask-status-text') as HTMLElement).textContent = text;
}

// The person writes: Gemini builds as it writes, step by step on the canvas, with what
// it is thinking and its reply shown as they come; then it tests, when asked to and it can
const pgAskThought = (t: string) => { const m = /\*\*(.+?)\*\*/.exec(t); return (m ? m[1] : t.split(/[.\n]/)[0]).trim().slice(0, 80); };
const pgAskPlanHtml = (plan: string[] | undefined) => plan && plan.length ? '<ol class="pg-ask-plan">' + plan.map(x => '<li>' + pgEsc(x) + '</li>').join('') + '</ol>' : '';
function pgAskLive(text: string, plan: string[] = []) {
    const list = document.getElementById('pgAskList');
    if (!list) return;
    let el = list.querySelector('.pg-ask-msg.is-live') as HTMLElement | null;
    if (!el) { list.insertAdjacentHTML('beforeend', '<div class="pg-ask-msg is-assistant is-live"><div class="pg-ask-live-plan"></div><p></p></div>'); el = list.lastElementChild as HTMLElement; }
    const box = el.querySelector('.pg-ask-live-plan') as HTMLElement;
    if (box.childElementCount !== (plan.length ? 1 : 0) || (box.querySelectorAll('li').length !== plan.length)) box.innerHTML = pgAskPlanHtml(plan);
    const p = el.querySelector('p')!;
    p.textContent = text;
    p.hidden = !text;
    el.classList.toggle('is-writing', !!text);
    list.scrollTop = list.scrollHeight;
}
async function pgAskSend(text?: string) {
    const box = document.querySelector('#pgAsk textarea') as HTMLTextAreaElement | null;
    const words = (text ?? box?.value ?? '').trim();
    if (!words && pgAsk.pics.length && !pgAsk.busy) { showToast('Say what to make with the pictures.', { title: 'Assistant' }); box?.focus(); return; }
    if (!words || pgAsk.busy || !pg.wf) return;
    if (pg.running) { showToast('Wait for the run to finish, then write.', { type: 'warn', title: 'Running' }); return; }
    if (box) box.value = '';
    const pics = pgAsk.pics.splice(0);
    pgAskPaintPics();
    pgAskPost({ role: 'user', text: words.slice(0, 4000), pics: pics.length ? pics.map(p => p.thumb) : undefined });
    // the pictures put in the message go on the canvas, each an Image of its own
    const dropped = pgAskPlacePics(pics);
    pgAsk.busy = true;
    pgAsk.stopping = false;
    const ctl = new AbortController();
    pgAsk.abort = ctl;
    pgAskPaint();
    let plan: PgAskMsg['test'] | undefined;
    let part = 1, builtAt = 0;
    const q = pgAskQueue(b => { builtAt = Date.now(); pgAskSay('Building' + (part > 1 ? ' · part ' + part : '') + ' · ' + b.steps + ' step' + (b.steps > 1 ? 's' : '') + ' · ' + b.last); });
    let taken = 0;
    const feed = (ops: any[]) => { while (taken < ops.length) q.items.push(ops[taken++]); };
    try {
        pgAskSay('Gemini is thinking…');
        // the pictures put in the message go along first, then the others on the canvas,
        // so the ideas fit the garment
        const pictures: string[] = [], said: string[] = [];
        const ids = new Set(dropped.map(n => n.id));
        for (const n of [...dropped, ...pg.wf.nodes.filter(x => x.type === 'image' && x.data.src && !ids.has(x.id)).slice(0, 3)]) {
            const t = await pgThumb(n.data.src, 768);
            if (!t) continue;
            pictures.push(t);
            said.push('picture ' + pictures.length + ' is on the Image node ' + n.id + (ids.has(n.id)
                ? ', put in this message by the person just now (their words say whether it is the garment, or a picture of the look, setting or person they want): wire it in where it belongs'
                : ''));
        }
        // The answer, part by part: a big flow comes in parts ("more"), and an answer that
        // broke off is gone on with; each part is read as it comes, and built as it is read
        let j: any = {}, reply = '', steps: string[] = [], test: any = null, left = '', broke = false, note = '';
        for (;;) {
            const prompt = part === 1 ? pgAskInstruction(words, said) : pgAskInstruction(words, [], { part, left, broke });
            const imgs = part === 1 ? pictures : [];
            let full = '', sofar = '', quiet = false;
            taken = 0;
            // Alive or not: while nothing is being built, what Gemini is thinking and how long the
            // part has taken; a part in which it says nothing for too long is stopped
            const pc = new AbortController();
            const relay = () => pc.abort();
            ctl.signal.addEventListener('abort', relay);
            const began = Date.now();
            let life = Date.now(), thought = '';
            const tick = setInterval(() => {
                if (Date.now() - life > pgAskQuietMs()) { quiet = true; pc.abort(); return; }
                if (q.items.length || Date.now() - builtAt < 1200) return;
                pgAskSay((part > 1 ? 'Part ' + part + ' · ' : '') + 'Gemini is ' + (thought ? 'thinking · ' + thought : sofar ? 'writing' : 'thinking') + ' · ' + Math.round((Date.now() - began) / 1000) + ' s');
            }, 1000);
            try {
                full = await pgLlmStream(PG_ASK_MODEL, prompt, imgs, 0.9, 32768, {
                    signal: pc.signal,
                    thought: t => { life = Date.now(); thought = pgAskThought(t); if (!q.b) pgAskSay('Gemini is thinking · ' + thought); },
                    text: all => { life = Date.now(); thought = ''; sofar = all; const p = pgAskPartial(all); if (part === 1 && (p.reply || p.plan.length)) pgAskLive(p.reply, p.plan); feed(p.ops); },
                });
                if (!full.trim()) throw new Error('Nothing came');
            } catch (e) {
                if (ctl.signal.aborted) throw e;
                if (quiet) {
                    // it went quiet: what came is read and built, and it stops there
                    if (part === 1 && !taken) throw new Error('Gemini said nothing for ' + Math.round(pgAskQuietMs() / 1000) + ' seconds');
                    full = sofar;
                } else if (taken) full = sofar;   // it broke off while it was being built: gone on with below
                else {
                    // the answer could not come as it is written: it comes whole, and is built the same way
                    try { full = await pgLlm(PG_ASK_MODEL, prompt, imgs, 0.9, 32768, true); }
                    catch (e2) {
                        if (part === 1) throw e2;
                        note = 'It stopped in part ' + part + ' (' + pgErrorText(e2) + '). What came before is built; ask it to go on.';
                        break;
                    }
                    const p = pgAskPartial(full);
                    if (part === 1 && (p.reply || p.plan.length)) pgAskLive(p.reply, p.plan);
                }
            } finally {
                clearInterval(tick);
                ctl.signal.removeEventListener('abort', relay);
            }
            const read = pgAskRead(full);
            j = read.j;
            feed(Array.isArray(j.ops) ? j.ops : []);
            if (!reply && typeof j.reply === 'string' && j.reply.trim()) reply = j.reply;
            if (!steps.length && Array.isArray(j.plan)) steps = j.plan;
            if (j.test && typeof j.test === 'object') test = j.test;
            if (quiet) { note = 'Part ' + part + ' went quiet for ' + Math.round(pgAskQuietMs() / 1000) + ' seconds and was stopped. What came is built; ask it to go on.'; break; }
            const more = j.more === true || read.cut;
            if (!more || pgAsk.stopping) break;
            // nothing came in a part: going on would only ask again
            if (!(Array.isArray(j.ops) && j.ops.length)) { note = 'It stopped in part ' + part + ' with nothing more to add. Ask it to go on if something is missing.'; break; }
            if (part >= PG_ASK_PARTS || (q.b && q.b.steps >= PG_ASK_STEPS)) { note = 'It stopped after ' + part + ' parts, the most one message builds. Ask it to go on for the rest.'; break; }
            left = String(j.next || '').slice(0, 1500);
            broke = read.cut;
            part++;
            // what is built is on the canvas before it is shown again
            while (q.items.length && !pgAsk.stopping && !q.stop) await pgSleep(40);
            pgAskSay('Gemini goes on · part ' + part + '…');
        }
        q.closed = true;
        await q.done;
        if (pgAsk.stopping) throw new Error('Stopped');
        const r = pgAskEnd(q.b);
        j = { ...j, reply, plan: steps, test };
        if (j.test && typeof j.test === 'object' && typeof j.test.goal === 'string' && j.test.goal.trim()) {
            const named = (Array.isArray(j.test.nodes) ? j.test.nodes : []).map((x: any) => r.refs.get(String(x)) || String(x));
            plan = { goal: j.test.goal.slice(0, 2000), nodes: pgAskTargets(named) };
            if (!plan.nodes.length) plan = undefined;
        }
        const plan2 = (Array.isArray(j.plan) ? j.plan : []).filter((x: any) => typeof x === 'string' && x.trim()).slice(0, 12).map((x: string) => x.slice(0, 200));
        pgAskPost({ role: 'assistant', text: (String(j.reply || (r.summary ? 'Done.' : 'Nothing was changed.')).slice(0, 2000) + (note ? '\n' + note : '')), changes: r.summary || undefined, plan: plan2.length ? plan2 : undefined,
            dropped: r.dropped.length ? r.dropped.slice(0, 4).join('; ') + (r.dropped.length > 4 ? ' and ' + (r.dropped.length - 4) + ' more' : '') : undefined,
            test: plan, words });
    } catch (e) {
        q.stop = true;
        await q.done;
        const r = pgAskEnd(q.b);
        const stopped = ctl.signal.aborted || pgAsk.stopping;
        if (!stopped) (window as any).ffActivity?.error({ tool: 'playground', slot: 'Assistant', message: (e as Error)?.message || String(e), product: pg.wf?.name });
        pgAskPost({ role: 'assistant', text: stopped ? (r.summary ? 'Stopped. What was built so far is on the canvas; Ctrl+Z takes it away.' : 'Stopped: nothing was changed.') : 'That could not be done: ' + pgErrorText(e), changes: r.summary || undefined });
        plan = undefined;
    } finally {
        pgAsk.busy = false;
        pgAsk.abort = null;
        pgAskSay('');
        pgAskPaint();
    }
    if (plan && pgAskTesting() && !pgAsk.stopping) await pgAskTest(plan, words);
}

// Tested at 1K: made, looked at, changed, and made again, round by round
async function pgAskTest(plan: NonNullable<PgAskMsg['test']>, words: string) {
    if (!pg.wf || pgAsk.busy) return;
    if (pg.running) { showToast('Wait for the run to finish, then test.', { type: 'warn', title: 'Running' }); return; }
    const targets = pgAskTargets(plan.nodes);
    if (!targets.length) { pgAskPost({ role: 'assistant', text: 'There is no Generate to test.' }); return; }
    const need = pgAskMissing(targets);
    if (need.nodes.length) {
        pg.sel = new Set(need.nodes);
        pgSelectPaint();
        pgFit(true);
        const s = need.say.join(', and ');
        pgAskPost({ role: 'assistant', text: 'To test it, ' + s + ' (chosen on the canvas). Then press Test at 1K.', test: plan, words });
        return;
    }
    const budget = pgAskBudget();
    // every Generate it takes, tested at 1K, one picture at a time; their own size comes back after
    const gensOf = () => [...new Set([...targets, ...targets.flatMap(id => pgUpstream(id))])].map(pgNode).filter((n): n is PgNode => !!n && n.type === 'generate');
    const sizes = new Map<string, { size: any; count: any }>();
    const toTest = () => gensOf().forEach(n => {
        if (!sizes.has(n.id)) sizes.set(n.id, { size: n.data.size, count: n.data.count });
        n.data.size = '1k';
        n.data.count = '1';
    });
    const rounds: { total: number; snap: { nodes: PgNode[]; edges: PgEdge[]; groups: PgGroup[] }; shots: PgAskShot[]; round: number; msg?: PgAskMsg }[] = [];
    const scores = new Map<string, number>();
    let todo = [...targets], spent = 0, why = '';
    pgAsk.busy = true;
    pgAsk.stopping = false;
    pgAskPaint();
    try {
        toTest();
        for (let round = 1; round <= PG_ASK_ROUNDS; round++) {
            // what this round costs, about: the pictures made again (and what they need), and a look at them
            const plan2 = pgPlan();
            const remake = new Set([...todo, ...todo.flatMap(id => pgUpstream(id)).filter(id => plan2.has(id))]);
            const cost = pgCostOf(remake).usd + PG_ASK_TEXT_USD;
            if (spent + cost > budget + 1e-9) {
                why = round === 1 ? 'A round costs about ' + pgUsd(cost) + ', more than the ' + pgUsd(budget) + ' set aside: set more aside, and test again.' : 'The money set aside does not reach another round.';
                break;
            }
            pgAskSay('Round ' + round + ': making ' + todo.length + ' picture' + (todo.length > 1 ? 's' : '') + ' at 1K…');
            await pgRun(todo);
            spent += cost - PG_ASK_TEXT_USD;
            if (pgAsk.stopping) { why = 'Stopped.'; break; }
            const failed = todo.map(pgNode).find(n => n && (n.status === 'error' || !n.result));
            if (failed) { why = (failed.message ? 'A picture could not be made: ' + failed.message : 'A picture could not be made.'); break; }
            pgAskSay('Round ' + round + ': Gemini is looking at the pictures…');
            const judged = await pgAskJudge(plan.goal, words, targets, todo);
            spent += PG_ASK_TEXT_USD;
            for (const r of judged.results) if (todo.includes(r.id)) scores.set(r.id, r.score);
            const shots: PgAskShot[] = [];
            for (const id of targets) {
                const n = pgNode(id), r = judged.results.find(x => x.id === id);
                shots.push({ node: id, title: pgAskTitle(id), thumb: n && n.result ? await pgThumb(n.result, 240) : '', score: scores.get(id), good: r?.good, problems: r?.problems });
            }
            const entry = { total: targets.reduce((sum, id) => sum + (scores.get(id) ?? 0), 0), snap: JSON.parse(JSON.stringify({ nodes: pg.wf.nodes, edges: pg.wf.edges, groups: pgGroups() })), shots, round };
            rounds.push(entry);
            todo = targets.filter(id => (scores.get(id) ?? 0) < PG_ASK_GOOD);
            const last = !todo.length || round === PG_ASK_ROUNDS || !judged.ops.length || pgAsk.stopping;
            (entry as typeof rounds[number]).msg = pgAskPost({ role: 'test', text: 'Round ' + round, note: judged.note && !last ? judged.note : undefined, shots, spent, budget });
            if (!todo.length) break;
            if (pgAsk.stopping) { why = 'Stopped.'; break; }
            if (round === PG_ASK_ROUNDS) { why = PG_ASK_ROUNDS + ' rounds made.'; break; }
            if (!judged.ops.length) { why = 'Gemini found nothing more to change.'; break; }
            await pgAskApplyLive(judged.ops, 'Round ' + round + ': changing the flow');
            toTest();
        }
    } catch (e) {
        why = 'The test stopped: ' + pgErrorText(e);
    } finally {
        // the round that did best, its Generates at their own size again
        const best = rounds.reduce<typeof rounds[number] | null>((a, b) => (!a || b.total > a.total ? b : a), null);
        pgRecord();
        if (best && best !== rounds[rounds.length - 1]) {
            pg.wf!.nodes = best.snap.nodes;
            pg.wf!.edges = best.snap.edges;
            pg.wf!.groups = best.snap.groups;
        }
        for (const [id, s] of sizes) {
            const n = pgNode(id);
            if (!n) continue;
            if (s.size === undefined) delete n.data.size; else n.data.size = s.size;
            if (s.count === undefined) delete n.data.count; else n.data.count = s.count;
        }
        pgRenderAll();
        pgSaveSoon();
        pgAsk.busy = false;
        pgAskSay('');
        if (best) {
            const scored = best.shots.map(s => s.title + ' ' + (s.score ?? '–') + '/10').join(', ');
            const allGood = best.shots.every(s => (s.score ?? 0) >= PG_ASK_GOOD);
            if (best.msg) { best.msg.best = true; best.msg.final = targets; }
            pgAskPost({ role: 'assistant', final: targets.filter(id => pgNode(id)), spent, budget,
                text: (allGood ? 'Good' : 'Best so far') + ': round ' + best.round + ' (' + scored + '). ' + (rounds.length > 1 && best !== rounds[rounds.length - 1] ? 'The flow is set back to that round. ' : '')
                    + 'Its pictures are 1K tests; Make at full size makes the final ones.' + (why ? ' ' + why : '') });
        } else pgAskPost({ role: 'assistant', text: why || 'Nothing was tested.', test: plan, words });
        pgAskPaint();
    }
}
// what making these at their own size costs, about: they, and what they need made again
function pgAskCost(ids: string[]): number {
    const live = ids.filter(id => pgNode(id)), plan = pgPlan();
    return pgCostOf(new Set([...live, ...live.flatMap(id => pgUpstream(id)).filter(id => plan.has(id))])).usd;
}
// The pictures made at their own size, as Run would make them
async function pgAskFinal(ids: string[]) {
    const live = ids.filter(id => pgNode(id));
    if (!live.length || pg.running || pgAsk.busy) return;
    await pgRun(live);
}
// One direction kept: the groups of the other directions tested are deleted (Ctrl+Z brings them back)
function pgAskKeepOnly(id: string, ids: string[]) {
    const mine = pgGroupOf(id);
    const others = new Set(ids.filter(x => x !== id).map(x => pgGroupOf(x)).filter((g): g is PgGroup => !!g && g !== mine).flatMap(g => g.nodes));
    if (others.size) { pgDeleteNodes([...others]); pgFit(true); }
}

// ── The panel ──
function pgAskShotsHtml(m: PgAskMsg, keep: boolean): string {
    const shots = m.shots || [];
    if (!shots.length) return '';
    const many = keep && shots.length > 1 && shots.some(s => pgGroupOf(s.node));
    return '<div class="pg-ask-shots">' + shots.map(s => '<figure class="pg-ask-shot' + ((s.score ?? 0) >= PG_ASK_GOOD ? ' is-good' : '') + '">'
        + '<button type="button" class="pg-ask-pic" data-ask-act="show" data-node="' + pgEsc(s.node) + '" title="Show it on the canvas">' + (s.thumb ? '<img src="' + pgEsc(s.thumb) + '" alt="' + pgEsc(s.title) + '">' : '<span>' + pgEsc(s.title) + '</span>') + '</button>'
        + '<figcaption><b>' + (s.score === undefined ? '–' : s.score) + '<small>/10</small></b><span>' + pgEsc(s.title) + '</span></figcaption>'
        + ((s.score ?? 10) < PG_ASK_GOOD && s.problems ? '<small class="pg-ask-why">' + pgEsc(s.problems) + '</small>' : '')
        + (many && pgGroupOf(s.node) ? '<button type="button" class="pg-chip is-small" data-ask-act="keep" data-node="' + pgEsc(s.node) + '" data-msg="' + pgEsc(m.id) + '">Keep only this</button>' : '')
        + '</figure>').join('') + '</div>';
}
function pgAskMsgHtml(m: PgAskMsg, latestTest: string): string {
    if (m.role === 'user') return '<div class="pg-ask-msg is-user">'
        + (m.pics && m.pics.length ? '<div class="pg-ask-msg-pics">' + m.pics.map(t => '<img src="' + pgEsc(t) + '" alt="A picture that went with it">').join('') + '</div>' : '')
        + '<p>' + pgEsc(m.text) + '</p></div>';
    const spent = m.spent !== undefined ? '<p class="pg-ask-spent">About ' + m.spent.toFixed(2) + ' of ' + pgUsd(m.budget || 0) + ' spent</p>' : '';
    if (m.role === 'test') return '<div class="pg-ask-msg is-test' + (m.best ? ' is-best' : '') + '"><p class="pg-ask-round"><b>' + pgEsc(m.text) + '</b>' + (m.best ? '<span class="pg-ask-best">Best</span>' : '')
        + (m.note ? ' · ' + pgEsc(m.note) : '') + '</p>' + pgAskShotsHtml(m, !!m.best && !pgAsk.busy) + spent + '</div>';
    const finalCost = m.final && m.final.length ? pgAskCost(m.final) : 0;
    return '<div class="pg-ask-msg is-assistant">' + pgAskPlanHtml(m.plan) + '<p>' + pgEsc(m.text) + '</p>'
        + (m.changes ? '<p class="pg-ask-changes">' + pgEsc(m.changes) + '</p>' : '')
        + (m.dropped ? '<p class="pg-ask-dropped">Left out: ' + pgEsc(m.dropped) + '</p>' : '')
        + pgAskShotsHtml(m, true) + spent
        + (m.test && m.id === latestTest ? '<div class="pg-ask-acts"><button type="button" class="pg-chip is-small" data-ask-act="test" data-msg="' + pgEsc(m.id) + '"' + (pgAsk.busy ? ' disabled' : '') + '>' + pgIcon('run') + 'Test at 1K</button></div>' : '')
        + (m.final && m.final.some(id => pgNode(id)) ? '<div class="pg-ask-acts"><button type="button" class="pg-chip is-primary is-small" data-ask-act="final" data-msg="' + pgEsc(m.id) + '"' + (pgAsk.busy ? ' disabled' : '') + '>' + pgIcon('generate') + 'Make at full size' + (finalCost ? ' · about ' + pgUsd(finalCost) : '') + '</button></div>' : '')
        + '</div>';
}
function pgAskRender() {
    const list = document.getElementById('pgAskList');
    if (!list || !pg.wf) return;
    const msgs = pgAskMsgs();
    // Test at 1K on the last answer that can be tested, until a test of it has ended
    const at = msgs.map(m => !!m.test).lastIndexOf(true);
    const latestTest = at >= 0 && !msgs.slice(at + 1).some(m => m.final) ? msgs[at].id : '';
    list.innerHTML = msgs.length ? msgs.map(m => pgAskMsgHtml(m, latestTest)).join('')
        : '<div class="pg-ask-empty"><p>Say what to make. Gemini builds the flow on the canvas, and changes it as you ask; with Test at 1K on, it makes the pictures small, looks at them and improves the flow until they are good.</p>'
            + '<div class="pg-ask-ex">' + PG_ASK_EXAMPLES.map((x, i) => '<button type="button" class="pg-chip is-small" data-ask-act="ex" data-i="' + i + '">' + pgEsc(x) + '</button>').join('') + '</div>'
            + '<p class="pg-note">Every change can be undone with Ctrl+Z.</p></div>';
    list.scrollTop = list.scrollHeight;
}
function pgAskPaint() {
    const el = document.getElementById('pgAsk');
    if (!el) return;
    el.classList.toggle('is-busy', pgAsk.busy);
    const send = el.querySelector('[data-ask-act="send"]') as HTMLButtonElement;
    send.innerHTML = pgAsk.busy ? pgIcon('stop') + 'Stop' : pgIcon('send') + 'Send';
    send.setAttribute('aria-label', pgAsk.busy ? 'Stop the assistant' : 'Send');
    const test = el.querySelector('[data-ask-act="testing"]') as HTMLButtonElement;
    test.setAttribute('aria-pressed', String(pgAskTesting()));
    (el.querySelector('[data-ask-budget]') as HTMLSelectElement).value = String(pgAskBudget());
    el.querySelectorAll<HTMLButtonElement>('[data-ask-act="test"], [data-ask-act="final"], [data-ask-act="keep"], [data-ask-act="ex"]').forEach(b => { b.disabled = pgAsk.busy; });
    pgAskPaintPics();
}

// ── Pictures in a message (2026-10-16, Simon: "Burde vi lave så man kan droppe billeder ind?") ──
// Dropped on the panel, pasted in the box or chosen with its picture button; shown over the
// box, and taken out again with ×; four at most. Sent, each goes on the canvas as an Image of
// its own, under what is there, and Gemini sees it and is told which Image it is on, so it
// wires it in where it belongs (the garment, or a picture of the look wanted) and the flow
// can be tested at once.
async function pgAskAddPics(files: File[]) {
    const imgs = files.filter(f => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    if (!imgs.length) { if (files.length) showToast('Only pictures go in a message.', { type: 'warn', title: 'Assistant' }); return; }
    const room = PG_ASK_PICS - pgAsk.pics.length;
    if (imgs.length > room) showToast('Up to ' + PG_ASK_PICS + ' pictures go in one message.', { type: 'warn', title: 'Assistant' });
    for (let f of imgs.slice(0, Math.max(0, room))) {
        try {
            if (typeof (window as any).ensureNotHeic === 'function') f = await (window as any).ensureNotHeic(f);
            const src = await pgBlobToDataUrl(f);
            pgAsk.pics.push({ id: pgId('ap'), src, thumb: await pgThumb(src, 200), name: f.name || 'Picture' });
        } catch { showToast((f.name || 'A picture') + ' could not be read.', { type: 'warn', title: 'Assistant' }); }
    }
    pgAskPaintPics();
}
function pgAskPaintPics() {
    const row = document.getElementById('pgAskPics');
    if (!row) return;
    row.hidden = !pgAsk.pics.length;
    row.innerHTML = pgAsk.pics.map(p => '<figure class="pg-ask-att"><img src="' + pgEsc(p.thumb) + '" alt="' + pgEsc(p.name) + '">'
        + '<button type="button" class="pg-ask-att-x" data-ask-act="unpic" data-id="' + p.id + '" aria-label="Take ' + pgEsc(p.name) + ' out of the message" title="Take it out">' + pgIcon('close') + '</button></figure>').join('');
    const box = document.querySelector('#pgAsk textarea') as HTMLTextAreaElement | null;
    if (box) box.placeholder = pgAsk.pics.length ? 'Say what to make with ' + (pgAsk.pics.length === 1 ? 'the picture' : 'the pictures') + '…' : 'Say what to build or change, or drop pictures here…';
    const add = document.querySelector('#pgAsk [data-ask-act="pic"]') as HTMLButtonElement | null;
    if (add) add.disabled = pgAsk.pics.length >= PG_ASK_PICS;
}
// the pictures of a message onto the canvas, each an Image, under what is there
function pgAskPlacePics(pics: PgAskPic[]): PgNode[] {
    if (!pg.wf || !pics.length) return [];
    pgRecord();
    const old = pg.wf.nodes;
    const x0 = old.length ? Math.min(...old.map(n => n.x)) : 80;
    const y0 = old.length ? Math.max(...old.map(n => n.y + (pgCard(n.id)?.offsetHeight || 300))) + 120 : 80;
    const made = pics.map((p, i) => pgMakeNode('image', Math.round(x0), Math.round(y0 + i * 320), { src: p.src, name: p.name, key: pgId('img') }));
    made.forEach(n => { pg.wf!.nodes.push(n); pg.pop.add(n.id); pgRenderNode(n.id); });
    pgPaintEmpty();
    // in view, where they landed (zoomed out only, as while the assistant builds)
    requestAnimationFrame(() => pgFit(true, undefined, true));
    pgSaveSoon();
    return made;
}
function pgAskSetupPics(el: HTMLElement) {
    const file = el.querySelector('[data-ask-file]') as HTMLInputElement;
    file.addEventListener('change', () => { const fs = [...(file.files || [])]; file.value = ''; pgAskAddPics(fs); });
    let depth = 0;
    const hasFiles = (ev: DragEvent) => !!ev.dataTransfer && [...ev.dataTransfer.types].includes('Files');
    el.addEventListener('dragenter', (ev) => { if (!hasFiles(ev)) return; ev.preventDefault(); depth++; el.classList.add('is-dropping'); });
    el.addEventListener('dragover', (ev) => { if (!hasFiles(ev)) return; ev.preventDefault(); ev.dataTransfer!.dropEffect = 'copy'; });
    el.addEventListener('dragleave', () => { depth = Math.max(0, depth - 1); if (!depth) el.classList.remove('is-dropping'); });
    el.addEventListener('drop', (ev) => {
        depth = 0;
        el.classList.remove('is-dropping');
        if (!hasFiles(ev)) return;
        ev.preventDefault();
        ev.stopPropagation();
        pgAskAddPics([...(ev.dataTransfer?.files || [])]);
    });
    // a picture pasted in the box (a screenshot, a copied picture); text is pasted as text
    el.querySelector('textarea')!.addEventListener('paste', (ev) => {
        const fs = [...((ev as ClipboardEvent).clipboardData?.files || [])].filter(f => f.type.startsWith('image/'));
        if (!fs.length) return;
        ev.preventDefault();
        pgAskAddPics(fs);
    });
}
function pgAskOpen(open: boolean, focus = true) {
    const el = document.getElementById('pgAsk'), btn = document.getElementById('pgAskBtn');
    if (!el || !btn) return;
    if (open) pgLibOpen(false, false);     // they take the same place
    el.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    try { localStorage.setItem(PG_ASK_OPEN_KEY, open ? '1' : '0'); } catch { /* private */ }
    if (open) { pgAskRender(); pgAskPaint(); if (focus) (el.querySelector('textarea') as HTMLTextAreaElement).focus(); }
}
const pgAskHtml = () => '<aside class="pg-ask" id="pgAsk" aria-label="Assistant" hidden>'
    + '<div class="pg-ask-head"><span class="pg-ask-mark" aria-hidden="true">' + pgIcon('generate') + '</span><div class="pg-ask-name"><b>Assistant</b><small>Gemini builds the flow, and tests it at 1K</small></div>'
    + '<button type="button" class="pg-icon" data-ask-act="close" aria-label="Close the assistant" title="Close (Esc)">' + pgIcon('close') + '</button></div>'
    + '<div class="pg-ask-list" id="pgAskList"></div>'
    + '<div class="pg-ask-status" id="pgAskStatus" role="status" hidden><span class="pg-spin" aria-hidden="true"></span><span class="pg-ask-status-text"></span></div>'
    + '<div class="pg-ask-foot"><div class="pg-ask-pics" id="pgAskPics" aria-label="Pictures that go with the message" hidden></div>'
    + '<textarea class="pg-text" rows="3" placeholder="Say what to build or change, or drop pictures here…" aria-label="Message to the assistant" spellcheck="true"></textarea>'
    + '<input type="file" accept="image/*,.heic,.heif" multiple hidden data-ask-file>'
    + '<div class="pg-ask-row"><button type="button" class="pg-icon pg-ask-add" data-ask-act="pic" aria-label="Put pictures in the message" title="Pictures: choose them, or drop or paste them here">' + pgIcon('image') + '</button>'
    + '<button type="button" class="pg-chip is-small" data-ask-act="testing" aria-pressed="true" title="After building, make the pictures at 1K, look at them and improve the flow until they are good">' + pgIcon('check') + 'Test at 1K</button>'
    + '<label class="pg-ask-budget"><span>up to</span><select data-ask-budget aria-label="The most the tests may cost">' + PG_ASK_BUDGETS.map(v => '<option value="' + v + '">' + pgUsd(v) + '</option>').join('') + '</select></label>'
    + '<span class="pg-ask-gap"></span><button type="button" class="pg-chip is-primary" data-ask-act="send" aria-label="Send">' + pgIcon('send') + 'Send</button></div></div>'
    + '</aside>';
// what is clicked or changed in the panel
async function pgAskClick(t: HTMLElement): Promise<boolean> {
    if (t.closest('#pgAskBtn')) { const el = document.getElementById('pgAsk'); pgAskOpen(!!el && el.hidden === true); return true; }
    const a = t.closest('[data-ask-act]') as HTMLElement | null;
    if (!a || !a.closest('#pgAsk')) return !!t.closest('#pgAsk');
    const act = a.dataset.askAct;
    const msg = pgAskMsgs().find(m => m.id === a.dataset.msg);
    if (act === 'close') { pgAskOpen(false); (document.getElementById('pgAskBtn') as HTMLElement | null)?.focus(); }
    else if (act === 'send') { if (pgAsk.busy) { pgAsk.stopping = true; pgAsk.abort?.abort(); if (pg.running) pgStop(); pgAskSay('Stopping…'); } else pgAskSend(); }
    else if (act === 'ex') { const box = document.querySelector('#pgAsk textarea') as HTMLTextAreaElement; box.value = PG_ASK_EXAMPLES[Number(a.dataset.i)] || ''; box.focus(); }
    else if (act === 'pic') (document.querySelector('#pgAsk [data-ask-file]') as HTMLInputElement).click();
    else if (act === 'unpic') { pgAsk.pics = pgAsk.pics.filter(p => p.id !== a.dataset.id); pgAskPaintPics(); (document.querySelector('#pgAsk textarea') as HTMLElement).focus(); }
    else if (act === 'testing') { try { localStorage.setItem(PG_ASK_TEST_KEY, pgAskTesting() ? '0' : '1'); } catch { /* private */ } pgAskPaint(); }
    else if (act === 'test' && msg && msg.test) await pgAskTest(msg.test, msg.words || '');
    else if (act === 'final' && msg && msg.final) await pgAskFinal(msg.final);
    else if (act === 'keep' && msg && msg.final) { pgAskKeepOnly(a.dataset.node!, msg.final); pgAskRender(); pgAskPaint(); }
    else if (act === 'show') {
        const id = a.dataset.node!;
        if (pgNode(id)) { pg.sel = new Set([id]); pg.edgeSel = ''; pgSelectPaint(); pgFit(true, [id]); }
    }
    return true;
}

// For the tests and the console.
(window as any).pg = pg;
(window as any).PG_TYPES_FOR_TESTS = PG_TYPES;
Object.assign(window as any, { pgOutDef, pgFits, pgInKind });
(window as any).pgRun = pgRun;

// ── Prompts: templates, workflows to start from, and the helpers' instructions ──
// Written as Google's and OpenAI's own prompting guides advise (2025-2026): start
// with what to do and what the picture is for; say what each image is (Image 1,
// Image 2 …) and what to take from it; describe in sentences, not keywords; name
// light, lens and finish; say what to keep with a "Preserve exactly" list; say
// what to do rather than what to avoid. The format is set on Generate, not in the
// text. {{fields}} are filled in on the node.
interface PgPromptTemplate { id: string; name: string; uses: string; aspect?: string; text: string; }
const PG_PRESERVE = 'Preserve exactly: the garment\'s colour, fabric texture and weave, pattern placement, cut and length, collar, seams, stitching, buttons and zips, and every logo, label and print with identical lettering.';
const PG_PROMPT_TEMPLATES: PgPromptTemplate[] = [
    { id: 'packshot', name: 'Packshot on white', uses: 'Image 1: the garment', aspect: '4:5',
        text: 'Edit Image 1, a photo of a garment, into a clean e-commerce packshot for a fashion web shop product page.\n'
            + 'Replace only the background with a seamless, pure white studio background (#FFFFFF) that runs edge to edge and is equally bright in every corner. Keep the garment as photographed: the same shape, angle, position and scale in the frame. Remove any hanger, clips, pins, tags or stand holding it and rebuild the fabric behind them to match. The fabric is smooth and freshly pressed, with only a few soft, natural folds.\n'
            + 'Lighting: soft, even, neutral studio light, with one faint, soft contact shadow directly beneath the garment.\n'
            + PG_PRESERVE + ' The only text in the picture is the text already on the garment.' },
    { id: 'ghost', name: 'Ghost mannequin', uses: 'Image 1: the garment worn or on a mannequin; Image 2 (if you have it): the inside of the back neck', aspect: '4:5',
        text: 'Create an invisible-mannequin (ghost mannequin) product photo of the garment in Image 1 for a fashion web shop. Image 2, if there is one, shows the inside of the back neckline: use it for the inner collar and neck label.\n'
            + 'Show the garment from the front, centred, as if worn by an invisible body: a natural, filled three-dimensional shape, sleeves hanging relaxed, and the inside of the back neckline visible through the neck opening. The garment stands alone; the picture holds no person, mannequin, skin or hair. The fabric is smooth and freshly steamed, with only the natural drape folds of the cut.\n'
            + 'Background: seamless pure white (#FFFFFF). Lighting: soft, even high-key studio light, neutral white balance, a faint soft shadow under the hem. Shot straight on at chest height with a 100 mm lens, sharp throughout.\n'
            + PG_PRESERVE },
    { id: 'flatlay', name: 'Flat lay from above', uses: 'Image 1: the garment', aspect: '4:5',
        text: 'Create a top-down flat-lay product photo of the garment in Image 1 for a fashion web shop.\n'
            + 'The garment lies flat on a seamless pure white surface, photographed from directly above, centred with even margins, arranged the way a catalogue stylist lays out this kind of garment: body straight and symmetrical, sleeves or legs aligned, collar and hem lying flat, the fabric smooth and freshly pressed.\n'
            + 'Lighting: soft, even, diffused light from above that keeps the fabric texture clearly visible, with a very soft natural shadow at the edges.\n'
            + PG_PRESERVE },
    { id: 'on-model', name: 'On a model', uses: 'Image 1: the garment; Image 2: the model', aspect: '3:4',
        text: 'Create a professional e-commerce fashion photo for a web shop product page. Image 1 shows the garment. Image 2 shows the model.\n'
            + 'Dress the model from Image 2 in the garment from Image 1, worn the way it is designed to be worn, with a natural fit and realistic drape at the shoulders, elbows and waist; the fabric smooth and pressed, with only a few soft folds. Full-body shot from head to shoes, the model standing in a relaxed, natural catalogue pose facing the camera, arms loose so the front of the garment is fully visible. The rest of the outfit is plain, neutral basics.\n'
            + 'Background: a seamless light-grey studio sweep. Lighting: a large soft key light from the front left with gentle fill and soft natural shadows. Shot at mid-body height with an 85 mm lens, sharp on the garment.\n'
            + 'Preserve exactly: from Image 2, the model\'s face, facial features, skin tone, hair and body shape; from Image 1, the garment\'s colour, fabric texture, pattern placement, length, buttons, seams, and every logo, label and print.' },
    { id: 'try-on', name: 'Dress a model photo', uses: 'Image 1: the photo of the person; Image 2: the garment',
        text: 'Edit Image 1 so the person wears the garment from Image 2. Image 1 is the photo to keep; Image 2 is a product photo of the garment.\n'
            + 'Replace only {{the garment to replace}} with the garment from Image 2, fitted naturally to the person\'s pose and body, with realistic drape and folds, and with hair, arms and hands staying in front of it exactly where they are now. Match the light, shadows and colour temperature of Image 1, so the garment looks photographed in the same shot.\n'
            + 'Keep everything else in Image 1 exactly the same: face, skin tone, hair, body, pose, hands, the other clothes, background, camera angle and framing.\n'
            + 'Preserve exactly from Image 2: the colour, fabric texture, pattern placement, length, collar, buttons, seams, and every logo, label and print.' },
    { id: 'model-swap', name: 'Another model, same clothes', uses: 'Image 1: the photo; Image 2: the new model',
        text: 'Edit Image 1 by replacing the person with the model from Image 2. Image 1 is the photo with the clothes to keep; Image 2 only shows who the new model is.\n'
            + 'The new model wears exactly the same clothes as in Image 1, in the same pose, position, scale and camera angle, each garment fitting their body naturally. The background, light, shadows, colours and framing of Image 1 stay as they are.\n'
            + 'Preserve exactly: from Image 2, the model\'s face, facial features, skin tone, hair and body proportions; from Image 1, every garment\'s colour, fabric texture, pattern placement, fit, buttons, seams, and every logo, label and print.' },
    { id: 'back', name: 'Back view to match', uses: 'Image 1: the approved front; Image 2: a real photo of the back', aspect: '4:5',
        text: 'Create the back view that matches Image 1, as the next picture in the same product gallery. Image 1 is the approved front view; Image 2 shows the real back of the garment.\n'
            + 'Show the same garment in the same presentation as Image 1, turned to show its back, with the same framing, scale, background, light and camera height as Image 1.\n'
            + 'Use Image 2 for every detail on the back: seams, yoke, vents, pleats, pockets and prints. The colour, fabric texture and fit stay identical to Image 1, the front.' },
    { id: 'background', name: 'New background', uses: 'Image 1: the photo; Image 2 (if you have it): the place',
        text: 'Edit Image 1: change only the background to {{the new background}}. Image 2, if there is one, shows the place to use.\n'
            + 'The person and everything they wear stay exactly as they are: the same pose, position, scale, hair and edges. Adjust the light on them gently so its direction, softness and warmth match the new background, keeping the garment\'s colours true, with natural contact shadows where they meet the ground. Keep the camera angle, perspective and framing of Image 1, the background softly out of focus.\n'
            + 'Preserve exactly: face, skin tone, body, pose, and every garment\'s colour, fabric texture, pattern, logo, label and print.' },
    { id: 'white', name: 'Background to pure white', uses: 'Image 1: the photo',
        text: 'Edit Image 1: replace only the background with seamless, pure white (#FFFFFF) that is equally bright in every corner, with one faint, soft contact shadow under the garment.\n'
            + 'Keep the garment and its edges exactly as they are: the same shape, folds, position and scale, nothing cropped or added.\n'
            + PG_PRESERVE },
    { id: 'recolour', name: 'Recolour', uses: 'Image 1: the garment; Image 2 (if you have it): a swatch of the colour',
        text: 'Edit Image 1: recolour only {{the garment}} to {{the colour}}, matching the swatch in Image 2 if there is one.\n'
            + 'Apply the new colour as if the fabric had been dyed it: every shadow, highlight, fold, knit or weave texture, stitch and sheen stays exactly where it is, so lit areas become lighter tints and shadows deeper shades of the same colour. Trims, buttons, zips, labels, prints and logos keep their own colours.\n'
            + 'Everything else in the picture stays exactly the same: background, skin, hair, other clothes, light and framing.' },
    { id: 'detail', name: 'Detail close-up', uses: 'Image 1: a sharp photo where the detail shows', aspect: '1:1',
        text: 'Create a macro detail photo of the {{detail}} of the garment in Image 1, for the detail gallery of a fashion product page.\n'
            + 'The {{detail}} fills most of the frame with a little of the fabric around it, shot with a 100 mm macro lens at a slight angle, crisp on the detail with a gentle fall-off in depth of field. Soft raking side light shows the real texture of the knit, weave or grain, in true-to-life colour. The fabric is clean and free of lint.\n'
            + 'Draw the detail exactly as it is in Image 1: the same shape, material, colour, size against the fabric, stitch pattern, and any lettering on it, letter for letter.' },
    { id: 'campaign', name: 'Campaign scene', uses: 'Image 1: the garment; Image 2: the model; Image 3 (if you have it): the mood', aspect: '4:5',
        text: 'Create a lifestyle campaign photo for a Scandinavian fashion brand. Image 1 shows the garment, Image 2 the model, and Image 3, if there is one, the mood and light to match.\n'
            + 'The model from Image 2 wears the garment from Image 1 in {{the scene}}. A medium-full shot with the model in the left third, walking towards the camera and looking slightly past it, the garment clearly visible and sharp. The place is quiet and uncluttered; the model is the only person in it.\n'
            + 'Natural light that fits the scene, shot on a 50 mm lens at f/2.8 with a gently blurred background, a subtle film grain and natural, slightly muted colours.\n'
            + 'Preserve exactly: from Image 2, the model\'s face, facial features, skin tone, hair and body; from Image 1, the garment\'s colour, fabric texture, pattern placement, fit, details, and every logo, label and print.' },
    { id: 'house-style', name: 'Match the house style', uses: 'Image 1: the new photo; Image 2: an approved picture from the shop',
        text: 'Restyle Image 1 so it matches the house style of Image 2 and sits naturally next to it in the same product grid. Image 1 holds the product to keep; Image 2 is only a style reference, and its product, model and text stay out of the result.\n'
            + 'Match from Image 2: the background colour and gradient, the direction and softness of the light, the shadows, the white balance and colours, the camera height and crop, and the product\'s size and place in the frame.\n'
            + 'Preserve exactly from Image 1: the garment (and the model, if there is one), with its colour, fabric texture, pattern placement, cut, details, and every logo, label and print.' },
    { id: 'press', name: 'Smooth out creases', uses: 'Image 1: the photo',
        text: 'Retouch Image 1 the way a high-end e-commerce retoucher would, so the garment looks freshly steamed and pressed.\n'
            + 'Smooth out creases, crumples and packing folds, keeping the natural drape folds made by the body and the cut, and every seam and construction line. The fabric keeps its real texture: knit stitches, weave and grain stay crisp. Remove lint, dust, loose threads, stray hairs, pins, clips and price tags.\n'
            + 'Keep everything else exactly the same: the garment\'s colour, pattern placement, shape, fit and every logo, label and print; the model\'s face, skin, hair and pose; the background, light and framing.' },
    { id: 'reframe', name: 'Another format', uses: 'Image 1: the approved picture (set the new format on Generate)',
        text: 'Extend the scene of Image 1 to fill the new frame.\n'
            + 'Keep what is in Image 1 as it is: the person, the garment, their size and the light. Fill the new area with background that carries the original on seamlessly: the same surface, colours, fall-off of light, grain and perspective, with no visible join, and calm, open space around the subject.\n'
            + 'Preserve exactly: face, body, pose, and every garment\'s colour, fabric texture, pattern, logo, label and print.' },
    { id: 'outfit', name: 'Complete the look', uses: 'Image 1, 2, 3 …: one product each (up to 6)', aspect: '4:5',
        text: 'Create a top-down "complete the look" flat-lay photo that brings together the products from all the images; each image shows one product.\n'
            + 'Arrange them as a styled outfit seen from directly above: the top in the centre, trousers or skirt below it, shoes and accessories around them, every item fully visible with its key details uncovered. Each product appears exactly once, at its true size next to the others. Surface: warm light-grey plaster. Soft, diffused daylight from the upper left with gentle, soft shadows.\n'
            + 'Preserve exactly, for each product: its colour, fabric texture, pattern placement, proportions, and every logo, label and print as in its own image.' },
];

// what Describe asks for, by what it is to describe
const PG_DESCRIBE: Record<string, string> = {
    garment: 'Describe the garment in this photo so that an image model can draw it exactly. Write one paragraph of plain English, with no list, heading or preamble. Say: the kind of garment and its fit; its exact colour, named precisely (for example "olive melange green"); the fabric, its texture and weave; the collar or neckline, sleeves, cuffs, hem and length; closures, buttons, pockets, seams and stitching; and any print, pattern, logo or label, with exactly where it sits and its text read letter for letter in quotation marks. Describe only what you can see. Do not guess the brand, and do not describe the person or the background.',
    photo: 'Describe this photo so that an image model could make a new photo the same way. Write one paragraph of plain English, with no list, heading or preamble. Say: the setting and background; the light (its direction, softness, colour and time of day); the camera (how much of the body is in the frame, the angle, the lens and the depth of field); the composition; the colour palette and the mood. Mention the clothes only briefly. Do not describe what the people look like, only how they stand and where they are in the frame.',
    style: 'Describe the visual style and mood of this picture, for a new fashion photo in the same style. Write one short paragraph of plain English, with no list, heading or preamble: the light, the colour palette, the kind of place, the styling, the texture and grain of the photo, and the camera. Do not describe the particular people, brands, products or text in it.',
};
// what Improve prompt asks for: the structure the guides give
function pgEnhanceInstruction(idea: string, images: number): string {
    return 'You write prompts for an image model (Gemini 3 Pro Image, "Nano Banana Pro") for the photo team of a Scandinavian fashion web shop. Turn the idea below into one prompt, ready to use.\n\n'
        + 'Write it in this order, as short paragraphs of clear, natural English sentences (no keyword lists), 80 to 200 words in all:\n'
        + '1. What to do and what the picture is for, starting with a verb (Create, Edit, Replace …), for example "Create a professional e-commerce fashion photo for a web shop product page."\n'
        + (images
            ? '2. What each image is: ' + images + ' image' + (images > 1 ? 's go' : ' goes') + ' to the model with the prompt, in this order: ' + Array.from({ length: images }, (_, i) => 'Image ' + (i + 1)).join(', ') + ' (attached here in the same order). Look at them, and write for example "Image 1 shows the garment. Image 2 shows the model."\n'
            : '2. No images go with the prompt: describe the garment fully in words, naming its fabric and colour precisely.\n')
        + '3. The picture: the garment and how it is worn or laid, the pose, the setting and background.\n'
        + '4. The light, and the camera (shot size, angle, lens).\n'
        + '5. A last line starting "Preserve exactly:" that lists what must stay as it is: the garment\'s colour, fabric texture, pattern placement, details and every logo, label and print; and a given person\'s face, skin tone, hair and body.\n\n'
        + 'Say what to do rather than what to avoid. Garments look smooth and freshly pressed, with only a few soft, natural folds, unless the idea says otherwise. Leave the format out (it is set elsewhere). Keep any {{fields}} in the idea just as they are. Answer with the prompt only: no title, no quotation marks round it, no explanation.\n\n'
        + 'The idea:\n' + idea;
}

